// ============================================
// OFFICEFORGED GRID — Spreadsheet Module
// Powered by Univer (Apache 2.0)
// Full Excel-class spreadsheet engine
// ============================================

export class GridModule {
  constructor(cardEngine) {
    this.cardEngine = cardEngine;
    this.univerAPI = null;
    this.initialized = false;
    this._autoSaveTimer = null;
    this._lastAutoSaveHash = '';
  }

  show() {
    if (!this.initialized) {
      this.initialized = true;
      // Delay init to next frame so the container has layout dimensions
      requestAnimationFrame(() => this._doInit());
    }
  }

  _doInit() {
    try {
      const container = document.getElementById('grid-spreadsheet');
      if (!container) { console.error('Grid: #grid-spreadsheet not found'); return; }

      if (!window.UniverPresets || !window.UniverPresetSheetsCore) {
        console.error('Grid: Univer CDN not loaded');
        return;
      }

      const { createUniver, defaultTheme, LocaleType } = window.UniverPresets;
      if (typeof createUniver !== 'function') { console.error('Grid: createUniver missing'); return; }

      const sheetsCorePresetFn = window.UniverPresetSheetsCore.UniverSheetsCorePreset;
      if (typeof sheetsCorePresetFn !== 'function') { console.error('Grid: sheets-core preset missing'); return; }

      // Build presets array
      const presets = [
        sheetsCorePresetFn({ container: 'grid-spreadsheet', header: true, footer: true }),
      ];

      // Optional: filter preset
      if (window.UniverPresetSheetsFilter && window.UniverPresetSheetsFilter.UniverSheetsFilterPreset) {
        presets.push(window.UniverPresetSheetsFilter.UniverSheetsFilterPreset());
      }

      // Optional: sort preset
      if (window.UniverPresetSheetsSort && window.UniverPresetSheetsSort.UniverSheetsSortPreset) {
        presets.push(window.UniverPresetSheetsSort.UniverSheetsSortPreset());
      }

      // Conditional formatting (color scales, data bars, icon sets)
      if (window.UniverPresetSheetsConditionalFormatting && window.UniverPresetSheetsConditionalFormatting.UniverSheetsConditionalFormattingPreset) {
        presets.push(window.UniverPresetSheetsConditionalFormatting.UniverSheetsConditionalFormattingPreset());
      }

      // Data validation (dropdowns, number/date constraints)
      if (window.UniverPresetSheetsDataValidation && window.UniverPresetSheetsDataValidation.UniverSheetsDataValidationPreset) {
        presets.push(window.UniverPresetSheetsDataValidation.UniverSheetsDataValidationPreset());
      }

      // Find & Replace
      if (window.UniverPresetSheetsFindReplace && window.UniverPresetSheetsFindReplace.UniverSheetsFindReplacePreset) {
        presets.push(window.UniverPresetSheetsFindReplace.UniverSheetsFindReplacePreset());
      }

      // Hyperlinks
      if (window.UniverPresetSheetsHyperLink && window.UniverPresetSheetsHyperLink.UniverSheetsHyperLinkPreset) {
        presets.push(window.UniverPresetSheetsHyperLink.UniverSheetsHyperLinkPreset());
      }

      // Threaded comments
      if (window.UniverPresetSheetsThreadComment && window.UniverPresetSheetsThreadComment.UniverSheetsThreadCommentPreset) {
        presets.push(window.UniverPresetSheetsThreadComment.UniverSheetsThreadCommentPreset());
      }

      // Build locales
      const locales = {};
      if (window.UniverPresetSheetsCoreEnUS) {
        Object.assign(locales, window.UniverPresetSheetsCoreEnUS);
      }

      // Create Univer instance
      const opts = {
        locale: LocaleType ? LocaleType.EN_US : 'enUS',
        locales: LocaleType ? { [LocaleType.EN_US]: locales } : { enUS: locales },
        presets,
      };
      if (defaultTheme) opts.theme = defaultTheme;

      const { univerAPI } = createUniver(opts);
      this.univerAPI = univerAPI;

      univerAPI.createWorkbook({ name: 'Untitled Spreadsheet' });

      // Toolbar buttons
      this._bindBtn('grid-new-btn', () => this._newSpreadsheet());
      this._bindBtn('grid-save-btn', () => this._exportXLSX());
      this._bindBtn('grid-export-csv', () => this._exportCSV());
      this._bindBtn('grid-import-csv', () => this._importCSV());
      this._bindBtn('grid-export-xlsx', () => this._exportXLSX());
      this._bindBtn('grid-import-xlsx', () => this._importXLSX());
      this._bindBtn('grid-insert-chart', () => this._insertChart());
      this._bindBtn('grid-pivot', () => this._openPivotDialog());
      this._bindBtn('grid-sql', () => this._openSQLDialog());
      this._bindBtn('grid-ai', () => this._openAIDialog());
      this._bindBtn('grid-search', () => this._openSearch());
      this._bindBtn('grid-export-docx', () => this._exportWord());
      this._bindBtn('grid-save-local', () => this._saveLocal());
      this._bindBtn('grid-load-local', () => this._loadLocal());
      this._bindBtn('grid-print', () => this._printSheet());
      this._bindBtn('grid-transform', () => this._openTransformDialog());
      this._bindBtn('grid-adv-chart', () => this._openAdvancedChartDialog());
      this._bindBtn('grid-macro', () => this._openMacroEditor());
      this._bindBtn('grid-finance', () => this._openFinanceCalc());
      this._bindBtn('grid-cluster', () => this._openClusterDialog());
      this._bindBtn('grid-sparkline', () => this._insertSparklines());
      this._bindBtn('grid-goalseek', () => this._openGoalSeek());
      this._bindBtn('grid-scenario', () => this._openScenarioManager());
      this._bindBtn('grid-templates', () => this._openTemplates());
      this._bindBtn('grid-profile', () => this._openDataProfile());
      this._bindBtn('grid-whatif', () => this._openWhatIfTable());
      this._bindBtn('grid-quickanalysis', () => this._openQuickAnalysis());
      this._bindBtn('grid-numformat', () => this._openNumFormatBuilder());
      this._bindBtn('grid-dashboard', () => this._openDashboard());
      this._bindBtn('grid-import-data', () => this._openDataImport());
      this._bindBtn('grid-condfmt', () => this._openCondFormatting());
      this._bindBtn('grid-form', () => this._openFormBuilder());
      this._bindBtn('grid-gantt', () => this._openGanttChart());
      this._bindBtn('grid-precision', () => this._togglePrecisionMode());
      this._bindBtn('grid-protect', () => this._openProtection());
      this._bindBtn('grid-freeze', () => this._openFreezePanes());
      this._bindBtn('grid-named-ranges', () => this._openNamedRanges());
      this._bindBtn('grid-create-card-btn', () => this._onCreateCardBtn());
      this._bindBtn('grid-data-validation', () => this._openDataValidation());
      this._bindBtn('grid-comments', () => this._openComments());
      this._bindBtn('grid-regex', () => this._openRegexFindReplace());
      this._bindBtn('grid-autofill', () => this._openAutoFill());
      this._bindBtn('grid-audit', () => this._openAuditTrail());
      this._bindBtn('grid-sheets', () => this._openSheetManager());
      this._bindBtn('grid-merge', () => this._openMergeCells());
      this._bindBtn('grid-hiderows', () => this._openHideRowsCols());
      this._bindBtn('grid-transpose', () => this._transposeData());
      this._bindBtn('grid-dedup', () => this._openRemoveDuplicates());
      this._bindBtn('grid-text2col', () => this._openTextToColumns());
      this._bindBtn('grid-cellstyles', () => this._openCellStyles());
      this._bindBtn('grid-open-any', () => this._openAnyFormat());
      this._bindBtn('grid-formula-explorer', () => this._openFormulaExplorer());
      this._bindBtn('grid-solver', () => this._openSolver());
      this._bindBtn('grid-matheval', () => this._openMathEval());
      this._bindBtn('grid-datecalc', () => this._openDateCalc());
      this._bindBtn('grid-currencyfmt', () => this._openCurrencyFormatter());
      this._bindBtn('grid-export-ods', () => this._exportODS());
      this._bindBtn('grid-copy-markdown', () => this._copyMarkdown());
      this._bindBtn('grid-scripting', () => this._openScriptConsole());
      this._bindBtn('grid-network-graph', () => this._openNetworkGraph());
      this._bindBtn('grid-geo-map', () => this._openGeoMap());
      this._bindBtn('grid-ml-predict', () => this._openMLPredict());
      this._bindBtn('grid-collab', () => this._toggleCollab());

      // Collaboration state
      this._collabActive = false;
      this._collabProvider = null;
      this._ydoc = null;
      this._collabSyncing = false;

      // Precision mode state
      this._precisionMode = false;

      // Activate Day.js plugins if available
      if (window.dayjs) {
        ['dayjs_plugin_quarterOfYear', 'dayjs_plugin_weekOfYear', 'dayjs_plugin_isBetween'].forEach(p => {
          if (window[p]) window.dayjs.extend(window[p]);
        });
      }

      // Init audit log
      if (!this._auditLog) this._auditLog = [];

      // Toolbar grouped dropdown menus — open/close
      this._initToolbarMenus();

      // Selection stats footer
      this._initSelectionStats();

      // Keyboard shortcuts (hotkeys-js)
      this._initKeyboardShortcuts();

      // Build search index
      this._searchIndex = null;

      // Inject "Create Card" into Univer's native right-click menu
      this._injectContextMenuItem();

      // Column autocomplete while typing + @Card resolution
      this._initColumnAutocomplete();
      this._initCardResolution();

      // Create card for card engine
      this.sheetCard = this.cardEngine.createCard('table', {
        name: 'Sheet1',
        preview: 'Grid spreadsheet',
        data: {}
      });

      // Auto-recovery: restore from autosave if available
      this._recoverAutosave();

      // Start periodic autosave (every 2 seconds)
      this._autoSaveTimer = setInterval(() => this._autosave(), 2000);

      // Save before page unload (back button, refresh, close)
      window.addEventListener('beforeunload', (e) => {
        this._autosaveSync();
        e.preventDefault();
        e.returnValue = '';
      });

    } catch (err) {
      console.error('Grid: init error:', err);
      this.initialized = false; // allow retry
    }
  }

  // ── Save-in-place helper (File System Access API) ──
  // Uses showSaveFilePicker so users can overwrite existing files.
  // Remembers the file handle — subsequent saves go to same file without dialog.
  async _saveFileInPlace(blob, suggestedName, fileTypes, formatKey) {
    try {
      // If we already have a handle for this format, write directly
      if (this._fileHandles && this._fileHandles[formatKey]) {
        try {
          const writable = await this._fileHandles[formatKey].createWritable();
          await writable.write(blob);
          await writable.close();
          return suggestedName;
        } catch (_) {
          // Handle may be stale, fall through to picker
          delete this._fileHandles[formatKey];
        }
      }

      // Try File System Access API (Chrome/Edge)
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: fileTypes,
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        // Remember handle for future saves
        if (!this._fileHandles) this._fileHandles = {};
        this._fileHandles[formatKey] = handle;
        return handle.name;
      }
    } catch (e) {
      if (e.name === 'AbortError') return null; // user cancelled
    }

    // Fallback: standard download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestedName;
    a.click();
    URL.revokeObjectURL(url);
    return suggestedName;
  }

  // ── Custom Dialog System (replaces browser confirm/alert) ──
  _showDialog({ title, message, buttons }) {
    return new Promise((resolve) => {
      const old = document.getElementById('ofg-dialog-overlay');
      if (old) old.remove();

      const overlay = document.createElement('div');
      overlay.id = 'ofg-dialog-overlay';
      overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:999999;display:flex;align-items:center;justify-content:center;';

      const dialog = document.createElement('div');
      dialog.style.cssText = 'background:#fff;border-radius:12px;box-shadow:0 12px 48px rgba(0,0,0,0.25);padding:28px 32px;min-width:380px;max-width:480px;font-family:Arial,sans-serif;';

      const titleEl = document.createElement('div');
      titleEl.style.cssText = 'font-size:17px;font-weight:700;color:#1a1a1a;margin-bottom:12px;';
      titleEl.textContent = title;

      const msgEl = document.createElement('div');
      msgEl.style.cssText = 'font-size:14px;color:#555;line-height:1.5;margin-bottom:24px;';
      msgEl.textContent = message;

      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'display:flex;justify-content:flex-end;gap:10px;';

      buttons.forEach(btn => {
        const b = document.createElement('button');
        b.textContent = btn.label;
        const isPrimary = btn.primary;
        const isDanger = btn.danger;
        let bg = '#f3f4f6'; let color = '#333'; let border = '1px solid #d1d5db';
        if (isPrimary) { bg = '#2563eb'; color = '#fff'; border = 'none'; }
        if (isDanger) { bg = '#dc2626'; color = '#fff'; border = 'none'; }
        b.style.cssText = `padding:9px 20px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;background:${bg};color:${color};border:${border};`;
        b.addEventListener('mouseenter', () => { b.style.opacity = '0.85'; });
        b.addEventListener('mouseleave', () => { b.style.opacity = '1'; });
        b.addEventListener('click', () => { overlay.remove(); resolve(btn.value); });
        btnRow.appendChild(b);
      });

      dialog.appendChild(titleEl);
      dialog.appendChild(msgEl);
      dialog.appendChild(btnRow);
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      // Close on Escape
      const escHandler = (e) => { if (e.key === 'Escape') { overlay.remove(); resolve('cancel'); document.removeEventListener('keydown', escHandler); } };
      document.addEventListener('keydown', escHandler);
    });
  }

  async _newSpreadsheet() {
    if (!this.univerAPI) return;
    const result = await this._showDialog({
      title: 'New Spreadsheet',
      message: 'Create a new blank spreadsheet? You can save your current work first.',
      buttons: [
        { label: 'Cancel', value: 'cancel' },
        { label: 'Save First', value: 'save', primary: true },
        { label: 'Don\'t Save', value: 'discard', danger: true },
      ]
    });

    if (result === 'cancel') return;
    if (result === 'save') await this._exportXLSX();

    const titleEl = document.getElementById('grid-title');
    if (titleEl) titleEl.textContent = 'Untitled Spreadsheet';
    this.univerAPI.createWorkbook({ name: 'Untitled Spreadsheet' });
    if (this._fileHandles) this._fileHandles = {};
    this._lastAutoSaveHash = '';
    if (window.localforage) window.localforage.removeItem('ofg_grid_autosave');
    const ind = document.getElementById('grid-save-indicator');
    if (ind) { ind.textContent = '● New'; ind.style.color = '#60a5fa'; }
  }

  _exportCSV() {
    if (!this.univerAPI) return;

    try {
      const workbook = this.univerAPI.getActiveWorkbook();
      if (!workbook) return;
      const sheet = workbook.getActiveSheet();
      if (!sheet) return;

      const range = sheet.getDataRange();
      if (!range) return;

      const values = range.getValues();
      if (!values || !values.length) return;

      const lines = values.map(row =>
        row.map(cell => {
          const s = String(cell ?? '');
          return s.includes(',') || s.includes('"') || s.includes('\n')
            ? '"' + s.replace(/"/g, '""') + '"'
            : s;
        }).join(',')
      );

      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const filename = (document.getElementById('grid-title')?.textContent || 'spreadsheet') + '.csv';
      this._saveFileInPlace(blob, filename, [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }], 'csv').then(name => {
        if (name && this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Export CSV', detail: name });
      });
    } catch (e) {
      console.error('CSV export error:', e);
    }
  }

  _bindBtn(id, fn) {
    const el = document.getElementById(id);
    if (el) el.addEventListener('click', (e) => {
      // Close any open toolbar menu when a menu item is clicked
      const menu = el.closest('.grid-menu');
      if (menu) menu.classList.remove('open');
      fn(e);
    });
  }

  // ── Toolbar grouped dropdown menus ──
  _initToolbarMenus() {
    const menus = document.querySelectorAll('#grid-title-right .grid-menu');
    menus.forEach(menu => {
      const trigger = menu.querySelector('.grid-menu-trigger');
      if (!trigger) return;
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = menu.classList.contains('open');
        // Close all menus first
        menus.forEach(m => m.classList.remove('open'));
        if (!wasOpen) menu.classList.add('open');
      });
    });
    // Close menus when clicking outside
    document.addEventListener('click', () => {
      menus.forEach(m => m.classList.remove('open'));
    });
    // Prevent dropdown clicks from closing the dropdown prematurely
    document.querySelectorAll('#grid-title-right .grid-menu-dropdown').forEach(dd => {
      dd.addEventListener('click', (e) => e.stopPropagation());
    });
  }

  // ── XLSX Export (ExcelJS, MIT) ──
  async _exportXLSX() {
    if (!this.univerAPI || !window.ExcelJS) return;
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return;
      const sheet = wb.getActiveSheet();
      if (!sheet) return;
      const values = sheet.getDataRange()?.getValues();
      if (!values || !values.length) return;

      const exWb = new window.ExcelJS.Workbook();
      const exWs = exWb.addWorksheet(sheet.getSheetName() || 'Sheet1');
      values.forEach(row => exWs.addRow(row.map(c => c ?? '')));

      // Auto-width columns
      exWs.columns.forEach(col => {
        let max = 10;
        col.eachCell(cell => {
          const len = String(cell.value ?? '').length;
          if (len > max) max = len;
        });
        col.width = Math.min(max + 2, 50);
      });

      const buf = await exWb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const xlsxFilename = (document.getElementById('grid-title')?.textContent || 'spreadsheet') + '.xlsx';
      const name = await this._saveFileInPlace(blob, xlsxFilename, [{ description: 'Excel', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }], 'xlsx');
      if (name && this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Export XLSX', detail: name });
    } catch (e) { console.error('XLSX export error:', e); }
  }

  // ── Rich XLSX Import (ExcelJS, MIT) — preserves styles, formulas, number formats ──
  _importXLSX() {
    if (!window.ExcelJS) { alert('ExcelJS not loaded'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const buf = await file.arrayBuffer();
        const exWb = new window.ExcelJS.Workbook();
        await exWb.xlsx.load(buf);

        const title = file.name.replace(/\.[^.]+$/, '');
        const titleEl = document.getElementById('grid-title');
        if (titleEl) titleEl.textContent = title;

        // Import all worksheets
        const sheets = {};
        const sheetOrder = [];
        exWb.eachSheet((exWs) => {
          const sid = 'ws_' + exWs.id;
          sheetOrder.push(sid);
          const cellData = {};
          const mergeData = [];
          const colWidths = {};

          // Column widths
          exWs.columns?.forEach((col, i) => {
            if (col.width) colWidths[i] = { w: col.width * 7 }; // approx px
          });

          exWs.eachRow((row, rowIdx) => {
            const r = rowIdx - 1;
            cellData[r] = {};
            row.eachCell({ includeEmpty: false }, (cell, colIdx) => {
              const c = colIdx - 1;
              const uCell = {};

              // Value — handle formulas, dates, rich text
              if (cell.formula) {
                uCell.f = cell.formula;
                uCell.v = cell.result ?? '';
              } else if (cell.value instanceof Date) {
                uCell.v = cell.value.toLocaleDateString();
              } else if (cell.value && typeof cell.value === 'object' && cell.value.richText) {
                uCell.v = cell.value.richText.map(rt => rt.text).join('');
              } else {
                uCell.v = cell.value ?? '';
              }

              // Styles
              const s = {};
              const font = cell.font;
              if (font) {
                if (font.bold) s.bl = 1;
                if (font.italic) s.it = 1;
                if (font.underline) s.ul = { s: 1 };
                if (font.size) s.fs = font.size;
                if (font.name) s.ff = font.name;
                if (font.color?.argb) s.cl = { rgb: '#' + font.color.argb.slice(2) };
              }

              const fill = cell.fill;
              if (fill?.fgColor?.argb) {
                s.bg = { rgb: '#' + fill.fgColor.argb.slice(2) };
              }

              const align = cell.alignment;
              if (align) {
                if (align.horizontal) s.ht = align.horizontal === 'center' ? 2 : align.horizontal === 'right' ? 3 : 1;
                if (align.vertical) s.vt = align.vertical === 'middle' ? 2 : align.vertical === 'bottom' ? 3 : 1;
                if (align.wrapText) s.tb = 3; // wrap
              }

              // Number format
              if (cell.numFmt) {
                uCell.s = { ...s, n: { pattern: cell.numFmt } };
              } else if (Object.keys(s).length > 0) {
                uCell.s = s;
              }

              // Borders
              const border = cell.border;
              if (border) {
                const bd = {};
                if (border.top) bd.t = { s: 1, cl: { rgb: '#000000' } };
                if (border.bottom) bd.b = { s: 1, cl: { rgb: '#000000' } };
                if (border.left) bd.l = { s: 1, cl: { rgb: '#000000' } };
                if (border.right) bd.r = { s: 1, cl: { rgb: '#000000' } };
                if (Object.keys(bd).length) {
                  uCell.s = { ...(uCell.s || {}), bd };
                }
              }

              cellData[r][c] = uCell;
            });
          });

          // Merged cells
          exWs.model?.merges?.forEach(merge => {
            // merge is like "A1:B3"
            mergeData.push(merge);
          });

          sheets[sid] = {
            id: sid,
            name: exWs.name || 'Sheet',
            rowCount: Math.max(exWs.rowCount + 100, 1000),
            columnCount: Math.max((exWs.columnCount || 26) + 10, 26),
            cellData,
          };
          if (Object.keys(colWidths).length) sheets[sid].columnData = colWidths;
        });

        this.univerAPI.createWorkbook({
          name: title,
          sheetOrder,
          sheets,
        });
        if (this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Import XLSX', detail: `${file.name} (${exWb.worksheets.length} sheets)` });
      } catch (e) { console.error('XLSX import error:', e); }
    };
    input.click();
  }

  // ── Chart Insertion (ECharts, Apache 2.0) ──
  _insertChart() {
    if (!this.univerAPI || !window.echarts) { alert('ECharts not loaded'); return; }
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;

    const range = sheet.getActiveRange();
    if (!range) { alert('Select a data range first'); return; }

    const values = range.getValues();
    if (!values || values.length < 2) { alert('Select at least 2 rows (header + data)'); return; }

    // First row = categories, remaining = series
    const categories = values[0].map(v => String(v ?? ''));
    const seriesData = values.slice(1);

    // Detect if first column is labels
    const hasLabels = isNaN(Number(seriesData[0]?.[0]));
    const seriesNames = hasLabels ? seriesData.map(r => String(r[0] ?? '')) : seriesData.map((_, i) => `Series ${i + 1}`);
    const dataStart = hasLabels ? 1 : 0;

    // Build ECharts option
    const series = seriesData.map((row, i) => ({
      name: seriesNames[i],
      type: 'bar',
      data: row.slice(dataStart).map(v => Number(v) || 0),
    }));

    const option = {
      tooltip: { trigger: 'axis' },
      legend: { data: seriesNames },
      xAxis: { type: 'category', data: categories.slice(dataStart) },
      yAxis: { type: 'value' },
      series,
    };

    // Create floating chart overlay
    const chartDiv = document.createElement('div');
    chartDiv.className = 'grid-chart-overlay';
    chartDiv.style.cssText = 'position:absolute;top:80px;left:80px;width:500px;height:350px;z-index:1000;background:#fff;border:1px solid #ccc;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.15);';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = 'position:absolute;top:4px;right:8px;z-index:1001;background:none;border:none;font-size:18px;cursor:pointer;color:#666;';
    closeBtn.onclick = () => { chart.dispose(); chartDiv.remove(); };
    chartDiv.appendChild(closeBtn);

    // Chart type switcher
    const typeBar = document.createElement('div');
    typeBar.style.cssText = 'position:absolute;top:4px;left:8px;z-index:1001;display:flex;gap:4px;';
    ['bar', 'line', 'pie', 'area', 'scatter'].forEach(type => {
      const btn = document.createElement('button');
      btn.textContent = type.charAt(0).toUpperCase() + type.slice(1);
      btn.style.cssText = 'padding:2px 8px;font-size:11px;cursor:pointer;border:1px solid #ccc;border-radius:3px;background:#f5f5f5;';
      btn.onclick = () => {
        if (type === 'pie') {
          chart.setOption({
            xAxis: { show: false },
            yAxis: { show: false },
            series: [{ type: 'pie', data: series[0].data.map((v, i) => ({ value: v, name: categories.slice(dataStart)[i] })) }]
          }, true);
        } else if (type === 'area') {
          chart.setOption({ ...option, series: series.map(s => ({ ...s, type: 'line', areaStyle: {} })) }, true);
        } else if (type === 'scatter') {
          chart.setOption({ ...option, xAxis: { type: 'value' }, series: series.map(s => ({ ...s, type: 'scatter' })) }, true);
        } else {
          chart.setOption({ ...option, series: series.map(s => ({ ...s, type, areaStyle: undefined })) }, true);
        }
      };
      typeBar.appendChild(btn);
    });

    // Trendline buttons
    if (window.regression) {
      const sep = document.createElement('span');
      sep.style.cssText = 'border-left:1px solid #ccc;margin:0 2px;';
      typeBar.appendChild(sep);
      ['linear', 'polynomial', 'exponential'].forEach(type => {
        const btn = document.createElement('button');
        btn.textContent = type === 'polynomial' ? 'Poly' : type.charAt(0).toUpperCase() + type.slice(1);
        btn.style.cssText = 'padding:2px 8px;font-size:10px;cursor:pointer;border:1px solid #ef4444;border-radius:3px;background:#fef2f2;color:#ef4444;';
        btn.onclick = () => this._addTrendline(chart, series[0].data, type);
        typeBar.appendChild(btn);
      });
    }
    chartDiv.appendChild(typeBar);

    // Make draggable
    let dragging = false, dx = 0, dy = 0;
    chartDiv.onmousedown = (e) => {
      if (e.target === closeBtn || e.target.parentElement === typeBar) return;
      dragging = true; dx = e.clientX - chartDiv.offsetLeft; dy = e.clientY - chartDiv.offsetTop;
    };
    document.onmousemove = (e) => { if (dragging) { chartDiv.style.left = (e.clientX - dx) + 'px'; chartDiv.style.top = (e.clientY - dy) + 'px'; } };
    document.onmouseup = () => { dragging = false; };

    document.getElementById('grid-container').appendChild(chartDiv);
    const chart = window.echarts.init(chartDiv);
    chart.setOption(option);
  }

  // (old print/pivot methods removed — replaced by upgraded versions below)

  // ── CSV Import (Papa Parse, MIT) ──
  _importCSV() {
    if (!window.Papa) { alert('Papa Parse not loaded'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.tsv,.txt';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      window.Papa.parse(file, {
        complete: (results) => {
          if (!results.data || results.data.length < 1) return;
          const cellData = {};
          results.data.forEach((row, r) => {
            cellData[r] = {};
            row.forEach((val, c) => {
              const num = Number(val);
              cellData[r][c] = { v: (val !== '' && !isNaN(num)) ? num : val };
            });
          });
          const title = file.name.replace(/\.[^.]+$/, '');
          const titleEl = document.getElementById('grid-title');
          if (titleEl) titleEl.textContent = title;
          const sheetId = 'csv_' + Date.now();
          this.univerAPI.createWorkbook({
            name: title,
            sheetOrder: [sheetId],
            sheets: { [sheetId]: {
              id: sheetId, name: title,
              rowCount: results.data.length + 100,
              columnCount: (results.data[0]?.length || 26) + 10,
              cellData,
            }}
          });
          if (this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Import CSV', detail: `${file.name} (${results.data.length} rows)` });
        },
        skipEmptyLines: true,
      });
    };
    input.click();
  }

  // ── SQL Query Engine (AlaSQL, MIT) ──
  _openSQLDialog() {
    if (!this.univerAPI) return;
    if (!window.alasql) { alert('AlaSQL not loaded'); return; }

    document.getElementById('sql-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'sql-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:550px;">
        <div class="pivot-dialog-header">
          <span>SQL Query</span>
          <button id="sql-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <p style="font-size:12px;color:#666;margin:0 0 8px;">Query your sheet data with SQL. Table name is <code>data</code>. Column names are from row 1.</p>
          <textarea id="sql-input" style="width:100%;height:100px;font-family:'Fira Code',monospace;font-size:13px;padding:8px;border:1px solid #ccc;border-radius:4px;resize:vertical;" placeholder="SELECT Region, SUM(Revenue) as Total FROM data GROUP BY Region ORDER BY Total DESC"></textarea>
          <div style="margin-top:8px;display:flex;gap:8px;">
            <button id="sql-run" class="toolbar-btn-sm" style="padding:8px 20px;background:#2563eb;color:#fff;border:none;border-radius:4px;font-weight:600;">Run Query</button>
            <span id="sql-status" style="font-size:12px;color:#666;line-height:36px;"></span>
          </div>
          <div id="sql-result" style="margin-top:12px;max-height:300px;overflow:auto;font-size:12px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('sql-close').onclick = () => dialog.remove();
    document.getElementById('sql-run').onclick = () => this._runSQL();
  }

  _runSQL() {
    const query = document.getElementById('sql-input')?.value?.trim();
    if (!query) return;

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Sheet needs headers + data'); return; }

    const headers = values[0].map(v => String(v ?? '').replace(/[^a-zA-Z0-9_]/g, '_'));
    const rows = values.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => {
        const val = row[i];
        const num = Number(val);
        obj[h] = (val !== '' && val != null && !isNaN(num)) ? num : (val ?? '');
      });
      return obj;
    });

    try {
      const result = window.alasql(query, [rows]);
      const statusEl = document.getElementById('sql-status');
      const resultEl = document.getElementById('sql-result');

      if (Array.isArray(result) && result.length > 0) {
        const cols = Object.keys(result[0]);
        let html = '<table style="width:100%;border-collapse:collapse;"><thead><tr>';
        cols.forEach(c => { html += `<th style="border:1px solid #ddd;padding:4px 8px;background:#f5f5f5;text-align:left;font-size:11px;">${c}</th>`; });
        html += '</tr></thead><tbody>';
        result.forEach(row => {
          html += '<tr>';
          cols.forEach(c => { html += `<td style="border:1px solid #ddd;padding:4px 8px;font-size:11px;">${row[c] ?? ''}</td>`; });
          html += '</tr>';
        });
        html += '</tbody></table>';
        resultEl.innerHTML = html;
        statusEl.textContent = `${result.length} rows returned`;

        // Add "Send to Sheet" button
        const sendBtn = document.createElement('button');
        sendBtn.textContent = 'Send to New Sheet';
        sendBtn.className = 'toolbar-btn-sm';
        sendBtn.style.cssText = 'margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;font-size:12px;cursor:pointer;';
        sendBtn.onclick = () => {
          const cellData = {};
          cellData[0] = {};
          cols.forEach((c, ci) => { cellData[0][ci] = { v: c, s: { bl: 1 } }; });
          result.forEach((row, ri) => {
            cellData[ri + 1] = {};
            cols.forEach((c, ci) => { cellData[ri + 1][ci] = { v: row[c] ?? '' }; });
          });
          const sid = 'sql_' + Date.now();
          this.univerAPI.createWorkbook({
            name: 'SQL Result',
            sheetOrder: [sid],
            sheets: { [sid]: { id: sid, name: 'Query Result', rowCount: result.length + 50, columnCount: cols.length + 5, cellData } }
          });
        };
        resultEl.appendChild(sendBtn);
      } else {
        resultEl.innerHTML = '<p style="color:#666;">No results</p>';
        statusEl.textContent = 'Query executed';
      }
    } catch (e) {
      document.getElementById('sql-result').innerHTML = `<p style="color:red;">${e.message}</p>`;
      document.getElementById('sql-status').textContent = 'Error';
    }
  }

  // ── Upgraded Pivot Table (PivotTable.js, MIT) ──
  _openPivotDialog() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;

    let values;
    const sel = sheet.getActiveRange();
    if (sel) values = sel.getValues();
    if (!values || values.length < 2) values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need at least a header row + one data row'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);

    // If PivotTable.js + jQuery available, use the real drag-and-drop UI
    if (window.jQuery && window.jQuery.fn.pivotUI) {
      this._openPivotTableJS(headers, dataRows);
    } else {
      this._openPivotFallback(headers, dataRows);
    }
  }

  _openPivotTableJS(headers, dataRows) {
    document.getElementById('pivot-dialog')?.remove();

    // Convert to array of objects for PivotTable.js
    const data = dataRows.map(row => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
      return obj;
    });

    const dialog = document.createElement('div');
    dialog.id = 'pivot-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:90vw;max-width:1000px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Pivot Table — drag fields to Rows, Columns, or Values</span>
          <div style="display:flex;gap:8px;">
            <button id="pivot-to-sheet" class="toolbar-btn-sm" style="padding:4px 12px;background:#059669;color:#fff;border:none;border-radius:4px;font-size:11px;">Send to Sheet</button>
            <button id="pivot-close">&times;</button>
          </div>
        </div>
        <div id="pivot-output" style="flex:1;overflow:auto;padding:8px;"></div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    // Initialize PivotTable.js drag-and-drop UI
    const $output = window.jQuery('#pivot-output');
    $output.pivotUI(data, {
      rows: [],
      cols: [],
      aggregatorName: 'Sum',
      vals: [],
      rendererName: 'Table',
      unusedAttrsVertical: true,
    });

    document.getElementById('pivot-close').onclick = () => dialog.remove();
    document.getElementById('pivot-to-sheet').onclick = () => {
      // Scrape the rendered pivot table HTML into sheet data
      const table = document.querySelector('#pivot-output table.pvtTable');
      if (!table) { alert('Generate a pivot first by dragging fields'); return; }
      const cellData = {};
      const rows = table.querySelectorAll('tr');
      rows.forEach((tr, r) => {
        cellData[r] = {};
        const cells = tr.querySelectorAll('th, td');
        cells.forEach((td, c) => {
          const val = td.textContent?.trim() ?? '';
          const num = Number(val.replace(/,/g, ''));
          cellData[r][c] = { v: (!isNaN(num) && val !== '') ? num : val };
          if (td.tagName === 'TH') cellData[r][c].s = { bl: 1 };
        });
      });
      const sid = 'pivot_' + Date.now();
      const maxCols = Math.max(...Object.values(cellData).map(r => Math.max(...Object.keys(r).map(Number)))) + 1;
      this.univerAPI.createWorkbook({
        name: 'Pivot Table',
        sheetOrder: [sid],
        sheets: { [sid]: { id: sid, name: 'Pivot', rowCount: rows.length + 50, columnCount: maxCols + 5, cellData } }
      });
      dialog.remove();
    };
  }

  // Fallback pivot if jQuery/PivotTable.js not loaded
  _openPivotFallback(headers, dataRows) {
    document.getElementById('pivot-dialog')?.remove();
    const dialog = document.createElement('div');
    dialog.id = 'pivot-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner">
        <div class="pivot-dialog-header">
          <span>Pivot Table</span>
          <button id="pivot-close">&times;</button>
        </div>
        <div class="pivot-fields">
          <div class="pivot-field-group">
            <label>Row Fields</label>
            <select id="pivot-rows" multiple size="5">
              ${headers.map((h, i) => `<option value="${i}">${h}</option>`).join('')}
            </select>
          </div>
          <div class="pivot-field-group">
            <label>Column Field</label>
            <select id="pivot-cols">
              <option value="-1">(none)</option>
              ${headers.map((h, i) => `<option value="${i}">${h}</option>`).join('')}
            </select>
          </div>
          <div class="pivot-field-group">
            <label>Value Field</label>
            <select id="pivot-val">
              ${headers.map((h, i) => `<option value="${i}">${h}</option>`).join('')}
            </select>
          </div>
          <div class="pivot-field-group">
            <label>Aggregation</label>
            <select id="pivot-agg">
              <option value="sum">SUM</option>
              <option value="count">COUNT</option>
              <option value="avg">AVERAGE</option>
              <option value="min">MIN</option>
              <option value="max">MAX</option>
            </select>
          </div>
        </div>
        <div class="pivot-actions">
          <button id="pivot-run" class="toolbar-btn-sm">Create Pivot</button>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('pivot-close').onclick = () => dialog.remove();
    document.getElementById('pivot-run').onclick = () => this._runPivot(headers, dataRows, dialog);
  }

  // ── AI Assistant (compromise NLP + data analysis) ──
  _openAIDialog() {
    if (!this.univerAPI) return;
    document.getElementById('ai-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'ai-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:500px;">
        <div class="pivot-dialog-header">
          <span>AI Assistant</span>
          <button id="ai-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <p style="font-size:12px;color:#666;margin:0 0 8px;">Ask questions about your data or request formulas.</p>
          <div style="display:flex;gap:8px;">
            <input id="ai-input" type="text" placeholder="e.g. 'sum of column B', 'what's the average revenue?', 'find duplicates'" style="flex:1;padding:8px;border:1px solid #ccc;border-radius:4px;font-size:13px;">
            <button id="ai-run" class="toolbar-btn-sm" style="padding:8px 16px;background:#7c3aed;color:#fff;border:none;border-radius:4px;font-weight:600;">Ask</button>
          </div>
          <div id="ai-result" style="margin-top:12px;font-size:13px;min-height:60px;"></div>
          <div style="margin-top:12px;border-top:1px solid #eee;padding-top:12px;">
            <p style="font-size:11px;color:#999;margin:0 0 6px;">Quick Actions:</p>
            <div style="display:flex;flex-wrap:wrap;gap:4px;">
              <button class="ai-quick" data-q="summarize this data">Summarize</button>
              <button class="ai-quick" data-q="find duplicates">Find Duplicates</button>
              <button class="ai-quick" data-q="show statistics">Statistics</button>
              <button class="ai-quick" data-q="find outliers">Find Outliers</button>
              <button class="ai-quick" data-q="count unique values per column">Unique Counts</button>
              <button class="ai-quick" data-q="what columns have missing data">Missing Data</button>
              <button class="ai-quick" data-q="show correlation matrix">Correlation</button>
              <button class="ai-quick" data-q="detect trends">Trends</button>
              <button class="ai-quick" data-q="suggest a formula for lookup">Formula Help</button>
            </div>
          </div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('ai-close').onclick = () => dialog.remove();
    document.getElementById('ai-run').onclick = () => this._runAI();
    document.getElementById('ai-input').addEventListener('keydown', (e) => { if (e.key === 'Enter') this._runAI(); });

    // Quick action buttons
    dialog.querySelectorAll('.ai-quick').forEach(btn => {
      btn.style.cssText = 'padding:4px 10px;font-size:11px;border:1px solid #ddd;border-radius:3px;background:#f9fafb;cursor:pointer;';
      btn.onclick = () => {
        document.getElementById('ai-input').value = btn.dataset.q;
        this._runAI();
      };
    });
  }

  _runAI() {
    const query = document.getElementById('ai-input')?.value?.trim()?.toLowerCase();
    if (!query) return;

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { this._aiResult('Need data in the sheet first.'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);
    const resultEl = document.getElementById('ai-result');

    // ── Data analysis engine ──
    if (query.includes('summar')) {
      this._aiSummarize(headers, dataRows);
    } else if (query.includes('statistic') || query.includes('stats')) {
      this._aiStatistics(headers, dataRows);
    } else if (query.includes('duplicate')) {
      this._aiFindDuplicates(headers, dataRows);
    } else if (query.includes('outlier')) {
      this._aiFindOutliers(headers, dataRows);
    } else if (query.includes('unique')) {
      this._aiUniqueCounts(headers, dataRows);
    } else if (query.includes('missing') || query.includes('empty') || query.includes('blank')) {
      this._aiMissingData(headers, dataRows);
    } else if (query.includes('sum of') || query.includes('total of') || query.includes('add up')) {
      this._aiColumnCalc(headers, dataRows, query, 'sum');
    } else if (query.includes('average') || query.includes('avg') || query.includes('mean')) {
      this._aiColumnCalc(headers, dataRows, query, 'avg');
    } else if (query.includes('max') || query.includes('highest') || query.includes('largest')) {
      this._aiColumnCalc(headers, dataRows, query, 'max');
    } else if (query.includes('min') || query.includes('lowest') || query.includes('smallest')) {
      this._aiColumnCalc(headers, dataRows, query, 'min');
    } else if (query.includes('count')) {
      this._aiColumnCalc(headers, dataRows, query, 'count');
    } else if (query.includes('formula') || query.includes('how do i') || query.includes('suggest')) {
      this._aiFormulaSuggest(query, headers);
    } else if (query.includes('correlat') || query.includes('relationship') || query.includes('r squared') || query.includes('r²')) {
      this._aiCorrelation(headers, dataRows);
    } else if (query.includes('trend') || query.includes('direction') || query.includes('moving average') || query.includes('volatil')) {
      this._aiTrendDetection(headers, dataRows);
    } else if (query.includes('show') || query.includes('where') || query.includes('filter') || query.includes('over ') || query.includes('under ') || query.includes('above ') || query.includes('below ')) {
      this._aiNLFilter(query, headers, dataRows);
    } else {
      this._aiResult(`I can help with: <b>summarize</b>, <b>statistics</b>, <b>duplicates</b>, <b>outliers</b>, <b>unique counts</b>, <b>missing data</b>, <b>correlation</b>, <b>trend detection</b>, <b>formula suggest</b>, <b>natural language filter</b> (e.g. "show sales over $1000"), or <b>sum/avg/min/max of [column]</b>.`);
    }
  }

  _aiResult(html) {
    const el = document.getElementById('ai-result');
    if (el) el.innerHTML = html;
  }

  _aiSummarize(headers, dataRows) {
    const numCols = [];
    const textCols = [];
    headers.forEach((h, i) => {
      const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
      if (nums.length > dataRows.length * 0.5) numCols.push({ name: h, idx: i, count: nums.length });
      else textCols.push({ name: h, idx: i });
    });

    let html = `<b>Data Summary</b><br>`;
    html += `Rows: <b>${dataRows.length}</b> | Columns: <b>${headers.length}</b><br>`;
    html += `Numeric columns: ${numCols.map(c => c.name).join(', ') || 'none'}<br>`;
    html += `Text columns: ${textCols.map(c => c.name).join(', ') || 'none'}<br>`;

    if (numCols.length > 0) {
      html += `<br><table style="width:100%;border-collapse:collapse;font-size:11px;">`;
      html += `<tr><th style="border:1px solid #ddd;padding:3px;">Column</th><th style="border:1px solid #ddd;padding:3px;">Sum</th><th style="border:1px solid #ddd;padding:3px;">Avg</th><th style="border:1px solid #ddd;padding:3px;">Min</th><th style="border:1px solid #ddd;padding:3px;">Max</th></tr>`;
      numCols.forEach(col => {
        const nums = dataRows.map(r => Number(r[col.idx])).filter(n => !isNaN(n));
        const sum = nums.reduce((a, b) => a + b, 0);
        html += `<tr><td style="border:1px solid #ddd;padding:3px;">${col.name}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px;">${sum.toLocaleString()}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px;">${(sum / nums.length).toLocaleString(undefined, {maximumFractionDigits: 2})}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px;">${Math.min(...nums).toLocaleString()}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px;">${Math.max(...nums).toLocaleString()}</td></tr>`;
      });
      html += `</table>`;
    }
    this._aiResult(html);
  }

  _aiStatistics(headers, dataRows) {
    const jstat = window.jStat || window.jstat;
    let html = `<b>Statistical Analysis</b><br><table style="width:100%;border-collapse:collapse;font-size:11px;">`;
    html += `<tr><th style="border:1px solid #ddd;padding:3px;">Column</th><th style="border:1px solid #ddd;padding:3px;">N</th><th style="border:1px solid #ddd;padding:3px;">Mean</th><th style="border:1px solid #ddd;padding:3px;">Median</th><th style="border:1px solid #ddd;padding:3px;">Std Dev</th><th style="border:1px solid #ddd;padding:3px;">Skew</th></tr>`;

    headers.forEach((h, i) => {
      const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
      if (nums.length < 3) return;

      const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
      const sorted = [...nums].sort((a, b) => a - b);
      const median = sorted.length % 2 === 0 ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2 : sorted[Math.floor(sorted.length / 2)];
      const variance = nums.reduce((sum, n) => sum + (n - mean) ** 2, 0) / (nums.length - 1);
      const stddev = Math.sqrt(variance);
      const skew = jstat ? jstat.skewness(nums) : (nums.reduce((sum, n) => sum + ((n - mean) / stddev) ** 3, 0) / nums.length);

      html += `<tr><td style="border:1px solid #ddd;padding:3px;">${h}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px;">${nums.length}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px;">${mean.toFixed(2)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px;">${median.toFixed(2)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px;">${stddev.toFixed(2)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px;">${(skew || 0).toFixed(3)}</td></tr>`;
    });
    html += `</table>`;
    this._aiResult(html);
  }

  _aiFindDuplicates(headers, dataRows) {
    // Check each column for duplicates
    let html = '<b>Duplicate Analysis</b><br>';
    headers.forEach((h, i) => {
      const vals = dataRows.map(r => String(r[i] ?? '')).filter(v => v !== '');
      const counts = {};
      vals.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
      const dupes = Object.entries(counts).filter(([, c]) => c > 1).sort((a, b) => b[1] - a[1]);
      if (dupes.length > 0) {
        html += `<b>${h}</b>: ${dupes.length} duplicate values (top: ${dupes.slice(0, 5).map(([v, c]) => `"${v}" x${c}`).join(', ')})<br>`;
      }
    });
    if (html === '<b>Duplicate Analysis</b><br>') html += 'No duplicates found in any column.';
    this._aiResult(html);
  }

  _aiFindOutliers(headers, dataRows) {
    let html = '<b>Outlier Detection</b> (IQR method)<br>';
    let found = false;
    headers.forEach((h, i) => {
      const nums = dataRows.map((r, ri) => ({ val: Number(r[i]), row: ri + 2 })).filter(n => !isNaN(n.val));
      if (nums.length < 4) return;
      const sorted = nums.map(n => n.val).sort((a, b) => a - b);
      const q1 = sorted[Math.floor(sorted.length * 0.25)];
      const q3 = sorted[Math.floor(sorted.length * 0.75)];
      const iqr = q3 - q1;
      const lower = q1 - 1.5 * iqr;
      const upper = q3 + 1.5 * iqr;
      const outliers = nums.filter(n => n.val < lower || n.val > upper);
      if (outliers.length > 0) {
        found = true;
        html += `<b>${h}</b>: ${outliers.length} outliers (rows: ${outliers.slice(0, 10).map(o => `${o.row}[${o.val}]`).join(', ')})<br>`;
      }
    });
    if (!found) html += 'No outliers detected.';
    this._aiResult(html);
  }

  _aiUniqueCounts(headers, dataRows) {
    let html = '<b>Unique Values per Column</b><br>';
    headers.forEach((h, i) => {
      const unique = new Set(dataRows.map(r => String(r[i] ?? '')).filter(v => v !== ''));
      html += `<b>${h}</b>: ${unique.size} unique values<br>`;
    });
    this._aiResult(html);
  }

  _aiMissingData(headers, dataRows) {
    let html = '<b>Missing Data Report</b><br>';
    let anyMissing = false;
    headers.forEach((h, i) => {
      const missing = dataRows.filter(r => r[i] == null || String(r[i]).trim() === '').length;
      if (missing > 0) {
        anyMissing = true;
        const pct = ((missing / dataRows.length) * 100).toFixed(1);
        html += `<b>${h}</b>: ${missing} missing (${pct}%)<br>`;
      }
    });
    if (!anyMissing) html += 'No missing data found.';
    this._aiResult(html);
  }

  _aiColumnCalc(headers, dataRows, query, op) {
    // Find which column the user is asking about
    const lowerHeaders = headers.map(h => h.toLowerCase());
    let colIdx = -1;
    for (let i = 0; i < headers.length; i++) {
      if (query.includes(headers[i].toLowerCase()) || query.includes(String.fromCharCode(65 + i).toLowerCase())) {
        colIdx = i;
        break;
      }
    }
    if (colIdx === -1) {
      // Try to find any column name mentioned
      const words = query.split(/\s+/);
      for (const word of words) {
        const idx = lowerHeaders.findIndex(h => h.includes(word));
        if (idx !== -1) { colIdx = idx; break; }
      }
    }
    if (colIdx === -1) {
      this._aiResult('Which column? Try: "sum of Revenue" or "average of column B"');
      return;
    }

    const nums = dataRows.map(r => Number(r[colIdx])).filter(n => !isNaN(n));
    if (nums.length === 0) {
      this._aiResult(`Column "${headers[colIdx]}" has no numeric values.`);
      return;
    }

    let result;
    switch (op) {
      case 'sum': result = nums.reduce((a, b) => a + b, 0); break;
      case 'avg': result = nums.reduce((a, b) => a + b, 0) / nums.length; break;
      case 'max': result = Math.max(...nums); break;
      case 'min': result = Math.min(...nums); break;
      case 'count': result = nums.length; break;
    }
    this._aiResult(`<b>${op.toUpperCase()}</b> of <b>${headers[colIdx]}</b>: <span style="font-size:18px;font-weight:700;color:#2563eb;">${result.toLocaleString(undefined, {maximumFractionDigits: 2})}</span> <span style="color:#999;">(${nums.length} values)</span>`);
  }

  _aiFormulaHelp(query) {
    const formulas = {
      'vlookup': '=VLOOKUP(lookup_value, table_array, col_index, [range_lookup])\nSearches the first column of a range and returns a value from the same row in another column.',
      'sumif': '=SUMIF(range, criteria, [sum_range])\nAdds cells that meet a condition.',
      'countif': '=COUNTIF(range, criteria)\nCounts cells that meet a condition.',
      'if': '=IF(logical_test, value_if_true, value_if_false)\nReturns one value if true, another if false.',
      'index match': '=INDEX(array, MATCH(lookup_value, lookup_array, 0))\nMore flexible than VLOOKUP — can look up in any direction.',
      'sumifs': '=SUMIFS(sum_range, criteria_range1, criteria1, ...)\nAdds cells that meet multiple conditions.',
      'average': '=AVERAGE(number1, [number2], ...)\nReturns the arithmetic mean.',
      'xlookup': '=XLOOKUP(lookup_value, lookup_array, return_array, [if_not_found])\nModern replacement for VLOOKUP.',
      'pivot': 'Use the Pivot button in the toolbar, or try:\n=SUMPRODUCT((range1=criteria1)*(range2=criteria2)*values)\nfor pivot-like calculations in formulas.',
    };

    let html = '<b>Formula Help</b><br>';
    let found = false;
    for (const [key, help] of Object.entries(formulas)) {
      if (query.includes(key)) {
        html += `<pre style="background:#f5f5f5;padding:8px;border-radius:4px;font-size:12px;white-space:pre-wrap;">${help}</pre>`;
        found = true;
        break;
      }
    }
    if (!found) {
      html += 'Available formula help: ' + Object.keys(formulas).map(k => `<b>${k}</b>`).join(', ');
    }
    this._aiResult(html);
  }

  // ── Full-Text Search (FlexSearch, Apache 2.0) ──
  _openSearch() {
    if (!this.univerAPI) return;
    document.getElementById('search-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'search-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:420px;">
        <div class="pivot-dialog-header">
          <span>Search All Sheets</span>
          <button id="search-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <input id="search-input" type="text" placeholder="Search for anything..." style="width:100%;padding:8px;border:1px solid #ccc;border-radius:4px;font-size:13px;">
          <div id="search-results" style="margin-top:12px;max-height:300px;overflow:auto;font-size:12px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('search-close').onclick = () => dialog.remove();
    const input = document.getElementById('search-input');
    input.focus();

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this._doSearch(input.value), 200);
    });
  }

  _doSearch(query) {
    if (!query || query.length < 2) {
      document.getElementById('search-results').innerHTML = '';
      return;
    }

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values) return;

    const q = query.toLowerCase();
    const results = [];
    values.forEach((row, r) => {
      row.forEach((cell, c) => {
        const str = String(cell ?? '');
        if (str.toLowerCase().includes(q)) {
          const col = String.fromCharCode(65 + (c % 26));
          results.push({ cell: `${col}${r + 1}`, value: str, row: r, col: c });
        }
      });
    });

    const el = document.getElementById('search-results');
    if (results.length === 0) {
      el.innerHTML = '<p style="color:#999;">No matches found</p>';
    } else {
      el.innerHTML = results.slice(0, 50).map(r =>
        `<div style="padding:4px 8px;border-bottom:1px solid #eee;cursor:pointer;" class="search-hit" data-r="${r.row}" data-c="${r.col}">
          <b>${r.cell}</b>: ${r.value.length > 60 ? r.value.slice(0, 60) + '...' : r.value}
        </div>`
      ).join('') + (results.length > 50 ? `<p style="color:#999;padding:4px 8px;">...and ${results.length - 50} more</p>` : '');
    }
  }

  // ── Keyboard Shortcuts ──
  _initKeyboardShortcuts() {
    if (!window.hotkeys) return;
    const hk = window.hotkeys;

    // Ctrl+S — Save as XLSX
    hk('ctrl+s,command+s', (e) => { e.preventDefault(); this._exportXLSX(); });
    // Ctrl+O — Open file
    hk('ctrl+o,command+o', (e) => { e.preventDefault(); this._importXLSX(); });
    // Ctrl+P — Print
    hk('ctrl+p,command+p', (e) => { e.preventDefault(); this._printSheet(); });
    // Ctrl+F — Search (use our search, not browser's)
    hk('ctrl+f,command+f', (e) => { e.preventDefault(); this._openSearch(); });
    // Ctrl+Q — SQL query
    hk('ctrl+q,command+q', (e) => { e.preventDefault(); this._openSQLDialog(); });

    // Prevent cell navigation wrapping at edges
    const container = document.getElementById('grid-spreadsheet');
    if (container) {
      container.addEventListener('keydown', (e) => {
        if (!this.univerAPI) return;
        if (!['ArrowLeft', 'ArrowRight', 'Tab', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
        // Don't interfere when editing a cell
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;

        try {
          const wb = this.univerAPI.getActiveWorkbook();
          if (!wb) return;
          const sheet = wb.getActiveSheet();
          if (!sheet) return;
          const sel = sheet.getActiveRange();
          if (!sel) return;
          const range = sel.getRange();
          const col = range.startColumn;
          const row = range.startRow;

          // Block leftward wrap at column 0
          if ((e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) && col === 0) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          // Block upward wrap at row 0
          if (e.key === 'ArrowUp' && row === 0) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          // Block rightward wrap — find last column with data
          const data = sheet.getDataRange()?.getValues();
          if (data && data.length > 0) {
            const maxCol = data[0].length - 1;
            if ((e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey)) && col >= maxCol) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            const maxRow = data.length - 1;
            if (e.key === 'ArrowDown' && row >= maxRow) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        } catch (_) {}
      }, true);
    }
  }

  // ── Better Print (jsPDF-AutoTable) ──
  async _printSheet() {
    const wb = this.univerAPI?.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();

    // If jsPDF-AutoTable available, use proper table PDF
    if (window.jspdf && values && values.length > 0) {
      try {
        const pdf = new window.jspdf.jsPDF({ orientation: 'landscape' });
        const headers = values[0].map(v => String(v ?? ''));
        const body = values.slice(1).map(row => row.map(c => String(c ?? '')));

        pdf.setFontSize(16);
        pdf.text(document.getElementById('grid-title')?.textContent || 'Spreadsheet', 14, 15);
        pdf.setFontSize(8);
        pdf.text(new Date().toLocaleDateString(), 14, 20);

        pdf.autoTable({
          head: [headers],
          body: body,
          startY: 25,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [245, 245, 245] },
        });

        pdf.save((document.getElementById('grid-title')?.textContent || 'spreadsheet') + '.pdf');
        return;
      } catch (e) { console.error('AutoTable print error:', e); }
    }

    // Fallback: html2canvas or browser print
    if (window.html2canvas) {
      try {
        const el = document.getElementById('grid-spreadsheet');
        const canvas = await window.html2canvas(el, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new window.jspdf.jsPDF({ orientation: canvas.width > canvas.height ? 'l' : 'p', unit: 'px', format: [canvas.width, canvas.height] });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save((document.getElementById('grid-title')?.textContent || 'spreadsheet') + '.pdf');
        return;
      } catch (e) { console.error('html2canvas print error:', e); }
    }

    window.print();
  }

  // ── Save to Local Storage (LocalForage, Apache 2.0) ──
  async _saveLocal() {
    if (!this.univerAPI || !window.localforage) { alert('LocalForage not loaded'); return; }
    try {
      const snapshot = this._getGridSnapshot();
      if (!snapshot) return;

      const title = snapshot.title;
      const key = 'ofg_sheet_' + title.replace(/[^a-zA-Z0-9]/g, '_');
      const payload = {
        title,
        savedAt: new Date().toISOString(),
        sheets: snapshot.sheets,
        sheetOrder: snapshot.sheetOrder,
      };

      await window.localforage.setItem(key, payload);

      // Also save to index
      let index = await window.localforage.getItem('ofg_sheet_index') || [];
      if (!index.find(e => e.key === key)) {
        index.push({ key, title, savedAt: payload.savedAt });
      } else {
        index = index.map(e => e.key === key ? { key, title, savedAt: payload.savedAt } : e);
      }
      await window.localforage.setItem('ofg_sheet_index', index);

      // Update autosave hash so periodic save skips redundant write
      this._lastAutoSaveHash = JSON.stringify(values);

      this._showDialog({ title: 'Saved', message: `"${title}" saved to browser storage.`, buttons: [{ label: 'OK', value: 'ok', primary: true }] });
    } catch (e) { console.error('Save error:', e); this._showDialog({ title: 'Save Failed', message: e.message, buttons: [{ label: 'OK', value: 'ok' }] }); }
  }

  async _loadLocal() {
    if (!this.univerAPI || !window.localforage) { alert('LocalForage not loaded'); return; }
    try {
      const index = await window.localforage.getItem('ofg_sheet_index') || [];
      if (index.length === 0) { alert('No saved spreadsheets found'); return; }

      document.getElementById('load-dialog')?.remove();
      const dialog = document.createElement('div');
      dialog.id = 'load-dialog';
      dialog.innerHTML = `
        <div class="pivot-dialog-inner" style="width:400px;">
          <div class="pivot-dialog-header">
            <span>Load Saved Spreadsheet</span>
            <button id="load-close">&times;</button>
          </div>
          <div style="padding:16px;max-height:400px;overflow:auto;">
            ${index.map(e => `
              <div style="display:flex;justify-content:space-between;align-items:center;padding:8px;border-bottom:1px solid #eee;">
                <div>
                  <b>${e.title}</b><br>
                  <span style="font-size:11px;color:#999;">${new Date(e.savedAt).toLocaleString()}</span>
                </div>
                <div style="display:flex;gap:4px;">
                  <button class="load-btn toolbar-btn-sm" data-key="${e.key}" style="padding:4px 12px;background:#2563eb;color:#fff;border:none;border-radius:4px;font-size:11px;">Load</button>
                  <button class="delete-btn toolbar-btn-sm" data-key="${e.key}" style="padding:4px 8px;background:#dc2626;color:#fff;border:none;border-radius:4px;font-size:11px;">X</button>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
      document.getElementById('grid-container').appendChild(dialog);

      document.getElementById('load-close').onclick = () => dialog.remove();

      dialog.querySelectorAll('.load-btn').forEach(btn => {
        btn.onclick = async () => {
          const payload = await window.localforage.getItem(btn.dataset.key);
          if (!payload) { alert('Data not found'); return; }

          const cellData = {};
          payload.data.forEach((row, r) => {
            cellData[r] = {};
            row.forEach((val, c) => {
              const num = Number(val);
              cellData[r][c] = { v: (val !== '' && val != null && !isNaN(num)) ? num : (val ?? '') };
            });
          });

          const titleEl = document.getElementById('grid-title');
          if (titleEl) titleEl.textContent = payload.title;

          const sid = 'loaded_' + Date.now();
          this.univerAPI.createWorkbook({
            name: payload.title,
            sheetOrder: [sid],
            sheets: { [sid]: { id: sid, name: payload.sheetName || 'Sheet1', rowCount: payload.data.length + 100, columnCount: (payload.data[0]?.length || 26) + 10, cellData } }
          });
          dialog.remove();
        };
      });

      dialog.querySelectorAll('.delete-btn').forEach(btn => {
        btn.onclick = async () => {
          const r = await this._showDialog({ title: 'Delete Spreadsheet', message: 'Permanently delete this saved spreadsheet?', buttons: [{ label: 'Cancel', value: 'cancel' }, { label: 'Delete', value: 'delete', danger: true }] });
          if (r !== 'delete') return;
          await window.localforage.removeItem(btn.dataset.key);
          let idx = await window.localforage.getItem('ofg_sheet_index') || [];
          idx = idx.filter(e => e.key !== btn.dataset.key);
          await window.localforage.setItem('ofg_sheet_index', idx);
          btn.closest('div[style]').remove();
        };
      });
    } catch (e) { console.error('Load error:', e); }
  }

  // ── Autosave (silent, periodic) ──

  // ── Snapshot ALL sheets using Univer's native snapshot (preserves cell positions) ──
  _getGridSnapshot() {
    if (!this.univerAPI) return null;
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return null;
      const snapshot = wb.getSnapshot();
      if (!snapshot || !snapshot.sheets) return null;

      // Use Univer's native snapshot which preserves cellData positions
      const sheetsData = {};
      const sheetOrder = snapshot.sheetOrder || Object.keys(snapshot.sheets);
      for (const sid of sheetOrder) {
        const sheetInfo = snapshot.sheets[sid];
        if (!sheetInfo) continue;
        sheetsData[sid] = {
          name: sheetInfo.name || 'Sheet',
          cellData: sheetInfo.cellData || {},
          rowCount: sheetInfo.rowCount || 100,
          columnCount: sheetInfo.columnCount || 26,
        };
      }

      if (Object.keys(sheetsData).length === 0) return null;
      return {
        title: document.getElementById('grid-title')?.textContent || 'Untitled',
        sheets: sheetsData,
        sheetOrder,
        nativeSnapshot: true,
        savedAt: new Date().toISOString(),
      };
    } catch (e) { return null; }
  }

  async _autosave() {
    if (!this.univerAPI || !window.localforage) return;
    try {
      const snapshot = this._getGridSnapshot();
      if (!snapshot || !snapshot.sheets) return;

      const hash = JSON.stringify(snapshot.sheets);
      if (hash === this._lastAutoSaveHash) return;
      this._lastAutoSaveHash = hash;

      await window.localforage.setItem('ofg_grid_autosave', snapshot);
      const ind = document.getElementById('grid-save-indicator');
      if (ind) { ind.textContent = '● Saved'; ind.style.color = '#4ade80'; }
    } catch (e) { /* silently fail */ }
  }

  // Synchronous fallback for beforeunload (uses localStorage)
  _autosaveSync() {
    try {
      const snapshot = this._getGridSnapshot();
      if (!snapshot) return;
      localStorage.setItem('ofg_grid_autosave_sync', JSON.stringify(snapshot));
    } catch (e) { /* best effort */ }
  }

  async _recoverAutosave() {
    if (!window.localforage) return;
    try {
      let snapshot = await window.localforage.getItem('ofg_grid_autosave');
      if (!snapshot) {
        const syncData = localStorage.getItem('ofg_grid_autosave_sync');
        if (syncData) {
          snapshot = JSON.parse(syncData);
          localStorage.removeItem('ofg_grid_autosave_sync');
        }
      }
      if (!snapshot) return;

      // Native snapshot format (preserves exact cell positions)
      if (snapshot.nativeSnapshot && snapshot.sheets) {
        const sheetOrder = snapshot.sheetOrder || Object.keys(snapshot.sheets);
        if (sheetOrder.length === 0) return;

        const sheets = {};
        for (const sid of sheetOrder) {
          const s = snapshot.sheets[sid];
          if (!s) continue;
          sheets[sid] = { id: sid, name: s.name || 'Sheet', rowCount: s.rowCount || 100, columnCount: s.columnCount || 26, cellData: s.cellData || {} };
        }

        const titleEl = document.getElementById('grid-title');
        if (titleEl) titleEl.textContent = snapshot.title || 'Untitled';

        this.univerAPI.createWorkbook({
          name: snapshot.title || 'Recovered Spreadsheet',
          sheetOrder,
          sheets,
        });
      } else if (snapshot.sheets) {
        // Old multi-sheet format with values arrays
        const sheets = {};
        const sheetOrder = [];
        for (const [sid, sheetInfo] of Object.entries(snapshot.sheets)) {
          const data = sheetInfo.data;
          if (!data) continue;
          const cellData = {};
          data.forEach((row, r) => {
            cellData[r] = {};
            row.forEach((val, c) => {
              const num = Number(val);
              cellData[r][c] = { v: (val !== '' && val != null && !isNaN(num)) ? num : (val ?? '') };
            });
          });
          const newId = sid.startsWith('recovered_') ? sid : 'recovered_' + sid + '_' + Date.now();
          sheets[newId] = { id: newId, name: sheetInfo.name || 'Sheet', rowCount: (data.length || 100) + 100, columnCount: (data[0]?.length || 26) + 10, cellData };
          sheetOrder.push(newId);
        }
        if (sheetOrder.length === 0) return;

        const titleEl = document.getElementById('grid-title');
        if (titleEl) titleEl.textContent = snapshot.title || 'Untitled';

        this.univerAPI.createWorkbook({ name: snapshot.title || 'Recovered Spreadsheet', sheetOrder, sheets });
      } else if (snapshot.data) {
        // Old single-sheet format
        const cellData = {};
        snapshot.data.forEach((row, r) => {
          cellData[r] = {};
          row.forEach((val, c) => {
            const num = Number(val);
            cellData[r][c] = { v: (val !== '' && val != null && !isNaN(num)) ? num : (val ?? '') };
          });
        });
        const titleEl = document.getElementById('grid-title');
        if (titleEl) titleEl.textContent = snapshot.title || 'Untitled';
        const sid = 'recovered_' + Date.now();
        this.univerAPI.createWorkbook({ name: snapshot.title || 'Recovered Spreadsheet', sheetOrder: [sid], sheets: { [sid]: { id: sid, name: snapshot.sheetName || 'Sheet1', rowCount: snapshot.data.length + 100, columnCount: (snapshot.data[0]?.length || 26) + 10, cellData } } });
      } else {
        return;
      }

      this._lastAutoSaveHash = JSON.stringify(snapshot.sheets || snapshot.data);
      console.log('Grid: recovered autosave from', snapshot.savedAt);
      const ind = document.getElementById('grid-save-indicator');
      if (ind) { ind.textContent = '● Recovered'; ind.style.color = '#60a5fa'; }
    } catch (e) { console.error('Grid: autosave recovery failed:', e); }
  }

  // ── Word Export (docx, MIT) ──
  async _exportWord() {
    if (!this.univerAPI) return;
    const docxLib = window.docx;
    if (!docxLib) { alert('docx library not loaded'); return; }

    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return;
      const sheet = wb.getActiveSheet();
      if (!sheet) return;
      const values = sheet.getDataRange()?.getValues();
      if (!values || values.length < 1) return;

      const title = document.getElementById('grid-title')?.textContent || 'Spreadsheet';
      const headers = values[0];
      const dataRows = values.slice(1);

      // Build Word document with table
      const tableRows = [];

      // Header row
      tableRows.push(new docxLib.TableRow({
        tableHeader: true,
        children: headers.map(h => new docxLib.TableCell({
          children: [new docxLib.Paragraph({
            children: [new docxLib.TextRun({ text: String(h ?? ''), bold: true, size: 20, font: 'Calibri' })],
          })],
          shading: { fill: '2563EB', color: 'FFFFFF' },
          verticalAlign: docxLib.VerticalAlign.CENTER,
        })),
      }));

      // Data rows
      dataRows.forEach((row, ri) => {
        tableRows.push(new docxLib.TableRow({
          children: row.map(cell => new docxLib.TableCell({
            children: [new docxLib.Paragraph({
              children: [new docxLib.TextRun({ text: String(cell ?? ''), size: 18, font: 'Calibri' })],
            })],
            shading: ri % 2 === 1 ? { fill: 'F5F5F5' } : undefined,
          })),
        }));
      });

      const doc = new docxLib.Document({
        sections: [{
          properties: { page: { margin: { top: 720, right: 720, bottom: 720, left: 720 } } },
          children: [
            new docxLib.Paragraph({
              children: [new docxLib.TextRun({ text: title, bold: true, size: 32, font: 'Calibri' })],
              spacing: { after: 200 },
            }),
            new docxLib.Paragraph({
              children: [new docxLib.TextRun({ text: `Generated ${new Date().toLocaleDateString()}`, size: 18, color: '999999', font: 'Calibri' })],
              spacing: { after: 400 },
            }),
            new docxLib.Table({ rows: tableRows }),
          ],
        }],
      });

      const blob = await docxLib.Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = title + '.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Word export error:', e); alert('Word export failed: ' + e.message); }
  }

  // ── Chart Trendlines (regression, MIT) ──
  _addTrendline(chartInstance, data, type) {
    if (!window.regression) return;

    const points = data.map((v, i) => [i, v]);
    let result;
    switch (type) {
      case 'linear': result = window.regression.linear(points); break;
      case 'polynomial': result = window.regression.polynomial(points, { order: 2 }); break;
      case 'exponential': result = window.regression.exponential(points); break;
      case 'logarithmic': result = window.regression.logarithmic(points); break;
      case 'power': result = window.regression.power(points); break;
      default: result = window.regression.linear(points);
    }

    if (result && result.points) {
      const trendData = result.points.map(p => p[1]);
      const currentOpt = chartInstance.getOption();
      chartInstance.setOption({
        series: [...(currentOpt.series || []), {
          name: `Trendline (${type}, R²=${result.r2?.toFixed(4) || 'N/A'})`,
          type: 'line',
          data: trendData,
          smooth: true,
          lineStyle: { type: 'dashed', width: 2, color: '#ef4444' },
          symbol: 'none',
        }]
      });
    }
  }

  // ── Data Transformation Engine (Arquero, BSD-3) ──
  _openTransformDialog() {
    if (!this.univerAPI) return;
    if (!window.aq) { alert('Arquero not loaded'); return; }

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need header row + data'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    document.getElementById('transform-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'transform-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:650px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Data Transform (Power Query)</span>
          <button id="transform-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;">
            <button class="transform-op" data-op="filter" style="padding:6px 14px;border:1px solid #2563eb;border-radius:4px;background:#eff6ff;color:#2563eb;cursor:pointer;font-size:12px;font-weight:600;">Filter Rows</button>
            <button class="transform-op" data-op="derive" style="padding:6px 14px;border:1px solid #7c3aed;border-radius:4px;background:#f5f3ff;color:#7c3aed;cursor:pointer;font-size:12px;font-weight:600;">Add Column</button>
            <button class="transform-op" data-op="group" style="padding:6px 14px;border:1px solid #059669;border-radius:4px;background:#ecfdf5;color:#059669;cursor:pointer;font-size:12px;font-weight:600;">Group &amp; Aggregate</button>
            <button class="transform-op" data-op="sort" style="padding:6px 14px;border:1px solid #d97706;border-radius:4px;background:#fffbeb;color:#d97706;cursor:pointer;font-size:12px;font-weight:600;">Sort</button>
            <button class="transform-op" data-op="select" style="padding:6px 14px;border:1px solid #6366f1;border-radius:4px;background:#eef2ff;color:#6366f1;cursor:pointer;font-size:12px;font-weight:600;">Select Columns</button>
            <button class="transform-op" data-op="rename" style="padding:6px 14px;border:1px solid #0891b2;border-radius:4px;background:#ecfeff;color:#0891b2;cursor:pointer;font-size:12px;font-weight:600;">Rename</button>
            <button class="transform-op" data-op="unpivot" style="padding:6px 14px;border:1px solid #dc2626;border-radius:4px;background:#fef2f2;color:#dc2626;cursor:pointer;font-size:12px;font-weight:600;">Unpivot (Melt)</button>
            <button class="transform-op" data-op="spread" style="padding:6px 14px;border:1px solid #ea580c;border-radius:4px;background:#fff7ed;color:#ea580c;cursor:pointer;font-size:12px;font-weight:600;">Pivot (Spread)</button>
            <button class="transform-op" data-op="dedupe" style="padding:6px 14px;border:1px solid #64748b;border-radius:4px;background:#f8fafc;color:#64748b;cursor:pointer;font-size:12px;font-weight:600;">Remove Duplicates</button>
            <button class="transform-op" data-op="filldown" style="padding:6px 14px;border:1px solid #9333ea;border-radius:4px;background:#faf5ff;color:#9333ea;cursor:pointer;font-size:12px;font-weight:600;">Fill Down</button>
          </div>
          <div style="font-size:11px;color:#999;margin-bottom:8px;">Columns: ${headers.map(h => '<code>' + h + '</code>').join(', ')}</div>
          <div id="transform-config" style="border:1px solid #e5e7eb;border-radius:6px;padding:12px;min-height:80px;background:#fafafa;"></div>
          <div id="transform-pipeline" style="margin-top:10px;font-size:11px;color:#666;"></div>
          <div id="transform-preview" style="margin-top:12px;max-height:200px;overflow:auto;font-size:11px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('transform-close').onclick = () => dialog.remove();

    // Store pipeline steps
    this._transformPipeline = [];
    this._transformHeaders = headers;
    this._transformValues = values;

    dialog.querySelectorAll('.transform-op').forEach(btn => {
      btn.onclick = () => this._showTransformConfig(btn.dataset.op, headers);
    });
  }

  _showTransformConfig(op, headers) {
    const cfg = document.getElementById('transform-config');
    if (!cfg) return;

    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');

    switch (op) {
      case 'filter':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Filter rows where:</label><br>
          <select id="tf-col" style="padding:4px;border:1px solid #ccc;border-radius:3px;margin:4px 0;">${colOpts}</select>
          <select id="tf-op" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
            <option value="eq">equals</option><option value="neq">not equals</option>
            <option value="gt">greater than</option><option value="lt">less than</option>
            <option value="gte">≥</option><option value="lte">≤</option>
            <option value="contains">contains</option><option value="starts">starts with</option>
            <option value="notempty">is not empty</option>
          </select>
          <input id="tf-val" type="text" placeholder="value" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:120px;">
          <button id="tf-add" style="padding:4px 12px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-left:4px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const col = document.getElementById('tf-col').value;
          const oper = document.getElementById('tf-op').value;
          const val = document.getElementById('tf-val').value;
          this._transformPipeline.push({ op: 'filter', col, oper, val });
          this._runTransformPreview();
        };
        break;

      case 'derive':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Add computed column:</label><br>
          <input id="tf-newcol" type="text" placeholder="New column name" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:150px;margin:4px 0;">
          <input id="tf-expr" type="text" placeholder="d.Price * d.Quantity" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:250px;" title="Use d.ColumnName to reference columns">
          <button id="tf-add" style="padding:4px 12px;background:#7c3aed;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-left:4px;">Add Step</button>
          <div style="font-size:10px;color:#999;margin-top:4px;">Use <code>d.ColumnName</code> to reference columns. Examples: <code>d.Price * d.Qty</code>, <code>d.Total > 100 ? 'High' : 'Low'</code></div>
        `;
        document.getElementById('tf-add').onclick = () => {
          const newCol = document.getElementById('tf-newcol').value.trim();
          const expr = document.getElementById('tf-expr').value.trim();
          if (!newCol || !expr) return;
          this._transformPipeline.push({ op: 'derive', newCol, expr });
          this._runTransformPreview();
        };
        break;

      case 'group':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Group by:</label><br>
          <select id="tf-groupby" multiple size="4" style="padding:4px;border:1px solid #ccc;border-radius:3px;min-width:150px;margin:4px 0;">${colOpts}</select>
          <div style="margin-top:6px;">
            <label style="font-size:11px;">Aggregate:</label>
            <select id="tf-aggcol" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
            <select id="tf-aggfn" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
              <option value="sum">SUM</option><option value="mean">AVG</option><option value="count">COUNT</option>
              <option value="min">MIN</option><option value="max">MAX</option><option value="median">MEDIAN</option>
              <option value="stdev">STDEV</option>
            </select>
          </div>
          <button id="tf-add" style="padding:4px 12px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-top:6px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const groupBy = Array.from(document.getElementById('tf-groupby').selectedOptions).map(o => o.value);
          if (groupBy.length === 0) { alert('Select at least one group-by column'); return; }
          const aggCol = document.getElementById('tf-aggcol').value;
          const aggFn = document.getElementById('tf-aggfn').value;
          this._transformPipeline.push({ op: 'group', groupBy, aggCol, aggFn });
          this._runTransformPreview();
        };
        break;

      case 'sort':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Sort by:</label><br>
          <select id="tf-sortcol" style="padding:4px;border:1px solid #ccc;border-radius:3px;margin:4px 0;">${colOpts}</select>
          <select id="tf-sortdir" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
            <option value="asc">Ascending (A→Z, 0→9)</option><option value="desc">Descending (Z→A, 9→0)</option>
          </select>
          <button id="tf-add" style="padding:4px 12px;background:#d97706;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-left:4px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const col = document.getElementById('tf-sortcol').value;
          const dir = document.getElementById('tf-sortdir').value;
          this._transformPipeline.push({ op: 'sort', col, dir });
          this._runTransformPreview();
        };
        break;

      case 'select':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Select columns to keep:</label><br>
          <select id="tf-selcols" multiple size="6" style="padding:4px;border:1px solid #ccc;border-radius:3px;min-width:200px;margin:4px 0;">${colOpts}</select>
          <button id="tf-add" style="padding:4px 12px;background:#6366f1;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-top:6px;display:block;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const cols = Array.from(document.getElementById('tf-selcols').selectedOptions).map(o => o.value);
          if (cols.length === 0) return;
          this._transformPipeline.push({ op: 'select', cols });
          this._runTransformPreview();
        };
        break;

      case 'rename':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Rename column:</label><br>
          <select id="tf-oldname" style="padding:4px;border:1px solid #ccc;border-radius:3px;margin:4px 0;">${colOpts}</select>
          <span style="margin:0 4px;">→</span>
          <input id="tf-newname" type="text" placeholder="New name" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:150px;">
          <button id="tf-add" style="padding:4px 12px;background:#0891b2;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-left:4px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const oldName = document.getElementById('tf-oldname').value;
          const newName = document.getElementById('tf-newname').value.trim();
          if (!newName) return;
          this._transformPipeline.push({ op: 'rename', oldName, newName });
          this._runTransformPreview();
        };
        break;

      case 'unpivot':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Unpivot (Melt) — turn columns into rows:</label><br>
          <div style="margin:4px 0;font-size:11px;color:#666;">Hold Ctrl/Cmd to select multiple ID columns (kept as-is):</div>
          <select id="tf-idcols" multiple size="4" style="padding:4px;border:1px solid #ccc;border-radius:3px;min-width:200px;">${colOpts}</select>
          <div style="margin-top:6px;font-size:11px;color:#666;">All other columns will be melted into Key/Value pairs.</div>
          <button id="tf-add" style="padding:4px 12px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-top:6px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const idCols = Array.from(document.getElementById('tf-idcols').selectedOptions).map(o => o.value);
          this._transformPipeline.push({ op: 'unpivot', idCols });
          this._runTransformPreview();
        };
        break;

      case 'spread':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Pivot (Spread) — turn row values into columns:</label><br>
          <div style="margin:4px 0;">
            <label style="font-size:11px;">Key column (becomes headers):</label>
            <select id="tf-spreadkey" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
          </div>
          <div style="margin:4px 0;">
            <label style="font-size:11px;">Value column (fills cells):</label>
            <select id="tf-spreadval" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
          </div>
          <button id="tf-add" style="padding:4px 12px;background:#ea580c;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-top:6px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const keyCol = document.getElementById('tf-spreadkey').value;
          const valCol = document.getElementById('tf-spreadval').value;
          this._transformPipeline.push({ op: 'spread', keyCol, valCol });
          this._runTransformPreview();
        };
        break;

      case 'dedupe':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Remove duplicate rows based on:</label><br>
          <select id="tf-dedcols" multiple size="4" style="padding:4px;border:1px solid #ccc;border-radius:3px;min-width:200px;margin:4px 0;">${colOpts}</select>
          <div style="font-size:10px;color:#999;">Select columns to check. Leave empty = check all columns.</div>
          <button id="tf-add" style="padding:4px 12px;background:#64748b;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-top:6px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const cols = Array.from(document.getElementById('tf-dedcols').selectedOptions).map(o => o.value);
          this._transformPipeline.push({ op: 'dedupe', cols });
          this._runTransformPreview();
        };
        break;

      case 'filldown':
        cfg.innerHTML = `
          <label style="font-size:12px;font-weight:600;">Fill empty cells with value above:</label><br>
          <select id="tf-fillcol" style="padding:4px;border:1px solid #ccc;border-radius:3px;margin:4px 0;">${colOpts}</select>
          <button id="tf-add" style="padding:4px 12px;background:#9333ea;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-left:4px;">Add Step</button>
        `;
        document.getElementById('tf-add').onclick = () => {
          const col = document.getElementById('tf-fillcol').value;
          this._transformPipeline.push({ op: 'filldown', col });
          this._runTransformPreview();
        };
        break;
    }
  }

  _runTransformPreview() {
    const aq = window.aq;
    if (!aq || !this._transformValues) return;

    const values = this._transformValues;
    const headers = this._transformHeaders;

    // Build Arquero table from sheet data
    const columns = {};
    headers.forEach((h, i) => {
      columns[h] = values.slice(1).map(row => {
        const v = row[i];
        if (v == null || v === '') return null;
        const num = Number(v);
        return (!isNaN(num) && v !== '') ? num : v;
      });
    });

    let table;
    try {
      table = aq.table(columns);
    } catch (e) {
      document.getElementById('transform-preview').innerHTML = `<p style="color:red;">Error building table: ${e.message}</p>`;
      return;
    }

    // Apply pipeline steps
    const pipeEl = document.getElementById('transform-pipeline');
    let pipeHTML = '<b>Pipeline:</b> ';
    try {
      for (const step of this._transformPipeline) {
        switch (step.op) {
          case 'filter': {
            const col = step.col;
            const val = step.val;
            const numVal = Number(val);
            const isNum = !isNaN(numVal) && val !== '';
            switch (step.oper) {
              case 'eq': table = table.filter(aq.escape(d => isNum ? d[col] === numVal : String(d[col]) === val)); break;
              case 'neq': table = table.filter(aq.escape(d => isNum ? d[col] !== numVal : String(d[col]) !== val)); break;
              case 'gt': table = table.filter(aq.escape(d => d[col] > numVal)); break;
              case 'lt': table = table.filter(aq.escape(d => d[col] < numVal)); break;
              case 'gte': table = table.filter(aq.escape(d => d[col] >= numVal)); break;
              case 'lte': table = table.filter(aq.escape(d => d[col] <= numVal)); break;
              case 'contains': table = table.filter(aq.escape(d => String(d[col] ?? '').toLowerCase().includes(val.toLowerCase()))); break;
              case 'starts': table = table.filter(aq.escape(d => String(d[col] ?? '').toLowerCase().startsWith(val.toLowerCase()))); break;
              case 'notempty': table = table.filter(aq.escape(d => d[col] != null && d[col] !== '')); break;
            }
            pipeHTML += `<span style="background:#eff6ff;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Filter: ${col} ${step.oper} ${val}</span>`;
            break;
          }
          case 'derive': {
            const deriveFn = new Function('d', `return ${step.expr}`);
            table = table.derive({ [step.newCol]: aq.escape(d => deriveFn(d)) });
            pipeHTML += `<span style="background:#f5f3ff;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Derive: ${step.newCol}</span>`;
            break;
          }
          case 'group': {
            const rollupSpec = {};
            rollupSpec[`${step.aggFn}_${step.aggCol}`] = `d => op.${step.aggFn}(d['${step.aggCol}'])`;
            table = table.groupby(step.groupBy).rollup(rollupSpec);
            pipeHTML += `<span style="background:#ecfdf5;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Group: ${step.groupBy.join(',')} → ${step.aggFn}(${step.aggCol})</span>`;
            break;
          }
          case 'sort': {
            table = step.dir === 'desc' ? table.orderby(aq.desc(step.col)) : table.orderby(step.col);
            pipeHTML += `<span style="background:#fffbeb;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Sort: ${step.col} ${step.dir}</span>`;
            break;
          }
          case 'select': {
            table = table.select(step.cols);
            pipeHTML += `<span style="background:#eef2ff;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Select: ${step.cols.join(', ')}</span>`;
            break;
          }
          case 'rename': {
            table = table.rename({ [step.oldName]: step.newName });
            pipeHTML += `<span style="background:#ecfeff;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Rename: ${step.oldName}→${step.newName}</span>`;
            break;
          }
          case 'unpivot': {
            const allCols = table.columnNames();
            const valueCols = allCols.filter(c => !step.idCols.includes(c));
            table = table.fold(valueCols, { as: ['Key', 'Value'] });
            pipeHTML += `<span style="background:#fef2f2;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Unpivot: keep ${step.idCols.join(',')}</span>`;
            break;
          }
          case 'spread': {
            table = table.groupby(table.columnNames().filter(c => c !== step.keyCol && c !== step.valCol))
              .pivot(step.keyCol, { value: aq.op.any(step.valCol) });
            pipeHTML += `<span style="background:#fff7ed;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Pivot: ${step.keyCol}→cols</span>`;
            break;
          }
          case 'dedupe': {
            table = step.cols.length > 0 ? table.dedupe(step.cols) : table.dedupe();
            pipeHTML += `<span style="background:#f8fafc;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Dedupe${step.cols.length ? ': ' + step.cols.join(',') : ''}</span>`;
            break;
          }
          case 'filldown': {
            const colName = step.col;
            const colData = table.array(colName);
            const filled = [];
            let last = null;
            for (const v of colData) {
              if (v == null || v === '') { filled.push(last); }
              else { filled.push(v); last = v; }
            }
            const newColData = {};
            newColData[colName] = filled;
            table = table.assign(aq.table(newColData));
            pipeHTML += `<span style="background:#faf5ff;padding:2px 6px;border-radius:3px;font-size:10px;margin-right:4px;">Fill: ${colName}</span>`;
            break;
          }
        }
      }
    } catch (e) {
      document.getElementById('transform-preview').innerHTML = `<p style="color:red;">Transform error: ${e.message}</p>`;
      pipeEl.innerHTML = pipeHTML;
      return;
    }

    // Add undo last + clear + send to sheet buttons
    pipeHTML += `<button id="tf-undo" style="padding:2px 8px;font-size:10px;border:1px solid #ccc;border-radius:3px;background:#fff;cursor:pointer;margin-left:4px;">Undo Last</button>`;
    pipeHTML += `<button id="tf-clear" style="padding:2px 8px;font-size:10px;border:1px solid #ccc;border-radius:3px;background:#fff;cursor:pointer;margin-left:2px;">Clear All</button>`;
    pipeEl.innerHTML = pipeHTML;

    document.getElementById('tf-undo').onclick = () => {
      this._transformPipeline.pop();
      this._runTransformPreview();
    };
    document.getElementById('tf-clear').onclick = () => {
      this._transformPipeline = [];
      this._runTransformPreview();
    };

    // Render preview table
    const resultCols = table.columnNames();
    const numRows = table.numRows();
    const previewRows = Math.min(numRows, 25);

    let html = `<div style="margin-bottom:6px;font-size:11px;color:#059669;font-weight:600;">${numRows} rows × ${resultCols.length} columns</div>`;
    html += '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
    resultCols.forEach(c => { html += `<th style="border:1px solid #ddd;padding:3px 6px;background:#f5f5f5;text-align:left;white-space:nowrap;">${c}</th>`; });
    html += '</tr></thead><tbody>';
    for (let i = 0; i < previewRows; i++) {
      html += '<tr>';
      resultCols.forEach(c => {
        const v = table.get(c, i);
        html += `<td style="border:1px solid #ddd;padding:3px 6px;white-space:nowrap;">${v ?? ''}</td>`;
      });
      html += '</tr>';
    }
    if (numRows > 25) html += `<tr><td colspan="${resultCols.length}" style="border:1px solid #ddd;padding:3px 6px;color:#999;text-align:center;">...${numRows - 25} more rows</td></tr>`;
    html += '</tbody></table>';

    // Send to Sheet button
    html += `<button id="tf-to-sheet" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;font-size:12px;font-weight:600;cursor:pointer;">Send to New Sheet</button>`;

    document.getElementById('transform-preview').innerHTML = html;

    document.getElementById('tf-to-sheet').onclick = () => {
      const cellData = {};
      cellData[0] = {};
      resultCols.forEach((c, ci) => { cellData[0][ci] = { v: c, s: { bl: 1 } }; });
      for (let r = 0; r < numRows; r++) {
        cellData[r + 1] = {};
        resultCols.forEach((c, ci) => {
          const val = table.get(c, r);
          cellData[r + 1][ci] = { v: val ?? '' };
        });
      }
      const sid = 'transform_' + Date.now();
      this.univerAPI.createWorkbook({
        name: 'Transformed Data',
        sheetOrder: [sid],
        sheets: { [sid]: { id: sid, name: 'Transform Result', rowCount: numRows + 50, columnCount: resultCols.length + 5, cellData } }
      });
      document.getElementById('transform-dialog')?.remove();
    };
  }

  // ── Advanced Charts (Plotly.js, MIT) ──
  _openAdvancedChartDialog() {
    if (!this.univerAPI) return;
    if (!window.Plotly) { alert('Plotly.js not loaded'); return; }

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;

    const range = sheet.getActiveRange();
    let values = range ? range.getValues() : null;
    if (!values || values.length < 2) values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Select data or add data to sheet first'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);

    document.getElementById('advchart-dialog')?.remove();

    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'advchart-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:700px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Advanced Chart</span>
          <button id="advchart-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;">
            <button class="achart-type" data-type="waterfall" style="padding:6px 14px;border:1px solid #2563eb;border-radius:4px;background:#eff6ff;color:#2563eb;cursor:pointer;font-size:12px;font-weight:600;">Waterfall</button>
            <button class="achart-type" data-type="funnel" style="padding:6px 14px;border:1px solid #7c3aed;border-radius:4px;background:#f5f3ff;color:#7c3aed;cursor:pointer;font-size:12px;font-weight:600;">Funnel</button>
            <button class="achart-type" data-type="candlestick" style="padding:6px 14px;border:1px solid #059669;border-radius:4px;background:#ecfdf5;color:#059669;cursor:pointer;font-size:12px;font-weight:600;">Candlestick</button>
            <button class="achart-type" data-type="heatmap" style="padding:6px 14px;border:1px solid #dc2626;border-radius:4px;background:#fef2f2;color:#dc2626;cursor:pointer;font-size:12px;font-weight:600;">Heatmap</button>
            <button class="achart-type" data-type="treemap" style="padding:6px 14px;border:1px solid #d97706;border-radius:4px;background:#fffbeb;color:#d97706;cursor:pointer;font-size:12px;font-weight:600;">Treemap</button>
            <button class="achart-type" data-type="sunburst" style="padding:6px 14px;border:1px solid #ea580c;border-radius:4px;background:#fff7ed;color:#ea580c;cursor:pointer;font-size:12px;font-weight:600;">Sunburst</button>
            <button class="achart-type" data-type="violin" style="padding:6px 14px;border:1px solid #6366f1;border-radius:4px;background:#eef2ff;color:#6366f1;cursor:pointer;font-size:12px;font-weight:600;">Violin</button>
            <button class="achart-type" data-type="box" style="padding:6px 14px;border:1px solid #64748b;border-radius:4px;background:#f8fafc;color:#64748b;cursor:pointer;font-size:12px;font-weight:600;">Box Plot</button>
            <button class="achart-type" data-type="histogram" style="padding:6px 14px;border:1px solid #0891b2;border-radius:4px;background:#ecfeff;color:#0891b2;cursor:pointer;font-size:12px;font-weight:600;">Histogram</button>
            <button class="achart-type" data-type="sankey" style="padding:6px 14px;border:1px solid #9333ea;border-radius:4px;background:#faf5ff;color:#9333ea;cursor:pointer;font-size:12px;font-weight:600;">Sankey</button>
          </div>
          <div id="advchart-config" style="border:1px solid #e5e7eb;border-radius:6px;padding:12px;background:#fafafa;margin-bottom:12px;font-size:12px;"></div>
          <div id="advchart-render" style="width:100%;min-height:350px;border:1px solid #eee;border-radius:6px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('advchart-close').onclick = () => {
      window.Plotly.purge(document.getElementById('advchart-render'));
      dialog.remove();
    };

    // Store data for chart builders
    this._advChartHeaders = headers;
    this._advChartData = dataRows;

    dialog.querySelectorAll('.achart-type').forEach(btn => {
      btn.onclick = () => this._buildAdvancedChart(btn.dataset.type, headers, dataRows);
    });
  }

  _buildAdvancedChart(type, headers, dataRows) {
    const renderEl = document.getElementById('advchart-render');
    const configEl = document.getElementById('advchart-config');
    if (!renderEl || !configEl) return;

    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');
    const colIdx = (name) => headers.indexOf(name);
    const colValues = (name) => dataRows.map(r => {
      const v = r[colIdx(name)];
      const num = Number(v);
      return (!isNaN(num) && v !== '' && v != null) ? num : v;
    });
    const colNums = (name) => dataRows.map(r => Number(r[colIdx(name)]) || 0);

    switch (type) {
      case 'waterfall': {
        configEl.innerHTML = `
          <label>Categories:</label> <select id="ac-cat">${colOpts}</select>
          <label style="margin-left:8px;">Values:</label> <select id="ac-val">${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const cats = colValues(document.getElementById('ac-cat').value).map(String);
          const vals = colNums(document.getElementById('ac-val').value);
          const measures = vals.map((v, i) => i === vals.length - 1 ? 'total' : 'relative');
          window.Plotly.newPlot(renderEl, [{
            type: 'waterfall', x: cats, y: vals, measure: measures,
            connector: { line: { color: '#2563eb' } },
            increasing: { marker: { color: '#059669' } },
            decreasing: { marker: { color: '#dc2626' } },
            totals: { marker: { color: '#2563eb' } },
          }], { title: 'Waterfall Chart', margin: { t: 40 } }, { responsive: true });
        };
        break;
      }
      case 'funnel': {
        configEl.innerHTML = `
          <label>Labels:</label> <select id="ac-cat">${colOpts}</select>
          <label style="margin-left:8px;">Values:</label> <select id="ac-val">${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#7c3aed;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const labels = colValues(document.getElementById('ac-cat').value).map(String);
          const vals = colNums(document.getElementById('ac-val').value);
          window.Plotly.newPlot(renderEl, [{
            type: 'funnel', y: labels, x: vals,
            textinfo: 'value+percent initial',
            marker: { color: ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'].slice(0, labels.length) },
          }], { title: 'Funnel Chart', margin: { t: 40, l: 120 } }, { responsive: true });
        };
        break;
      }
      case 'candlestick': {
        const opts2 = headers.length >= 5 ? colOpts : colOpts;
        configEl.innerHTML = `
          <label>Date:</label> <select id="ac-date">${colOpts}</select>
          <label style="margin-left:4px;">Open:</label> <select id="ac-open">${colOpts}</select>
          <label style="margin-left:4px;">High:</label> <select id="ac-high">${colOpts}</select>
          <label style="margin-left:4px;">Low:</label> <select id="ac-low">${colOpts}</select>
          <label style="margin-left:4px;">Close:</label> <select id="ac-close">${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        // Auto-select if headers match common names
        setTimeout(() => {
          const lh = headers.map(h => h.toLowerCase());
          ['date', 'open', 'high', 'low', 'close'].forEach((key, i) => {
            const sel = document.getElementById(['ac-date','ac-open','ac-high','ac-low','ac-close'][i]);
            const match = lh.findIndex(h => h.includes(key));
            if (match >= 0 && sel) sel.selectedIndex = match;
          });
        }, 0);
        document.getElementById('ac-render').onclick = () => {
          const dates = colValues(document.getElementById('ac-date').value).map(String);
          const open = colNums(document.getElementById('ac-open').value);
          const high = colNums(document.getElementById('ac-high').value);
          const low = colNums(document.getElementById('ac-low').value);
          const close = colNums(document.getElementById('ac-close').value);
          window.Plotly.newPlot(renderEl, [{
            type: 'candlestick', x: dates, open, high, low, close,
            increasing: { line: { color: '#059669' } },
            decreasing: { line: { color: '#dc2626' } },
          }], { title: 'Candlestick Chart', xaxis: { rangeslider: { visible: true } }, margin: { t: 40 } }, { responsive: true });
        };
        break;
      }
      case 'heatmap': {
        configEl.innerHTML = `
          <label>Row labels:</label> <select id="ac-rows">${colOpts}</select>
          <label style="margin-left:8px;">Numeric columns will be used as heatmap cells.</label>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const rowLabels = colValues(document.getElementById('ac-rows').value).map(String);
          const numHeaders = headers.filter((h, i) => {
            const vals = dataRows.map(r => Number(r[i]));
            return vals.filter(n => !isNaN(n)).length > dataRows.length * 0.5;
          });
          const z = dataRows.map(row => numHeaders.map(h => Number(row[colIdx(h)]) || 0));
          window.Plotly.newPlot(renderEl, [{
            type: 'heatmap', z, x: numHeaders, y: rowLabels,
            colorscale: 'YlOrRd',
          }], { title: 'Heatmap', margin: { t: 40, l: 120 } }, { responsive: true });
        };
        break;
      }
      case 'treemap': {
        configEl.innerHTML = `
          <label>Labels:</label> <select id="ac-labels">${colOpts}</select>
          <label style="margin-left:8px;">Values (size):</label> <select id="ac-val">${colOpts}</select>
          <label style="margin-left:8px;">Parent (optional):</label> <select id="ac-parent"><option value="">(none — flat)</option>${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#d97706;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const labels = colValues(document.getElementById('ac-labels').value).map(String);
          const vals = colNums(document.getElementById('ac-val').value);
          const parentCol = document.getElementById('ac-parent').value;
          const parents = parentCol ? colValues(parentCol).map(String) : labels.map(() => '');
          window.Plotly.newPlot(renderEl, [{
            type: 'treemap', labels, values: vals, parents,
            textinfo: 'label+value+percent parent',
          }], { title: 'Treemap', margin: { t: 40 } }, { responsive: true });
        };
        break;
      }
      case 'sunburst': {
        configEl.innerHTML = `
          <label>Labels:</label> <select id="ac-labels">${colOpts}</select>
          <label style="margin-left:8px;">Values:</label> <select id="ac-val">${colOpts}</select>
          <label style="margin-left:8px;">Parent:</label> <select id="ac-parent"><option value="">(none)</option>${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#ea580c;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const labels = colValues(document.getElementById('ac-labels').value).map(String);
          const vals = colNums(document.getElementById('ac-val').value);
          const parentCol = document.getElementById('ac-parent').value;
          const parents = parentCol ? colValues(parentCol).map(String) : labels.map(() => '');
          window.Plotly.newPlot(renderEl, [{
            type: 'sunburst', labels, values: vals, parents,
          }], { title: 'Sunburst Chart', margin: { t: 40 } }, { responsive: true });
        };
        break;
      }
      case 'violin': {
        configEl.innerHTML = `
          <label>Data column:</label> <select id="ac-val">${colOpts}</select>
          <label style="margin-left:8px;">Group by (optional):</label> <select id="ac-group"><option value="">(none)</option>${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#6366f1;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const valCol = document.getElementById('ac-val').value;
          const groupCol = document.getElementById('ac-group').value;
          if (groupCol) {
            const groups = [...new Set(colValues(groupCol).map(String))];
            const traces = groups.map(g => ({
              type: 'violin', y: dataRows.filter(r => String(r[colIdx(groupCol)]) === g).map(r => Number(r[colIdx(valCol)]) || 0),
              name: g, box: { visible: true }, meanline: { visible: true },
            }));
            window.Plotly.newPlot(renderEl, traces, { title: `Violin: ${valCol} by ${groupCol}`, margin: { t: 40 } }, { responsive: true });
          } else {
            window.Plotly.newPlot(renderEl, [{
              type: 'violin', y: colNums(valCol), name: valCol,
              box: { visible: true }, meanline: { visible: true },
            }], { title: `Violin: ${valCol}`, margin: { t: 40 } }, { responsive: true });
          }
        };
        break;
      }
      case 'box': {
        configEl.innerHTML = `
          <label>Data column:</label> <select id="ac-val">${colOpts}</select>
          <label style="margin-left:8px;">Group by (optional):</label> <select id="ac-group"><option value="">(none)</option>${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#64748b;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const valCol = document.getElementById('ac-val').value;
          const groupCol = document.getElementById('ac-group').value;
          if (groupCol) {
            const groups = [...new Set(colValues(groupCol).map(String))];
            const traces = groups.map(g => ({
              type: 'box', y: dataRows.filter(r => String(r[colIdx(groupCol)]) === g).map(r => Number(r[colIdx(valCol)]) || 0),
              name: g, boxpoints: 'outliers',
            }));
            window.Plotly.newPlot(renderEl, traces, { title: `Box Plot: ${valCol} by ${groupCol}`, margin: { t: 40 } }, { responsive: true });
          } else {
            window.Plotly.newPlot(renderEl, [{
              type: 'box', y: colNums(valCol), name: valCol, boxpoints: 'outliers',
            }], { title: `Box Plot: ${valCol}`, margin: { t: 40 } }, { responsive: true });
          }
        };
        break;
      }
      case 'histogram': {
        configEl.innerHTML = `
          <label>Data column:</label> <select id="ac-val">${colOpts}</select>
          <label style="margin-left:8px;">Bins:</label> <input id="ac-bins" type="number" value="20" min="5" max="200" style="width:60px;padding:4px;border:1px solid #ccc;border-radius:3px;">
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#0891b2;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const valCol = document.getElementById('ac-val').value;
          const bins = parseInt(document.getElementById('ac-bins').value) || 20;
          window.Plotly.newPlot(renderEl, [{
            type: 'histogram', x: colNums(valCol),
            nbinsx: bins,
            marker: { color: '#2563eb' },
          }], { title: `Histogram: ${valCol}`, margin: { t: 40 }, bargap: 0.05 }, { responsive: true });
        };
        break;
      }
      case 'sankey': {
        configEl.innerHTML = `
          <label>Source:</label> <select id="ac-src">${colOpts}</select>
          <label style="margin-left:8px;">Target:</label> <select id="ac-tgt">${colOpts}</select>
          <label style="margin-left:8px;">Value (flow):</label> <select id="ac-val">${colOpts}</select>
          <button id="ac-render" style="margin-left:8px;padding:4px 14px;background:#9333ea;color:#fff;border:none;border-radius:4px;cursor:pointer;">Draw</button>
        `;
        document.getElementById('ac-render').onclick = () => {
          const srcs = colValues(document.getElementById('ac-src').value).map(String);
          const tgts = colValues(document.getElementById('ac-tgt').value).map(String);
          const vals = colNums(document.getElementById('ac-val').value);
          // Build node list
          const nodeSet = new Set([...srcs, ...tgts]);
          const nodes = [...nodeSet];
          const srcIdx = srcs.map(s => nodes.indexOf(s));
          const tgtIdx = tgts.map(t => nodes.indexOf(t));
          window.Plotly.newPlot(renderEl, [{
            type: 'sankey',
            node: { label: nodes, pad: 15, thickness: 20 },
            link: { source: srcIdx, target: tgtIdx, value: vals },
          }], { title: 'Sankey Diagram', margin: { t: 40 } }, { responsive: true });
        };
        break;
      }
    }
  }

  // ── Macro / Scripting Engine ──
  _openMacroEditor() {
    if (!this.univerAPI) return;
    document.getElementById('macro-dialog')?.remove();

    // Load saved macros
    const savedMacros = JSON.parse(localStorage.getItem('ofg_macros') || '[]');

    const templates = [
      { name: 'Auto-sum column', code: `// Sum all numeric values in column B and put result below\nconst vals = sheet.getDataRange().getValues();\nlet sum = 0, lastRow = 0;\nfor (let r = 0; r < vals.length; r++) {\n  const v = Number(vals[r][1]); // column B = index 1\n  if (!isNaN(v)) { sum += v; lastRow = r; }\n}\nsetCell(lastRow + 1, 1, sum);\nlog('Sum of column B: ' + sum);` },
      { name: 'Format currency', code: `// Format all numbers in selection as currency\nconst vals = sheet.getDataRange().getValues();\nfor (let r = 1; r < vals.length; r++) {\n  for (let c = 0; c < vals[r].length; c++) {\n    const v = Number(vals[r][c]);\n    if (!isNaN(v) && vals[r][c] !== '') {\n      setCell(r, c, '$' + v.toFixed(2).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ','));\n    }\n  }\n}\nlog('Formatted ' + (vals.length - 1) + ' rows as currency');` },
      { name: 'Highlight duplicates', code: `// Find and log duplicate values in column A\nconst vals = sheet.getDataRange().getValues();\nconst seen = {}, dupes = [];\nfor (let r = 1; r < vals.length; r++) {\n  const v = String(vals[r][0]);\n  if (seen[v]) dupes.push({ row: r + 1, value: v });\n  else seen[v] = true;\n}\nif (dupes.length > 0) {\n  log('Found ' + dupes.length + ' duplicates:');\n  dupes.forEach(d => log('  Row ' + d.row + ': ' + d.value));\n} else {\n  log('No duplicates found in column A');\n}` },
      { name: 'Generate sequence', code: `// Fill column A rows 1-100 with sequential numbers\nfor (let i = 0; i < 100; i++) {\n  setCell(i, 0, i + 1);\n}\nlog('Generated sequence 1-100 in column A');` },
      { name: 'Transpose selection', code: `// Transpose the current data (swap rows/columns)\nconst vals = sheet.getDataRange().getValues();\nconst rows = vals.length, cols = vals[0].length;\nfor (let r = 0; r < cols; r++) {\n  for (let c = 0; c < rows; c++) {\n    setCell(r, c + cols + 1, vals[c][r]);\n  }\n}\nlog('Transposed ' + rows + 'x' + cols + ' → ' + cols + 'x' + rows + ' (placed to the right)');` },
      { name: 'Calculate running total', code: `// Add running total in the column after the last data column\nconst vals = sheet.getDataRange().getValues();\nconst lastCol = vals[0].length;\nsetCell(0, lastCol, 'Running Total');\nlet total = 0;\nfor (let r = 1; r < vals.length; r++) {\n  const v = Number(vals[r][lastCol - 1]);\n  if (!isNaN(v)) total += v;\n  setCell(r, lastCol, total);\n}\nlog('Running total calculated in column ' + String.fromCharCode(65 + lastCol));` },
      { name: 'Clean whitespace', code: `// Trim whitespace from all cells\nconst vals = sheet.getDataRange().getValues();\nlet cleaned = 0;\nfor (let r = 0; r < vals.length; r++) {\n  for (let c = 0; c < vals[r].length; c++) {\n    const v = vals[r][c];\n    if (typeof v === 'string' && v !== v.trim()) {\n      setCell(r, c, v.trim());\n      cleaned++;\n    }\n  }\n}\nlog('Cleaned whitespace in ' + cleaned + ' cells');` },
      { name: 'Split column by delimiter', code: `// Split column A by comma into multiple columns\nconst vals = sheet.getDataRange().getValues();\nfor (let r = 0; r < vals.length; r++) {\n  const parts = String(vals[r][0]).split(',');\n  parts.forEach((p, i) => setCell(r, i, p.trim()));\n}\nlog('Split column A into ' + String(vals[0]?.[0] || '').split(',').length + ' columns');` },
    ];

    const templateOpts = templates.map((t, i) => `<option value="${i}">${t.name}</option>`).join('');
    const savedOpts = savedMacros.map((m, i) => `<option value="saved_${i}">${m.name}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'macro-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:700px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Macro Editor</span>
          <button id="macro-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;margin-bottom:10px;align-items:center;">
            <select id="macro-templates" style="padding:4px 8px;border:1px solid #ccc;border-radius:4px;font-size:12px;flex:1;">
              <option value="">— Load template —</option>
              ${templateOpts}
              ${savedOpts ? '<optgroup label="Saved Macros">' + savedOpts + '</optgroup>' : ''}
            </select>
            <input id="macro-name" type="text" placeholder="Macro name" value="My Macro" style="padding:4px 8px;border:1px solid #ccc;border-radius:4px;font-size:12px;width:150px;">
          </div>
          <div style="position:relative;">
            <textarea id="macro-code" style="width:100%;height:280px;font-family:'Fira Code','Source Code Pro',monospace;font-size:13px;padding:10px;border:1px solid #ccc;border-radius:6px;resize:vertical;line-height:1.5;tab-size:2;background:#1e1e2e;color:#cdd6f4;" spellcheck="false" placeholder="// Write your macro here...
// Available: sheet, setCell(row, col, value), getCell(row, col), log(msg)
// All row/col are 0-based."></textarea>
          </div>
          <div style="display:flex;gap:8px;margin-top:10px;">
            <button id="macro-run" style="padding:6px 20px;background:#059669;color:#fff;border:none;border-radius:4px;font-weight:600;cursor:pointer;font-size:13px;">▶ Run</button>
            <button id="macro-save" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Save Macro</button>
            <button id="macro-delete" style="padding:6px 16px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Delete Saved</button>
            <span id="macro-status" style="font-size:12px;color:#666;line-height:32px;margin-left:auto;"></span>
          </div>
          <div style="margin-top:10px;">
            <div style="font-size:11px;font-weight:600;color:#666;margin-bottom:4px;">Console Output:</div>
            <pre id="macro-output" style="background:#1e1e2e;color:#a6e3a1;padding:10px;border-radius:6px;font-family:'Fira Code',monospace;font-size:12px;max-height:150px;overflow:auto;min-height:40px;white-space:pre-wrap;"></pre>
          </div>
          <div style="margin-top:8px;font-size:10px;color:#999;border-top:1px solid #eee;padding-top:8px;">
            <b>API:</b> <code>sheet</code> = active sheet data (2D array) · <code>setCell(row, col, value)</code> · <code>getCell(row, col)</code> · <code>log(msg)</code> · <code>headers</code> = first row · <code>rowCount</code> / <code>colCount</code> · <code>Decimal</code> = decimal.js (if precision needed)
          </div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('macro-close').onclick = () => dialog.remove();

    // Template loader
    document.getElementById('macro-templates').onchange = (e) => {
      const val = e.target.value;
      if (val === '') return;
      if (val.startsWith('saved_')) {
        const idx = parseInt(val.replace('saved_', ''));
        const m = savedMacros[idx];
        if (m) {
          document.getElementById('macro-code').value = m.code;
          document.getElementById('macro-name').value = m.name;
        }
      } else {
        const t = templates[parseInt(val)];
        if (t) {
          document.getElementById('macro-code').value = t.code;
          document.getElementById('macro-name').value = t.name;
        }
      }
    };

    // Tab key support in editor
    document.getElementById('macro-code').addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const ta = e.target;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        ta.value = ta.value.substring(0, start) + '  ' + ta.value.substring(end);
        ta.selectionStart = ta.selectionEnd = start + 2;
      }
    });

    document.getElementById('macro-run').onclick = () => this._runMacro();
    document.getElementById('macro-save').onclick = () => this._saveMacro();
    document.getElementById('macro-delete').onclick = () => this._deleteSavedMacro();
  }

  _runMacro() {
    const code = document.getElementById('macro-code')?.value?.trim();
    if (!code) return;

    const outputEl = document.getElementById('macro-output');
    const statusEl = document.getElementById('macro-status');
    if (outputEl) outputEl.textContent = '';

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;
    const values = sheetAPI.getDataRange()?.getValues() || [];

    const logs = [];
    const cellUpdates = [];

    // Build sandbox API
    const sandbox = {
      sheet: values,
      headers: values[0] || [],
      rowCount: values.length,
      colCount: values[0]?.length || 0,
      log: (msg) => { logs.push(String(msg)); },
      getCell: (r, c) => values[r]?.[c] ?? null,
      setCell: (r, c, v) => { cellUpdates.push({ r, c, v }); },
      Decimal: window.Decimal || null,
      Math: Math,
      Number: Number,
      String: String,
      parseInt: parseInt,
      parseFloat: parseFloat,
      isNaN: isNaN,
      JSON: JSON,
      Date: Date,
      Array: Array,
      Object: Object,
    };

    const startTime = performance.now();
    try {
      const argNames = Object.keys(sandbox);
      const argValues = Object.values(sandbox);
      const fn = new Function(...argNames, code);
      fn(...argValues);

      // Apply cell updates to sheet
      if (cellUpdates.length > 0) {
        const range = sheetAPI.getRange(0, 0, values.length + cellUpdates.length, (values[0]?.length || 26) + 10);
        cellUpdates.forEach(({ r, c, v }) => {
          try {
            const cellRange = sheetAPI.getRange(r, c, 1, 1);
            cellRange.setValue(v);
          } catch (e) {
            logs.push(`Error setting cell (${r},${c}): ${e.message}`);
          }
        });
      }

      const elapsed = (performance.now() - startTime).toFixed(1);
      if (statusEl) statusEl.textContent = `Completed in ${elapsed}ms — ${cellUpdates.length} cells updated`;
      if (statusEl) statusEl.style.color = '#059669';
    } catch (e) {
      logs.push(`ERROR: ${e.message}`);
      if (statusEl) { statusEl.textContent = 'Macro failed'; statusEl.style.color = '#dc2626'; }
    }

    if (outputEl) outputEl.textContent = logs.join('\n') || '(no output)';
  }

  _saveMacro() {
    const name = document.getElementById('macro-name')?.value?.trim() || 'Unnamed';
    const code = document.getElementById('macro-code')?.value || '';
    const macros = JSON.parse(localStorage.getItem('ofg_macros') || '[]');
    const existing = macros.findIndex(m => m.name === name);
    if (existing >= 0) macros[existing] = { name, code, savedAt: new Date().toISOString() };
    else macros.push({ name, code, savedAt: new Date().toISOString() });
    localStorage.setItem('ofg_macros', JSON.stringify(macros));
    const statusEl = document.getElementById('macro-status');
    if (statusEl) { statusEl.textContent = `Saved "${name}"`; statusEl.style.color = '#2563eb'; }
  }

  _deleteSavedMacro() {
    const name = document.getElementById('macro-name')?.value?.trim();
    if (!name) return;
    let macros = JSON.parse(localStorage.getItem('ofg_macros') || '[]');
    const before = macros.length;
    macros = macros.filter(m => m.name !== name);
    localStorage.setItem('ofg_macros', JSON.stringify(macros));
    const statusEl = document.getElementById('macro-status');
    if (statusEl) {
      if (macros.length < before) { statusEl.textContent = `Deleted "${name}"`; statusEl.style.color = '#dc2626'; }
      else { statusEl.textContent = `"${name}" not found in saved macros`; statusEl.style.color = '#999'; }
    }
  }

  // ── JS Scripting Console (CodeMirror 6) ──
  _openScriptConsole() {
    if (!this.univerAPI) return;
    document.getElementById('script-console-dialog')?.remove();

    const cm6 = window.CM6;
    const hasCM6 = !!(cm6 && cm6.EditorView);

    const savedScripts = JSON.parse(localStorage.getItem('ofg_scripts') || '[]');

    const templates = [
      { name: 'Sum a Column', code: '// Sum all numeric values in column B\nlet sum = 0, count = 0;\nfor (let r = 1; r < rowCount; r++) {\n  const v = Number(getCell(r, 1));\n  if (!isNaN(v) && getCell(r, 1) !== \'\') { sum += v; count++; }\n}\nsetCell(rowCount, 1, sum);\nlog(`Sum of ${count} values in column B: ${sum}`);' },
      { name: 'Filter Rows by Condition', code: '// Log rows where column C > 100\nconst threshold = 100;\nlet matches = 0;\nfor (let r = 1; r < rowCount; r++) {\n  const v = Number(getCell(r, 2));\n  if (v > threshold) {\n    log(`Row ${r + 1}: ${headers[2]} = ${v}`);\n    matches++;\n  }\n}\nlog(`\\nFound ${matches} rows where ${headers[2] || "C"} > ${threshold}`);' },
      { name: 'Statistical Summary', code: '// Compute stats for each numeric column\nfor (let c = 0; c < colCount; c++) {\n  const nums = [];\n  for (let r = 1; r < rowCount; r++) {\n    const v = Number(getCell(r, c));\n    if (!isNaN(v) && getCell(r, c) !== \'\') nums.push(v);\n  }\n  if (nums.length < 2) continue;\n  const sum = nums.reduce((a, b) => a + b, 0);\n  const mean = sum / nums.length;\n  const sorted = [...nums].sort((a, b) => a - b);\n  const median = sorted.length % 2 === 0\n    ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2\n    : sorted[Math.floor(sorted.length / 2)];\n  const variance = nums.reduce((s, v) => s + (v - mean) ** 2, 0) / nums.length;\n  const stdDev = Math.sqrt(variance);\n  log(`Column ${headers[c] || String.fromCharCode(65 + c)}:`);\n  log(`  Count: ${nums.length}  Sum: ${sum.toFixed(2)}`);\n  log(`  Mean: ${mean.toFixed(2)}  Median: ${median}`);\n  log(`  StdDev: ${stdDev.toFixed(2)}  Min: ${sorted[0]}  Max: ${sorted[sorted.length - 1]}`);\n  log(\'\');\n}' },
      { name: 'Pivot Summary', code: '// Group by column A, sum column B\nconst groups = {};\nfor (let r = 1; r < rowCount; r++) {\n  const key = String(getCell(r, 0));\n  const val = Number(getCell(r, 1)) || 0;\n  groups[key] = (groups[key] || 0) + val;\n}\nlog(\'Group | Sum\');\nlog(\'------|------\');\nObject.entries(groups)\n  .sort((a, b) => b[1] - a[1])\n  .forEach(([k, v]) => log(`${k} | ${v.toFixed(2)}`));\nlog(`\\n${Object.keys(groups).length} groups from ${rowCount - 1} rows`);' },
      { name: 'Export to JSON', code: '// Convert sheet to JSON array\nconst result = [];\nfor (let r = 1; r < rowCount; r++) {\n  const obj = {};\n  for (let c = 0; c < colCount; c++) {\n    obj[headers[c] || `col${c}`] = getCell(r, c);\n  }\n  result.push(obj);\n}\nlog(JSON.stringify(result, null, 2));\nlog(`\\n${result.length} records exported`);' },
      { name: 'Bulk Formula Apply', code: '// Apply tax (8%) to column B, write results in column C\nconst taxRate = 0.08;\nsetCell(0, 2, \'With Tax\');\nfor (let r = 1; r < rowCount; r++) {\n  const v = Number(getCell(r, 1));\n  if (!isNaN(v) && getCell(r, 1) !== \'\') {\n    setCell(r, 2, Number((v * (1 + taxRate)).toFixed(2)));\n  }\n}\nlog(`Applied ${taxRate * 100}% tax to ${rowCount - 1} rows`);' },
      { name: 'Date Analysis', code: '// Parse dates in column A, compute range & gaps\nconst dates = [];\nfor (let r = 1; r < rowCount; r++) {\n  const d = new Date(getCell(r, 0));\n  if (!isNaN(d.getTime())) dates.push({ row: r, date: d });\n}\ndates.sort((a, b) => a.date - b.date);\nif (dates.length > 1) {\n  const first = dates[0].date, last = dates[dates.length - 1].date;\n  const days = Math.round((last - first) / 86400000);\n  log(`Date range: ${first.toLocaleDateString()} → ${last.toLocaleDateString()} (${days} days)`);\n  log(`Records with dates: ${dates.length}`);\n  const gaps = [];\n  for (let i = 1; i < dates.length; i++) {\n    gaps.push(Math.round((dates[i].date - dates[i-1].date) / 86400000));\n  }\n  const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;\n  log(`Average gap: ${avgGap.toFixed(1)} days`);\n  log(`Max gap: ${Math.max(...gaps)} days`);\n} else {\n  log(\'Need at least 2 dates in column A\');\n}' },
      { name: 'Create ECharts Bar', code: '// Build a bar chart from columns A (labels) and B (values)\nconst labels = [], values = [];\nfor (let r = 1; r < Math.min(rowCount, 50); r++) {\n  const lbl = getCell(r, 0), val = Number(getCell(r, 1));\n  if (lbl && !isNaN(val)) { labels.push(String(lbl)); values.push(val); }\n}\nif (typeof echarts !== \'undefined\' && labels.length > 0) {\n  const div = document.createElement(\'div\');\n  div.style.cssText = \'position:fixed;top:10%;left:10%;width:80%;height:70%;z-index:9999;background:#fff;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.3);\';\n  const close = document.createElement(\'button\');\n  close.textContent = \'✕ Close\';\n  close.style.cssText = \'position:absolute;top:8px;right:12px;z-index:10;padding:4px 12px;border:none;background:#dc2626;color:#fff;border-radius:4px;cursor:pointer;\';\n  close.onclick = () => div.remove();\n  div.appendChild(close);\n  document.body.appendChild(div);\n  const chart = echarts.init(div);\n  chart.setOption({ title: { text: headers[1] || \'Values\', left: \'center\' }, tooltip: {}, xAxis: { data: labels, axisLabel: { rotate: 30 } }, yAxis: {}, series: [{ type: \'bar\', data: values, itemStyle: { color: \'#3b82f6\' } }] });\n  log(`Chart created with ${labels.length} data points`);\n} else {\n  log(\'Need echarts + data in columns A & B\');\n}' },
      { name: 'Running Total', code: '// Add running total after last column\nconst lastCol = colCount;\nsetCell(0, lastCol, \'Running Total\');\nlet total = 0;\nfor (let r = 1; r < rowCount; r++) {\n  const v = Number(getCell(r, lastCol - 1));\n  if (!isNaN(v)) total += v;\n  setCell(r, lastCol, total);\n}\nlog(`Running total in column ${String.fromCharCode(65 + lastCol)}, final: ${total.toFixed(2)}`);' },
      { name: 'Clean & Trim Data', code: '// Trim whitespace, normalize empty strings\nlet cleaned = 0;\nfor (let r = 0; r < rowCount; r++) {\n  for (let c = 0; c < colCount; c++) {\n    const v = getCell(r, c);\n    if (typeof v === \'string\') {\n      const trimmed = v.trim();\n      if (trimmed !== v) { setCell(r, c, trimmed); cleaned++; }\n    }\n  }\n}\nlog(`Trimmed whitespace in ${cleaned} cells across ${rowCount} rows`);' },
    ];

    const templateOpts = templates.map((t, i) => `<option value="${i}">${t.name}</option>`).join('');
    const savedOpts = savedScripts.map((s, i) => `<option value="saved_${i}">${s.name}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'script-console-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner script-console-inner">
        <div class="pivot-dialog-header">
          <span>&#128187; JS Scripting Console</span>
          <button id="script-console-close">&times;</button>
        </div>
        <div class="script-console-body">
          <div class="script-console-toolbar">
            <select id="script-tpl-select">
              <option value="">— Templates —</option>
              ${templateOpts}
            </select>
            <select id="script-saved-select">
              <option value="">— Saved Scripts —</option>
              ${savedOpts}
            </select>
            <input id="script-name" type="text" placeholder="Script name" value="My Script">
            <button id="script-save-btn" class="script-btn script-btn-blue">Save</button>
            <button id="script-delete-btn" class="script-btn script-btn-red">Delete</button>
          </div>
          <div id="script-editor-mount"></div>
          <div class="script-console-actions">
            <button id="script-run-btn" class="script-btn script-btn-green">&#9654; Run</button>
            <button id="script-clear-output-btn" class="script-btn script-btn-gray">Clear</button>
            <button id="script-copy-output-btn" class="script-btn script-btn-gray">Copy Output</button>
            <span id="script-status"></span>
          </div>
          <div class="script-console-output-header">Console Output:</div>
          <pre id="script-output"></pre>
          <div class="script-console-api-ref">
            <b>API:</b> <code>sheet</code> (2D array) &middot; <code>setCell(row, col, val)</code> &middot; <code>getCell(row, col)</code> &middot;
            <code>log(msg)</code> &middot; <code>warn(msg)</code> &middot; <code>error(msg)</code> &middot; <code>table(arrOfObj)</code> &middot;
            <code>headers</code> &middot; <code>rowCount</code> / <code>colCount</code> &middot;
            <code>Decimal</code> &middot; <code>jStat</code> &middot; <code>dayjs</code> &middot; <code>formulajs</code> &middot;
            <code>echarts</code> &middot; <code>Plotly</code> &middot; <code>aq</code> (arquero) &middot; <code>math</code> (mathjs) &middot;
            All rows/cols are 0-based.
          </div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    // Mount CodeMirror 6 or fallback textarea
    let editorView = null;
    const mountEl = document.getElementById('script-editor-mount');

    if (hasCM6) {
      try {
        const { EditorView, basicSetup, javascript, oneDark, EditorState, keymap: km, indentWithTab } = cm6;
        const startState = EditorState.create({
          doc: '// Write your script here...\n// Use the Templates dropdown above for examples.\n',
          extensions: [
            basicSetup,
            javascript(),
            oneDark,
            km.of([indentWithTab]),
            EditorView.theme({
              '&': { height: '300px', fontSize: '13px' },
              '.cm-scroller': { overflow: 'auto' },
              '.cm-content': { fontFamily: "'Fira Code','Source Code Pro',monospace" },
            }),
          ],
        });
        editorView = new EditorView({ state: startState, parent: mountEl });
      } catch (e) {
        // CM6 failed to init — use fallback
        editorView = null;
      }
    }

    if (!editorView) {
      mountEl.innerHTML = `<textarea id="script-editor-fallback" spellcheck="false" placeholder="// Write your script here...&#10;// Available: sheet, setCell(row, col, value), getCell(row, col), log(msg)"></textarea>`;
      // Tab key support
      const ta = document.getElementById('script-editor-fallback');
      if (ta) ta.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          const start = ta.selectionStart, end = ta.selectionEnd;
          ta.value = ta.value.substring(0, start) + '  ' + ta.value.substring(end);
          ta.selectionStart = ta.selectionEnd = start + 2;
        }
      });
    }

    // Helpers to get/set code
    const getCode = () => {
      if (editorView) return editorView.state.doc.toString();
      return document.getElementById('script-editor-fallback')?.value || '';
    };
    const setCode = (code) => {
      if (editorView) {
        editorView.dispatch({ changes: { from: 0, to: editorView.state.doc.length, insert: code } });
      } else {
        const ta = document.getElementById('script-editor-fallback');
        if (ta) ta.value = code;
      }
    };

    // Wire events
    document.getElementById('script-console-close').onclick = () => dialog.remove();

    document.getElementById('script-tpl-select').onchange = (e) => {
      const val = e.target.value;
      if (val === '') return;
      const t = templates[parseInt(val)];
      if (t) { setCode(t.code); document.getElementById('script-name').value = t.name; }
    };

    document.getElementById('script-saved-select').onchange = (e) => {
      const val = e.target.value;
      if (!val || !val.startsWith('saved_')) return;
      const idx = parseInt(val.replace('saved_', ''));
      const s = savedScripts[idx];
      if (s) { setCode(s.code); document.getElementById('script-name').value = s.name; }
    };

    document.getElementById('script-run-btn').onclick = () => {
      this._runScript(getCode);
    };

    document.getElementById('script-save-btn').onclick = () => {
      const name = document.getElementById('script-name')?.value?.trim() || 'Unnamed';
      const code = getCode();
      const scripts = JSON.parse(localStorage.getItem('ofg_scripts') || '[]');
      const existing = scripts.findIndex(s => s.name === name);
      if (existing >= 0) scripts[existing] = { name, code, savedAt: new Date().toISOString() };
      else scripts.push({ name, code, savedAt: new Date().toISOString() });
      localStorage.setItem('ofg_scripts', JSON.stringify(scripts));
      const statusEl = document.getElementById('script-status');
      if (statusEl) { statusEl.textContent = `Saved "${name}"`; statusEl.style.color = '#2563eb'; }
      // Refresh saved dropdown
      const sel = document.getElementById('script-saved-select');
      if (sel) {
        const updated = JSON.parse(localStorage.getItem('ofg_scripts') || '[]');
        sel.innerHTML = '<option value="">— Saved Scripts —</option>' +
          updated.map((s, i) => `<option value="saved_${i}">${s.name}</option>`).join('');
      }
    };

    document.getElementById('script-delete-btn').onclick = () => {
      const name = document.getElementById('script-name')?.value?.trim();
      if (!name) return;
      let scripts = JSON.parse(localStorage.getItem('ofg_scripts') || '[]');
      const before = scripts.length;
      scripts = scripts.filter(s => s.name !== name);
      localStorage.setItem('ofg_scripts', JSON.stringify(scripts));
      const statusEl = document.getElementById('script-status');
      if (scripts.length < before) {
        if (statusEl) { statusEl.textContent = `Deleted "${name}"`; statusEl.style.color = '#dc2626'; }
        const sel = document.getElementById('script-saved-select');
        if (sel) {
          sel.innerHTML = '<option value="">— Saved Scripts —</option>' +
            scripts.map((s, i) => `<option value="saved_${i}">${s.name}</option>`).join('');
        }
      } else {
        if (statusEl) { statusEl.textContent = `"${name}" not found`; statusEl.style.color = '#999'; }
      }
    };

    document.getElementById('script-clear-output-btn').onclick = () => {
      const out = document.getElementById('script-output');
      if (out) out.innerHTML = '';
    };

    document.getElementById('script-copy-output-btn').onclick = () => {
      const out = document.getElementById('script-output');
      if (out) {
        navigator.clipboard.writeText(out.textContent).then(() => {
          const statusEl = document.getElementById('script-status');
          if (statusEl) { statusEl.textContent = 'Copied to clipboard'; statusEl.style.color = '#059669'; }
        });
      }
    };
  }

  _runScript(getCodeFn) {
    const code = typeof getCodeFn === 'function' ? getCodeFn() : '';
    if (!code.trim()) return;

    const outputEl = document.getElementById('script-output');
    const statusEl = document.getElementById('script-status');
    if (outputEl) outputEl.innerHTML = '';

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;
    const values = sheetAPI.getDataRange()?.getValues() || [];

    const logs = [];
    const cellUpdates = [];

    const addLog = (msg, cls) => {
      if (cls) logs.push(`<span class="${cls}">${String(msg).replace(/</g, '&lt;')}</span>`);
      else logs.push(String(msg).replace(/</g, '&lt;'));
    };

    // table() helper — format array of objects as aligned text table
    const tableLog = (data) => {
      if (!Array.isArray(data) || data.length === 0) { addLog('(empty table)'); return; }
      const keys = Object.keys(data[0]);
      const widths = keys.map(k => Math.max(k.length, ...data.map(row => String(row[k] ?? '').length)));
      const header = keys.map((k, i) => k.padEnd(widths[i])).join(' | ');
      const sep = widths.map(w => '-'.repeat(w)).join('-+-');
      addLog(header);
      addLog(sep);
      data.forEach(row => {
        addLog(keys.map((k, i) => String(row[k] ?? '').padEnd(widths[i])).join(' | '));
      });
    };

    const sandbox = {
      sheet: values,
      headers: values[0] || [],
      rowCount: values.length,
      colCount: values[0]?.length || 0,
      log: (msg) => addLog(msg),
      warn: (msg) => addLog(`⚠ ${msg}`, 'script-warn'),
      error: (msg) => addLog(`✖ ${msg}`, 'script-error'),
      table: tableLog,
      getCell: (r, c) => values[r]?.[c] ?? null,
      setCell: (r, c, v) => { cellUpdates.push({ r, c, v }); },
      Decimal: window.Decimal || null,
      jStat: window.jStat || null,
      dayjs: window.dayjs || null,
      formulajs: window.formulajs || null,
      echarts: window.echarts || null,
      Plotly: window.Plotly || null,
      aq: window.aq || null,
      math: window.math || null,
      regression: window.regression || null,
      chroma: window.chroma || null,
      currency: window.currency || null,
      Papa: window.Papa || null,
      alasql: window.alasql || null,
      Math: Math,
      Number: Number,
      String: String,
      parseInt: parseInt,
      parseFloat: parseFloat,
      isNaN: isNaN,
      JSON: JSON,
      Date: Date,
      Array: Array,
      Object: Object,
      Map: Map,
      Set: Set,
      RegExp: RegExp,
      console: { log: (msg) => addLog(msg), warn: (msg) => addLog(`⚠ ${msg}`, 'script-warn'), error: (msg) => addLog(`✖ ${msg}`, 'script-error') },
    };

    const startTime = performance.now();
    try {
      const argNames = Object.keys(sandbox);
      const argValues = Object.values(sandbox);
      const fn = new Function(...argNames, code);
      fn(...argValues);

      if (cellUpdates.length > 0) {
        cellUpdates.forEach(({ r, c, v }) => {
          try {
            sheetAPI.getRange(r, c, 1, 1).setValue(v);
          } catch (e) {
            addLog(`Error setting cell (${r},${c}): ${e.message}`, 'script-error');
          }
        });
      }

      const elapsed = (performance.now() - startTime).toFixed(1);
      if (statusEl) { statusEl.textContent = `Completed in ${elapsed}ms — ${cellUpdates.length} cells updated`; statusEl.style.color = '#059669'; }
    } catch (e) {
      addLog(`ERROR: ${e.message}${e.stack ? '\n' + e.stack.split('\n').slice(0, 3).join('\n') : ''}`, 'script-error');
      if (statusEl) { statusEl.textContent = 'Script failed'; statusEl.style.color = '#dc2626'; }
    }

    if (outputEl) outputEl.innerHTML = logs.join('\n') || '(no output)';
  }

  // ── Network Graph (Cytoscape.js) ──
  _openNetworkGraph() {
    if (!this.univerAPI) return;
    document.getElementById('network-graph-dialog')?.remove();

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;
    const values = sheetAPI.getDataRange()?.getValues() || [];

    if (!window.cytoscape) {
      alert('Cytoscape.js not loaded — network graph unavailable');
      return;
    }

    const dialog = document.createElement('div');
    dialog.id = 'network-graph-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:900px;max-height:90vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>&#128280; Network Graph</span>
          <button id="netgraph-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <div style="font-size:12px;color:#666;margin-bottom:10px;">
            Data format: Column A = Source node, Column B = Target node, Column C (optional) = Edge label/weight.
            Each row is an edge connecting two nodes.
          </div>
          <div style="display:flex;gap:8px;margin-bottom:10px;">
            <select id="netgraph-layout" style="padding:4px 8px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
              <option value="cose">Force-directed (CoSE)</option>
              <option value="circle">Circle</option>
              <option value="grid">Grid</option>
              <option value="breadthfirst">Hierarchy</option>
              <option value="concentric">Concentric</option>
            </select>
            <button id="netgraph-render" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">Render Graph</button>
            <button id="netgraph-export" style="padding:6px 12px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Export PNG</button>
            <span id="netgraph-status" style="font-size:12px;color:#666;line-height:32px;margin-left:auto;"></span>
          </div>
          <div id="netgraph-canvas" style="width:100%;height:500px;border:1px solid #ddd;border-radius:6px;background:#fafafa;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('netgraph-close').onclick = () => dialog.remove();

    let cy = null;

    document.getElementById('netgraph-render').onclick = () => {
      const elements = [];
      const nodeSet = new Set();

      for (let r = 1; r < values.length; r++) {
        const src = String(values[r][0] || '').trim();
        const tgt = String(values[r][1] || '').trim();
        const label = values[r][2] != null ? String(values[r][2]) : '';
        if (!src && !tgt) continue;

        if (src && !nodeSet.has(src)) { nodeSet.add(src); elements.push({ data: { id: src, label: src } }); }
        if (tgt && !nodeSet.has(tgt)) { nodeSet.add(tgt); elements.push({ data: { id: tgt, label: tgt } }); }
        if (src && tgt) {
          elements.push({ data: { id: `e${r}`, source: src, target: tgt, label: label } });
        }
      }

      if (elements.length === 0) {
        document.getElementById('netgraph-status').textContent = 'No edges found — need Source in A, Target in B';
        return;
      }

      const layoutName = document.getElementById('netgraph-layout').value;

      cy = window.cytoscape({
        container: document.getElementById('netgraph-canvas'),
        elements: elements,
        layout: { name: layoutName, animate: true, animationDuration: 500 },
        style: [
          { selector: 'node', style: {
            'background-color': '#3b82f6', 'label': 'data(label)', 'color': '#333',
            'text-valign': 'bottom', 'text-halign': 'center', 'font-size': '11px',
            'width': 30, 'height': 30, 'border-width': 2, 'border-color': '#1d4ed8',
          }},
          { selector: 'edge', style: {
            'width': 2, 'line-color': '#94a3b8', 'target-arrow-color': '#64748b',
            'target-arrow-shape': 'triangle', 'curve-style': 'bezier',
            'label': 'data(label)', 'font-size': '10px', 'text-rotation': 'autorotate',
          }},
          { selector: 'node:selected', style: { 'background-color': '#f59e0b', 'border-color': '#d97706' }},
        ],
      });

      document.getElementById('netgraph-status').textContent = `${nodeSet.size} nodes, ${elements.length - nodeSet.size} edges`;
    };

    document.getElementById('netgraph-export').onclick = () => {
      if (!cy) return;
      const png = cy.png({ output: 'blob', full: true, scale: 2, bg: '#ffffff' });
      const url = URL.createObjectURL(png);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'network-graph.png';
      a.click();
      URL.revokeObjectURL(url);
    };

    // Auto-render on open
    document.getElementById('netgraph-render').click();
  }

  // ── Geographic Map (Leaflet) ──
  _openGeoMap() {
    if (!this.univerAPI) return;
    document.getElementById('geo-map-dialog')?.remove();

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;
    const values = sheetAPI.getDataRange()?.getValues() || [];

    if (!window.L) {
      alert('Leaflet not loaded — geographic map unavailable');
      return;
    }

    const dialog = document.createElement('div');
    dialog.id = 'geo-map-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:900px;max-height:90vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>&#127758; Geographic Map</span>
          <button id="geomap-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <div style="font-size:12px;color:#666;margin-bottom:10px;">
            Data format: Column A = Latitude, Column B = Longitude, Column C (optional) = Label, Column D (optional) = Value for sizing.
          </div>
          <div style="display:flex;gap:8px;margin-bottom:10px;">
            <select id="geomap-tile" style="padding:4px 8px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
              <option value="streets">Street Map</option>
              <option value="satellite">Satellite</option>
              <option value="topo">Topographic</option>
            </select>
            <button id="geomap-render" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">Plot Points</button>
            <label style="font-size:12px;line-height:32px;"><input type="checkbox" id="geomap-cluster" checked> Cluster nearby</label>
            <span id="geomap-status" style="font-size:12px;color:#666;line-height:32px;margin-left:auto;"></span>
          </div>
          <div id="geomap-canvas" style="width:100%;height:500px;border:1px solid #ddd;border-radius:6px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('geomap-close').onclick = () => dialog.remove();

    let map = null;

    const tileUrls = {
      streets: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      topo: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    };

    document.getElementById('geomap-render').onclick = () => {
      const points = [];
      for (let r = 1; r < values.length; r++) {
        const lat = parseFloat(values[r][0]);
        const lng = parseFloat(values[r][1]);
        if (isNaN(lat) || isNaN(lng)) continue;
        const label = values[r][2] != null ? String(values[r][2]) : `Point ${r}`;
        const val = values[r][3] != null ? parseFloat(values[r][3]) : null;
        points.push({ lat, lng, label, val });
      }

      if (points.length === 0) {
        document.getElementById('geomap-status').textContent = 'No valid lat/lng pairs found in columns A & B';
        return;
      }

      // Init or reset map
      if (map) map.remove();
      const tileKey = document.getElementById('geomap-tile').value;

      map = window.L.map('geomap-canvas').setView([points[0].lat, points[0].lng], 4);
      window.L.tileLayer(tileUrls[tileKey] || tileUrls.streets, {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 18,
      }).addTo(map);

      // Add markers
      const bounds = window.L.latLngBounds();
      points.forEach(p => {
        const radius = p.val != null ? Math.max(5, Math.min(20, Math.sqrt(Math.abs(p.val)))) : 8;
        const marker = window.L.circleMarker([p.lat, p.lng], {
          radius: radius,
          fillColor: '#3b82f6',
          color: '#1d4ed8',
          weight: 2,
          fillOpacity: 0.7,
        }).addTo(map);

        let popup = `<b>${p.label}</b><br>Lat: ${p.lat.toFixed(4)}, Lng: ${p.lng.toFixed(4)}`;
        if (p.val != null) popup += `<br>Value: ${p.val}`;
        marker.bindPopup(popup);

        bounds.extend([p.lat, p.lng]);
      });

      map.fitBounds(bounds, { padding: [30, 30] });
      document.getElementById('geomap-status').textContent = `${points.length} points plotted`;
    };

    // Auto-render
    setTimeout(() => document.getElementById('geomap-render').click(), 100);
  }

  // ── ML Predictions (brain.js) ──
  _openMLPredict() {
    if (!this.univerAPI) return;
    document.getElementById('ml-predict-dialog')?.remove();

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;
    const values = sheetAPI.getDataRange()?.getValues() || [];

    if (!window.brain) {
      alert('brain.js not loaded — ML predictions unavailable');
      return;
    }

    const headers = values[0] || [];
    const colOpts = headers.map((h, i) => `<option value="${i}">${h || `Col ${String.fromCharCode(65 + i)}`}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'ml-predict-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:750px;max-height:90vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>&#129504; ML Predictions (brain.js)</span>
          <button id="ml-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <div style="font-size:12px;color:#666;margin-bottom:10px;">
            Train a neural network on your data to make predictions. Select input columns (features) and one output column (target).
          </div>
          <div style="display:flex;gap:8px;margin-bottom:10px;flex-wrap:wrap;align-items:center;">
            <label style="font-size:12px;">Input columns:</label>
            <select id="ml-inputs" multiple style="padding:4px;border:1px solid #ccc;border-radius:4px;font-size:12px;min-width:200px;height:80px;">
              ${colOpts}
            </select>
            <label style="font-size:12px;">Target column:</label>
            <select id="ml-target" style="padding:4px 8px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
              ${colOpts}
            </select>
          </div>
          <div style="display:flex;gap:8px;margin-bottom:10px;align-items:center;">
            <label style="font-size:12px;">Network type:</label>
            <select id="ml-type" style="padding:4px 8px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
              <option value="NeuralNetwork">Feed-Forward NN</option>
              <option value="RNNTimeStep">RNN Time Step (sequence)</option>
            </select>
            <label style="font-size:12px;">Iterations:</label>
            <input id="ml-iterations" type="number" value="2000" min="100" max="50000" style="width:80px;padding:4px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
            <label style="font-size:12px;">Learning rate:</label>
            <input id="ml-lr" type="number" value="0.01" min="0.001" max="1" step="0.001" style="width:80px;padding:4px;border:1px solid #ccc;border-radius:4px;font-size:12px;">
          </div>
          <div style="display:flex;gap:8px;margin-bottom:10px;">
            <button id="ml-train" style="padding:6px 20px;background:#059669;color:#fff;border:none;border-radius:4px;font-weight:600;cursor:pointer;font-size:13px;">&#9654; Train Model</button>
            <button id="ml-predict-btn" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;" disabled>Predict & Fill</button>
            <span id="ml-status" style="font-size:12px;color:#666;line-height:32px;margin-left:auto;"></span>
          </div>
          <div style="font-size:11px;font-weight:600;color:#666;margin-bottom:4px;">Training Output:</div>
          <pre id="ml-output" style="background:#1e1e2e;color:#a6e3a1;padding:10px;border-radius:6px;font-family:'Fira Code',monospace;font-size:12px;max-height:200px;overflow:auto;min-height:60px;white-space:pre-wrap;"></pre>
          <div style="margin-top:8px;font-size:10px;color:#999;">
            <b>How it works:</b> Rows with values in ALL selected columns (input + target) become training data.
            Rows with input values but empty target will receive predictions.
            Data is auto-normalized to 0–1 range.
          </div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('ml-close').onclick = () => dialog.remove();

    let trainedNet = null;
    let normMeta = null;

    document.getElementById('ml-train').onclick = () => {
      const outputEl = document.getElementById('ml-output');
      const statusEl = document.getElementById('ml-status');
      outputEl.textContent = 'Training...\n';

      const inputCols = Array.from(document.getElementById('ml-inputs').selectedOptions).map(o => parseInt(o.value));
      const targetCol = parseInt(document.getElementById('ml-target').value);
      const iterations = parseInt(document.getElementById('ml-iterations').value) || 2000;
      const lr = parseFloat(document.getElementById('ml-lr').value) || 0.01;
      const netType = document.getElementById('ml-type').value;

      if (inputCols.length === 0) { outputEl.textContent = 'Select at least one input column'; return; }
      if (inputCols.includes(targetCol)) { outputEl.textContent = 'Target column should not be in inputs'; return; }

      // Build training data — normalize to 0-1
      const allCols = [...inputCols, targetCol];
      const mins = {}, maxes = {};
      allCols.forEach(c => { mins[c] = Infinity; maxes[c] = -Infinity; });

      // First pass: find min/max
      for (let r = 1; r < values.length; r++) {
        allCols.forEach(c => {
          const v = parseFloat(values[r][c]);
          if (!isNaN(v)) {
            if (v < mins[c]) mins[c] = v;
            if (v > maxes[c]) maxes[c] = v;
          }
        });
      }

      normMeta = { mins, maxes, inputCols, targetCol };

      const normalize = (v, c) => {
        const range = maxes[c] - mins[c];
        return range === 0 ? 0.5 : (v - mins[c]) / range;
      };

      const trainingData = [];
      for (let r = 1; r < values.length; r++) {
        const inputVals = inputCols.map(c => parseFloat(values[r][c]));
        const targetVal = parseFloat(values[r][targetCol]);
        if (inputVals.some(v => isNaN(v)) || isNaN(targetVal)) continue;
        trainingData.push({
          input: inputVals.map((v, i) => normalize(v, inputCols[i])),
          output: [normalize(targetVal, targetCol)],
        });
      }

      if (trainingData.length < 3) {
        outputEl.textContent = 'Need at least 3 complete rows for training';
        return;
      }

      outputEl.textContent += `Training on ${trainingData.length} rows...\n`;
      outputEl.textContent += `Inputs: ${inputCols.map(c => headers[c] || `Col ${c}`).join(', ')}\n`;
      outputEl.textContent += `Target: ${headers[targetCol] || `Col ${targetCol}`}\n`;
      outputEl.textContent += `Iterations: ${iterations}, Learning rate: ${lr}\n\n`;

      const startTime = performance.now();

      try {
        let net;
        if (netType === 'RNNTimeStep' && brain.recurrent) {
          net = new brain.recurrent.RNNTimeStep({ learningRate: lr });
        } else {
          net = new brain.NeuralNetwork({
            hiddenLayers: [Math.max(4, inputCols.length * 2), Math.max(3, inputCols.length)],
            learningRate: lr,
            activation: 'sigmoid',
          });
        }

        const result = net.train(trainingData, {
          iterations: iterations,
          errorThresh: 0.005,
          log: (detail) => { outputEl.textContent += detail + '\n'; outputEl.scrollTop = outputEl.scrollHeight; },
          logPeriod: Math.max(1, Math.floor(iterations / 20)),
        });

        const elapsed = ((performance.now() - startTime) / 1000).toFixed(1);
        outputEl.textContent += `\nDone in ${elapsed}s — Final error: ${result.error.toFixed(6)} (${result.iterations} iterations)\n`;
        statusEl.textContent = `Trained — error: ${result.error.toFixed(6)}`;
        statusEl.style.color = '#059669';

        trainedNet = net;
        document.getElementById('ml-predict-btn').disabled = false;
      } catch (e) {
        outputEl.textContent += `\nERROR: ${e.message}`;
        statusEl.textContent = 'Training failed';
        statusEl.style.color = '#dc2626';
      }
    };

    document.getElementById('ml-predict-btn').onclick = () => {
      if (!trainedNet || !normMeta) return;
      const { mins, maxes, inputCols, targetCol } = normMeta;
      const outputEl = document.getElementById('ml-output');
      const statusEl = document.getElementById('ml-status');

      const normalize = (v, c) => {
        const range = maxes[c] - mins[c];
        return range === 0 ? 0.5 : (v - mins[c]) / range;
      };
      const denormalize = (v, c) => {
        const range = maxes[c] - mins[c];
        return v * range + mins[c];
      };

      let filled = 0;
      for (let r = 1; r < values.length; r++) {
        const inputVals = inputCols.map(c => parseFloat(values[r][c]));
        const targetVal = values[r][targetCol];

        // Only predict for rows with inputs but empty/missing target
        if (inputVals.some(v => isNaN(v))) continue;
        if (targetVal !== '' && targetVal != null && !isNaN(parseFloat(targetVal))) continue;

        const normalizedInput = inputVals.map((v, i) => normalize(v, inputCols[i]));
        const prediction = trainedNet.run(normalizedInput);
        const rawVal = denormalize(prediction[0], targetCol);
        const rounded = Math.round(rawVal * 100) / 100;

        try {
          sheetAPI.getRange(r, targetCol, 1, 1).setValue(rounded);
          filled++;
        } catch (e) {
          outputEl.textContent += `Error row ${r + 1}: ${e.message}\n`;
        }
      }

      outputEl.textContent += `\nFilled ${filled} predictions in column ${headers[targetCol] || String.fromCharCode(65 + targetCol)}`;
      statusEl.textContent = `${filled} predictions written`;
      statusEl.style.color = '#2563eb';
    };
  }

  // ── Real-Time Collaboration (Yjs + y-websocket) ──
  _toggleCollab() {
    if (this._collabActive) {
      this._disconnectCollab();
      return;
    }
    this._connectCollab();
  }

  _connectCollab() {
    if (!this.univerAPI) return;

    const Y = window.Y;
    const Provider = window.YWebsocketProvider;

    if (!Y || !Provider) {
      alert('Yjs not loaded yet — please wait a moment and try again. The collaboration library loads in the background.');
      return;
    }

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;

    // Determine server address — same host as page, port 3334
    const wsHost = window.location.hostname || 'localhost';
    const wsUrl = `ws://${wsHost}:3334`;

    // Create Yjs document
    const ydoc = new Y.Doc();
    const ymap = ydoc.getMap('cells');
    const ymeta = ydoc.getMap('meta');

    // Generate a user color and name
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
    const userColor = colors[Math.floor(Math.random() * colors.length)];
    const userId = 'user-' + Math.random().toString(36).substr(2, 6);
    const userName = 'User ' + userId.substr(5);

    let provider;
    try {
      provider = new Provider(wsUrl, 'officeforged-sheet', ydoc, {
        connect: true,
      });

      // Set awareness (who's online)
      provider.awareness.setLocalStateField('user', {
        name: userName,
        color: userColor,
        id: userId,
      });
    } catch (e) {
      alert('Could not connect to collaboration server at ' + wsUrl + '. Make sure the app is running in Electron.');
      return;
    }

    this._ydoc = ydoc;
    this._collabProvider = provider;
    this._collabActive = true;
    this._collabSyncing = false;

    // UI updates
    const btn = document.getElementById('grid-collab');
    if (btn) {
      btn.style.background = '#059669';
      btn.style.borderColor = '#059669';
      btn.textContent = '\u{1F6D1} Disconnect';
    }

    const statusEl = document.getElementById('grid-collab-status');

    // Track connected users via awareness
    const updateUsers = () => {
      const states = provider.awareness.getStates();
      const users = [];
      states.forEach((state, clientId) => {
        if (state.user) users.push(state.user);
      });
      if (statusEl) {
        const dots = users.map(u => `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${u.color};margin-right:2px;" title="${u.name}"></span>`).join('');
        statusEl.innerHTML = `${dots} ${users.length} online`;
      }
    };
    provider.awareness.on('change', updateUsers);
    updateUsers();

    // Connection status
    provider.on('status', (event) => {
      if (event.status === 'connected') {
        if (statusEl) statusEl.style.color = 'rgba(255,255,255,0.8)';
        // Push current sheet data to Yjs on first connect
        this._pushSheetToYjs(ymap);
      } else if (event.status === 'disconnected') {
        if (statusEl) statusEl.style.color = '#f87171';
      }
    });

    // Listen for remote changes from Yjs → update Univer
    ymap.observe((event) => {
      if (this._collabSyncing) return; // ignore our own changes
      const sheetAPI = wb.getActiveSheet();
      if (!sheetAPI) return;

      this._collabSyncing = true;
      event.changes.keys.forEach((change, key) => {
        if (change.action === 'add' || change.action === 'update') {
          const [r, c] = key.split(':').map(Number);
          const val = ymap.get(key);
          try {
            sheetAPI.getRange(r, c, 1, 1).setValue(val);
          } catch (e) { /* ignore */ }
        }
      });
      this._collabSyncing = false;
    });

    // Listen for local Univer changes → push to Yjs
    // Poll for changes every 500ms (Univer doesn't expose a reliable cell-change event in UMD)
    this._collabPollTimer = setInterval(() => {
      if (!this._collabActive) return;
      this._syncLocalChangesToYjs(ymap);
    }, 500);

    // Store initial snapshot for diff detection
    const sheetAPI = wb.getActiveSheet();
    const initialValues = sheetAPI?.getDataRange()?.getValues() || [];
    this._lastCollabSnapshot = initialValues.map(row => [...row]);

    // Audit log
    if (this._auditLog) {
      this._auditLog.push({ time: new Date().toISOString(), action: 'Collab Connect', detail: `Connected to ${wsUrl} as ${userName}` });
    }
  }

  _disconnectCollab() {
    if (this._collabPollTimer) {
      clearInterval(this._collabPollTimer);
      this._collabPollTimer = null;
    }
    if (this._collabProvider) {
      this._collabProvider.disconnect();
      this._collabProvider.destroy();
      this._collabProvider = null;
    }
    if (this._ydoc) {
      this._ydoc.destroy();
      this._ydoc = null;
    }
    this._collabActive = false;
    this._lastCollabSnapshot = null;

    const btn = document.getElementById('grid-collab');
    if (btn) {
      btn.style.background = '#7c3aed';
      btn.style.borderColor = '#7c3aed';
      btn.textContent = '\u{1F465} Collaborate';
    }
    const statusEl = document.getElementById('grid-collab-status');
    if (statusEl) statusEl.innerHTML = '';

    if (this._auditLog) {
      this._auditLog.push({ time: new Date().toISOString(), action: 'Collab Disconnect', detail: 'Disconnected from collaboration' });
    }
  }

  _pushSheetToYjs(ymap) {
    if (!this.univerAPI || this._collabSyncing) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;
    const values = sheetAPI.getDataRange()?.getValues() || [];

    this._collabSyncing = true;
    this._ydoc.transact(() => {
      for (let r = 0; r < values.length; r++) {
        for (let c = 0; c < values[r].length; c++) {
          const val = values[r][c];
          if (val !== '' && val != null) {
            ymap.set(`${r}:${c}`, val);
          }
        }
      }
    });
    this._collabSyncing = false;
  }

  _syncLocalChangesToYjs(ymap) {
    if (!this.univerAPI || this._collabSyncing || !this._lastCollabSnapshot) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheetAPI = wb.getActiveSheet();
    if (!sheetAPI) return;

    const currentValues = sheetAPI.getDataRange()?.getValues() || [];
    const prev = this._lastCollabSnapshot;
    const changes = [];

    const maxRows = Math.max(currentValues.length, prev.length);
    for (let r = 0; r < maxRows; r++) {
      const curRow = currentValues[r] || [];
      const prevRow = prev[r] || [];
      const maxCols = Math.max(curRow.length, prevRow.length);
      for (let c = 0; c < maxCols; c++) {
        const curVal = curRow[c] ?? '';
        const prevVal = prevRow[c] ?? '';
        if (String(curVal) !== String(prevVal)) {
          changes.push({ r, c, v: curVal });
        }
      }
    }

    if (changes.length > 0) {
      this._collabSyncing = true;
      this._ydoc.transact(() => {
        changes.forEach(({ r, c, v }) => {
          ymap.set(`${r}:${c}`, v);
        });
      });
      this._collabSyncing = false;
    }

    // Update snapshot
    this._lastCollabSnapshot = currentValues.map(row => [...row]);
  }

  // ── Financial Precision Mode (decimal.js) ──
  _togglePrecisionMode() {
    this._precisionMode = !this._precisionMode;
    const btn = document.getElementById('grid-precision');
    if (btn) {
      btn.style.background = this._precisionMode ? '#059669' : '';
      btn.style.color = this._precisionMode ? '#fff' : '';
      btn.title = this._precisionMode
        ? 'Precision ON — financial calculations use decimal.js (click to toggle)'
        : 'Toggle financial precision mode (decimal.js)';
    }

    if (this._precisionMode && !window.Decimal) {
      alert('decimal.js not loaded — precision mode unavailable');
      this._precisionMode = false;
      if (btn) { btn.style.background = ''; btn.style.color = ''; }
      return;
    }

    if (this._precisionMode) {
      this._applyPrecisionToSheet();
    }
  }

  _applyPrecisionToSheet() {
    if (!this._precisionMode || !window.Decimal || !this.univerAPI) return;

    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values) return;

    let fixed = 0;
    const D = window.Decimal;

    for (let r = 0; r < values.length; r++) {
      for (let c = 0; c < values[r].length; c++) {
        const v = values[r][c];
        if (typeof v === 'number' && !Number.isInteger(v)) {
          // Check for floating-point imprecision
          const str = String(v);
          if (str.length > 15 || (str.includes('.') && str.split('.')[1].length > 10)) {
            try {
              const precise = new D(v).toDecimalPlaces(10).toNumber();
              if (precise !== v) {
                const cellRange = sheet.getRange(r, c, 1, 1);
                cellRange.setValue(precise);
                fixed++;
              }
            } catch (_) { /* skip */ }
          }
        }
      }
    }

    const btn = document.getElementById('grid-precision');
    if (btn) btn.title = `Precision ON — ${fixed} cells corrected (click to toggle)`;
  }

  // ── Enhanced AI: Correlation Analysis ──
  _aiCorrelation(headers, dataRows) {
    const numCols = [];
    headers.forEach((h, i) => {
      const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
      if (nums.length > dataRows.length * 0.5) numCols.push({ name: h, idx: i });
    });

    if (numCols.length < 2) {
      this._aiResult('Need at least 2 numeric columns for correlation analysis.');
      return;
    }

    let html = '<b>Correlation Matrix</b><br><table style="width:100%;border-collapse:collapse;font-size:10px;">';
    html += '<tr><th style="border:1px solid #ddd;padding:3px;"></th>';
    numCols.forEach(c => { html += `<th style="border:1px solid #ddd;padding:3px;writing-mode:vertical-lr;height:60px;">${c.name}</th>`; });
    html += '</tr>';

    numCols.forEach(c1 => {
      html += `<tr><th style="border:1px solid #ddd;padding:3px;text-align:left;">${c1.name}</th>`;
      numCols.forEach(c2 => {
        const x = dataRows.map(r => Number(r[c1.idx])).filter(n => !isNaN(n));
        const y = dataRows.map(r => Number(r[c2.idx])).filter(n => !isNaN(n));
        const n = Math.min(x.length, y.length);
        if (n < 3) { html += '<td style="border:1px solid #ddd;padding:3px;">-</td>'; return; }

        const xSlice = x.slice(0, n), ySlice = y.slice(0, n);
        const xMean = xSlice.reduce((a, b) => a + b, 0) / n;
        const yMean = ySlice.reduce((a, b) => a + b, 0) / n;
        let num = 0, denX = 0, denY = 0;
        for (let i = 0; i < n; i++) {
          const dx = xSlice[i] - xMean, dy = ySlice[i] - yMean;
          num += dx * dy; denX += dx * dx; denY += dy * dy;
        }
        const corr = denX && denY ? (num / Math.sqrt(denX * denY)) : 0;
        const r = corr.toFixed(3);
        const abs = Math.abs(corr);
        const color = abs > 0.7 ? (corr > 0 ? '#059669' : '#dc2626') : abs > 0.4 ? '#d97706' : '#999';
        const bg = abs > 0.7 ? (corr > 0 ? '#ecfdf5' : '#fef2f2') : abs > 0.4 ? '#fffbeb' : '#fff';
        html += `<td style="border:1px solid #ddd;padding:3px;text-align:center;color:${color};background:${bg};font-weight:${abs > 0.7 ? '700' : '400'};">${r}</td>`;
      });
      html += '</tr>';
    });
    html += '</table>';
    html += '<div style="font-size:10px;color:#999;margin-top:4px;">Strong (>0.7) = <span style="color:#059669;">green</span>/<span style="color:#dc2626;">red</span> · Moderate (0.4-0.7) = <span style="color:#d97706;">amber</span></div>';
    this._aiResult(html);
  }

  // ── Enhanced AI: Trend Detection ──
  _aiTrendDetection(headers, dataRows) {
    let html = '<b>Trend Detection</b><br>';
    const numCols = [];
    headers.forEach((h, i) => {
      const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
      if (nums.length > dataRows.length * 0.5) numCols.push({ name: h, idx: i, data: nums });
    });

    if (numCols.length === 0) { this._aiResult('No numeric columns found for trend detection.'); return; }

    numCols.forEach(col => {
      const data = col.data;
      const n = data.length;
      if (n < 3) return;

      // Linear regression for trend
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
      for (let i = 0; i < n; i++) {
        sumX += i; sumY += data[i]; sumXY += i * data[i]; sumX2 += i * i;
      }
      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const mean = sumY / n;
      const pctChange = mean !== 0 ? ((slope * n) / mean * 100).toFixed(1) : 0;

      // Moving average (5-period)
      const ma = [];
      for (let i = 4; i < n; i++) {
        ma.push((data[i] + data[i-1] + data[i-2] + data[i-3] + data[i-4]) / 5);
      }
      const maDirection = ma.length >= 2 ? (ma[ma.length - 1] > ma[0] ? 'up' : 'down') : 'flat';

      // Volatility (coefficient of variation)
      const variance = data.reduce((sum, v) => sum + (v - mean) ** 2, 0) / n;
      const cv = mean !== 0 ? (Math.sqrt(variance) / Math.abs(mean) * 100).toFixed(1) : 0;

      const direction = slope > 0 ? '↑ Uptrend' : slope < 0 ? '↓ Downtrend' : '→ Flat';
      const dirColor = slope > 0 ? '#059669' : slope < 0 ? '#dc2626' : '#666';

      html += `<div style="margin-bottom:6px;padding:6px;border:1px solid #eee;border-radius:4px;">`;
      html += `<b>${col.name}</b>: <span style="color:${dirColor};font-weight:700;">${direction}</span> `;
      html += `<span style="font-size:11px;">(${pctChange > 0 ? '+' : ''}${pctChange}% total change)</span><br>`;
      html += `<span style="font-size:11px;color:#666;">Volatility: ${cv}% · MA(5): ${maDirection} · Range: ${Math.min(...data).toLocaleString()} – ${Math.max(...data).toLocaleString()}</span>`;
      html += `</div>`;
    });
    this._aiResult(html);
  }

  // ── Enhanced AI: Formula Suggestion ──
  _aiFormulaSuggest(query, headers) {
    const suggestions = [];
    const q = query.toLowerCase();

    // Match column names in query
    const matchedCols = headers.filter(h => q.includes(h.toLowerCase()));
    const colLetter = (h) => String.fromCharCode(65 + headers.indexOf(h));

    if (q.includes('lookup') || q.includes('find') || q.includes('match')) {
      if (matchedCols.length >= 1) {
        const c = colLetter(matchedCols[0]);
        suggestions.push({ formula: `=VLOOKUP(value, ${c}:${String.fromCharCode(c.charCodeAt(0)+2)}, 2, FALSE)`, desc: 'Look up a value and return from adjacent column' });
        suggestions.push({ formula: `=INDEX(B:B, MATCH(value, ${c}:${c}, 0))`, desc: 'More flexible lookup using INDEX/MATCH' });
      } else {
        suggestions.push({ formula: '=VLOOKUP(lookup_value, range, col_num, FALSE)', desc: 'Exact match lookup' });
        suggestions.push({ formula: '=XLOOKUP(lookup, lookup_range, return_range)', desc: 'Modern lookup (if supported)' });
      }
    }

    if (q.includes('percent') || q.includes('%') || q.includes('ratio')) {
      suggestions.push({ formula: '=A2/SUM(A:A)*100', desc: 'Percentage of total' });
      suggestions.push({ formula: '=(B2-B1)/B1*100', desc: 'Percentage change' });
      suggestions.push({ formula: '=A2/B2', desc: 'Simple ratio' });
    }

    if (q.includes('rank') || q.includes('top') || q.includes('bottom')) {
      suggestions.push({ formula: '=RANK(A2, A:A, 0)', desc: 'Rank descending (largest = 1)' });
      suggestions.push({ formula: '=RANK(A2, A:A, 1)', desc: 'Rank ascending (smallest = 1)' });
      suggestions.push({ formula: '=LARGE(A:A, 1)', desc: 'Nth largest value' });
    }

    if (q.includes('date') || q.includes('day') || q.includes('month') || q.includes('year')) {
      suggestions.push({ formula: '=YEAR(A2)', desc: 'Extract year' });
      suggestions.push({ formula: '=MONTH(A2)', desc: 'Extract month' });
      suggestions.push({ formula: '=DATEDIF(A2, B2, "D")', desc: 'Days between dates' });
      suggestions.push({ formula: '=EOMONTH(A2, 0)', desc: 'End of month' });
    }

    if (q.includes('round') || q.includes('decimal')) {
      suggestions.push({ formula: '=ROUND(A2, 2)', desc: 'Round to 2 decimal places' });
      suggestions.push({ formula: '=ROUNDUP(A2, 0)', desc: 'Round up to integer' });
      suggestions.push({ formula: '=TRUNC(A2, 2)', desc: 'Truncate (no rounding)' });
    }

    if (q.includes('text') || q.includes('combine') || q.includes('concat') || q.includes('join')) {
      suggestions.push({ formula: '=CONCATENATE(A2, " ", B2)', desc: 'Combine cells with space' });
      suggestions.push({ formula: '=LEFT(A2, 3)', desc: 'First N characters' });
      suggestions.push({ formula: '=PROPER(A2)', desc: 'Title case' });
      suggestions.push({ formula: '=TRIM(A2)', desc: 'Remove extra spaces' });
    }

    if (q.includes('condition') || q.includes('if') || q.includes('when')) {
      suggestions.push({ formula: '=IF(A2>100, "High", "Low")', desc: 'Conditional value' });
      suggestions.push({ formula: '=IFS(A2>100,"High",A2>50,"Med",TRUE,"Low")', desc: 'Multiple conditions' });
      suggestions.push({ formula: '=SUMIFS(B:B, A:A, ">100", C:C, "East")', desc: 'Sum with conditions' });
      suggestions.push({ formula: '=COUNTIFS(A:A, ">0", B:B, "<100")', desc: 'Count with conditions' });
    }

    if (suggestions.length === 0) {
      suggestions.push({ formula: '=SUM(range)', desc: 'Add values' });
      suggestions.push({ formula: '=AVERAGE(range)', desc: 'Average' });
      suggestions.push({ formula: '=COUNTIF(range, criteria)', desc: 'Conditional count' });
      suggestions.push({ formula: '=SUMIF(range, criteria, sum_range)', desc: 'Conditional sum' });
    }

    let html = '<b>Formula Suggestions</b><br>';
    suggestions.forEach(s => {
      html += `<div style="margin:4px 0;padding:6px;background:#f5f5f5;border-radius:4px;cursor:pointer;" onclick="navigator.clipboard.writeText('${s.formula.replace(/'/g, "\\'")}');this.style.background='#ecfdf5';">`;
      html += `<code style="font-size:12px;color:#2563eb;">${s.formula}</code><br>`;
      html += `<span style="font-size:10px;color:#666;">${s.desc} — click to copy</span>`;
      html += `</div>`;
    });
    this._aiResult(html);
  }

  // ── Enhanced AI: Natural Language Filter ──
  _aiNLFilter(query, headers, dataRows) {
    const q = query.toLowerCase();
    let filteredRows = [...dataRows];
    let filterDesc = [];

    // Parse "over/above/greater than X" patterns
    const overMatch = q.match(/(?:over|above|greater than|more than|>)\s*\$?(\d+[\d,.]*)/);
    if (overMatch) {
      const threshold = parseFloat(overMatch[1].replace(/,/g, ''));
      // Find the most likely numeric column
      const numCols = headers.map((h, i) => {
        const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
        return { name: h, idx: i, numCount: nums.length };
      }).filter(c => c.numCount > 0).sort((a, b) => b.numCount - a.numCount);

      // Check if a column name was mentioned
      let targetCol = numCols[0];
      for (const col of numCols) {
        if (q.includes(col.name.toLowerCase())) { targetCol = col; break; }
      }
      if (targetCol) {
        filteredRows = filteredRows.filter(r => Number(r[targetCol.idx]) > threshold);
        filterDesc.push(`${targetCol.name} > ${threshold}`);
      }
    }

    // Parse "under/below/less than X" patterns
    const underMatch = q.match(/(?:under|below|less than|<)\s*\$?(\d+[\d,.]*)/);
    if (underMatch) {
      const threshold = parseFloat(underMatch[1].replace(/,/g, ''));
      const numCols = headers.map((h, i) => ({
        name: h, idx: i,
        numCount: dataRows.map(r => Number(r[i])).filter(n => !isNaN(n)).length
      })).filter(c => c.numCount > 0);
      let targetCol = numCols[0];
      for (const col of numCols) {
        if (q.includes(col.name.toLowerCase())) { targetCol = col; break; }
      }
      if (targetCol) {
        filteredRows = filteredRows.filter(r => Number(r[targetCol.idx]) < threshold);
        filterDesc.push(`${targetCol.name} < ${threshold}`);
      }
    }

    // Parse "contains/with/including 'text'" or just word matching
    const containsMatch = q.match(/(?:contains?|with|including|where.*(?:is|=))\s+["']?(\w+)["']?/);
    if (containsMatch) {
      const term = containsMatch[1].toLowerCase();
      // Check all text columns
      filteredRows = filteredRows.filter(r =>
        r.some(cell => String(cell ?? '').toLowerCase().includes(term))
      );
      filterDesc.push(`contains "${term}"`);
    }

    // Parse "in Q1/Q2/Q3/Q4" or month names
    const quarterMatch = q.match(/\b(q[1-4])\b/i);
    if (quarterMatch) {
      const qtr = quarterMatch[1].toUpperCase();
      const months = { Q1: [1,2,3], Q2: [4,5,6], Q3: [7,8,9], Q4: [10,11,12] };
      const qMonths = months[qtr] || [];
      // Find date-like column
      const dateCols = headers.map((h, i) => ({
        name: h, idx: i,
        looksLikeDate: dataRows.some(r => !isNaN(Date.parse(String(r[i]))))
      })).filter(c => c.looksLikeDate);
      if (dateCols.length > 0) {
        const dc = dateCols[0];
        filteredRows = filteredRows.filter(r => {
          const d = new Date(String(r[dc.idx]));
          return !isNaN(d) && qMonths.includes(d.getMonth() + 1);
        });
        filterDesc.push(`${qtr} dates`);
      }
    }

    if (filterDesc.length === 0) {
      this._aiResult(`Couldn't parse a filter from that query. Try: "show sales over $1000" or "where region contains East" or "revenue in Q1"`);
      return;
    }

    let html = `<b>Filtered Results</b> (${filterDesc.join(' + ')})<br>`;
    html += `<span style="font-size:11px;color:#059669;">${filteredRows.length} of ${dataRows.length} rows match</span><br>`;

    if (filteredRows.length > 0) {
      html += '<table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:6px;"><thead><tr>';
      headers.forEach(h => { html += `<th style="border:1px solid #ddd;padding:3px 6px;background:#f5f5f5;">${h}</th>`; });
      html += '</tr></thead><tbody>';
      filteredRows.slice(0, 20).forEach(row => {
        html += '<tr>';
        row.forEach(cell => { html += `<td style="border:1px solid #ddd;padding:3px 6px;">${cell ?? ''}</td>`; });
        html += '</tr>';
      });
      if (filteredRows.length > 20) html += `<tr><td colspan="${headers.length}" style="text-align:center;color:#999;border:1px solid #ddd;padding:3px;">...${filteredRows.length - 20} more</td></tr>`;
      html += '</tbody></table>';

      // Send to Sheet button
      html += `<button id="ai-filter-to-sheet" style="margin-top:8px;padding:4px 14px;background:#059669;color:#fff;border:none;border-radius:4px;font-size:11px;cursor:pointer;">Send to New Sheet</button>`;
    }

    this._aiResult(html);

    // Wire send-to-sheet
    setTimeout(() => {
      const btn = document.getElementById('ai-filter-to-sheet');
      if (btn) {
        btn.onclick = () => {
          const cellData = {};
          cellData[0] = {};
          headers.forEach((h, ci) => { cellData[0][ci] = { v: h, s: { bl: 1 } }; });
          filteredRows.forEach((row, ri) => {
            cellData[ri + 1] = {};
            row.forEach((cell, ci) => { cellData[ri + 1][ci] = { v: cell ?? '' }; });
          });
          const sid = 'filter_' + Date.now();
          this.univerAPI.createWorkbook({
            name: 'Filtered Data',
            sheetOrder: [sid],
            sheets: { [sid]: { id: sid, name: 'Filtered', rowCount: filteredRows.length + 50, columnCount: headers.length + 5, cellData } }
          });
        };
      }
    }, 0);
  }

  // ── Financial Calculator ──
  _openFinanceCalc() {
    document.getElementById('finance-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'finance-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:600px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Financial Calculator</span>
          <button id="finance-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px;">
            <button class="fin-tab" data-tab="tvm" style="padding:6px 12px;border:1px solid #2563eb;border-radius:4px;background:#eff6ff;color:#2563eb;cursor:pointer;font-size:12px;font-weight:600;">TVM (PV/FV/PMT)</button>
            <button class="fin-tab" data-tab="npv" style="padding:6px 12px;border:1px solid #059669;border-radius:4px;background:#ecfdf5;color:#059669;cursor:pointer;font-size:12px;font-weight:600;">NPV</button>
            <button class="fin-tab" data-tab="irr" style="padding:6px 12px;border:1px solid #dc2626;border-radius:4px;background:#fef2f2;color:#dc2626;cursor:pointer;font-size:12px;font-weight:600;">IRR</button>
            <button class="fin-tab" data-tab="depreciation" style="padding:6px 12px;border:1px solid #d97706;border-radius:4px;background:#fffbeb;color:#d97706;cursor:pointer;font-size:12px;font-weight:600;">Depreciation</button>
            <button class="fin-tab" data-tab="amortization" style="padding:6px 12px;border:1px solid #7c3aed;border-radius:4px;background:#f5f3ff;color:#7c3aed;cursor:pointer;font-size:12px;font-weight:600;">Amortization</button>
            <button class="fin-tab" data-tab="breakeven" style="padding:6px 12px;border:1px solid #0891b2;border-radius:4px;background:#ecfeff;color:#0891b2;cursor:pointer;font-size:12px;font-weight:600;">Break-Even</button>
          </div>
          <div id="finance-body" style="border:1px solid #e5e7eb;border-radius:6px;padding:16px;background:#fafafa;"></div>
          <div id="finance-result" style="margin-top:12px;font-size:13px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('finance-close').onclick = () => dialog.remove();
    dialog.querySelectorAll('.fin-tab').forEach(btn => {
      btn.onclick = () => this._showFinanceTab(btn.dataset.tab);
    });

    // Default tab
    this._showFinanceTab('tvm');
  }

  _showFinanceTab(tab) {
    const body = document.getElementById('finance-body');
    const result = document.getElementById('finance-result');
    if (!body) return;
    if (result) result.innerHTML = '';

    const field = (id, label, val, hint) =>
      `<div style="margin:4px 0;"><label style="font-size:12px;font-weight:600;display:inline-block;width:180px;">${label}:</label>
       <input id="${id}" type="number" value="${val}" step="any" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:140px;" title="${hint || ''}">
       ${hint ? `<span style="font-size:10px;color:#999;margin-left:4px;">${hint}</span>` : ''}</div>`;

    switch (tab) {
      case 'tvm':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Time Value of Money — solve for any one variable (leave it as 0).</div>
          ${field('fin-pv', 'Present Value (PV)', '0', 'Loan amount or initial investment')}
          ${field('fin-fv', 'Future Value (FV)', '0', 'Target amount')}
          ${field('fin-pmt', 'Payment (PMT)', '0', 'Regular payment per period')}
          ${field('fin-rate', 'Interest Rate (%/yr)', '5', 'Annual rate')}
          ${field('fin-nper', 'Periods (NPER)', '12', 'Number of payment periods')}
          <div style="margin-top:8px;">
            <label style="font-size:12px;">Solve for:</label>
            <select id="fin-solve" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
              <option value="fv">Future Value</option><option value="pv">Present Value</option>
              <option value="pmt">Payment</option><option value="nper">Periods</option>
            </select>
            <button id="fin-calc" style="margin-left:8px;padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Calculate</button>
          </div>
        `;
        document.getElementById('fin-calc').onclick = () => this._calcTVM();
        break;

      case 'npv':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Net Present Value — enter cash flows separated by commas. First value is the initial investment (negative).</div>
          ${field('fin-npv-rate', 'Discount Rate (%)', '10', 'Annual')}
          <div style="margin:4px 0;"><label style="font-size:12px;font-weight:600;">Cash Flows:</label><br>
          <input id="fin-npv-flows" type="text" value="-100000, 30000, 35000, 40000, 45000" style="width:100%;padding:6px 8px;border:1px solid #ccc;border-radius:3px;font-family:monospace;" placeholder="-100000, 30000, 35000, 40000, 45000"></div>
          <div style="font-size:10px;color:#999;">Or select a column from the sheet:</div>
          <button id="fin-npv-fromsheet" style="padding:4px 10px;border:1px solid #ccc;border-radius:3px;background:#fff;cursor:pointer;font-size:11px;margin:4px 0;">Load from Column A</button>
          <button id="fin-npv-calc" style="margin-left:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Calculate NPV</button>
        `;
        document.getElementById('fin-npv-fromsheet').onclick = () => {
          const wb = this.univerAPI?.getActiveWorkbook();
          if (!wb) return;
          const sheet = wb.getActiveSheet();
          const vals = sheet?.getDataRange()?.getValues();
          if (!vals) return;
          const nums = vals.map(r => Number(r[0])).filter(n => !isNaN(n));
          document.getElementById('fin-npv-flows').value = nums.join(', ');
        };
        document.getElementById('fin-npv-calc').onclick = () => this._calcNPV();
        break;

      case 'irr':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Internal Rate of Return — cash flows must start with a negative (investment).</div>
          <div style="margin:4px 0;"><label style="font-size:12px;font-weight:600;">Cash Flows:</label><br>
          <input id="fin-irr-flows" type="text" value="-100000, 30000, 35000, 40000, 45000" style="width:100%;padding:6px 8px;border:1px solid #ccc;border-radius:3px;font-family:monospace;"></div>
          <button id="fin-irr-fromsheet" style="padding:4px 10px;border:1px solid #ccc;border-radius:3px;background:#fff;cursor:pointer;font-size:11px;margin:4px 0;">Load from Column A</button>
          <button id="fin-irr-calc" style="margin-left:8px;padding:6px 16px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Calculate IRR</button>
        `;
        document.getElementById('fin-irr-fromsheet').onclick = () => {
          const wb = this.univerAPI?.getActiveWorkbook();
          if (!wb) return;
          const vals = wb.getActiveSheet()?.getDataRange()?.getValues();
          if (!vals) return;
          document.getElementById('fin-irr-flows').value = vals.map(r => Number(r[0])).filter(n => !isNaN(n)).join(', ');
        };
        document.getElementById('fin-irr-calc').onclick = () => this._calcIRR();
        break;

      case 'depreciation':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Depreciation schedule — generates full table.</div>
          ${field('fin-dep-cost', 'Asset Cost', '50000', '')}
          ${field('fin-dep-salvage', 'Salvage Value', '5000', '')}
          ${field('fin-dep-life', 'Useful Life (years)', '5', '')}
          <div style="margin:6px 0;">
            <label style="font-size:12px;font-weight:600;">Method:</label>
            <select id="fin-dep-method" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
              <option value="sl">Straight Line</option>
              <option value="ddb">Double Declining Balance</option>
              <option value="syd">Sum-of-Years Digits</option>
            </select>
          </div>
          <button id="fin-dep-calc" style="padding:6px 16px;background:#d97706;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Generate Schedule</button>
          <button id="fin-dep-tosheet" style="margin-left:8px;padding:6px 12px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;display:none;">Send to Sheet</button>
        `;
        document.getElementById('fin-dep-calc').onclick = () => this._calcDepreciation();
        break;

      case 'amortization':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Loan amortization schedule — full payment breakdown.</div>
          ${field('fin-amort-principal', 'Loan Amount', '250000', '')}
          ${field('fin-amort-rate', 'Annual Rate (%)', '6.5', '')}
          ${field('fin-amort-term', 'Term (years)', '30', '')}
          ${field('fin-amort-extra', 'Extra Monthly Payment', '0', 'Optional')}
          <button id="fin-amort-calc" style="padding:6px 16px;background:#7c3aed;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Generate Schedule</button>
          <button id="fin-amort-tosheet" style="margin-left:8px;padding:6px 12px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;display:none;">Send to Sheet</button>
        `;
        document.getElementById('fin-amort-calc').onclick = () => this._calcAmortization();
        break;

      case 'breakeven':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Break-even analysis — find the point where revenue covers costs.</div>
          ${field('fin-be-fixed', 'Fixed Costs', '50000', 'Rent, salaries, etc.')}
          ${field('fin-be-price', 'Price per Unit', '25', '')}
          ${field('fin-be-variable', 'Variable Cost per Unit', '10', '')}
          <button id="fin-be-calc" style="padding:6px 16px;background:#0891b2;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Calculate</button>
        `;
        document.getElementById('fin-be-calc').onclick = () => this._calcBreakEven();
        break;
    }
  }

  _calcTVM() {
    const pv = parseFloat(document.getElementById('fin-pv').value) || 0;
    const fv = parseFloat(document.getElementById('fin-fv').value) || 0;
    const pmt = parseFloat(document.getElementById('fin-pmt').value) || 0;
    const rate = (parseFloat(document.getElementById('fin-rate').value) || 0) / 100 / 12; // monthly
    const nper = parseFloat(document.getElementById('fin-nper').value) || 0;
    const solve = document.getElementById('fin-solve').value;

    let answer, label;
    switch (solve) {
      case 'fv':
        answer = -(pv * Math.pow(1 + rate, nper) + pmt * ((Math.pow(1 + rate, nper) - 1) / rate));
        label = 'Future Value';
        break;
      case 'pv':
        answer = -(fv / Math.pow(1 + rate, nper) + pmt * ((1 - Math.pow(1 + rate, -nper)) / rate));
        label = 'Present Value';
        break;
      case 'pmt':
        answer = rate === 0 ? -(pv + fv) / nper : -(pv * rate * Math.pow(1 + rate, nper) + fv * rate) / (Math.pow(1 + rate, nper) - 1);
        label = 'Payment';
        break;
      case 'nper':
        answer = rate === 0 ? -(pv + fv) / pmt : Math.log((-fv * rate + pmt) / (pv * rate + pmt)) / Math.log(1 + rate);
        label = 'Periods';
        break;
    }

    const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    document.getElementById('finance-result').innerHTML =
      `<div style="padding:12px;background:#eff6ff;border-radius:6px;text-align:center;">
        <span style="font-size:14px;color:#666;">${label}:</span><br>
        <span style="font-size:28px;font-weight:700;color:#2563eb;">$${fmt(answer)}</span>
      </div>`;
  }

  _calcNPV() {
    const rate = (parseFloat(document.getElementById('fin-npv-rate').value) || 0) / 100;
    const flows = document.getElementById('fin-npv-flows').value.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
    if (flows.length === 0) return;

    let npv = 0;
    flows.forEach((cf, t) => { npv += cf / Math.pow(1 + rate, t); });

    const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const color = npv >= 0 ? '#059669' : '#dc2626';
    const verdict = npv >= 0 ? 'ACCEPT — positive NPV' : 'REJECT — negative NPV';

    document.getElementById('finance-result').innerHTML =
      `<div style="padding:12px;background:${npv >= 0 ? '#ecfdf5' : '#fef2f2'};border-radius:6px;text-align:center;">
        <span style="font-size:14px;color:#666;">Net Present Value:</span><br>
        <span style="font-size:28px;font-weight:700;color:${color};">$${fmt(npv)}</span><br>
        <span style="font-size:12px;color:${color};font-weight:600;">${verdict}</span>
      </div>`;
  }

  _calcIRR() {
    const flows = document.getElementById('fin-irr-flows').value.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
    if (flows.length < 2) return;

    // Newton-Raphson method for IRR
    let irr = 0.1; // initial guess
    for (let iter = 0; iter < 1000; iter++) {
      let npv = 0, dnpv = 0;
      flows.forEach((cf, t) => {
        npv += cf / Math.pow(1 + irr, t);
        dnpv -= t * cf / Math.pow(1 + irr, t + 1);
      });
      if (Math.abs(npv) < 0.0001) break;
      if (dnpv === 0) break;
      irr -= npv / dnpv;
    }

    const pct = (irr * 100).toFixed(4);
    document.getElementById('finance-result').innerHTML =
      `<div style="padding:12px;background:#fef2f2;border-radius:6px;text-align:center;">
        <span style="font-size:14px;color:#666;">Internal Rate of Return:</span><br>
        <span style="font-size:28px;font-weight:700;color:#dc2626;">${pct}%</span>
      </div>`;
  }

  _calcDepreciation() {
    const cost = parseFloat(document.getElementById('fin-dep-cost').value) || 0;
    const salvage = parseFloat(document.getElementById('fin-dep-salvage').value) || 0;
    const life = parseInt(document.getElementById('fin-dep-life').value) || 1;
    const method = document.getElementById('fin-dep-method').value;

    const schedule = [];
    let bookValue = cost;
    const depBase = cost - salvage;

    for (let yr = 1; yr <= life; yr++) {
      let dep;
      switch (method) {
        case 'sl':
          dep = depBase / life;
          break;
        case 'ddb':
          dep = Math.min(bookValue * (2 / life), bookValue - salvage);
          break;
        case 'syd': {
          const syDigits = (life * (life + 1)) / 2;
          dep = depBase * ((life - yr + 1) / syDigits);
          break;
        }
      }
      bookValue -= dep;
      schedule.push({ year: yr, depreciation: dep, accumulated: cost - bookValue, bookValue });
    }

    const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    let html = '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
    ['Year', 'Depreciation', 'Accumulated', 'Book Value'].forEach(h => {
      html += `<th style="border:1px solid #ddd;padding:4px 8px;background:#f5f5f5;">${h}</th>`;
    });
    html += '</tr></thead><tbody>';
    schedule.forEach(r => {
      html += `<tr><td style="border:1px solid #ddd;padding:4px 8px;text-align:center;">${r.year}</td>`;
      html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:right;">$${fmt(r.depreciation)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:right;">$${fmt(r.accumulated)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:right;">$${fmt(r.bookValue)}</td></tr>`;
    });
    html += '</tbody></table>';

    document.getElementById('finance-result').innerHTML = html;

    // Show and wire Send to Sheet button
    const toSheetBtn = document.getElementById('fin-dep-tosheet');
    if (toSheetBtn) {
      toSheetBtn.style.display = 'inline-block';
      toSheetBtn.onclick = () => {
        const cellData = {};
        cellData[0] = {};
        ['Year', 'Depreciation', 'Accumulated', 'Book Value'].forEach((h, ci) => {
          cellData[0][ci] = { v: h, s: { bl: 1 } };
        });
        schedule.forEach((r, ri) => {
          cellData[ri + 1] = {};
          cellData[ri + 1][0] = { v: r.year };
          cellData[ri + 1][1] = { v: Math.round(r.depreciation * 100) / 100 };
          cellData[ri + 1][2] = { v: Math.round(r.accumulated * 100) / 100 };
          cellData[ri + 1][3] = { v: Math.round(r.bookValue * 100) / 100 };
        });
        const sid = 'dep_' + Date.now();
        this.univerAPI.createWorkbook({
          name: 'Depreciation Schedule',
          sheetOrder: [sid],
          sheets: { [sid]: { id: sid, name: 'Depreciation', rowCount: schedule.length + 50, columnCount: 10, cellData } }
        });
      };
    }
  }

  _calcAmortization() {
    const principal = parseFloat(document.getElementById('fin-amort-principal').value) || 0;
    const annualRate = parseFloat(document.getElementById('fin-amort-rate').value) || 0;
    const termYears = parseFloat(document.getElementById('fin-amort-term').value) || 1;
    const extra = parseFloat(document.getElementById('fin-amort-extra').value) || 0;

    const monthlyRate = annualRate / 100 / 12;
    const totalPayments = termYears * 12;
    const payment = monthlyRate === 0
      ? principal / totalPayments
      : principal * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1);

    const schedule = [];
    let balance = principal;
    let totalInterest = 0;

    for (let m = 1; m <= totalPayments && balance > 0; m++) {
      const interest = balance * monthlyRate;
      let principalPart = payment - interest + extra;
      if (principalPart > balance) principalPart = balance;
      balance -= principalPart;
      totalInterest += interest;
      schedule.push({ month: m, payment: payment + extra, interest, principal: principalPart, balance: Math.max(0, balance) });
      if (balance <= 0) break;
    }

    const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    let html = `<div style="padding:8px;background:#f5f3ff;border-radius:4px;margin-bottom:8px;font-size:12px;">
      Monthly Payment: <b>$${fmt(payment)}</b>${extra > 0 ? ` + $${fmt(extra)} extra` : ''} ·
      Total Interest: <b>$${fmt(totalInterest)}</b> ·
      Total Cost: <b>$${fmt(principal + totalInterest)}</b> ·
      Paid Off: <b>${schedule.length} months</b>${schedule.length < totalPayments ? ` (${totalPayments - schedule.length} months early!)` : ''}
    </div>`;

    html += '<div style="max-height:250px;overflow:auto;">';
    html += '<table style="width:100%;border-collapse:collapse;font-size:10px;"><thead><tr>';
    ['Month', 'Payment', 'Interest', 'Principal', 'Balance'].forEach(h => {
      html += `<th style="border:1px solid #ddd;padding:3px 6px;background:#f5f5f5;position:sticky;top:0;">${h}</th>`;
    });
    html += '</tr></thead><tbody>';
    // Show first 12, last 12, and year markers
    schedule.forEach((r, i) => {
      if (i < 12 || i >= schedule.length - 12 || r.month % 12 === 0) {
        html += `<tr${r.month % 12 === 0 ? ' style="font-weight:600;background:#eff6ff;"' : ''}>`;
        html += `<td style="border:1px solid #ddd;padding:2px 6px;text-align:center;">${r.month}</td>`;
        html += `<td style="border:1px solid #ddd;padding:2px 6px;text-align:right;">$${fmt(r.payment)}</td>`;
        html += `<td style="border:1px solid #ddd;padding:2px 6px;text-align:right;color:#dc2626;">$${fmt(r.interest)}</td>`;
        html += `<td style="border:1px solid #ddd;padding:2px 6px;text-align:right;color:#059669;">$${fmt(r.principal)}</td>`;
        html += `<td style="border:1px solid #ddd;padding:2px 6px;text-align:right;">$${fmt(r.balance)}</td></tr>`;
      } else if (i === 12) {
        html += `<tr><td colspan="5" style="border:1px solid #ddd;padding:2px 6px;text-align:center;color:#999;">... ${schedule.length - 24} more rows (year boundaries shown) ...</td></tr>`;
      }
    });
    html += '</tbody></table></div>';

    document.getElementById('finance-result').innerHTML = html;

    const toSheetBtn = document.getElementById('fin-amort-tosheet');
    if (toSheetBtn) {
      toSheetBtn.style.display = 'inline-block';
      toSheetBtn.onclick = () => {
        const cellData = {};
        cellData[0] = {};
        ['Month', 'Payment', 'Interest', 'Principal', 'Balance'].forEach((h, ci) => {
          cellData[0][ci] = { v: h, s: { bl: 1 } };
        });
        schedule.forEach((r, ri) => {
          cellData[ri + 1] = {};
          cellData[ri + 1][0] = { v: r.month };
          cellData[ri + 1][1] = { v: Math.round(r.payment * 100) / 100 };
          cellData[ri + 1][2] = { v: Math.round(r.interest * 100) / 100 };
          cellData[ri + 1][3] = { v: Math.round(r.principal * 100) / 100 };
          cellData[ri + 1][4] = { v: Math.round(r.balance * 100) / 100 };
        });
        const sid = 'amort_' + Date.now();
        this.univerAPI.createWorkbook({
          name: 'Amortization Schedule',
          sheetOrder: [sid],
          sheets: { [sid]: { id: sid, name: 'Amortization', rowCount: schedule.length + 50, columnCount: 10, cellData } }
        });
      };
    }
  }

  _calcBreakEven() {
    const fixed = parseFloat(document.getElementById('fin-be-fixed').value) || 0;
    const price = parseFloat(document.getElementById('fin-be-price').value) || 0;
    const variable = parseFloat(document.getElementById('fin-be-variable').value) || 0;

    const contribution = price - variable;
    if (contribution <= 0) {
      document.getElementById('finance-result').innerHTML = '<p style="color:red;">Price must exceed variable cost per unit.</p>';
      return;
    }

    const units = Math.ceil(fixed / contribution);
    const revenue = units * price;
    const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    document.getElementById('finance-result').innerHTML =
      `<div style="padding:12px;background:#ecfeff;border-radius:6px;text-align:center;">
        <span style="font-size:14px;color:#666;">Break-Even Point:</span><br>
        <span style="font-size:28px;font-weight:700;color:#0891b2;">${units.toLocaleString()} units</span><br>
        <span style="font-size:14px;color:#666;">Revenue needed: <b>$${fmt(revenue)}</b></span><br>
        <span style="font-size:12px;color:#999;">Contribution margin: $${fmt(contribution)}/unit (${((contribution / price) * 100).toFixed(1)}%)</span>
      </div>`;
  }

  // ── K-Means Clustering ──
  _openClusterDialog() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 3) { alert('Need header + at least 2 data rows'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const numHeaders = headers.filter((h, i) => {
      const nums = values.slice(1).map(r => Number(r[i])).filter(n => !isNaN(n));
      return nums.length > values.length * 0.4;
    });

    if (numHeaders.length < 1) { alert('Need at least 1 numeric column for clustering'); return; }

    document.getElementById('cluster-dialog')?.remove();
    const colOpts = numHeaders.map(h => `<option value="${h}">${h}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'cluster-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:650px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>K-Means Clustering</span>
          <button id="cluster-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:12px;align-items:end;margin-bottom:12px;">
            <div>
              <label style="font-size:12px;font-weight:600;">Feature columns:</label><br>
              <select id="cl-cols" multiple size="4" style="padding:4px;border:1px solid #ccc;border-radius:3px;min-width:180px;">${colOpts}</select>
            </div>
            <div>
              <label style="font-size:12px;font-weight:600;">Number of clusters (K):</label><br>
              <input id="cl-k" type="number" value="3" min="2" max="20" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:60px;">
            </div>
            <div>
              <label style="font-size:12px;font-weight:600;">Max iterations:</label><br>
              <input id="cl-iter" type="number" value="100" min="10" max="1000" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:70px;">
            </div>
            <button id="cl-run" style="padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;height:32px;">Run Clustering</button>
          </div>
          <div id="cluster-result" style="font-size:12px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('cluster-close').onclick = () => dialog.remove();

    this._clusterHeaders = headers;
    this._clusterValues = values;

    document.getElementById('cl-run').onclick = () => this._runKMeans();
  }

  _runKMeans() {
    const headers = this._clusterHeaders;
    const values = this._clusterValues;
    const selectedCols = Array.from(document.getElementById('cl-cols').selectedOptions).map(o => o.value);
    const K = parseInt(document.getElementById('cl-k').value) || 3;
    const maxIter = parseInt(document.getElementById('cl-iter').value) || 100;

    if (selectedCols.length === 0) { alert('Select at least one feature column'); return; }

    const colIndices = selectedCols.map(c => headers.indexOf(c));
    const dataRows = values.slice(1);

    // Extract and normalize feature data
    const rawData = dataRows.map(row => colIndices.map(ci => Number(row[ci]) || 0));
    const n = rawData.length;
    const dims = colIndices.length;

    // Normalize (z-score)
    const means = Array(dims).fill(0);
    const stds = Array(dims).fill(0);
    for (let d = 0; d < dims; d++) {
      for (let i = 0; i < n; i++) means[d] += rawData[i][d];
      means[d] /= n;
      for (let i = 0; i < n; i++) stds[d] += (rawData[i][d] - means[d]) ** 2;
      stds[d] = Math.sqrt(stds[d] / n) || 1;
    }
    const data = rawData.map(row => row.map((v, d) => (v - means[d]) / stds[d]));

    // Initialize centroids (k-means++ style)
    const centroids = [];
    centroids.push(data[Math.floor(Math.random() * n)].slice());
    for (let c = 1; c < K; c++) {
      const dists = data.map(point => {
        return Math.min(...centroids.map(cen =>
          point.reduce((sum, v, d) => sum + (v - cen[d]) ** 2, 0)
        ));
      });
      const total = dists.reduce((a, b) => a + b, 0);
      let r = Math.random() * total;
      for (let i = 0; i < n; i++) {
        r -= dists[i];
        if (r <= 0) { centroids.push(data[i].slice()); break; }
      }
    }

    // K-Means iteration
    let assignments = Array(n).fill(0);
    for (let iter = 0; iter < maxIter; iter++) {
      // Assign points to nearest centroid
      let changed = false;
      for (let i = 0; i < n; i++) {
        let minDist = Infinity, best = 0;
        for (let c = 0; c < K; c++) {
          const dist = data[i].reduce((sum, v, d) => sum + (v - centroids[c][d]) ** 2, 0);
          if (dist < minDist) { minDist = dist; best = c; }
        }
        if (assignments[i] !== best) { assignments[i] = best; changed = true; }
      }
      if (!changed) break;

      // Update centroids
      for (let c = 0; c < K; c++) {
        const members = data.filter((_, i) => assignments[i] === c);
        if (members.length === 0) continue;
        for (let d = 0; d < dims; d++) {
          centroids[c][d] = members.reduce((sum, p) => sum + p[d], 0) / members.length;
        }
      }
    }

    // Build results
    const clusters = {};
    for (let c = 0; c < K; c++) {
      const members = assignments.map((a, i) => i).filter(i => assignments[i] === c);
      const clusterData = members.map(i => rawData[i]);
      const centroid = colIndices.map((ci, d) => {
        const vals = clusterData.map(row => row[d]);
        return vals.reduce((a, b) => a + b, 0) / vals.length;
      });
      clusters[c] = { count: members.length, centroid, members };
    }

    const colors = ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#ea580c', '#0891b2', '#64748b', '#ec4899', '#84cc16'];
    const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 2 });

    let html = `<b>K-Means Results (K=${K})</b><br>`;
    html += '<table style="width:100%;border-collapse:collapse;font-size:11px;margin:8px 0;"><thead><tr>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Cluster</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Size</th>';
    selectedCols.forEach(c => { html += `<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Avg ${c}</th>`; });
    html += '</tr></thead><tbody>';

    for (let c = 0; c < K; c++) {
      const cl = clusters[c];
      html += `<tr><td style="border:1px solid #ddd;padding:4px 6px;text-align:center;"><span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${colors[c % colors.length]};vertical-align:middle;margin-right:4px;"></span>${c + 1}</td>`;
      html += `<td style="border:1px solid #ddd;padding:4px 6px;text-align:center;">${cl.count}</td>`;
      cl.centroid.forEach(v => { html += `<td style="border:1px solid #ddd;padding:4px 6px;text-align:right;">${fmt(v)}</td>`; });
      html += '</tr>';
    }
    html += '</tbody></table>';

    // Scatter plot if 2 feature columns selected
    if (selectedCols.length >= 2 && window.Plotly) {
      html += '<div id="cluster-plot" style="width:100%;height:300px;margin-top:8px;"></div>';
    }

    html += `<button id="cl-to-sheet" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;font-size:12px;cursor:pointer;font-weight:600;">Add Cluster Column to Sheet</button>`;

    document.getElementById('cluster-result').innerHTML = html;

    // Plotly scatter
    if (selectedCols.length >= 2 && window.Plotly) {
      setTimeout(() => {
        const plotEl = document.getElementById('cluster-plot');
        if (!plotEl) return;
        const traces = [];
        for (let c = 0; c < K; c++) {
          const members = assignments.map((a, i) => i).filter(i => assignments[i] === c);
          traces.push({
            x: members.map(i => rawData[i][0]),
            y: members.map(i => rawData[i][1]),
            mode: 'markers',
            name: `Cluster ${c + 1} (n=${members.length})`,
            marker: { color: colors[c % colors.length], size: 6 },
            type: 'scatter',
          });
        }
        window.Plotly.newPlot(plotEl, traces, {
          title: `Clusters: ${selectedCols[0]} vs ${selectedCols[1]}`,
          xaxis: { title: selectedCols[0] },
          yaxis: { title: selectedCols[1] },
          margin: { t: 40 },
        }, { responsive: true });
      }, 0);
    }

    // Send cluster column to sheet
    document.getElementById('cl-to-sheet').onclick = () => {
      const lastCol = values[0].length;
      try {
        const headerRange = sheet => sheet.getRange(0, lastCol, 1, 1);
        const sh = this.univerAPI.getActiveWorkbook().getActiveSheet();
        sh.getRange(0, lastCol, 1, 1).setValue('Cluster');
        assignments.forEach((c, i) => {
          sh.getRange(i + 1, lastCol, 1, 1).setValue(c + 1);
        });
      } catch (e) {
        console.error('Cluster column error:', e);
      }
    };
  }

  // ── Sparklines ──
  _insertSparklines() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data first'); return; }

    document.getElementById('spark-dialog')?.remove();
    const headers = values[0].map(v => String(v ?? ''));
    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'spark-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:450px;">
        <div class="pivot-dialog-header">
          <span>Insert Sparklines</span>
          <button id="spark-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <div style="margin-bottom:8px;">
            <label style="font-size:12px;font-weight:600;">Data columns (numeric):</label><br>
            <select id="spark-cols" multiple size="4" style="padding:4px;border:1px solid #ccc;border-radius:3px;min-width:200px;">${colOpts}</select>
          </div>
          <div style="margin-bottom:8px;">
            <label style="font-size:12px;font-weight:600;">Chart type:</label>
            <select id="spark-type" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
              <option value="line">Line</option>
              <option value="bar">Bar</option>
              <option value="dot">Dot</option>
            </select>
            <label style="margin-left:8px;font-size:12px;font-weight:600;">Color:</label>
            <input id="spark-color" type="color" value="#2563eb" style="vertical-align:middle;">
          </div>
          <div style="margin-bottom:8px;">
            <label style="font-size:12px;font-weight:600;">Place sparklines in:</label>
            <select id="spark-dest" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
              <option value="after">Column after last data</option>
              <option value="header">Header row (row 1)</option>
            </select>
          </div>
          <button id="spark-gen" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Generate Sparklines</button>
          <div id="spark-preview" style="margin-top:12px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('spark-close').onclick = () => dialog.remove();

    document.getElementById('spark-gen').onclick = () => {
      const selectedCols = Array.from(document.getElementById('spark-cols').selectedOptions).map(o => o.value);
      const chartType = document.getElementById('spark-type').value;
      const color = document.getElementById('spark-color').value;
      const dest = document.getElementById('spark-dest').value;

      if (selectedCols.length === 0) { alert('Select columns'); return; }

      const sparklines = [];

      if (dest === 'header') {
        // One sparkline per selected column showing distribution
        selectedCols.forEach(colName => {
          const ci = headers.indexOf(colName);
          const nums = values.slice(1).map(r => Number(r[ci])).filter(n => !isNaN(n));
          if (nums.length < 2) return;
          const svg = this._generateSparkSVG(nums, chartType, color, 120, 24);
          sparklines.push({ type: 'header', col: ci, svg, label: colName });
        });
      } else {
        // Per-row sparklines across selected columns
        for (let r = 1; r < values.length; r++) {
          const nums = selectedCols.map(c => Number(values[r][headers.indexOf(c)]) || 0);
          if (nums.some(n => n !== 0)) {
            const svg = this._generateSparkSVG(nums, chartType, color, 100, 20);
            sparklines.push({ type: 'row', row: r, svg });
          }
        }
      }

      // Preview
      let preview = `<div style="font-size:11px;color:#059669;margin-bottom:4px;">${sparklines.length} sparklines generated</div>`;
      sparklines.slice(0, 5).forEach(s => {
        preview += `<div style="margin:2px 0;">${s.label ? s.label + ': ' : `Row ${s.row}: `}<img src="${s.svg}" style="vertical-align:middle;"></div>`;
      });
      if (sparklines.length > 5) preview += `<div style="color:#999;font-size:10px;">...and ${sparklines.length - 5} more</div>`;
      document.getElementById('spark-preview').innerHTML = preview;
    };
  }

  _generateSparkSVG(data, type, color, width, height) {
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const pad = 2;
    const w = width - pad * 2;
    const h = height - pad * 2;

    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;

    if (type === 'line') {
      const points = data.map((v, i) => {
        const x = pad + (i / (data.length - 1)) * w;
        const y = pad + h - ((v - min) / range) * h;
        return `${x},${y}`;
      }).join(' ');
      svg += `<polyline points="${points}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linejoin="round"/>`;
      // Dot on last point
      const lastX = pad + w;
      const lastY = pad + h - ((data[data.length - 1] - min) / range) * h;
      svg += `<circle cx="${lastX}" cy="${lastY}" r="2" fill="${color}"/>`;
    } else if (type === 'bar') {
      const barW = w / data.length * 0.8;
      const gap = w / data.length * 0.2;
      data.forEach((v, i) => {
        const barH = ((v - min) / range) * h;
        const x = pad + i * (barW + gap);
        const y = pad + h - barH;
        svg += `<rect x="${x}" y="${y}" width="${barW}" height="${barH}" fill="${color}" rx="1"/>`;
      });
    } else if (type === 'dot') {
      data.forEach((v, i) => {
        const x = pad + (i / (data.length - 1)) * w;
        const y = pad + h - ((v - min) / range) * h;
        const r = 1.5 + ((v - min) / range) * 2;
        svg += `<circle cx="${x}" cy="${y}" r="${r}" fill="${color}" opacity="0.7"/>`;
      });
    }

    svg += '</svg>';
    return 'data:image/svg+xml;base64,' + btoa(svg);
  }

  // ── Goal Seek (What-If Solver) ──
  _openGoalSeek() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data in sheet'); return; }

    document.getElementById('goalseek-dialog')?.remove();
    const headers = values[0].map(v => String(v ?? ''));
    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'goalseek-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:500px;">
        <div class="pivot-dialog-header">
          <span>Goal Seek</span>
          <button id="gs-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <div style="font-size:12px;color:#666;margin-bottom:10px;">Find the input value that produces a target result. Works by iterating over the input column to find which value makes the formula column equal the target.</div>
          <div style="margin:6px 0;">
            <label style="font-size:12px;font-weight:600;display:inline-block;width:140px;">Target column:</label>
            <select id="gs-target-col" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
          </div>
          <div style="margin:6px 0;">
            <label style="font-size:12px;font-weight:600;display:inline-block;width:140px;">Target row:</label>
            <input id="gs-target-row" type="number" value="2" min="2" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:80px;">
            <span style="font-size:10px;color:#999;">(data row number, 2 = first data row)</span>
          </div>
          <div style="margin:6px 0;">
            <label style="font-size:12px;font-weight:600;display:inline-block;width:140px;">Target value:</label>
            <input id="gs-target-val" type="number" value="0" step="any" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:120px;">
          </div>
          <div style="margin:6px 0;">
            <label style="font-size:12px;font-weight:600;display:inline-block;width:140px;">Changing column:</label>
            <select id="gs-change-col" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
          </div>
          <div style="margin:6px 0;">
            <label style="font-size:12px;font-weight:600;display:inline-block;width:140px;">Formula:</label>
            <input id="gs-formula" type="text" placeholder="e.g. input * 1.08 - 500" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:250px;">
            <div style="font-size:10px;color:#999;margin-top:2px;margin-left:140px;">Use <code>input</code> for the changing value. Other columns: <code>col_Name</code></div>
          </div>
          <div style="margin-top:12px;">
            <button id="gs-run" style="padding:6px 20px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Seek</button>
            <span id="gs-status" style="font-size:12px;color:#666;margin-left:8px;"></span>
          </div>
          <div id="gs-result" style="margin-top:12px;font-size:13px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('gs-close').onclick = () => dialog.remove();

    this._gsHeaders = headers;
    this._gsValues = values;

    document.getElementById('gs-run').onclick = () => this._runGoalSeek();
  }

  _runGoalSeek() {
    const headers = this._gsHeaders;
    const values = this._gsValues;
    const targetCol = document.getElementById('gs-target-col').value;
    const targetRow = parseInt(document.getElementById('gs-target-row').value) || 2;
    const targetVal = parseFloat(document.getElementById('gs-target-val').value);
    const changeCol = document.getElementById('gs-change-col').value;
    const formula = document.getElementById('gs-formula').value.trim();
    const statusEl = document.getElementById('gs-status');
    const resultEl = document.getElementById('gs-result');

    if (!formula) { alert('Enter a formula'); return; }
    if (isNaN(targetVal)) { alert('Enter a target value'); return; }

    const rowIdx = targetRow - 1; // 0-based
    if (rowIdx < 1 || rowIdx >= values.length) { alert('Row out of range'); return; }

    const changeColIdx = headers.indexOf(changeCol);
    const row = values[rowIdx];

    // Build function from formula
    const buildEval = (inputVal) => {
      try {
        let expr = formula;
        // Replace col_Name references with actual values
        headers.forEach((h, i) => {
          const safeH = h.replace(/[^a-zA-Z0-9]/g, '_');
          expr = expr.replace(new RegExp('col_' + safeH, 'g'), String(Number(row[i]) || 0));
        });
        expr = expr.replace(/\binput\b/g, String(inputVal));
        return new Function('return ' + expr)();
      } catch (e) { return NaN; }
    };

    // Binary search / Newton's method hybrid
    const currentVal = Number(row[changeColIdx]) || 0;
    let lo = currentVal - Math.abs(currentVal) * 10 - 10000;
    let hi = currentVal + Math.abs(currentVal) * 10 + 10000;
    let best = currentVal;
    let bestDiff = Math.abs(buildEval(currentVal) - targetVal);

    // First: try bisection on a wide range
    for (let iter = 0; iter < 200; iter++) {
      const mid = (lo + hi) / 2;
      const fLo = buildEval(lo) - targetVal;
      const fMid = buildEval(mid) - targetVal;

      if (Math.abs(fMid) < bestDiff) { bestDiff = Math.abs(fMid); best = mid; }
      if (Math.abs(fMid) < 0.0001) break;

      if ((fLo > 0) !== (fMid > 0)) { hi = mid; }
      else { lo = mid; }
    }

    // Second: Newton-Raphson refinement
    let x = best;
    for (let iter = 0; iter < 100; iter++) {
      const fx = buildEval(x) - targetVal;
      if (Math.abs(fx) < 0.00001) break;
      const h = Math.max(Math.abs(x) * 0.0001, 0.0001);
      const fxh = buildEval(x + h) - targetVal;
      const deriv = (fxh - fx) / h;
      if (deriv === 0) break;
      x -= fx / deriv;
      if (Math.abs(buildEval(x) - targetVal) < bestDiff) {
        bestDiff = Math.abs(buildEval(x) - targetVal);
        best = x;
      }
    }

    const finalResult = buildEval(best);
    const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 6 });
    const success = bestDiff < 0.01;

    statusEl.textContent = success ? 'Solution found!' : 'Approximate solution';
    statusEl.style.color = success ? '#059669' : '#d97706';

    resultEl.innerHTML = `
      <div style="padding:12px;background:${success ? '#ecfdf5' : '#fffbeb'};border-radius:6px;text-align:center;">
        <span style="font-size:14px;color:#666;">Set <b>${changeCol}</b> to:</span><br>
        <span style="font-size:28px;font-weight:700;color:${success ? '#059669' : '#d97706'};">${fmt(best)}</span><br>
        <span style="font-size:12px;color:#666;">Result: ${fmt(finalResult)} (target: ${fmt(targetVal)}, diff: ${fmt(bestDiff)})</span>
      </div>
      <button id="gs-apply" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">Apply to Sheet</button>
    `;

    document.getElementById('gs-apply').onclick = () => {
      try {
        const sh = this.univerAPI.getActiveWorkbook().getActiveSheet();
        sh.getRange(rowIdx, changeColIdx, 1, 1).setValue(Math.round(best * 1000000) / 1000000);
        statusEl.textContent = 'Applied!';
      } catch (e) { console.error('Goal seek apply error:', e); }
    };
  }

  // ── Scenario Manager ──
  _openScenarioManager() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data in sheet'); return; }

    document.getElementById('scenario-dialog')?.remove();
    const headers = values[0].map(v => String(v ?? ''));

    // Load saved scenarios
    const scenarios = JSON.parse(localStorage.getItem('ofg_scenarios') || '[]');

    const dialog = document.createElement('div');
    dialog.id = 'scenario-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:600px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Scenario Manager</span>
          <button id="sc-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;margin-bottom:12px;">
            <input id="sc-name" type="text" placeholder="Scenario name" style="flex:1;padding:6px 8px;border:1px solid #ccc;border-radius:4px;">
            <button id="sc-save" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Save Current</button>
          </div>
          <div id="sc-list" style="margin-bottom:12px;"></div>
          <div id="sc-compare" style=""></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('sc-close').onclick = () => dialog.remove();

    this._scenarioHeaders = headers;
    this._scenarioValues = values;

    document.getElementById('sc-save').onclick = () => {
      const name = document.getElementById('sc-name').value.trim();
      if (!name) { alert('Enter a scenario name'); return; }
      const currentVals = this.univerAPI.getActiveWorkbook().getActiveSheet().getDataRange()?.getValues();
      if (!currentVals) return;
      const sc = { name, savedAt: new Date().toISOString(), headers: currentVals[0].map(v => String(v ?? '')), data: currentVals.slice(1) };
      const scList = JSON.parse(localStorage.getItem('ofg_scenarios') || '[]');
      const existing = scList.findIndex(s => s.name === name);
      if (existing >= 0) scList[existing] = sc;
      else scList.push(sc);
      localStorage.setItem('ofg_scenarios', JSON.stringify(scList));
      this._renderScenarioList();
    };

    this._renderScenarioList();
  }

  _renderScenarioList() {
    const scenarios = JSON.parse(localStorage.getItem('ofg_scenarios') || '[]');
    const listEl = document.getElementById('sc-list');
    if (!listEl) return;

    if (scenarios.length === 0) {
      listEl.innerHTML = '<div style="color:#999;font-size:12px;padding:8px;">No saved scenarios. Save the current sheet state to create one.</div>';
      return;
    }

    let html = '<div style="font-size:12px;font-weight:600;margin-bottom:6px;">Saved Scenarios:</div>';
    scenarios.forEach((sc, i) => {
      html += `<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 8px;border:1px solid #eee;border-radius:4px;margin:4px 0;">
        <div>
          <b>${sc.name}</b>
          <span style="font-size:10px;color:#999;margin-left:8px;">${new Date(sc.savedAt).toLocaleString()} · ${sc.data.length} rows</span>
        </div>
        <div style="display:flex;gap:4px;">
          <button class="sc-load" data-idx="${i}" style="padding:3px 10px;background:#2563eb;color:#fff;border:none;border-radius:3px;font-size:11px;cursor:pointer;">Load</button>
          <button class="sc-cmp" data-idx="${i}" style="padding:3px 10px;background:#7c3aed;color:#fff;border:none;border-radius:3px;font-size:11px;cursor:pointer;">Compare</button>
          <button class="sc-del" data-idx="${i}" style="padding:3px 8px;background:#dc2626;color:#fff;border:none;border-radius:3px;font-size:11px;cursor:pointer;">X</button>
        </div>
      </div>`;
    });

    if (scenarios.length >= 2) {
      html += `<button id="sc-compare-all" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">Compare All Scenarios</button>`;
    }

    listEl.innerHTML = html;

    // Wire buttons
    listEl.querySelectorAll('.sc-load').forEach(btn => {
      btn.onclick = () => {
        const sc = scenarios[parseInt(btn.dataset.idx)];
        if (!sc) return;
        const cellData = {};
        cellData[0] = {};
        sc.headers.forEach((h, ci) => { cellData[0][ci] = { v: h, s: { bl: 1 } }; });
        sc.data.forEach((row, ri) => {
          cellData[ri + 1] = {};
          row.forEach((v, ci) => {
            const num = Number(v);
            cellData[ri + 1][ci] = { v: (v !== '' && v != null && !isNaN(num)) ? num : (v ?? '') };
          });
        });
        const sid = 'scenario_' + Date.now();
        this.univerAPI.createWorkbook({
          name: sc.name,
          sheetOrder: [sid],
          sheets: { [sid]: { id: sid, name: sc.name, rowCount: sc.data.length + 50, columnCount: sc.headers.length + 5, cellData } }
        });
      };
    });

    listEl.querySelectorAll('.sc-del').forEach(btn => {
      btn.onclick = () => {
        if (!confirm('Delete this scenario?')) return;
        const scList = JSON.parse(localStorage.getItem('ofg_scenarios') || '[]');
        scList.splice(parseInt(btn.dataset.idx), 1);
        localStorage.setItem('ofg_scenarios', JSON.stringify(scList));
        this._renderScenarioList();
      };
    });

    listEl.querySelectorAll('.sc-cmp').forEach(btn => {
      btn.onclick = () => {
        const sc = scenarios[parseInt(btn.dataset.idx)];
        this._compareScenario(sc);
      };
    });

    const cmpAllBtn = document.getElementById('sc-compare-all');
    if (cmpAllBtn) {
      cmpAllBtn.onclick = () => this._compareAllScenarios(scenarios);
    }
  }

  _compareScenario(scenario) {
    const currentVals = this.univerAPI.getActiveWorkbook().getActiveSheet().getDataRange()?.getValues();
    if (!currentVals || !scenario) return;

    const cmpEl = document.getElementById('sc-compare');
    if (!cmpEl) return;

    const headers = currentVals[0].map(v => String(v ?? ''));
    const numCols = headers.map((h, i) => {
      const nums = currentVals.slice(1).map(r => Number(r[i])).filter(n => !isNaN(n));
      return nums.length > currentVals.length * 0.3 ? { name: h, idx: i } : null;
    }).filter(Boolean);

    let html = `<div style="font-size:12px;font-weight:600;margin-bottom:6px;">Comparison: Current vs "${scenario.name}"</div>`;
    html += '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Column</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Current Total</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Scenario Total</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Difference</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">% Change</th>';
    html += '</tr></thead><tbody>';

    const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    numCols.forEach(col => {
      const curNums = currentVals.slice(1).map(r => Number(r[col.idx]) || 0);
      const scIdx = scenario.headers.indexOf(col.name);
      const scNums = scIdx >= 0 ? scenario.data.map(r => Number(r[scIdx]) || 0) : [];
      const curSum = curNums.reduce((a, b) => a + b, 0);
      const scSum = scNums.reduce((a, b) => a + b, 0);
      const diff = curSum - scSum;
      const pct = scSum !== 0 ? ((diff / Math.abs(scSum)) * 100).toFixed(1) : '—';
      const color = diff > 0 ? '#059669' : diff < 0 ? '#dc2626' : '#666';

      html += `<tr><td style="border:1px solid #ddd;padding:3px 6px;">${col.name}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;">${fmt(curSum)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;">${fmt(scSum)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;color:${color};font-weight:600;">${diff > 0 ? '+' : ''}${fmt(diff)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;color:${color};">${pct}%</td></tr>`;
    });
    html += '</tbody></table>';
    cmpEl.innerHTML = html;
  }

  _compareAllScenarios(scenarios) {
    const cmpEl = document.getElementById('sc-compare');
    if (!cmpEl) return;

    const currentVals = this.univerAPI.getActiveWorkbook().getActiveSheet().getDataRange()?.getValues();
    if (!currentVals) return;
    const headers = currentVals[0].map(v => String(v ?? ''));

    const numCols = headers.map((h, i) => {
      const nums = currentVals.slice(1).map(r => Number(r[i])).filter(n => !isNaN(n));
      return nums.length > currentVals.length * 0.3 ? { name: h, idx: i } : null;
    }).filter(Boolean);

    let html = '<div style="font-size:12px;font-weight:600;margin-bottom:6px;">All Scenarios Comparison (Totals)</div>';
    html += '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;">Column</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 6px;background:#eff6ff;">Current</th>';
    scenarios.forEach(sc => {
      html += `<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f3ff;">${sc.name}</th>`;
    });
    html += '</tr></thead><tbody>';

    const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    numCols.forEach(col => {
      const curSum = currentVals.slice(1).map(r => Number(r[col.idx]) || 0).reduce((a, b) => a + b, 0);
      html += `<tr><td style="border:1px solid #ddd;padding:3px 6px;font-weight:600;">${col.name}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;background:#eff6ff;">${fmt(curSum)}</td>`;
      scenarios.forEach(sc => {
        const scIdx = sc.headers.indexOf(col.name);
        const scSum = scIdx >= 0 ? sc.data.map(r => Number(r[scIdx]) || 0).reduce((a, b) => a + b, 0) : 0;
        const diff = scSum - curSum;
        const color = diff > 0 ? '#059669' : diff < 0 ? '#dc2626' : '#666';
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;">${fmt(scSum)} <span style="color:${color};font-size:9px;">(${diff >= 0 ? '+' : ''}${fmt(diff)})</span></td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table>';

    // Send comparison to sheet
    html += `<button id="sc-cmp-tosheet" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Send Comparison to Sheet</button>`;
    cmpEl.innerHTML = html;

    document.getElementById('sc-cmp-tosheet').onclick = () => {
      const cellData = {};
      cellData[0] = { 0: { v: 'Column', s: { bl: 1 } }, 1: { v: 'Current', s: { bl: 1 } } };
      scenarios.forEach((sc, si) => { cellData[0][si + 2] = { v: sc.name, s: { bl: 1 } }; });
      numCols.forEach((col, ri) => {
        cellData[ri + 1] = { 0: { v: col.name } };
        const curSum = currentVals.slice(1).map(r => Number(r[col.idx]) || 0).reduce((a, b) => a + b, 0);
        cellData[ri + 1][1] = { v: Math.round(curSum * 100) / 100 };
        scenarios.forEach((sc, si) => {
          const scIdx = sc.headers.indexOf(col.name);
          const scSum = scIdx >= 0 ? sc.data.map(r => Number(r[scIdx]) || 0).reduce((a, b) => a + b, 0) : 0;
          cellData[ri + 1][si + 2] = { v: Math.round(scSum * 100) / 100 };
        });
      });
      const sid = 'compare_' + Date.now();
      this.univerAPI.createWorkbook({
        name: 'Scenario Comparison',
        sheetOrder: [sid],
        sheets: { [sid]: { id: sid, name: 'Comparison', rowCount: numCols.length + 50, columnCount: scenarios.length + 10, cellData } }
      });
    };
  }

  // ── Accounting Templates ──
  _openTemplates() {
    if (!this.univerAPI) return;
    document.getElementById('template-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'template-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:550px;max-height:80vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Accounting Templates</span>
          <button id="tpl-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
            <button class="tpl-card" data-tpl="pnl" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;cursor:pointer;text-align:left;">
              <div style="font-size:20px;margin-bottom:4px;">&#128200;</div>
              <div style="font-weight:700;font-size:13px;">Income Statement (P&L)</div>
              <div style="font-size:11px;color:#666;margin-top:4px;">Revenue, COGS, gross profit, operating expenses, net income</div>
            </button>
            <button class="tpl-card" data-tpl="bs" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;cursor:pointer;text-align:left;">
              <div style="font-size:20px;margin-bottom:4px;">&#128203;</div>
              <div style="font-weight:700;font-size:13px;">Balance Sheet</div>
              <div style="font-size:11px;color:#666;margin-top:4px;">Assets, liabilities, equity with standard categories</div>
            </button>
            <button class="tpl-card" data-tpl="cf" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;cursor:pointer;text-align:left;">
              <div style="font-size:20px;margin-bottom:4px;">&#128176;</div>
              <div style="font-weight:700;font-size:13px;">Cash Flow Statement</div>
              <div style="font-size:11px;color:#666;margin-top:4px;">Operating, investing, financing activities</div>
            </button>
            <button class="tpl-card" data-tpl="budget" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;cursor:pointer;text-align:left;">
              <div style="font-size:20px;margin-bottom:4px;">&#128197;</div>
              <div style="font-weight:700;font-size:13px;">Monthly Budget</div>
              <div style="font-size:11px;color:#666;margin-top:4px;">12-month budget with categories, actual vs budget</div>
            </button>
            <button class="tpl-card" data-tpl="invoice" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;cursor:pointer;text-align:left;">
              <div style="font-size:20px;margin-bottom:4px;">&#128196;</div>
              <div style="font-weight:700;font-size:13px;">Invoice</div>
              <div style="font-size:11px;color:#666;margin-top:4px;">Professional invoice with line items, tax, totals</div>
            </button>
            <button class="tpl-card" data-tpl="aging" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;cursor:pointer;text-align:left;">
              <div style="font-size:20px;margin-bottom:4px;">&#9200;</div>
              <div style="font-weight:700;font-size:13px;">AR Aging Report</div>
              <div style="font-size:11px;color:#666;margin-top:4px;">Accounts receivable aging by 30/60/90/120+ days</div>
            </button>
          </div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('tpl-close').onclick = () => dialog.remove();

    dialog.querySelectorAll('.tpl-card').forEach(btn => {
      btn.onmouseenter = () => { btn.style.borderColor = '#2563eb'; btn.style.background = '#eff6ff'; };
      btn.onmouseleave = () => { btn.style.borderColor = '#e5e7eb'; btn.style.background = '#fff'; };
      btn.onclick = () => { this._loadTemplate(btn.dataset.tpl); dialog.remove(); };
    });
  }

  _loadTemplate(tpl) {
    const cd = {}; // cellData
    const s = (bl, bg, cl, fs, ht) => {
      const style = {};
      if (bl) style.bl = 1;
      if (bg) style.bg = { rgb: bg };
      if (cl) style.cl = { rgb: cl };
      if (fs) style.fs = fs;
      if (ht) style.ht = ht;
      return style;
    };
    const hdr = s(1, '#1e3a5f', '#ffffff', 12);
    const section = s(1, '#e8eef6', '#1e3a5f', 11);
    const sub = s(0, null, null, 11);
    const total = s(1, '#f0f0f0', '#000000', 11);
    const grandTotal = s(1, '#1e3a5f', '#ffffff', 12);

    const setRow = (r, cells) => {
      cd[r] = {};
      cells.forEach((cell, c) => {
        if (cell === null) return;
        if (typeof cell === 'object') cd[r][c] = cell;
        else cd[r][c] = { v: cell };
      });
    };

    let rows, cols, name;

    switch (tpl) {
      case 'pnl':
        name = 'Income Statement';
        setRow(0, [{ v: 'INCOME STATEMENT', s: grandTotal }, null, { v: 'Q1', s: hdr }, { v: 'Q2', s: hdr }, { v: 'Q3', s: hdr }, { v: 'Q4', s: hdr }, { v: 'TOTAL', s: hdr }]);
        setRow(1, [{ v: 'REVENUE', s: section }]);
        setRow(2, [null, { v: 'Product Sales', s: sub }, 0, 0, 0, 0, '']);
        setRow(3, [null, { v: 'Service Revenue', s: sub }, 0, 0, 0, 0, '']);
        setRow(4, [null, { v: 'Other Revenue', s: sub }, 0, 0, 0, 0, '']);
        setRow(5, [{ v: 'Total Revenue', s: total }, null, '', '', '', '', '']);
        setRow(6, []);
        setRow(7, [{ v: 'COST OF GOODS SOLD', s: section }]);
        setRow(8, [null, { v: 'Materials', s: sub }, 0, 0, 0, 0, '']);
        setRow(9, [null, { v: 'Direct Labor', s: sub }, 0, 0, 0, 0, '']);
        setRow(10, [null, { v: 'Manufacturing Overhead', s: sub }, 0, 0, 0, 0, '']);
        setRow(11, [{ v: 'Total COGS', s: total }, null, '', '', '', '', '']);
        setRow(12, []);
        setRow(13, [{ v: 'GROSS PROFIT', s: grandTotal }, null, '', '', '', '', '']);
        setRow(14, []);
        setRow(15, [{ v: 'OPERATING EXPENSES', s: section }]);
        setRow(16, [null, { v: 'Salaries & Wages', s: sub }, 0, 0, 0, 0, '']);
        setRow(17, [null, { v: 'Rent & Utilities', s: sub }, 0, 0, 0, 0, '']);
        setRow(18, [null, { v: 'Marketing & Advertising', s: sub }, 0, 0, 0, 0, '']);
        setRow(19, [null, { v: 'Insurance', s: sub }, 0, 0, 0, 0, '']);
        setRow(20, [null, { v: 'Depreciation', s: sub }, 0, 0, 0, 0, '']);
        setRow(21, [null, { v: 'Professional Fees', s: sub }, 0, 0, 0, 0, '']);
        setRow(22, [null, { v: 'Office Supplies', s: sub }, 0, 0, 0, 0, '']);
        setRow(23, [null, { v: 'Other OpEx', s: sub }, 0, 0, 0, 0, '']);
        setRow(24, [{ v: 'Total Operating Expenses', s: total }, null, '', '', '', '', '']);
        setRow(25, []);
        setRow(26, [{ v: 'OPERATING INCOME (EBIT)', s: grandTotal }, null, '', '', '', '', '']);
        setRow(27, []);
        setRow(28, [null, { v: 'Interest Expense', s: sub }, 0, 0, 0, 0, '']);
        setRow(29, [null, { v: 'Tax Expense', s: sub }, 0, 0, 0, 0, '']);
        setRow(30, []);
        setRow(31, [{ v: 'NET INCOME', s: grandTotal }, null, '', '', '', '', '']);
        rows = 50; cols = 10;
        break;

      case 'bs':
        name = 'Balance Sheet';
        setRow(0, [{ v: 'BALANCE SHEET', s: grandTotal }, null, { v: 'Current Period', s: hdr }, { v: 'Prior Period', s: hdr }, { v: 'Change', s: hdr }]);
        setRow(1, [{ v: 'ASSETS', s: section }]);
        setRow(2, [{ v: 'Current Assets', s: s(1) }]);
        setRow(3, [null, { v: 'Cash & Cash Equivalents', s: sub }, 0, 0, '']);
        setRow(4, [null, { v: 'Accounts Receivable', s: sub }, 0, 0, '']);
        setRow(5, [null, { v: 'Inventory', s: sub }, 0, 0, '']);
        setRow(6, [null, { v: 'Prepaid Expenses', s: sub }, 0, 0, '']);
        setRow(7, [{ v: 'Total Current Assets', s: total }, null, '', '', '']);
        setRow(8, []);
        setRow(9, [{ v: 'Non-Current Assets', s: s(1) }]);
        setRow(10, [null, { v: 'Property, Plant & Equipment', s: sub }, 0, 0, '']);
        setRow(11, [null, { v: 'Less: Accumulated Depreciation', s: sub }, 0, 0, '']);
        setRow(12, [null, { v: 'Intangible Assets', s: sub }, 0, 0, '']);
        setRow(13, [null, { v: 'Long-term Investments', s: sub }, 0, 0, '']);
        setRow(14, [{ v: 'Total Non-Current Assets', s: total }, null, '', '', '']);
        setRow(15, []);
        setRow(16, [{ v: 'TOTAL ASSETS', s: grandTotal }, null, '', '', '']);
        setRow(17, []);
        setRow(18, [{ v: 'LIABILITIES', s: section }]);
        setRow(19, [{ v: 'Current Liabilities', s: s(1) }]);
        setRow(20, [null, { v: 'Accounts Payable', s: sub }, 0, 0, '']);
        setRow(21, [null, { v: 'Accrued Expenses', s: sub }, 0, 0, '']);
        setRow(22, [null, { v: 'Short-term Debt', s: sub }, 0, 0, '']);
        setRow(23, [null, { v: 'Current Portion LTD', s: sub }, 0, 0, '']);
        setRow(24, [{ v: 'Total Current Liabilities', s: total }, null, '', '', '']);
        setRow(25, []);
        setRow(26, [null, { v: 'Long-term Debt', s: sub }, 0, 0, '']);
        setRow(27, [null, { v: 'Deferred Tax Liabilities', s: sub }, 0, 0, '']);
        setRow(28, [{ v: 'Total Liabilities', s: total }, null, '', '', '']);
        setRow(29, []);
        setRow(30, [{ v: 'EQUITY', s: section }]);
        setRow(31, [null, { v: 'Common Stock', s: sub }, 0, 0, '']);
        setRow(32, [null, { v: 'Retained Earnings', s: sub }, 0, 0, '']);
        setRow(33, [null, { v: 'Additional Paid-in Capital', s: sub }, 0, 0, '']);
        setRow(34, [{ v: 'Total Equity', s: total }, null, '', '', '']);
        setRow(35, []);
        setRow(36, [{ v: 'TOTAL LIABILITIES + EQUITY', s: grandTotal }, null, '', '', '']);
        rows = 50; cols = 8;
        break;

      case 'cf':
        name = 'Cash Flow Statement';
        setRow(0, [{ v: 'CASH FLOW STATEMENT', s: grandTotal }, null, { v: 'Amount', s: hdr }]);
        setRow(1, [{ v: 'OPERATING ACTIVITIES', s: section }]);
        setRow(2, [null, { v: 'Net Income', s: sub }, 0]);
        setRow(3, [null, { v: 'Depreciation & Amortization', s: sub }, 0]);
        setRow(4, [null, { v: 'Changes in Accounts Receivable', s: sub }, 0]);
        setRow(5, [null, { v: 'Changes in Inventory', s: sub }, 0]);
        setRow(6, [null, { v: 'Changes in Accounts Payable', s: sub }, 0]);
        setRow(7, [null, { v: 'Changes in Accrued Expenses', s: sub }, 0]);
        setRow(8, [null, { v: 'Other Operating', s: sub }, 0]);
        setRow(9, [{ v: 'Net Cash from Operations', s: total }, null, '']);
        setRow(10, []);
        setRow(11, [{ v: 'INVESTING ACTIVITIES', s: section }]);
        setRow(12, [null, { v: 'Purchase of PP&E', s: sub }, 0]);
        setRow(13, [null, { v: 'Sale of Assets', s: sub }, 0]);
        setRow(14, [null, { v: 'Investments', s: sub }, 0]);
        setRow(15, [{ v: 'Net Cash from Investing', s: total }, null, '']);
        setRow(16, []);
        setRow(17, [{ v: 'FINANCING ACTIVITIES', s: section }]);
        setRow(18, [null, { v: 'Proceeds from Borrowing', s: sub }, 0]);
        setRow(19, [null, { v: 'Repayment of Debt', s: sub }, 0]);
        setRow(20, [null, { v: 'Dividends Paid', s: sub }, 0]);
        setRow(21, [null, { v: 'Stock Issued/Repurchased', s: sub }, 0]);
        setRow(22, [{ v: 'Net Cash from Financing', s: total }, null, '']);
        setRow(23, []);
        setRow(24, [{ v: 'NET CHANGE IN CASH', s: grandTotal }, null, '']);
        setRow(25, []);
        setRow(26, [null, { v: 'Beginning Cash Balance', s: sub }, 0]);
        setRow(27, [{ v: 'ENDING CASH BALANCE', s: grandTotal }, null, '']);
        rows = 40; cols = 6;
        break;

      case 'budget':
        name = 'Monthly Budget';
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        setRow(0, [{ v: 'MONTHLY BUDGET', s: grandTotal }, ...months.map(m => ({ v: m, s: hdr })), { v: 'TOTAL', s: hdr }]);
        setRow(1, [{ v: 'INCOME', s: section }]);
        setRow(2, [{ v: 'Salary/Revenue' }, ...months.map(() => 0), '']);
        setRow(3, [{ v: 'Other Income' }, ...months.map(() => 0), '']);
        setRow(4, [{ v: 'Total Income', s: total }, ...months.map(() => ''), '']);
        setRow(5, []);
        setRow(6, [{ v: 'EXPENSES', s: section }]);
        const expCats = ['Rent/Mortgage', 'Utilities', 'Insurance', 'Groceries', 'Transportation', 'Healthcare', 'Entertainment', 'Savings', 'Debt Payments', 'Subscriptions', 'Miscellaneous'];
        expCats.forEach((cat, i) => {
          setRow(7 + i, [{ v: cat }, ...months.map(() => 0), '']);
        });
        const afterExp = 7 + expCats.length;
        setRow(afterExp, [{ v: 'Total Expenses', s: total }, ...months.map(() => ''), '']);
        setRow(afterExp + 1, []);
        setRow(afterExp + 2, [{ v: 'NET (Income - Expenses)', s: grandTotal }, ...months.map(() => ''), '']);
        rows = afterExp + 20; cols = 16;
        break;

      case 'invoice':
        name = 'Invoice';
        setRow(0, [{ v: 'INVOICE', s: grandTotal }]);
        setRow(1, []);
        setRow(2, [{ v: 'From:', s: s(1) }, null, null, { v: 'Invoice #:', s: s(1) }, 'INV-001']);
        setRow(3, [{ v: 'Company Name' }, null, null, { v: 'Date:', s: s(1) }, new Date().toLocaleDateString()]);
        setRow(4, [{ v: 'Address Line 1' }, null, null, { v: 'Due Date:', s: s(1) }, '']);
        setRow(5, [{ v: 'City, State ZIP' }, null, null, { v: 'Terms:', s: s(1) }, 'Net 30']);
        setRow(6, []);
        setRow(7, [{ v: 'Bill To:', s: s(1) }]);
        setRow(8, [{ v: 'Client Name' }]);
        setRow(9, [{ v: 'Client Address' }]);
        setRow(10, []);
        setRow(11, [{ v: 'Description', s: hdr }, null, { v: 'Qty', s: hdr }, { v: 'Rate', s: hdr }, { v: 'Amount', s: hdr }]);
        setRow(12, [{ v: 'Service/Product 1' }, null, 1, 0, '']);
        setRow(13, [{ v: 'Service/Product 2' }, null, 1, 0, '']);
        setRow(14, [{ v: 'Service/Product 3' }, null, 1, 0, '']);
        setRow(15, [{ v: 'Service/Product 4' }, null, 1, 0, '']);
        setRow(16, []);
        setRow(17, [null, null, null, { v: 'Subtotal:', s: s(1) }, '']);
        setRow(18, [null, null, null, { v: 'Tax (%):', s: s(1) }, 0]);
        setRow(19, [null, null, null, { v: 'Tax Amount:', s: s(1) }, '']);
        setRow(20, [null, null, null, { v: 'TOTAL:', s: grandTotal }, '']);
        setRow(21, []);
        setRow(22, [{ v: 'Notes:', s: s(1) }]);
        setRow(23, [{ v: 'Thank you for your business!' }]);
        rows = 30; cols = 8;
        break;

      case 'aging':
        name = 'AR Aging Report';
        setRow(0, [{ v: 'ACCOUNTS RECEIVABLE AGING', s: grandTotal }, null, null, null, null, null, { v: new Date().toLocaleDateString(), s: s(0, null, '#999999') }]);
        setRow(1, [{ v: 'Customer', s: hdr }, { v: 'Invoice #', s: hdr }, { v: 'Current', s: hdr }, { v: '1-30 Days', s: hdr }, { v: '31-60 Days', s: hdr }, { v: '61-90 Days', s: hdr }, { v: '90+ Days', s: hdr }, { v: 'Total', s: hdr }]);
        for (let i = 0; i < 10; i++) {
          setRow(2 + i, ['', '', 0, 0, 0, 0, 0, '']);
        }
        setRow(12, [{ v: 'TOTALS', s: total }, null, '', '', '', '', '', '']);
        setRow(13, []);
        setRow(14, [{ v: 'AGING SUMMARY', s: section }]);
        setRow(15, [null, { v: 'Bucket' }, { v: 'Amount' }, { v: '% of Total' }]);
        setRow(16, [null, { v: 'Current' }, '', '']);
        setRow(17, [null, { v: '1-30 Days' }, '', '']);
        setRow(18, [null, { v: '31-60 Days' }, '', '']);
        setRow(19, [null, { v: '61-90 Days' }, '', '']);
        setRow(20, [null, { v: '90+ Days' }, '', '']);
        rows = 30; cols = 10;
        break;
    }

    const sid = 'tpl_' + Date.now();
    this.univerAPI.createWorkbook({
      name,
      sheetOrder: [sid],
      sheets: { [sid]: { id: sid, name, rowCount: rows, columnCount: cols, cellData: cd } }
    });
  }

  // ── Data Profiling / Quality Report ──
  _openDataProfile() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data to profile'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);
    const totalRows = dataRows.length;

    document.getElementById('profile-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'profile-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:750px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Data Profile & Quality Report</span>
          <button id="profile-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;" id="profile-body">
          <div style="text-align:center;color:#999;padding:20px;">Analyzing...</div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('profile-close').onclick = () => dialog.remove();

    // Run profiling async to not block UI
    setTimeout(() => {
      const profileBody = document.getElementById('profile-body');
      if (!profileBody) return;

      // Overall stats
      let html = `<div style="display:flex;gap:12px;margin-bottom:16px;">`;
      html += `<div style="flex:1;padding:12px;background:#eff6ff;border-radius:8px;text-align:center;">
        <div style="font-size:24px;font-weight:700;color:#2563eb;">${totalRows.toLocaleString()}</div>
        <div style="font-size:11px;color:#666;">Rows</div></div>`;
      html += `<div style="flex:1;padding:12px;background:#ecfdf5;border-radius:8px;text-align:center;">
        <div style="font-size:24px;font-weight:700;color:#059669;">${headers.length}</div>
        <div style="font-size:11px;color:#666;">Columns</div></div>`;

      const totalCells = totalRows * headers.length;
      let emptyCells = 0;
      dataRows.forEach(r => r.forEach(c => { if (c == null || String(c).trim() === '') emptyCells++; }));
      const completeness = ((1 - emptyCells / totalCells) * 100).toFixed(1);

      html += `<div style="flex:1;padding:12px;background:${completeness >= 95 ? '#ecfdf5' : completeness >= 80 ? '#fffbeb' : '#fef2f2'};border-radius:8px;text-align:center;">
        <div style="font-size:24px;font-weight:700;color:${completeness >= 95 ? '#059669' : completeness >= 80 ? '#d97706' : '#dc2626'};">${completeness}%</div>
        <div style="font-size:11px;color:#666;">Completeness</div></div>`;

      let dupRows = 0;
      const rowSigs = new Set();
      dataRows.forEach(r => {
        const sig = r.map(c => String(c ?? '')).join('|');
        if (rowSigs.has(sig)) dupRows++;
        else rowSigs.add(sig);
      });

      html += `<div style="flex:1;padding:12px;background:${dupRows === 0 ? '#ecfdf5' : '#fef2f2'};border-radius:8px;text-align:center;">
        <div style="font-size:24px;font-weight:700;color:${dupRows === 0 ? '#059669' : '#dc2626'};">${dupRows}</div>
        <div style="font-size:11px;color:#666;">Duplicate Rows</div></div>`;
      html += `</div>`;

      // Column-level profiling
      html += '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
      ['Column', 'Type', 'Filled', 'Unique', 'Min', 'Max', 'Mean', 'Top Values', 'Quality'].forEach(h => {
        html += `<th style="border:1px solid #ddd;padding:4px 6px;background:#f5f5f5;text-align:left;white-space:nowrap;">${h}</th>`;
      });
      html += '</tr></thead><tbody>';

      headers.forEach((header, colIdx) => {
        const colVals = dataRows.map(r => r[colIdx]);
        const nonEmpty = colVals.filter(v => v != null && String(v).trim() !== '');
        const filledPct = ((nonEmpty.length / totalRows) * 100).toFixed(0);
        const unique = new Set(nonEmpty.map(v => String(v))).size;

        // Detect type
        const nums = nonEmpty.map(v => Number(v)).filter(n => !isNaN(n));
        const isNumeric = nums.length > nonEmpty.length * 0.7;
        const dates = nonEmpty.filter(v => !isNaN(Date.parse(String(v))));
        const isDate = dates.length > nonEmpty.length * 0.7 && !isNumeric;
        const type = isNumeric ? 'Numeric' : isDate ? 'Date' : 'Text';

        let min = '', max = '', mean = '';
        if (isNumeric && nums.length > 0) {
          min = Math.min(...nums).toLocaleString(undefined, { maximumFractionDigits: 2 });
          max = Math.max(...nums).toLocaleString(undefined, { maximumFractionDigits: 2 });
          mean = (nums.reduce((a, b) => a + b, 0) / nums.length).toLocaleString(undefined, { maximumFractionDigits: 2 });
        } else if (isDate && dates.length > 0) {
          const timestamps = dates.map(d => new Date(String(d)).getTime()).filter(t => !isNaN(t));
          if (timestamps.length > 0) {
            min = new Date(Math.min(...timestamps)).toLocaleDateString();
            max = new Date(Math.max(...timestamps)).toLocaleDateString();
          }
        }

        // Top values
        const counts = {};
        nonEmpty.forEach(v => { const s = String(v); counts[s] = (counts[s] || 0) + 1; });
        const topVals = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
        const topStr = topVals.map(([v, c]) => `${v.length > 12 ? v.slice(0, 12) + '..' : v} (${c})`).join(', ');

        // Quality score
        let quality = 100;
        if (filledPct < 100) quality -= (100 - filledPct) * 0.5;
        if (unique === 1 && nonEmpty.length > 10) quality -= 20;
        if (dupRows > totalRows * 0.1) quality -= 10;
        quality = Math.max(0, Math.round(quality));
        const qColor = quality >= 90 ? '#059669' : quality >= 70 ? '#d97706' : '#dc2626';
        const qBg = quality >= 90 ? '#ecfdf5' : quality >= 70 ? '#fffbeb' : '#fef2f2';

        const typeColor = type === 'Numeric' ? '#2563eb' : type === 'Date' ? '#7c3aed' : '#666';

        html += `<tr>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;font-weight:600;">${header}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;color:${typeColor};">${type}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:center;${filledPct < 100 ? 'color:#dc2626;font-weight:600;' : ''}">${filledPct}%</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:center;">${unique}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;">${min}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;">${max}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;">${mean}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;font-size:10px;">${topStr}</td>`;
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:center;background:${qBg};color:${qColor};font-weight:700;">${quality}%</td>`;
        html += `</tr>`;
      });

      html += '</tbody></table>';

      // Quality issues summary
      const issues = [];
      if (emptyCells > 0) issues.push(`${emptyCells.toLocaleString()} empty cells (${((emptyCells/totalCells)*100).toFixed(1)}%)`);
      if (dupRows > 0) issues.push(`${dupRows} duplicate rows`);
      headers.forEach((h, i) => {
        const vals = dataRows.map(r => r[i]);
        const nonEmpty = vals.filter(v => v != null && String(v).trim() !== '');
        const nums = nonEmpty.map(v => Number(v)).filter(n => !isNaN(n));
        if (nums.length > 0 && nums.length < nonEmpty.length * 0.7 && nums.length > nonEmpty.length * 0.3) {
          issues.push(`"${h}" has mixed types (${nums.length} numbers, ${nonEmpty.length - nums.length} text)`);
        }
      });

      if (issues.length > 0) {
        html += '<div style="margin-top:12px;padding:10px;background:#fef2f2;border-radius:6px;border:1px solid #fecaca;">';
        html += '<div style="font-size:12px;font-weight:600;color:#dc2626;margin-bottom:4px;">Quality Issues:</div>';
        issues.forEach(issue => { html += `<div style="font-size:11px;color:#666;margin:2px 0;">&#9888; ${issue}</div>`; });
        html += '</div>';
      } else {
        html += '<div style="margin-top:12px;padding:10px;background:#ecfdf5;border-radius:6px;border:1px solid #a7f3d0;">';
        html += '<div style="font-size:12px;font-weight:600;color:#059669;">Data quality looks good — no issues detected.</div>';
        html += '</div>';
      }

      profileBody.innerHTML = html;
    }, 50);
  }

  // ── Inject Create Card into Univer's native context menu ──
  _injectContextMenuItem() {
    const container = document.getElementById('grid-spreadsheet');
    if (!container) return;
    const self = this;

    // Watch for Univer's context menu appearing anywhere in the DOM
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          // Skip our own elements
          if (node.id === 'grid-context-menu' || node.id === 'grid-createcard-dialog' || node.id === 'grid-ac-dropdown') continue;

          // Look for Univer's context menu — detect by its structure
          // Univer menus contain items with text like "Copy", "Paste", "Insert", etc.
          const menuEl = self._findUniverContextMenu(node);
          if (menuEl && !menuEl._cardInjected) {
            menuEl._cardInjected = true;
            self._addCreateCardToMenu(menuEl);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  _findUniverContextMenu(node) {
    // Univer's context menu is a positioned element containing menu items
    // Look for the element that contains "Copy" or "Paste" text
    const text = node.textContent || '';
    if (text.includes('Copy') && text.includes('Paste')) return node;
    // Also check children
    if (node.querySelector) {
      const items = node.querySelectorAll('*');
      for (const item of items) {
        const t = item.textContent || '';
        if (t === 'Copy' || t === 'Paste') {
          // Walk up to the menu container
          let el = item;
          while (el && el !== document.body) {
            const style = window.getComputedStyle(el);
            if ((style.position === 'fixed' || style.position === 'absolute') && el.children.length >= 3) {
              return el;
            }
            el = el.parentElement;
          }
        }
      }
    }
    return null;
  }

  _addCreateCardToMenu(menuContainer) {
    const self = this;

    // Find the first child list/container inside the menu to append to
    // Univer typically uses a list-like structure
    let target = menuContainer;
    // Look for the element that directly contains the menu items
    const children = menuContainer.children;
    for (const child of children) {
      if (child.children.length >= 3) {
        target = child;
        break;
      }
    }

    // Create separator
    const sep = document.createElement('div');
    sep.style.cssText = 'height:1px;background:#e5e5e5;margin:4px 8px;';

    // Create "Create Card" item matching Univer's menu style
    const item = document.createElement('div');
    item.style.cssText = 'display:flex;align-items:center;gap:8px;padding:6px 12px;cursor:pointer;font-size:13px;color:#1e40af;font-weight:600;';
    item.innerHTML = '<span style="font-size:15px;">&#127991;&#65039;</span><span>Create Card from Selection</span>';
    item.addEventListener('mouseenter', () => { item.style.background = '#e8f0fe'; });
    item.addEventListener('mouseleave', () => { item.style.background = 'none'; });
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      // Close the Univer menu
      menuContainer.style.display = 'none';
      try { menuContainer.remove(); } catch (_) {}
      // Trigger create card
      self._onCreateCardBtn();
    });

    target.appendChild(sep);
    target.appendChild(item);
  }

  // ── Right-Click Context Menu (legacy — disabled) ──
  _initContextMenu() {
    const container = document.getElementById('grid-spreadsheet');
    if (!container) return;
    const self = this;
    let pendingRightClick = null;

    // Known Univer menu text fragments — these i18n keys appear in their context menu
    const UNIVER_MENU_TEXTS = ['sheets-sort', 'sheetThreadComment', 'hyperLink.menu', 'rightClick', 'sheet.menu'];
    const SAFE_IDS = new Set(['grid-context-menu', 'grid-createcard-dialog', 'grid-ac-dropdown']);

    // Nuclear option: walk entire DOM, find elements containing Univer menu text,
    // then remove their closest positioned ancestor container
    const nukeUniverMenus = () => {
      // Strategy 1: Find elements by Univer i18n text content
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      const toRemove = new Set();
      while (walker.nextNode()) {
        const text = walker.currentNode.textContent;
        if (UNIVER_MENU_TEXTS.some(t => text.includes(t))) {
          // Walk up to find the positioned container
          let el = walker.currentNode.parentElement;
          while (el && el !== document.body) {
            if (SAFE_IDS.has(el.id)) break;
            const style = window.getComputedStyle(el);
            if (style.position === 'fixed' || style.position === 'absolute') {
              toRemove.add(el);
              break;
            }
            el = el.parentElement;
          }
        }
      }
      toRemove.forEach(el => el.remove());

      // Strategy 2: Also scan all fixed/absolute elements anywhere in body
      // that aren't our known elements and appeared as floating panels
      document.querySelectorAll('body *').forEach(el => {
        if (SAFE_IDS.has(el.id)) return;
        if (el.closest('#grid-context-menu') || el.closest('#grid-createcard-dialog')) return;
        // Skip elements that are part of the main app structure
        if (el.closest('#app') && !el.closest('[class*="menu"]') && !el.closest('[class*="Menu"]') && !el.closest('[class*="popup"]') && !el.closest('[class*="Popup"]')) return;
        const style = window.getComputedStyle(el);
        if ((style.position === 'fixed' || style.position === 'absolute') && style.zIndex !== '' && parseInt(style.zIndex) > 1000) {
          // High z-index floating element that's not ours — likely Univer menu
          if (el.textContent && UNIVER_MENU_TEXTS.some(t => el.textContent.includes(t))) {
            el.remove();
          }
        }
      });
    };

    // Step 1: Capture right mousedown coords
    document.addEventListener('mousedown', (e) => {
      if (e.button === 2 && container.contains(e.target)) {
        pendingRightClick = { x: e.clientX, y: e.clientY };
      } else {
        pendingRightClick = null;
      }
    }, true);

    // Step 2: Kill ALL contextmenu events inside the grid
    document.addEventListener('contextmenu', (e) => {
      if (!container.contains(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (!pendingRightClick) return;
      const { x, y } = pendingRightClick;
      pendingRightClick = null;

      // Show our menu, nuke Univer's repeatedly
      setTimeout(() => {
        nukeUniverMenus();
        self._showGridContextMenu(x, y);
        for (let i = 1; i <= 30; i++) setTimeout(nukeUniverMenus, i * 30);
      }, 10);
      return false;
    }, true);

    // Step 3: MutationObserver that fires nukeUniverMenus whenever anything is added
    let rightClickActive = false;
    const observer = new MutationObserver(() => {
      if (rightClickActive) nukeUniverMenus();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    container.addEventListener('mousedown', (e) => {
      if (e.button === 2) {
        rightClickActive = true;
        // Stop watching after 1 second
        setTimeout(() => { rightClickActive = false; }, 1000);
      }
    });

    container.addEventListener('mouseup', (e) => {
      if (e.button === 2) {
        for (let i = 0; i < 30; i++) setTimeout(nukeUniverMenus, 20 + i * 30);
      }
    });
  }

  _showGridContextMenu(x, y) {
    // Remove any existing
    const old = document.getElementById('grid-context-menu');
    if (old) old.remove();

    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const sel = sheet.getActiveRange();
    if (!sel) return;

    const menu = document.createElement('div');
    menu.id = 'grid-context-menu';
    menu.style.cssText = 'position:fixed;z-index:999999;background:#fff;border:1px solid #d0d0d0;border-radius:10px;box-shadow:0 8px 32px rgba(0,0,0,0.22);padding:6px 0;min-width:230px;font-family:Arial,sans-serif;';
    menu.style.left = x + 'px';
    menu.style.top = y + 'px';

    const createItem = (icon, label, bold, onClick) => {
      const item = document.createElement('div');
      const weight = bold ? 'font-weight:700;' : '';
      item.style.cssText = `padding:10px 16px;cursor:pointer;font-size:13px;display:flex;align-items:center;gap:10px;color:#333;${weight}`;
      item.innerHTML = `<span style="font-size:16px;width:22px;text-align:center;">${icon}</span><span>${label}</span>`;
      item.addEventListener('mouseenter', () => { item.style.background = '#1e40af'; item.style.color = '#fff'; item.style.borderRadius = '6px'; });
      item.addEventListener('mouseleave', () => { item.style.background = 'none'; item.style.color = '#333'; item.style.borderRadius = '0'; });
      item.addEventListener('click', (e) => { e.stopPropagation(); menu.remove(); onClick(); });
      return item;
    };

    menu.appendChild(createItem('\uD83C\uDFF7\uFE0F', 'Create Card', true, () => {
      this._showCreateCardDialog(sel, sheet);
    }));

    const sep = document.createElement('div');
    sep.style.cssText = 'height:1px;background:#e5e5e5;margin:4px 12px;';
    menu.appendChild(sep);

    menu.appendChild(createItem('\uD83D\uDCCB', 'Copy', false, () => {
      try {
        const values = sel.getValues();
        const text = values.map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(text);
      } catch (_) {}
    }));

    menu.appendChild(createItem('\u2702\uFE0F', 'Cut', false, () => {
      try {
        const values = sel.getValues();
        const text = values.map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(text);
      } catch (_) {}
    }));

    menu.appendChild(createItem('\uD83D\uDCCC', 'Name This Range', false, () => {
      this._openNamedRanges();
    }));

    // Separator before row/column ops
    const sep2 = document.createElement('div');
    sep2.style.cssText = 'height:1px;background:#e5e5e5;margin:4px 12px;';
    menu.appendChild(sep2);

    menu.appendChild(createItem('+', 'Insert Row Above', false, () => {
      try {
        const range = sel.getRange();
        sheet.getRange(range.startRow, 0, 1, 1).insertRows();
      } catch (_) {}
    }));

    menu.appendChild(createItem('+', 'Insert Column Left', false, () => {
      try {
        const range = sel.getRange();
        sheet.getRange(0, range.startColumn, 1, 1).insertColumns();
      } catch (_) {}
    }));

    menu.appendChild(createItem('\u2212', 'Delete Row', false, () => {
      try {
        const range = sel.getRange();
        sheet.getRange(range.startRow, 0, range.endRow - range.startRow + 1, 1).deleteRows();
      } catch (_) {}
    }));

    menu.appendChild(createItem('\u2212', 'Delete Column', false, () => {
      try {
        const range = sel.getRange();
        sheet.getRange(0, range.startColumn, 1, range.endColumn - range.startColumn + 1).deleteColumns();
      } catch (_) {}
    }));

    document.body.appendChild(menu);

    // Clamp to viewport so menu doesn't overflow off-screen
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      if (rect.right > window.innerWidth) menu.style.left = (window.innerWidth - rect.width - 8) + 'px';
      if (rect.bottom > window.innerHeight) menu.style.top = (window.innerHeight - rect.height - 8) + 'px';
    });

    // Close on any click outside
    const closeHandler = (ev) => {
      if (!menu.contains(ev.target)) {
        menu.remove();
        document.removeEventListener('mousedown', closeHandler, true);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', closeHandler, true), 80);
  }

  _onCreateCardBtn() {
    if (!this.univerAPI) {
      this._showDialog({ title: 'Not Ready', message: 'Grid is not ready yet.', buttons: [{ label: 'OK', value: 'ok', primary: true }] });
      return;
    }
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const sel = sheet.getActiveRange();
    if (!sel) {
      this._showDialog({ title: 'Select Data First', message: 'Select the cells you want to turn into a card, then click Create Card.', buttons: [{ label: 'OK', value: 'ok', primary: true }] });
      return;
    }
    this._showCardWizard(sel, sheet);
  }

  // ── Card Wizard v2 — Data group IS the card, AI shows options at USE time ──
  _showCardWizard(sel, sheet) {
    const old = document.getElementById('grid-createcard-dialog');
    if (old) old.remove();

    const rangeInfo = sel.getRange();
    const startRow = rangeInfo.startRow;
    const startCol = rangeInfo.startColumn;
    const endRow = rangeInfo.endRow;
    const endCol = rangeInfo.endColumn;
    const numCols = endCol - startCol + 1;
    const numRows = endRow - startRow + 1;

    const colToLetter = (idx) => { let r = '', n = idx + 1; while (n > 0) { n--; r = String.fromCharCode(65 + (n % 26)) + r; n = Math.floor(n / 26); } return r; };
    const selLabel = `${colToLetter(startCol)}${startRow + 1}:${colToLetter(endCol)}${endRow + 1}`;

    // Read values
    let values = [];
    try { values = sel.getValues(); } catch (_) {}

    // Detect column headers (first row if text, rest numeric or mixed)
    const headers = [];
    const colTypes = []; // 'text', 'number', 'mixed', 'date'
    for (let c = 0; c < numCols; c++) {
      const headerVal = values[0]?.[c];
      headers.push(headerVal != null ? String(headerVal) : colToLetter(startCol + c));

      // Analyze column type from rows 1+ (skip header)
      let nums = 0, texts = 0, dates = 0;
      for (let r = 1; r < values.length; r++) {
        const v = values[r]?.[c];
        if (v == null || v === '') continue;
        if (!isNaN(Number(v))) nums++;
        else if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(String(v))) dates++;
        else texts++;
      }
      if (dates > nums && dates > texts) colTypes.push('date');
      else if (nums > texts) colTypes.push('number');
      else if (texts > nums) colTypes.push('text');
      else colTypes.push('mixed');
    }

    // Auto-detect what this data can do
    const suggestions = [];
    const hasTextCol = colTypes.includes('text');
    const hasNumCol = colTypes.includes('number');
    const hasDateCol = colTypes.includes('date');
    const textCols = colTypes.map((t, i) => t === 'text' ? i : -1).filter(i => i >= 0);
    const numCols_ = colTypes.map((t, i) => t === 'number' ? i : -1).filter(i => i >= 0);

    if (hasTextCol && (hasNumCol || numCols >= 2)) {
      suggestions.push({ icon: '🔍', type: 'lookup', label: 'Look up a value', desc: `Use ${headers[textCols[0]] || 'column A'} to find its ${headers[numCols_[0] !== undefined ? numCols_[0] : 1] || 'value'}` });
    }
    if (hasTextCol) {
      suggestions.push({ icon: '📋', type: 'dropdown', label: 'Create a dropdown list', desc: `Use ${headers[textCols[0]] || 'names'} as dropdown choices in other cells` });
    }
    if (hasNumCol) {
      suggestions.push({ icon: '➕', type: 'aggregate', label: 'Sum, Average, or Count', desc: `Calculate totals from ${headers[numCols_[0]] || 'numbers'}` });
    }
    if (numRows >= 2 && numCols >= 1) {
      suggestions.push({ icon: '📊', type: 'table', label: 'Show as a live table', desc: `Insert the full data as a live reference in Docs` });
    }
    if (hasNumCol && numRows >= 3) {
      suggestions.push({ icon: '📈', type: 'chart', label: 'Create a chart', desc: `Visualize ${headers[numCols_[0]] || 'the numbers'} as a chart` });
    }
    if (hasTextCol && numRows >= 2) {
      suggestions.push({ icon: '📝', type: 'list', label: 'Show as a list', desc: `Insert ${headers[textCols[0]] || 'values'} as a bullet list in Docs` });
    }
    // Always offer named range
    suggestions.push({ icon: '📌', type: 'range', label: 'Named range only', desc: `Reference this data as a range in formulas` });

    // Preview table
    let previewHtml = '<table style="width:100%;border-collapse:collapse;font-size:12px;">';
    // Header row
    previewHtml += '<tr>';
    headers.forEach((h, i) => {
      const typeIcon = colTypes[i] === 'number' ? '123' : colTypes[i] === 'date' ? '📅' : 'ABC';
      previewHtml += `<th style="padding:4px 8px;border-bottom:2px solid #2563eb;text-align:left;font-size:11px;color:#1e40af;"><span style="font-size:9px;color:#999;margin-right:4px;">${typeIcon}</span>${h}</th>`;
    });
    previewHtml += '</tr>';
    // Data rows (max 5)
    values.slice(1, 6).forEach(row => {
      previewHtml += '<tr>';
      row.forEach(cell => {
        previewHtml += `<td style="padding:3px 8px;border-bottom:1px solid #f0f0f0;font-size:12px;">${cell != null ? cell : ''}</td>`;
      });
      previewHtml += '</tr>';
    });
    if (values.length > 6) previewHtml += `<tr><td colspan="${numCols}" style="padding:3px 8px;color:#999;font-size:11px;font-style:italic;">...${values.length - 6} more rows</td></tr>`;
    previewHtml += '</table>';

    // Suggestion cards
    let suggestionsHtml = suggestions.map((s, i) => `
      <div class="card-suggestion" data-type="${s.type}" data-index="${i}" style="display:flex;align-items:center;gap:12px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:8px;cursor:pointer;transition:all 0.15s;">
        <span style="font-size:22px;flex-shrink:0;">${s.icon}</span>
        <div>
          <div style="font-weight:600;font-size:13px;color:#1a1a1a;">${s.label}</div>
          <div style="font-size:11px;color:#888;margin-top:1px;">${s.desc}</div>
        </div>
      </div>
    `).join('');

    // Build dialog
    const dlg = document.createElement('div');
    dlg.id = 'grid-createcard-dialog';
    dlg.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:999999;display:flex;align-items:center;justify-content:center;';

    dlg.innerHTML = `
      <div style="background:#fff;border-radius:14px;box-shadow:0 12px 48px rgba(0,0,0,0.25);width:520px;max-height:85vh;overflow-y:auto;font-family:Arial,sans-serif;">
        <div style="padding:24px 28px 0;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
            <h2 style="margin:0;font-size:18px;color:#1a1a1a;">Create Card</h2>
            <button id="cw-close" style="background:none;border:none;font-size:20px;cursor:pointer;color:#999;">&times;</button>
          </div>
          <div style="font-size:12px;color:#888;margin-bottom:16px;">Selected: <strong>${selLabel}</strong> &middot; ${numRows} rows &middot; ${numCols} columns</div>

          <!-- Data Preview -->
          <div style="max-height:140px;overflow-y:auto;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;">
            ${previewHtml}
          </div>

          <!-- Card Name -->
          <div style="margin-bottom:16px;">
            <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:4px;">Name this card</label>
            <input type="text" id="cw-name" placeholder="e.g. FamilyAges, Appointments, Inventory" style="width:100%;padding:10px 14px;border:1px solid #ddd;border-radius:8px;font-size:14px;box-sizing:border-box;">
          </div>

          <!-- AI Suggestions -->
          <div style="margin-bottom:8px;">
            <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:8px;">What would you like to do with this data?</label>
            <div id="cw-suggestions" style="display:flex;flex-direction:column;gap:6px;">
              ${suggestionsHtml}
            </div>
          </div>

          <!-- Usage Preview -->
          <div id="cw-usage" style="display:none;margin-top:16px;padding:12px 14px;background:#f0f4ff;border-radius:8px;border:1px solid #c7d2fe;">
            <div style="font-size:12px;font-weight:600;color:#1e40af;margin-bottom:6px;">How to use this card:</div>
            <div id="cw-usage-text" style="font-size:12px;color:#555;line-height:1.6;"></div>
          </div>
        </div>

        <div style="padding:16px 28px 24px;display:flex;gap:8px;justify-content:flex-end;">
          <button id="cw-cancel" style="padding:9px 18px;border:1px solid #d1d5db;border-radius:8px;cursor:pointer;font-size:13px;background:#fff;font-weight:600;">Cancel</button>
          <button id="cw-create" style="padding:9px 22px;border:none;border-radius:8px;cursor:pointer;font-size:13px;background:#1e40af;color:#fff;font-weight:700;">Create Card</button>
        </div>
      </div>
    `;

    document.body.appendChild(dlg);
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.remove(); });
    document.getElementById('cw-close').onclick = () => dlg.remove();
    document.getElementById('cw-cancel').onclick = () => dlg.remove();

    // Suggestion hover + selection
    let selectedType = null;
    const usageDiv = document.getElementById('cw-usage');
    const usageText = document.getElementById('cw-usage-text');
    const nameInput = document.getElementById('cw-name');

    dlg.querySelectorAll('.card-suggestion').forEach(card => {
      card.addEventListener('mouseenter', () => { card.style.borderColor = '#2563eb'; card.style.background = '#f8faff'; });
      card.addEventListener('mouseleave', () => {
        if (card.dataset.type !== selectedType) { card.style.borderColor = '#e5e7eb'; card.style.background = '#fff'; }
      });
      card.addEventListener('click', () => {
        // Deselect others
        dlg.querySelectorAll('.card-suggestion').forEach(c => { c.style.borderColor = '#e5e7eb'; c.style.background = '#fff'; });
        card.style.borderColor = '#2563eb'; card.style.background = '#eff6ff';
        selectedType = card.dataset.type;

        // Show usage preview
        const cardName = nameInput.value.trim() || 'CardName';
        usageDiv.style.display = 'block';
        if (selectedType === 'lookup') {
          usageText.innerHTML = `<strong>In Docs:</strong> Type <code>@${cardName}</code> → pick "Look up" → enter a ${headers[textCols[0]] || 'name'} → get the ${headers[numCols_[0] !== undefined ? numCols_[0] : 1] || 'result'}<br><strong>In Grid:</strong> <code>@Card(${cardName},feed)</code> and <code>@Card(${cardName},result)</code>`;
        } else if (selectedType === 'dropdown') {
          usageText.innerHTML = `<strong>In Grid:</strong> Select a cell → Data Validation → Source: <code>${cardName}</code><br><strong>In Docs:</strong> Type <code>@${cardName}</code> → pick "Dropdown" → inserts an inline selector`;
        } else if (selectedType === 'aggregate') {
          usageText.innerHTML = `<strong>In Docs:</strong> Type <code>@${cardName}</code> → pick Sum, Average, Count, Min, or Max<br><strong>In Grid:</strong> <code>@Card(${cardName},sum)</code> or <code>@Card(${cardName},avg)</code>`;
        } else if (selectedType === 'table') {
          usageText.innerHTML = `<strong>In Docs:</strong> Type <code>@${cardName}</code> → pick "Table" → inserts live-updating table<br><strong>In Grid:</strong> Reference as a named range in formulas`;
        } else if (selectedType === 'chart') {
          usageText.innerHTML = `<strong>In Docs:</strong> Type <code>@${cardName}</code> → pick "Chart" → inserts a live chart<br><strong>In Grid:</strong> Use the Charts menu with this range`;
        } else if (selectedType === 'list') {
          usageText.innerHTML = `<strong>In Docs:</strong> Type <code>@${cardName}</code> → pick "List" → inserts as bullet points<br><strong>In Grid:</strong> Reference as a named range`;
        } else if (selectedType === 'range') {
          usageText.innerHTML = `<strong>In Grid:</strong> Use <code>${cardName}</code> as a range name in any formula<br><strong>In Docs:</strong> Type <code>@${cardName}</code> to reference the data`;
        }
      });
    });

    // Update usage text when name changes
    nameInput.addEventListener('input', () => {
      if (selectedType) {
        dlg.querySelector(`.card-suggestion[data-type="${selectedType}"]`).click();
      }
    });

    setTimeout(() => nameInput.focus(), 100);

    // Create card
    document.getElementById('cw-create').onclick = () => {
      const name = nameInput.value.trim();
      if (!name) {
        this._showDialog({ title: 'Name Required', message: 'Give your card a name so you can reference it later.', buttons: [{ label: 'OK', value: 'ok', primary: true }] });
        return;
      }
      if (this.cardEngine.getCardByName(name)) {
        this._showDialog({ title: 'Name Taken', message: `A card named "${name}" already exists. Choose a different name.`, buttons: [{ label: 'OK', value: 'ok', primary: true }] });
        return;
      }
      if (!selectedType) selectedType = 'table'; // default

      // Snapshot the data
      const cardData = {
        range: selLabel,
        headers: headers,
        colTypes: colTypes,
        selectedType: selectedType,
        values: values,
        gridLinked: true,
      };

      // Create card in engine
      const card = this.cardEngine.createCard(selectedType === 'lookup' ? 'lookup' : 'table', {
        name: name,
        tags: ['grid', selectedType],
        data: cardData,
        preview: `${headers.join(', ')} (${numRows} rows)`,
      });
      this.cardEngine.saveToLibrary(card);

      // Also save as named range for formulas
      this._namedRanges = this._namedRanges || {};
      this._namedRanges[name] = selLabel;

      dlg.remove();
      this._showDialog({
        title: 'Card Created',
        message: `"${name}" is ready. Type @${name} in Docs or use it in Grid formulas.`,
        buttons: [{ label: 'Done', value: 'ok', primary: true }]
      });
    };

    // Enter to submit
    nameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') document.getElementById('cw-create').click(); });
  }

  // Legacy create card dialog (kept for backward compat)
  _showCreateCardDialog(sel, sheet) {
    const old = document.getElementById('grid-createcard-dialog');
    if (old) old.remove();

    // Get selection info
    const rangeInfo = sel.getRange();
    const startRow = rangeInfo.startRow;
    const startCol = rangeInfo.startColumn;
    const endRow = rangeInfo.endRow;
    const endCol = rangeInfo.endColumn;

    const colToLetter = (idx) => {
      let result = '';
      let n = idx + 1;
      while (n > 0) { n--; result = String.fromCharCode(65 + (n % 26)) + result; n = Math.floor(n / 26); }
      return result;
    };

    const selLabel = `${colToLetter(startCol)}${startRow + 1}:${colToLetter(endCol)}${endRow + 1}`;

    // Read current values for preview
    let values = [];
    try { values = sel.getValues(); } catch (_) {}

    // Detect if this looks like a lookup (2+ columns, input + formula/output)
    const numCols = endCol - startCol + 1;
    const numRows = endRow - startRow + 1;

    // Try to find the data table that feeds this selection
    // Look for the VLOOKUP formula in the output cell to extract the table range
    let detectedTableRange = '';
    let detectedInputCol = startCol;
    let detectedOutputCol = endCol;

    if (numCols >= 2) {
      // Check if any cell in the selection has a VLOOKUP formula
      // Try to get formula from the output column cells
      try {
        for (let r = startRow; r <= endRow; r++) {
          const cell = sheet.getRange(r, endCol, 1, 1);
          const formula = cell.getFormula ? cell.getFormula() : null;
          if (formula && /VLOOKUP/i.test(String(formula))) {
            // Extract table range from VLOOKUP(lookup, TABLE, col, match)
            const fmatch = String(formula).match(/VLOOKUP\s*\([^,]+,\s*([A-Z]+\d+:[A-Z]+\d+)/i);
            if (fmatch) {
              detectedTableRange = fmatch[1];
            }
            break;
          }
        }
      } catch (_) {}
    }

    // If no VLOOKUP found, try to detect from data patterns
    // If selection has 2 columns with data, treat col1 as input, col2 as output
    if (!detectedTableRange && numCols >= 2 && numRows >= 1) {
      // Check if there's a larger data block nearby that could be the table
      // For now, let the user specify via the dialog
    }

    // Build the dialog
    const dlg = document.createElement('div');
    dlg.id = 'grid-createcard-dialog';
    dlg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:12px;padding:24px;z-index:10000;width:420px;box-shadow:0 8px 32px rgba(0,0,0,0.2);';

    // Preview of selected data
    let previewHtml = '';
    if (values.length > 0) {
      previewHtml = '<div style="max-height:120px;overflow-y:auto;border:1px solid #e0e0e0;border-radius:6px;margin-bottom:16px;">';
      previewHtml += '<table style="width:100%;border-collapse:collapse;font-size:12px;">';
      values.slice(0, 8).forEach(row => {
        previewHtml += '<tr>';
        row.forEach(cell => {
          previewHtml += `<td style="padding:4px 8px;border-bottom:1px solid #f0f0f0;">${cell != null ? cell : ''}</td>`;
        });
        previewHtml += '</tr>';
      });
      if (values.length > 8) previewHtml += '<tr><td colspan="99" style="padding:4px 8px;color:#999;font-style:italic;">...and more rows</td></tr>';
      previewHtml += '</table></div>';
    }

    // Build column letter options from the selection range
    const colOptions = [];
    for (let c = startCol; c <= endCol; c++) {
      const letter = colToLetter(c);
      // Try to get header value (first row) for label
      let headerLabel = letter;
      try {
        const hVal = sheet.getRange(startRow, c, 1, 1).getValue();
        if (hVal != null && String(hVal).trim()) headerLabel = `${letter} — ${hVal}`;
      } catch (_) {}
      colOptions.push({ col: c, letter, label: headerLabel });
    }

    const feedColDefault = startCol;
    const resultColDefault = endCol;
    const feedOptions = colOptions.map(o => `<option value="${o.col}" ${o.col === feedColDefault ? 'selected' : ''}>${o.label}</option>`).join('');
    const resultOptions = colOptions.map(o => `<option value="${o.col}" ${o.col === resultColDefault ? 'selected' : ''}>${o.label}</option>`).join('');

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;font-size:16px;">\uD83C\uDFF7\uFE0F Create Card</h3>
        <button id="createcard-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="font-size:12px;color:#666;margin-bottom:12px;">Selected: <strong>${selLabel}</strong></div>
      ${previewHtml}
      <div style="margin-bottom:12px;">
        <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:4px;">Card Name</label>
        <input type="text" id="cc-name" placeholder="e.g. Age" style="width:100%;padding:8px 12px;border:1px solid #ddd;border-radius:6px;font-size:14px;box-sizing:border-box;">
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:4px;">Type</label>
        <select id="cc-type" style="width:100%;padding:8px 12px;border:1px solid #ddd;border-radius:6px;font-size:13px;box-sizing:border-box;">
          <option value="lookup" ${numCols >= 2 ? 'selected' : ''}>Lookup (feed → result)</option>
          <option value="spreadsheet" ${numCols < 2 ? 'selected' : ''}>Named Range</option>
        </select>
      </div>
      <div id="cc-lookup-fields" style="${numCols >= 2 ? '' : 'display:none;'}">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;">
          <div>
            <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:4px;">Feed Column <span style="color:#999;font-weight:400;">(what you pass in)</span></label>
            <select id="cc-feed-col" style="width:100%;padding:8px 12px;border:1px solid #ddd;border-radius:6px;font-size:13px;box-sizing:border-box;">
              ${feedOptions}
            </select>
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:4px;">Result Column <span style="color:#999;font-weight:400;">(what comes back)</span></label>
            <select id="cc-result-col" style="width:100%;padding:8px 12px;border:1px solid #ddd;border-radius:6px;font-size:13px;box-sizing:border-box;">
              ${resultOptions}
            </select>
          </div>
        </div>
        <div style="margin-bottom:12px;">
          <label style="font-size:12px;font-weight:600;color:#555;display:block;margin-bottom:4px;">Data Table Range <span style="color:#999;font-weight:400;">(auto-detected from selection)</span></label>
          <input type="text" id="cc-table-range" value="${detectedTableRange || selLabel}" placeholder="e.g. G3:H5" style="width:100%;padding:8px 12px;border:1px solid #ddd;border-radius:6px;font-size:13px;font-family:monospace;box-sizing:border-box;">
        </div>
        <div id="cc-preview-formula" style="font-size:11px;color:#1e40af;background:#f0f4ff;padding:8px 12px;border-radius:6px;margin-bottom:12px;font-family:monospace;">
          In Docs: @<span id="cc-preview-name">CardName</span>(value) → result from ${colToLetter(resultColDefault)} column
        </div>
      </div>
      <div id="cc-status" style="font-size:12px;color:#666;margin-bottom:12px;"></div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button id="cc-cancel" style="padding:8px 16px;border:1px solid #ddd;border-radius:6px;cursor:pointer;font-size:13px;background:#f8f8f8;">Cancel</button>
        <button id="cc-create" style="padding:8px 20px;border:none;border-radius:6px;cursor:pointer;font-size:13px;background:#1e40af;color:#fff;font-weight:600;">Create Card</button>
      </div>
    `;

    document.body.appendChild(dlg);

    document.getElementById('createcard-close').onclick = () => dlg.remove();
    document.getElementById('cc-cancel').onclick = () => dlg.remove();

    // Toggle lookup fields visibility based on type
    const typeSelect = document.getElementById('cc-type');
    const lookupFields = document.getElementById('cc-lookup-fields');
    typeSelect.addEventListener('change', () => {
      lookupFields.style.display = typeSelect.value === 'lookup' ? '' : 'none';
    });

    // Update preview formula when name changes
    const nameInput = document.getElementById('cc-name');
    const previewName = document.getElementById('cc-preview-name');
    if (nameInput && previewName) {
      nameInput.addEventListener('input', () => {
        previewName.textContent = nameInput.value || 'CardName';
      });
    }

    // Update preview when result column changes
    const resultColSel = document.getElementById('cc-result-col');
    const previewFormula = document.getElementById('cc-preview-formula');
    if (resultColSel && previewFormula) {
      resultColSel.addEventListener('change', () => {
        const resCol = parseInt(resultColSel.value);
        previewFormula.innerHTML = `In Docs: @<span id="cc-preview-name">${nameInput.value || 'CardName'}</span>(value) \u2192 result from ${colToLetter(resCol)} column`;
      });
    }

    // Auto-focus the name field
    setTimeout(() => nameInput.focus(), 100);

    document.getElementById('cc-create').onclick = () => {
      const name = nameInput.value.trim();
      const type = typeSelect.value;

      if (!name) {
        document.getElementById('cc-status').innerHTML = '<span style="color:#dc2626;">Name is required.</span>';
        return;
      }

      // Check name collision
      if (this.cardEngine.getCardByName(name)) {
        document.getElementById('cc-status').innerHTML = '<span style="color:#dc2626;">A card with that name already exists.</span>';
        return;
      }

      if (type === 'lookup') {
        const tableRange = document.getElementById('cc-table-range').value.trim().toUpperCase();
        const feedCol = parseInt(document.getElementById('cc-feed-col').value);
        const resultCol = parseInt(document.getElementById('cc-result-col').value);

        if (!tableRange || !/^[A-Z]+\d+:[A-Z]+\d+$/.test(tableRange)) {
          document.getElementById('cc-status').innerHTML = '<span style="color:#dc2626;">Table range must be like G3:H5.</span>';
          return;
        }

        if (feedCol === resultCol) {
          document.getElementById('cc-status').innerHTML = '<span style="color:#dc2626;">Feed and Result columns must be different.</span>';
          return;
        }

        // Snapshot lookup values using the explicit feed/result columns
        const cachedValues = [];
        const rngParts = tableRange.split(':');
        const startRef = this.cardEngine._parseCell(rngParts[0]);
        const endRef = this.cardEngine._parseCell(rngParts[1]);
        try {
          const wb = this.univerAPI.getActiveWorkbook();
          const sht = wb ? wb.getActiveSheet() : null;
          if (sht && startRef && endRef) {
            for (let r = startRef.row; r <= endRef.row; r++) {
              const inVal = sht.getRange(r, feedCol, 1, 1).getValue();
              const outVal = sht.getRange(r, resultCol, 1, 1).getValue();
              if (inVal != null && String(inVal).trim() !== '') {
                cachedValues.push({ input: String(inVal), output: outVal != null ? String(outVal) : '' });
              }
            }
          }
        } catch (e) { /* grid read failed, empty cache */ }

        // Create lookup card with explicit feed/result columns
        const card = this.cardEngine.createCard('lookup', {
          name: name,
          tags: ['grid', 'lookup'],
          data: {
            tableRange: tableRange,
            selectionRange: selLabel,
            feedCol: feedCol,
            resultCol: resultCol,
            gridLinked: true,
            cachedValues: cachedValues
          },
          preview: `Lookup: ${colToLetter(feedCol)} \u2192 ${colToLetter(resultCol)} (${tableRange})`,
        });

        // Also save to library so it persists
        this.cardEngine.saveToLibrary(card);

        document.getElementById('cc-status').innerHTML = `<span style="color:#16a34a;">\u2705 Card "${name}" created \u2014 type @${name}(value) in Docs</span>`;
        setTimeout(() => dlg.remove(), 1500);
      } else {
        // Named range card
        const rangeStr = tableRange || selLabel;
        this._namedRanges = this._namedRanges || {};
        this._namedRanges[name] = rangeStr;
        this._registerNamedRangeCard(name, rangeStr);

        document.getElementById('cc-status').innerHTML = `<span style="color:#16a34a;">\u2705 Named range "${name}" = ${rangeStr} — use @${name} in Docs</span>`;
        setTimeout(() => dlg.remove(), 1500);
      }
    };

    // Enter key to submit
    document.getElementById('cc-name').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') document.getElementById('cc-create').click();
    });
  }

  // ── Selection Stats Footer ──
  _initSelectionStats() {
    if (!this.univerAPI) return;
    // Poll selection every 500ms (Univer API may not fire events on selection change)
    setInterval(() => this._updateSelectionStats(), 500);
  }

  _updateSelectionStats() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;

    const range = sheet.getActiveRange();
    if (!range) return;

    let values;
    try { values = range.getValues(); } catch (_) { return; }
    if (!values || values.length === 0) return;

    // Flatten and extract numbers
    const allVals = values.flat();
    const nums = allVals.map(v => Number(v)).filter(n => !isNaN(n) && v !== '' && v != null);

    const cellsEl = document.getElementById('grid-stat-cells');
    const sumEl = document.getElementById('grid-stat-sum');
    const avgEl = document.getElementById('grid-stat-avg');
    const countEl = document.getElementById('grid-stat-count');
    const minEl = document.getElementById('grid-stat-min');
    const maxEl = document.getElementById('grid-stat-max');

    if (!sumEl) return;

    const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 4 });

    if (nums.length > 0) {
      const sum = nums.reduce((a, b) => a + b, 0);
      const avg = sum / nums.length;
      if (cellsEl) cellsEl.textContent = `${allVals.length} cells selected`;
      sumEl.textContent = `Sum: ${fmt(sum)}`;
      avgEl.textContent = `Avg: ${fmt(avg)}`;
      countEl.textContent = `Count: ${nums.length}`;
      minEl.textContent = `Min: ${fmt(Math.min(...nums))}`;
      maxEl.textContent = `Max: ${fmt(Math.max(...nums))}`;
      [sumEl, avgEl, countEl, minEl, maxEl].forEach(el => el.style.display = '');
    } else if (allVals.length > 1) {
      if (cellsEl) cellsEl.textContent = `${allVals.length} cells selected`;
      countEl.textContent = `Count: ${allVals.filter(v => v != null && String(v).trim() !== '').length}`;
      countEl.style.display = '';
      [sumEl, avgEl, minEl, maxEl].forEach(el => el.style.display = 'none');
    } else {
      if (cellsEl) cellsEl.textContent = 'Ready';
      [sumEl, avgEl, countEl, minEl, maxEl].forEach(el => el.style.display = 'none');
    }
  }

  // ── What-If Data Tables (Sensitivity Analysis) ──
  _openWhatIfTable() {
    if (!this.univerAPI) return;
    document.getElementById('whatif-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'whatif-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:600px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>What-If Data Table</span>
          <button id="wi-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;margin-bottom:12px;">
            <button id="wi-1var" class="transform-op" data-active="true" style="padding:6px 14px;border:2px solid #2563eb;border-radius:4px;background:#eff6ff;color:#2563eb;cursor:pointer;font-size:12px;font-weight:600;">One-Variable</button>
            <button id="wi-2var" class="transform-op" style="padding:6px 14px;border:1px solid #7c3aed;border-radius:4px;background:#f5f3ff;color:#7c3aed;cursor:pointer;font-size:12px;font-weight:600;">Two-Variable</button>
          </div>
          <div id="wi-config"></div>
          <div id="wi-result" style="margin-top:12px;max-height:350px;overflow:auto;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('wi-close').onclick = () => dialog.remove();

    document.getElementById('wi-1var').onclick = () => this._showWhatIfConfig(1);
    document.getElementById('wi-2var').onclick = () => this._showWhatIfConfig(2);

    this._showWhatIfConfig(1);
  }

  _showWhatIfConfig(vars) {
    const cfg = document.getElementById('wi-config');
    if (!cfg) return;

    if (vars === 1) {
      cfg.innerHTML = `
        <div style="font-size:12px;color:#666;margin-bottom:8px;">Change one input across a range and see how the output changes.</div>
        <div style="margin:6px 0;">
          <label style="font-size:12px;font-weight:600;display:inline-block;width:120px;">Formula:</label>
          <input id="wi-formula" type="text" placeholder="e.g. x * 1.08 - 500" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:280px;">
          <div style="font-size:10px;color:#999;margin-left:120px;">Use <code>x</code> for the variable</div>
        </div>
        <div style="margin:6px 0;">
          <label style="font-size:12px;font-weight:600;display:inline-block;width:120px;">Start value:</label>
          <input id="wi-start" type="number" value="0" step="any" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:100px;">
        </div>
        <div style="margin:6px 0;">
          <label style="font-size:12px;font-weight:600;display:inline-block;width:120px;">End value:</label>
          <input id="wi-end" type="number" value="100" step="any" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:100px;">
        </div>
        <div style="margin:6px 0;">
          <label style="font-size:12px;font-weight:600;display:inline-block;width:120px;">Step:</label>
          <input id="wi-step" type="number" value="10" step="any" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:100px;">
        </div>
        <button id="wi-run" style="margin-top:8px;padding:6px 20px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Generate Table</button>
      `;
      document.getElementById('wi-run').onclick = () => this._runWhatIf1Var();
    } else {
      cfg.innerHTML = `
        <div style="font-size:12px;color:#666;margin-bottom:8px;">Change two inputs and see how the output changes in a matrix.</div>
        <div style="margin:6px 0;">
          <label style="font-size:12px;font-weight:600;display:inline-block;width:120px;">Formula:</label>
          <input id="wi-formula" type="text" placeholder="e.g. x * y - 500" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:280px;">
          <div style="font-size:10px;color:#999;margin-left:120px;">Use <code>x</code> for row variable, <code>y</code> for column variable</div>
        </div>
        <div style="display:flex;gap:16px;margin-top:8px;">
          <div style="flex:1;padding:8px;border:1px solid #dbeafe;border-radius:4px;background:#eff6ff;">
            <div style="font-size:11px;font-weight:600;color:#2563eb;margin-bottom:4px;">Row variable (X)</div>
            <label style="font-size:11px;">Start:</label> <input id="wi-x-start" type="number" value="0" step="any" style="width:70px;padding:3px;border:1px solid #ccc;border-radius:3px;">
            <label style="font-size:11px;margin-left:4px;">End:</label> <input id="wi-x-end" type="number" value="100" step="any" style="width:70px;padding:3px;border:1px solid #ccc;border-radius:3px;">
            <label style="font-size:11px;margin-left:4px;">Step:</label> <input id="wi-x-step" type="number" value="20" step="any" style="width:70px;padding:3px;border:1px solid #ccc;border-radius:3px;">
          </div>
          <div style="flex:1;padding:8px;border:1px solid #f3e8ff;border-radius:4px;background:#f5f3ff;">
            <div style="font-size:11px;font-weight:600;color:#7c3aed;margin-bottom:4px;">Column variable (Y)</div>
            <label style="font-size:11px;">Start:</label> <input id="wi-y-start" type="number" value="0" step="any" style="width:70px;padding:3px;border:1px solid #ccc;border-radius:3px;">
            <label style="font-size:11px;margin-left:4px;">End:</label> <input id="wi-y-end" type="number" value="50" step="any" style="width:70px;padding:3px;border:1px solid #ccc;border-radius:3px;">
            <label style="font-size:11px;margin-left:4px;">Step:</label> <input id="wi-y-step" type="number" value="10" step="any" style="width:70px;padding:3px;border:1px solid #ccc;border-radius:3px;">
          </div>
        </div>
        <button id="wi-run" style="margin-top:8px;padding:6px 20px;background:#7c3aed;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Generate Table</button>
      `;
      document.getElementById('wi-run').onclick = () => this._runWhatIf2Var();
    }
  }

  _runWhatIf1Var() {
    const formula = document.getElementById('wi-formula').value.trim();
    const start = parseFloat(document.getElementById('wi-start').value);
    const end = parseFloat(document.getElementById('wi-end').value);
    const step = parseFloat(document.getElementById('wi-step').value) || 1;
    if (!formula) return;

    const results = [];
    for (let x = start; x <= end + step * 0.001; x += step) {
      try {
        const val = new Function('x', 'return ' + formula)(x);
        results.push({ x: Math.round(x * 1000000) / 1000000, result: val });
      } catch (e) { results.push({ x, result: 'ERR' }); }
    }

    const fmt = v => typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 4 }) : v;
    let html = '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
    html += '<th style="border:1px solid #ddd;padding:4px 8px;background:#eff6ff;">X (Input)</th>';
    html += '<th style="border:1px solid #ddd;padding:4px 8px;background:#f5f5f5;">Result</th>';
    html += '</tr></thead><tbody>';
    results.forEach(r => {
      const color = typeof r.result === 'number' ? (r.result >= 0 ? '#059669' : '#dc2626') : '#666';
      html += `<tr><td style="border:1px solid #ddd;padding:3px 8px;text-align:right;font-weight:600;">${fmt(r.x)}</td>`;
      html += `<td style="border:1px solid #ddd;padding:3px 8px;text-align:right;color:${color};">${fmt(r.result)}</td></tr>`;
    });
    html += '</tbody></table>';
    html += `<button id="wi-tosheet" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">Send to Sheet</button>`;

    document.getElementById('wi-result').innerHTML = html;

    document.getElementById('wi-tosheet').onclick = () => {
      const cd = {};
      cd[0] = { 0: { v: 'Input (X)', s: { bl: 1 } }, 1: { v: 'Result', s: { bl: 1 } } };
      results.forEach((r, i) => {
        cd[i + 1] = { 0: { v: r.x }, 1: { v: typeof r.result === 'number' ? Math.round(r.result * 10000) / 10000 : r.result } };
      });
      const sid = 'whatif_' + Date.now();
      this.univerAPI.createWorkbook({
        name: 'What-If Table',
        sheetOrder: [sid],
        sheets: { [sid]: { id: sid, name: 'What-If', rowCount: results.length + 20, columnCount: 10, cellData: cd } }
      });
    };
  }

  _runWhatIf2Var() {
    const formula = document.getElementById('wi-formula').value.trim();
    if (!formula) return;

    const xStart = parseFloat(document.getElementById('wi-x-start').value);
    const xEnd = parseFloat(document.getElementById('wi-x-end').value);
    const xStep = parseFloat(document.getElementById('wi-x-step').value) || 1;
    const yStart = parseFloat(document.getElementById('wi-y-start').value);
    const yEnd = parseFloat(document.getElementById('wi-y-end').value);
    const yStep = parseFloat(document.getElementById('wi-y-step').value) || 1;

    const xVals = [];
    for (let x = xStart; x <= xEnd + xStep * 0.001; x += xStep) xVals.push(Math.round(x * 1000000) / 1000000);
    const yVals = [];
    for (let y = yStart; y <= yEnd + yStep * 0.001; y += yStep) yVals.push(Math.round(y * 1000000) / 1000000);

    const matrix = [];
    xVals.forEach(x => {
      const row = [];
      yVals.forEach(y => {
        try { row.push(new Function('x', 'y', 'return ' + formula)(x, y)); }
        catch (_) { row.push('ERR'); }
      });
      matrix.push(row);
    });

    const fmt = v => typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v;

    // Find min/max for heatmap coloring
    const allNums = matrix.flat().filter(v => typeof v === 'number');
    const minVal = Math.min(...allNums);
    const maxVal = Math.max(...allNums);
    const valRange = maxVal - minVal || 1;

    const heatColor = (v) => {
      if (typeof v !== 'number') return '#fff';
      const ratio = (v - minVal) / valRange;
      const r = Math.round(255 - ratio * 200);
      const g = Math.round(240 - ratio * 40);
      const b = Math.round(255 - ratio * 180);
      return `rgb(${r},${g},${b})`;
    };

    let html = '<table style="border-collapse:collapse;font-size:10px;"><thead><tr>';
    html += '<th style="border:1px solid #ddd;padding:3px 6px;background:#f5f5f5;">X \\ Y</th>';
    yVals.forEach(y => { html += `<th style="border:1px solid #ddd;padding:3px 6px;background:#f5f3ff;white-space:nowrap;">${fmt(y)}</th>`; });
    html += '</tr></thead><tbody>';
    xVals.forEach((x, ri) => {
      html += `<tr><th style="border:1px solid #ddd;padding:3px 6px;background:#eff6ff;text-align:right;white-space:nowrap;">${fmt(x)}</th>`;
      matrix[ri].forEach(v => {
        html += `<td style="border:1px solid #ddd;padding:3px 6px;text-align:right;background:${heatColor(v)};white-space:nowrap;">${fmt(v)}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    html += `<button id="wi-tosheet" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">Send to Sheet</button>`;

    document.getElementById('wi-result').innerHTML = html;

    document.getElementById('wi-tosheet').onclick = () => {
      const cd = {};
      cd[0] = { 0: { v: 'X \\ Y', s: { bl: 1 } } };
      yVals.forEach((y, ci) => { cd[0][ci + 1] = { v: y, s: { bl: 1 } }; });
      xVals.forEach((x, ri) => {
        cd[ri + 1] = { 0: { v: x, s: { bl: 1 } } };
        matrix[ri].forEach((v, ci) => {
          cd[ri + 1][ci + 1] = { v: typeof v === 'number' ? Math.round(v * 10000) / 10000 : v };
        });
      });
      const sid = 'whatif2_' + Date.now();
      this.univerAPI.createWorkbook({
        name: 'What-If 2-Var Table',
        sheetOrder: [sid],
        sheets: { [sid]: { id: sid, name: 'Sensitivity', rowCount: xVals.length + 20, columnCount: yVals.length + 10, cellData: cd } }
      });
    };
  }

  // ── Quick Analysis ──
  _openQuickAnalysis() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;

    let values;
    const sel = sheet.getActiveRange();
    if (sel) values = sel.getValues();
    if (!values || values.length < 2) values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Select data or add data to the sheet'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);
    const totalRows = dataRows.length;

    document.getElementById('quick-dialog')?.remove();

    // Analyze the data
    const numCols = [];
    const textCols = [];
    headers.forEach((h, i) => {
      const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
      if (nums.length > totalRows * 0.5) numCols.push({ name: h, idx: i, data: nums });
      else textCols.push({ name: h, idx: i });
    });

    const dialog = document.createElement('div');
    dialog.id = 'quick-dialog';

    let html = `
      <div class="pivot-dialog-inner" style="width:550px;max-height:80vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Quick Analysis</span>
          <button id="quick-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
    `;

    // Totals section
    if (numCols.length > 0) {
      html += '<div style="margin-bottom:12px;"><div style="font-size:12px;font-weight:700;margin-bottom:6px;color:#2563eb;">Totals</div>';
      html += '<div style="display:flex;gap:6px;flex-wrap:wrap;">';
      numCols.forEach(col => {
        const sum = col.data.reduce((a, b) => a + b, 0);
        const avg = sum / col.data.length;
        html += `<div style="padding:8px 12px;background:#eff6ff;border-radius:6px;border:1px solid #dbeafe;min-width:120px;">
          <div style="font-size:10px;color:#666;">${col.name}</div>
          <div style="font-size:16px;font-weight:700;color:#2563eb;">${sum.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
          <div style="font-size:10px;color:#999;">Avg: ${avg.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
        </div>`;
      });
      html += '</div></div>';
    }

    // Chart suggestions
    html += '<div style="margin-bottom:12px;"><div style="font-size:12px;font-weight:700;margin-bottom:6px;color:#059669;">Suggested Charts</div>';
    html += '<div style="display:flex;gap:6px;flex-wrap:wrap;">';

    const suggestions = [];
    if (numCols.length >= 1 && textCols.length >= 1) {
      suggestions.push({ type: 'bar', label: 'Bar Chart', desc: `${textCols[0].name} vs ${numCols[0].name}` });
      suggestions.push({ type: 'pie', label: 'Pie Chart', desc: `${numCols[0].name} by ${textCols[0].name}` });
    }
    if (numCols.length >= 2) {
      suggestions.push({ type: 'scatter', label: 'Scatter Plot', desc: `${numCols[0].name} vs ${numCols[1].name}` });
      suggestions.push({ type: 'heatmap', label: 'Heatmap', desc: 'Correlation matrix' });
    }
    if (numCols.length >= 1 && totalRows >= 5) {
      suggestions.push({ type: 'line', label: 'Line Chart', desc: `${numCols[0].name} trend` });
      suggestions.push({ type: 'histogram', label: 'Histogram', desc: `${numCols[0].name} distribution` });
    }
    if (numCols.length >= 1 && textCols.length >= 1) {
      suggestions.push({ type: 'box', label: 'Box Plot', desc: `${numCols[0].name} by ${textCols[0].name}` });
    }

    suggestions.forEach(s => {
      html += `<button class="qa-chart" data-type="${s.type}" style="padding:8px 12px;border:1px solid #d1fae5;border-radius:6px;background:#ecfdf5;cursor:pointer;text-align:left;min-width:120px;">
        <div style="font-size:11px;font-weight:600;color:#059669;">${s.label}</div>
        <div style="font-size:10px;color:#666;">${s.desc}</div>
      </button>`;
    });
    html += '</div></div>';

    // Insights
    html += '<div style="margin-bottom:12px;"><div style="font-size:12px;font-weight:700;margin-bottom:6px;color:#7c3aed;">Insights</div>';
    html += '<div style="font-size:11px;color:#666;">';

    // Auto-detect patterns
    numCols.forEach(col => {
      const data = col.data;
      const mean = data.reduce((a, b) => a + b, 0) / data.length;
      const stddev = Math.sqrt(data.reduce((s, v) => s + (v - mean) ** 2, 0) / data.length);
      const cv = mean !== 0 ? (stddev / Math.abs(mean) * 100) : 0;

      if (cv > 100) html += `<div style="margin:2px 0;">&#9888; <b>${col.name}</b> has very high variability (CV: ${cv.toFixed(0)}%)</div>`;

      // Check for negative values
      const negCount = data.filter(v => v < 0).length;
      if (negCount > 0) html += `<div style="margin:2px 0;">&#128308; <b>${col.name}</b> has ${negCount} negative values</div>`;

      // Check for zeros
      const zeroCount = data.filter(v => v === 0).length;
      if (zeroCount > data.length * 0.1) html += `<div style="margin:2px 0;">&#9898; <b>${col.name}</b> has ${zeroCount} zeros (${((zeroCount/data.length)*100).toFixed(0)}%)</div>`;

      // Trend
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
      for (let i = 0; i < data.length; i++) { sumX += i; sumY += data[i]; sumXY += i * data[i]; sumX2 += i * i; }
      const slope = (data.length * sumXY - sumX * sumY) / (data.length * sumX2 - sumX * sumX);
      if (Math.abs(slope) > mean * 0.01) {
        html += `<div style="margin:2px 0;">${slope > 0 ? '&#128200;' : '&#128201;'} <b>${col.name}</b> shows a ${slope > 0 ? 'positive' : 'negative'} trend</div>`;
      }
    });

    // Unique values check on text columns
    textCols.forEach(col => {
      const vals = dataRows.map(r => String(r[col.idx] ?? '')).filter(v => v);
      const unique = new Set(vals).size;
      if (unique <= 10 && unique > 1) {
        html += `<div style="margin:2px 0;">&#128218; <b>${col.name}</b> has ${unique} categories — good for grouping/pivot</div>`;
      }
    });

    html += '</div></div>';

    // Quick actions
    html += '<div><div style="font-size:12px;font-weight:700;margin-bottom:6px;color:#d97706;">Quick Actions</div>';
    html += '<div style="display:flex;gap:6px;flex-wrap:wrap;">';
    html += '<button class="qa-action" data-action="pivot" style="padding:6px 12px;border:1px solid #fde68a;border-radius:4px;background:#fffbeb;cursor:pointer;font-size:11px;">Open Pivot Table</button>';
    html += '<button class="qa-action" data-action="sql" style="padding:6px 12px;border:1px solid #fde68a;border-radius:4px;background:#fffbeb;cursor:pointer;font-size:11px;">SQL Query</button>';
    html += '<button class="qa-action" data-action="profile" style="padding:6px 12px;border:1px solid #fde68a;border-radius:4px;background:#fffbeb;cursor:pointer;font-size:11px;">Data Profile</button>';
    html += '<button class="qa-action" data-action="cluster" style="padding:6px 12px;border:1px solid #fde68a;border-radius:4px;background:#fffbeb;cursor:pointer;font-size:11px;">Find Clusters</button>';
    html += '<button class="qa-action" data-action="transform" style="padding:6px 12px;border:1px solid #fde68a;border-radius:4px;background:#fffbeb;cursor:pointer;font-size:11px;">Transform Data</button>';
    html += '</div></div>';

    html += '</div></div>';
    dialog.innerHTML = html;
    document.getElementById('grid-container').appendChild(dialog);

    document.getElementById('quick-close').onclick = () => dialog.remove();

    // Wire chart buttons
    dialog.querySelectorAll('.qa-chart').forEach(btn => {
      btn.onclick = () => {
        dialog.remove();
        // Use Plotly for advanced types, ECharts for basic
        const type = btn.dataset.type;
        if (['heatmap', 'histogram', 'box', 'scatter'].includes(type)) {
          this._openAdvancedChartDialog();
        } else {
          this._insertChart();
        }
      };
    });

    // Wire quick actions
    dialog.querySelectorAll('.qa-action').forEach(btn => {
      btn.onclick = () => {
        dialog.remove();
        switch (btn.dataset.action) {
          case 'pivot': this._openPivotDialog(); break;
          case 'sql': this._openSQLDialog(); break;
          case 'profile': this._openDataProfile(); break;
          case 'cluster': this._openClusterDialog(); break;
          case 'transform': this._openTransformDialog(); break;
        }
      };
    });
  }

  // ── Custom Number Format Builder ──
  _openNumFormatBuilder() {
    if (!this.univerAPI) return;
    document.getElementById('numfmt-dialog')?.remove();

    const presets = [
      { name: 'Currency ($)', fmt: '$#,##0.00', example: '$1,234.56' },
      { name: 'Currency (no cents)', fmt: '$#,##0', example: '$1,235' },
      { name: 'Accounting', fmt: '$ #,##0.00 ;($ #,##0.00)', example: '$ 1,234.56' },
      { name: 'Percentage', fmt: '0.00%', example: '12.34%' },
      { name: 'Percentage (integer)', fmt: '0%', example: '12%' },
      { name: 'Number (2 dec)', fmt: '#,##0.00', example: '1,234.56' },
      { name: 'Number (integer)', fmt: '#,##0', example: '1,235' },
      { name: 'Scientific', fmt: '0.00E+0', example: '1.23E+3' },
      { name: 'Date (MM/DD/YYYY)', fmt: 'MM/DD/YYYY', example: '03/15/2026' },
      { name: 'Date (YYYY-MM-DD)', fmt: 'YYYY-MM-DD', example: '2026-03-15' },
      { name: 'Date (MMM D, YYYY)', fmt: 'MMM D, YYYY', example: 'Mar 15, 2026' },
      { name: 'Time (12h)', fmt: 'h:mm AM/PM', example: '2:30 PM' },
      { name: 'Phone (US)', fmt: '(###) ###-####', example: '(555) 123-4567' },
      { name: 'SSN', fmt: '###-##-####', example: '123-45-6789' },
      { name: 'Zip Code', fmt: '#####', example: '90210' },
      { name: 'Fraction (1 digit)', fmt: '# ?/?', example: '1 1/2' },
      { name: 'Basis Points', fmt: '0.00 "bps"', example: '150.00 bps' },
      { name: 'Thousands (K)', fmt: '#,##0,"K"', example: '1,235K' },
      { name: 'Millions (M)', fmt: '#,##0.0,,"M"', example: '1.2M' },
      { name: 'Red if Negative', fmt: '#,##0.00;[Red]-#,##0.00', example: '-1,234.56 (red)' },
    ];

    const dialog = document.createElement('div');
    dialog.id = 'numfmt-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:500px;max-height:80vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Number Format</span>
          <button id="nf-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="margin-bottom:12px;">
            <label style="font-size:12px;font-weight:600;">Custom format string:</label>
            <div style="display:flex;gap:8px;margin-top:4px;">
              <input id="nf-custom" type="text" placeholder="#,##0.00" style="flex:1;padding:6px 8px;border:1px solid #ccc;border-radius:4px;font-family:monospace;">
              <button id="nf-apply-custom" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Apply</button>
            </div>
            <div id="nf-preview" style="margin-top:4px;font-size:11px;color:#666;"></div>
          </div>
          <div style="font-size:12px;font-weight:600;margin-bottom:6px;">Presets:</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
            ${presets.map(p => `
              <button class="nf-preset" data-fmt="${p.fmt}" style="padding:8px 10px;border:1px solid #e5e7eb;border-radius:6px;background:#fff;cursor:pointer;text-align:left;">
                <div style="font-size:11px;font-weight:600;">${p.name}</div>
                <div style="font-size:10px;color:#999;font-family:monospace;">${p.example}</div>
              </button>
            `).join('')}
          </div>
          <div style="margin-top:12px;padding:8px;background:#f5f5f5;border-radius:4px;font-size:10px;color:#666;">
            <b>Format codes:</b> <code>#</code> optional digit · <code>0</code> required digit · <code>,</code> thousands separator · <code>.</code> decimal · <code>%</code> multiply by 100 · <code>"text"</code> literal text · <code>;</code> separate positive;negative;zero
          </div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('nf-close').onclick = () => dialog.remove();

    // Preview custom format
    const previewInput = document.getElementById('nf-custom');
    previewInput.addEventListener('input', () => {
      const fmt = previewInput.value;
      const previewEl = document.getElementById('nf-preview');
      if (previewEl && fmt) {
        try {
          const sample = this._applyNumFormat(1234567.89, fmt);
          const neg = this._applyNumFormat(-1234.56, fmt);
          previewEl.textContent = `Preview: ${sample} | Negative: ${neg}`;
        } catch (_) {
          previewEl.textContent = 'Invalid format';
        }
      }
    });

    // Apply custom format
    document.getElementById('nf-apply-custom').onclick = () => {
      const fmt = previewInput.value.trim();
      if (!fmt) return;
      this._applyFormatToSelection(fmt);
    };

    // Preset buttons
    dialog.querySelectorAll('.nf-preset').forEach(btn => {
      btn.onmouseenter = () => { btn.style.borderColor = '#2563eb'; btn.style.background = '#eff6ff'; };
      btn.onmouseleave = () => { btn.style.borderColor = '#e5e7eb'; btn.style.background = '#fff'; };
      btn.onclick = () => {
        previewInput.value = btn.dataset.fmt;
        this._applyFormatToSelection(btn.dataset.fmt);
      };
    });
  }

  _applyNumFormat(num, format) {
    // Simple format interpreter for preview
    let fmt = format;
    const isNeg = num < 0;
    const absNum = Math.abs(num);

    // Handle positive;negative sections
    const sections = fmt.split(';');
    if (isNeg && sections.length >= 2) {
      fmt = sections[1].replace('[Red]', '');
      return '-' + this._applyNumFormat(absNum, fmt.replace('-', ''));
    }
    fmt = sections[0];

    // Percentage
    if (fmt.includes('%')) {
      return (absNum * 100).toFixed(fmt.split('.')[1]?.replace(/%.*/, '').length || 0) + '%';
    }

    // Millions
    if (fmt.includes(',,')) {
      const val = absNum / 1000000;
      const decs = fmt.includes('.') ? (fmt.split('.')[1]?.match(/0|#/g)?.length || 0) : 0;
      const lit = fmt.match(/"([^"]+)"/)?.[1] || '';
      return val.toFixed(decs) + lit;
    }

    // Thousands
    if (fmt.includes(',') && fmt.includes('"K"')) {
      const val = absNum / 1000;
      return Math.round(val).toLocaleString() + 'K';
    }

    // Currency
    const prefix = fmt.startsWith('$') ? '$' : '';
    const hasParen = fmt.includes('(');

    // Decimal places
    const decPart = fmt.split('.')[1]?.replace(/[^0#]/g, '') || '';
    const decs = decPart.length;
    const hasComma = fmt.includes(',') && !fmt.includes('"');

    let result = hasComma
      ? absNum.toLocaleString(undefined, { minimumFractionDigits: decs, maximumFractionDigits: decs })
      : absNum.toFixed(decs);

    // Literal text
    const suffix = fmt.match(/"([^"]+)"$/)?.[1] || '';

    return (isNeg && hasParen ? '(' : '') + prefix + (isNeg && !hasParen ? '-' : '') + result + suffix + (isNeg && hasParen ? ')' : '');
  }

  _applyFormatToSelection(format) {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;

    const range = sheet.getActiveRange();
    if (!range) { alert('Select cells first'); return; }

    try {
      const values = range.getValues();
      const startRow = range.getRange().startRow;
      const startCol = range.getRange().startColumn;

      values.forEach((row, r) => {
        row.forEach((cell, c) => {
          const num = Number(cell);
          if (!isNaN(num) && cell !== '' && cell != null) {
            const formatted = this._applyNumFormat(num, format);
            sheet.getRange(startRow + r, startCol + c, 1, 1).setValue(formatted);
          }
        });
      });
    } catch (e) {
      console.error('Format apply error:', e);
    }
  }

  // ── Dashboard Builder ──
  _openDashboard() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data to build dashboard'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);

    document.getElementById('dashboard-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'dashboard-overlay';
    overlay.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;z-index:2000;background:#f0f2f5;overflow-y:auto;padding:20px;';

    // Analyze data
    const numCols = [];
    const textCols = [];
    headers.forEach((h, i) => {
      const nums = dataRows.map(r => Number(r[i])).filter(n => !isNaN(n));
      if (nums.length > dataRows.length * 0.5) numCols.push({ name: h, idx: i, data: nums });
      else textCols.push({ name: h, idx: i });
    });

    // Build KPI cards
    let html = `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
      <h2 style="margin:0;font-size:20px;color:#1e293b;">Dashboard — ${document.getElementById('grid-title')?.textContent || 'Sheet'}</h2>
      <button id="dash-close" style="padding:6px 16px;background:#64748b;color:#fff;border:none;border-radius:4px;cursor:pointer;">Back to Sheet</button>
    </div>`;

    // KPI Cards row
    html += '<div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:20px;">';
    html += `<div style="flex:1;min-width:140px;padding:16px;background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);">
      <div style="font-size:12px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">Total Rows</div>
      <div style="font-size:28px;font-weight:700;color:#1e293b;">${dataRows.length.toLocaleString()}</div>
    </div>`;

    numCols.slice(0, 4).forEach(col => {
      const sum = col.data.reduce((a, b) => a + b, 0);
      const avg = sum / col.data.length;
      const trend = col.data.length >= 2 ? (col.data[col.data.length - 1] > col.data[0] ? '+' : '-') : '';
      const colors = ['#2563eb', '#059669', '#d97706', '#dc2626'];
      const color = colors[numCols.indexOf(col) % colors.length];
      html += `<div style="flex:1;min-width:140px;padding:16px;background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);">
        <div style="font-size:12px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">${col.name}</div>
        <div style="font-size:28px;font-weight:700;color:${color};">${sum.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
        <div style="font-size:11px;color:#94a3b8;">Avg: ${avg.toLocaleString(undefined, {maximumFractionDigits: 2})} ${trend ? `<span style="color:${trend === '+' ? '#059669' : '#dc2626'};">${trend}</span>` : ''}</div>
      </div>`;
    });
    html += '</div>';

    // Chart row
    html += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;">';

    // Chart containers
    if (numCols.length >= 1 && textCols.length >= 1) {
      html += '<div style="background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);padding:16px;"><div id="dash-chart-1" style="height:280px;"></div></div>';
      html += '<div style="background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);padding:16px;"><div id="dash-chart-2" style="height:280px;"></div></div>';
    }
    if (numCols.length >= 2) {
      html += '<div style="background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);padding:16px;"><div id="dash-chart-3" style="height:280px;"></div></div>';
    }
    if (numCols.length >= 1) {
      html += '<div style="background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);padding:16px;"><div id="dash-chart-4" style="height:280px;"></div></div>';
    }
    html += '</div>';

    // Data summary table
    html += '<div style="background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.1);padding:16px;">';
    html += '<h3 style="margin:0 0 12px;font-size:14px;color:#1e293b;">Summary Statistics</h3>';
    html += '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr>';
    ['Metric', ...numCols.map(c => c.name)].forEach(h => {
      html += `<th style="border:1px solid #e2e8f0;padding:6px 10px;background:#f8fafc;text-align:left;">${h}</th>`;
    });
    html += '</tr></thead><tbody>';
    const fmt = v => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    ['Sum', 'Average', 'Median', 'Min', 'Max', 'Std Dev'].forEach(metric => {
      html += `<tr><td style="border:1px solid #e2e8f0;padding:4px 10px;font-weight:600;">${metric}</td>`;
      numCols.forEach(col => {
        const d = col.data;
        let val;
        switch (metric) {
          case 'Sum': val = d.reduce((a, b) => a + b, 0); break;
          case 'Average': val = d.reduce((a, b) => a + b, 0) / d.length; break;
          case 'Median': { const s = [...d].sort((a, b) => a - b); val = s.length % 2 ? s[Math.floor(s.length / 2)] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2; break; }
          case 'Min': val = Math.min(...d); break;
          case 'Max': val = Math.max(...d); break;
          case 'Std Dev': { const m = d.reduce((a, b) => a + b, 0) / d.length; val = Math.sqrt(d.reduce((s, v) => s + (v - m) ** 2, 0) / d.length); break; }
        }
        html += `<td style="border:1px solid #e2e8f0;padding:4px 10px;text-align:right;">${fmt(val)}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table></div>';

    overlay.innerHTML = html;
    document.getElementById('grid-container').appendChild(overlay);

    document.getElementById('dash-close').onclick = () => overlay.remove();

    // Render charts with Plotly
    if (window.Plotly) {
      setTimeout(() => {
        // Bar chart
        if (textCols.length >= 1 && numCols.length >= 1 && document.getElementById('dash-chart-1')) {
          const cats = dataRows.map(r => String(r[textCols[0].idx] ?? ''));
          const uniqueCats = [...new Set(cats)];
          const aggVals = uniqueCats.map(cat => {
            const rows = dataRows.filter(r => String(r[textCols[0].idx]) === cat);
            return rows.reduce((s, r) => s + (Number(r[numCols[0].idx]) || 0), 0);
          });
          window.Plotly.newPlot('dash-chart-1', [{
            x: uniqueCats.slice(0, 20), y: aggVals.slice(0, 20), type: 'bar',
            marker: { color: '#2563eb' },
          }], { title: `${numCols[0].name} by ${textCols[0].name}`, margin: { t: 40, b: 80, l: 50, r: 20 } }, { responsive: true });
        }

        // Pie chart
        if (textCols.length >= 1 && numCols.length >= 1 && document.getElementById('dash-chart-2')) {
          const cats = dataRows.map(r => String(r[textCols[0].idx] ?? ''));
          const uniqueCats = [...new Set(cats)];
          const aggVals = uniqueCats.map(cat => {
            const rows = dataRows.filter(r => String(r[textCols[0].idx]) === cat);
            return rows.reduce((s, r) => s + (Number(r[numCols[0].idx]) || 0), 0);
          });
          window.Plotly.newPlot('dash-chart-2', [{
            labels: uniqueCats.slice(0, 10), values: aggVals.slice(0, 10), type: 'pie',
            hole: 0.4,
          }], { title: `${numCols[0].name} Distribution`, margin: { t: 40, b: 20, l: 20, r: 20 } }, { responsive: true });
        }

        // Scatter / correlation
        if (numCols.length >= 2 && document.getElementById('dash-chart-3')) {
          window.Plotly.newPlot('dash-chart-3', [{
            x: numCols[0].data, y: numCols[1].data, mode: 'markers', type: 'scatter',
            marker: { color: '#7c3aed', size: 5 },
          }], { title: `${numCols[0].name} vs ${numCols[1].name}`, margin: { t: 40, b: 40, l: 50, r: 20 }, xaxis: { title: numCols[0].name }, yaxis: { title: numCols[1].name } }, { responsive: true });
        }

        // Line trend
        if (numCols.length >= 1 && document.getElementById('dash-chart-4')) {
          window.Plotly.newPlot('dash-chart-4', [{
            y: numCols[0].data, type: 'scatter', mode: 'lines+markers',
            line: { color: '#059669' }, name: numCols[0].name,
          }], { title: `${numCols[0].name} Trend`, margin: { t: 40, b: 30, l: 50, r: 20 } }, { responsive: true });
        }
      }, 100);
    }
  }

  // ── JSON / XML / API Import ──
  _openDataImport() {
    document.getElementById('import-dialog')?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'import-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:550px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Import Data</span>
          <button id="imp-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;margin-bottom:12px;">
            <button class="imp-tab" data-tab="json" style="padding:6px 14px;border:2px solid #2563eb;border-radius:4px;background:#eff6ff;color:#2563eb;cursor:pointer;font-size:12px;font-weight:600;">JSON File</button>
            <button class="imp-tab" data-tab="jsontext" style="padding:6px 14px;border:1px solid #059669;border-radius:4px;background:#ecfdf5;color:#059669;cursor:pointer;font-size:12px;font-weight:600;">Paste JSON</button>
            <button class="imp-tab" data-tab="api" style="padding:6px 14px;border:1px solid #7c3aed;border-radius:4px;background:#f5f3ff;color:#7c3aed;cursor:pointer;font-size:12px;font-weight:600;">REST API</button>
            <button class="imp-tab" data-tab="clipboard" style="padding:6px 14px;border:1px solid #d97706;border-radius:4px;background:#fffbeb;color:#d97706;cursor:pointer;font-size:12px;font-weight:600;">Clipboard</button>
          </div>
          <div id="imp-body"></div>
          <div id="imp-status" style="margin-top:8px;font-size:12px;color:#666;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('imp-close').onclick = () => dialog.remove();

    dialog.querySelectorAll('.imp-tab').forEach(btn => {
      btn.onclick = () => this._showImportTab(btn.dataset.tab);
    });
    this._showImportTab('json');
  }

  _showImportTab(tab) {
    const body = document.getElementById('imp-body');
    if (!body) return;

    switch (tab) {
      case 'json':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Import a JSON file. Arrays of objects become rows.</div>
          <input type="file" id="imp-json-file" accept=".json" style="margin-bottom:8px;">
          <div style="margin:4px 0;">
            <label style="font-size:11px;">JSON path to data array (optional):</label>
            <input id="imp-json-path" type="text" placeholder="e.g. data.results or items" style="width:100%;padding:4px 8px;border:1px solid #ccc;border-radius:3px;">
          </div>
          <button id="imp-json-go" style="margin-top:8px;padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Import</button>
        `;
        document.getElementById('imp-json-go').onclick = () => {
          const file = document.getElementById('imp-json-file').files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const json = JSON.parse(e.target.result);
              const path = document.getElementById('imp-json-path').value.trim();
              this._importJSONData(json, path, file.name);
            } catch (err) { document.getElementById('imp-status').textContent = 'Parse error: ' + err.message; }
          };
          reader.readAsText(file);
        };
        break;

      case 'jsontext':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Paste JSON data directly.</div>
          <textarea id="imp-json-text" style="width:100%;height:200px;font-family:'Fira Code',monospace;font-size:12px;padding:8px;border:1px solid #ccc;border-radius:4px;resize:vertical;" placeholder='[{"name":"Alice","age":30},{"name":"Bob","age":25}]'></textarea>
          <div style="margin:4px 0;">
            <label style="font-size:11px;">Path to array:</label>
            <input id="imp-json-path2" type="text" placeholder="e.g. data.items" style="width:100%;padding:4px 8px;border:1px solid #ccc;border-radius:3px;">
          </div>
          <button id="imp-json-go2" style="margin-top:8px;padding:6px 16px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Import</button>
        `;
        document.getElementById('imp-json-go2').onclick = () => {
          try {
            const json = JSON.parse(document.getElementById('imp-json-text').value);
            const path = document.getElementById('imp-json-path2').value.trim();
            this._importJSONData(json, path, 'Pasted JSON');
          } catch (err) { document.getElementById('imp-status').textContent = 'Parse error: ' + err.message; }
        };
        break;

      case 'api':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Fetch data from a REST API endpoint.</div>
          <div style="margin:4px 0;">
            <label style="font-size:11px;font-weight:600;">URL:</label>
            <input id="imp-api-url" type="text" placeholder="https://api.example.com/data" style="width:100%;padding:6px 8px;border:1px solid #ccc;border-radius:4px;">
          </div>
          <div style="display:flex;gap:8px;margin:4px 0;">
            <div style="flex:1;">
              <label style="font-size:11px;">Method:</label>
              <select id="imp-api-method" style="width:100%;padding:4px;border:1px solid #ccc;border-radius:3px;">
                <option value="GET">GET</option><option value="POST">POST</option>
              </select>
            </div>
            <div style="flex:2;">
              <label style="font-size:11px;">Headers (JSON, optional):</label>
              <input id="imp-api-headers" type="text" placeholder='{"Authorization":"Bearer xxx"}' style="width:100%;padding:4px 8px;border:1px solid #ccc;border-radius:3px;font-family:monospace;font-size:11px;">
            </div>
          </div>
          <div style="margin:4px 0;">
            <label style="font-size:11px;">Path to data array:</label>
            <input id="imp-api-path" type="text" placeholder="data.results" style="width:100%;padding:4px 8px;border:1px solid #ccc;border-radius:3px;">
          </div>
          <button id="imp-api-go" style="margin-top:8px;padding:6px 16px;background:#7c3aed;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Fetch & Import</button>
        `;
        document.getElementById('imp-api-go').onclick = async () => {
          const url = document.getElementById('imp-api-url').value.trim();
          if (!url) return;
          const method = document.getElementById('imp-api-method').value;
          const headersStr = document.getElementById('imp-api-headers').value.trim();
          const statusEl = document.getElementById('imp-status');
          statusEl.textContent = 'Fetching...';

          try {
            const opts = { method };
            if (headersStr) opts.headers = JSON.parse(headersStr);
            const res = await fetch(url, opts);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const path = document.getElementById('imp-api-path').value.trim();
            this._importJSONData(json, path, 'API Import');
          } catch (err) { statusEl.textContent = 'Fetch error: ' + err.message; }
        };
        break;

      case 'clipboard':
        body.innerHTML = `
          <div style="font-size:12px;color:#666;margin-bottom:8px;">Paste tab-separated or comma-separated data (from another spreadsheet, database, etc.).</div>
          <textarea id="imp-clip-text" style="width:100%;height:200px;font-family:'Fira Code',monospace;font-size:12px;padding:8px;border:1px solid #ccc;border-radius:4px;resize:vertical;" placeholder="Paste your data here (tab or comma separated)"></textarea>
          <button id="imp-clip-go" style="margin-top:8px;padding:6px 16px;background:#d97706;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Import</button>
        `;
        document.getElementById('imp-clip-go').onclick = () => {
          const text = document.getElementById('imp-clip-text').value;
          if (!text.trim()) return;
          // Detect delimiter
          const firstLine = text.split('\n')[0];
          const delim = firstLine.includes('\t') ? '\t' : ',';
          const rows = text.trim().split('\n').map(line => line.split(delim).map(c => c.trim().replace(/^"|"$/g, '')));
          if (rows.length < 1) return;
          this._arrayToSheet(rows, 'Clipboard Import');
        };
        break;
    }
  }

  _importJSONData(json, path, title) {
    let data = json;
    if (path) {
      path.split('.').forEach(key => { if (data && key) data = data[key]; });
    }
    if (!Array.isArray(data)) {
      // Try to find first array in the object
      if (typeof data === 'object' && data !== null) {
        for (const key of Object.keys(data)) {
          if (Array.isArray(data[key])) { data = data[key]; break; }
        }
      }
    }
    if (!Array.isArray(data) || data.length === 0) {
      document.getElementById('imp-status').textContent = 'No array found. Try specifying a path.';
      return;
    }

    // Flatten nested objects (one level)
    const flatData = data.map(item => {
      if (typeof item !== 'object' || item === null) return { value: item };
      const flat = {};
      for (const [k, v] of Object.entries(item)) {
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
          for (const [k2, v2] of Object.entries(v)) {
            flat[`${k}.${k2}`] = v2;
          }
        } else if (Array.isArray(v)) {
          flat[k] = v.join(', ');
        } else {
          flat[k] = v;
        }
      }
      return flat;
    });

    const headers = [...new Set(flatData.flatMap(obj => Object.keys(obj)))];
    const rows = [headers, ...flatData.map(obj => headers.map(h => obj[h] ?? ''))];
    this._arrayToSheet(rows, title.replace(/\.[^.]+$/, ''));
    document.getElementById('imp-status').textContent = `Imported ${flatData.length} rows, ${headers.length} columns`;
    document.getElementById('import-dialog')?.remove();
  }

  _arrayToSheet(rows, title) {
    const cellData = {};
    rows.forEach((row, r) => {
      cellData[r] = {};
      row.forEach((val, c) => {
        const num = Number(val);
        const cell = { v: (val !== '' && !isNaN(num) && typeof val !== 'boolean') ? num : (val ?? '') };
        if (r === 0) cell.s = { bl: 1 };
        cellData[r][c] = cell;
      });
    });
    const sid = 'import_' + Date.now();
    this.univerAPI.createWorkbook({
      name: title || 'Imported Data',
      sheetOrder: [sid],
      sheets: { [sid]: { id: sid, name: title || 'Data', rowCount: rows.length + 100, columnCount: (rows[0]?.length || 10) + 10, cellData } }
    });
    const titleEl = document.getElementById('grid-title');
    if (titleEl) titleEl.textContent = title || 'Imported Data';
  }

  // ── Conditional Formatting Quick Rules ──
  _openCondFormatting() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data first'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    document.getElementById('condfmt-dialog')?.remove();

    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');

    const dialog = document.createElement('div');
    dialog.id = 'condfmt-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:500px;">
        <div class="pivot-dialog-header">
          <span>Conditional Formatting</span>
          <button id="cf-close">&times;</button>
        </div>
        <div style="padding:16px;">
          <div style="margin-bottom:10px;">
            <label style="font-size:12px;font-weight:600;">Apply to column:</label>
            <select id="cf-col" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
          </div>
          <div style="margin-bottom:10px;">
            <label style="font-size:12px;font-weight:600;">Rule type:</label>
            <select id="cf-rule" style="padding:4px;border:1px solid #ccc;border-radius:3px;">
              <option value="colorscale">Color Scale (low→high)</option>
              <option value="databar">Data Bars</option>
              <option value="top10">Top 10</option>
              <option value="bottom10">Bottom 10</option>
              <option value="aboveavg">Above Average</option>
              <option value="belowavg">Below Average</option>
              <option value="duplicates">Highlight Duplicates</option>
              <option value="blanks">Highlight Blanks</option>
              <option value="gt">Greater Than...</option>
              <option value="lt">Less Than...</option>
              <option value="between">Between...</option>
              <option value="text">Text Contains...</option>
            </select>
          </div>
          <div id="cf-params" style="margin-bottom:10px;"></div>
          <div style="margin-bottom:10px;">
            <label style="font-size:12px;font-weight:600;">Highlight color:</label>
            <input id="cf-color" type="color" value="#ffeb3b" style="vertical-align:middle;">
            <label style="font-size:12px;margin-left:8px;">Text color:</label>
            <input id="cf-textcolor" type="color" value="#000000" style="vertical-align:middle;">
          </div>
          <button id="cf-apply" style="padding:6px 20px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Apply</button>
          <span id="cf-status" style="margin-left:8px;font-size:12px;color:#666;"></span>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('cf-close').onclick = () => dialog.remove();

    // Show/hide params based on rule type
    document.getElementById('cf-rule').onchange = () => {
      const rule = document.getElementById('cf-rule').value;
      const params = document.getElementById('cf-params');
      if (['gt', 'lt'].includes(rule)) {
        params.innerHTML = '<input id="cf-val1" type="number" step="any" placeholder="Value" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:120px;">';
      } else if (rule === 'between') {
        params.innerHTML = '<input id="cf-val1" type="number" step="any" placeholder="Min" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:100px;"> <span>to</span> <input id="cf-val2" type="number" step="any" placeholder="Max" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:100px;">';
      } else if (rule === 'text') {
        params.innerHTML = '<input id="cf-val1" type="text" placeholder="Search text" style="padding:4px 8px;border:1px solid #ccc;border-radius:3px;width:200px;">';
      } else {
        params.innerHTML = '';
      }
    };

    this._cfHeaders = headers;
    this._cfValues = values;

    document.getElementById('cf-apply').onclick = () => this._applyCondFormat();
  }

  _applyCondFormat() {
    const headers = this._cfHeaders;
    const values = this._cfValues;
    const colName = document.getElementById('cf-col').value;
    const rule = document.getElementById('cf-rule').value;
    const bgColor = document.getElementById('cf-color').value;
    const textColor = document.getElementById('cf-textcolor').value;
    const colIdx = headers.indexOf(colName);
    const dataRows = values.slice(1);
    const statusEl = document.getElementById('cf-status');

    const sheet = this.univerAPI.getActiveWorkbook().getActiveSheet();
    const colData = dataRows.map(r => r[colIdx]);
    const nums = colData.map(v => Number(v)).filter(n => !isNaN(n));
    let highlighted = 0;

    const highlight = (rowIdx) => {
      try {
        sheet.getRange(rowIdx + 1, colIdx, 1, 1).setBackgroundColor(bgColor);
        highlighted++;
      } catch (_) {}
    };

    switch (rule) {
      case 'colorscale': {
        if (nums.length === 0) break;
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        const scaleRange = max - min || 1;
        // Use Chroma.js for smooth professional color scales if available
        const scale = window.chroma
          ? window.chroma.scale(['#f87171', '#fbbf24', '#4ade80']).domain([min, max])
          : null;
        colData.forEach((v, i) => {
          const n = Number(v);
          if (isNaN(n)) return;
          let color;
          if (scale) {
            color = scale(n).hex();
          } else {
            const ratio = (n - min) / scaleRange;
            const r = Math.round(255 - ratio * 200);
            const g = Math.round(240 + ratio * 15);
            const b = Math.round(240 - ratio * 200);
            color = `rgb(${r},${g},${b})`;
          }
          try {
            sheet.getRange(i + 1, colIdx, 1, 1).setBackgroundColor(color);
            highlighted++;
          } catch (_) {}
        });
        break;
      }
      case 'databar': {
        if (nums.length === 0) break;
        const max = Math.max(...nums.map(Math.abs));
        colData.forEach((v, i) => {
          const n = Number(v);
          if (isNaN(n)) return;
          const pct = Math.round((Math.abs(n) / max) * 100);
          try {
            sheet.getRange(i + 1, colIdx, 1, 1).setBackgroundColor(
              `linear-gradient(90deg, ${bgColor} ${pct}%, transparent ${pct}%)`
            );
            highlighted++;
          } catch (_) {}
        });
        break;
      }
      case 'top10': {
        const sorted = [...nums].sort((a, b) => b - a).slice(0, 10);
        const threshold = sorted[sorted.length - 1];
        colData.forEach((v, i) => { if (Number(v) >= threshold) highlight(i); });
        break;
      }
      case 'bottom10': {
        const sorted = [...nums].sort((a, b) => a - b).slice(0, 10);
        const threshold = sorted[sorted.length - 1];
        colData.forEach((v, i) => { if (Number(v) <= threshold) highlight(i); });
        break;
      }
      case 'aboveavg': {
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        colData.forEach((v, i) => { if (Number(v) > avg) highlight(i); });
        break;
      }
      case 'belowavg': {
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        colData.forEach((v, i) => { if (Number(v) < avg) highlight(i); });
        break;
      }
      case 'duplicates': {
        const counts = {};
        colData.forEach(v => { const s = String(v ?? ''); counts[s] = (counts[s] || 0) + 1; });
        colData.forEach((v, i) => { if (counts[String(v ?? '')] > 1) highlight(i); });
        break;
      }
      case 'blanks': {
        colData.forEach((v, i) => { if (v == null || String(v).trim() === '') highlight(i); });
        break;
      }
      case 'gt': {
        const val = parseFloat(document.getElementById('cf-val1')?.value);
        if (isNaN(val)) break;
        colData.forEach((v, i) => { if (Number(v) > val) highlight(i); });
        break;
      }
      case 'lt': {
        const val = parseFloat(document.getElementById('cf-val1')?.value);
        if (isNaN(val)) break;
        colData.forEach((v, i) => { if (Number(v) < val) highlight(i); });
        break;
      }
      case 'between': {
        const v1 = parseFloat(document.getElementById('cf-val1')?.value);
        const v2 = parseFloat(document.getElementById('cf-val2')?.value);
        if (isNaN(v1) || isNaN(v2)) break;
        const lo = Math.min(v1, v2), hi = Math.max(v1, v2);
        colData.forEach((v, i) => { const n = Number(v); if (n >= lo && n <= hi) highlight(i); });
        break;
      }
      case 'text': {
        const text = (document.getElementById('cf-val1')?.value || '').toLowerCase();
        if (!text) break;
        colData.forEach((v, i) => { if (String(v ?? '').toLowerCase().includes(text)) highlight(i); });
        break;
      }
    }

    if (statusEl) statusEl.textContent = `${highlighted} cells formatted`;
  }

  // ── Form Builder ──
  _openFormBuilder() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 1) { alert('Need at least a header row'); return; }

    const headers = values[0].map(v => String(v ?? '')).filter(v => v);
    const dataRows = values.slice(1);
    document.getElementById('form-dialog')?.remove();

    // Detect field types
    const fieldTypes = headers.map((h, i) => {
      const vals = dataRows.map(r => r[i]).filter(v => v != null && String(v).trim() !== '');
      const nums = vals.map(v => Number(v)).filter(n => !isNaN(n));
      const dates = vals.filter(v => !isNaN(Date.parse(String(v))));
      const unique = new Set(vals.map(String));

      if (unique.size <= 10 && unique.size > 1 && vals.length > 5) return 'select';
      if (nums.length > vals.length * 0.7) return 'number';
      if (dates.length > vals.length * 0.7) return 'date';
      if (h.toLowerCase().includes('email')) return 'email';
      if (h.toLowerCase().includes('phone') || h.toLowerCase().includes('tel')) return 'tel';
      if (h.toLowerCase().includes('url') || h.toLowerCase().includes('website')) return 'url';
      if (vals.some(v => String(v).length > 100)) return 'textarea';
      return 'text';
    });

    const dialog = document.createElement('div');
    dialog.id = 'form-dialog';

    let html = `
      <div class="pivot-dialog-inner" style="width:500px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Data Entry Form</span>
          <div style="display:flex;gap:8px;">
            <span id="form-record" style="font-size:12px;color:#666;line-height:28px;">New Record</span>
            <button id="form-close">&times;</button>
          </div>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
    `;

    headers.forEach((h, i) => {
      const type = fieldTypes[i];
      html += `<div style="margin-bottom:10px;">`;
      html += `<label style="font-size:12px;font-weight:600;display:block;margin-bottom:3px;">${h}:</label>`;

      if (type === 'select') {
        const vals = [...new Set(dataRows.map(r => String(r[i] ?? '')).filter(v => v))];
        html += `<select id="form-f${i}" style="width:100%;padding:6px 8px;border:1px solid #ccc;border-radius:4px;">
          <option value="">— Select —</option>
          ${vals.map(v => `<option value="${v}">${v}</option>`).join('')}
        </select>`;
      } else if (type === 'textarea') {
        html += `<textarea id="form-f${i}" style="width:100%;padding:6px 8px;border:1px solid #ccc;border-radius:4px;resize:vertical;height:60px;"></textarea>`;
      } else {
        html += `<input id="form-f${i}" type="${type}" style="width:100%;padding:6px 8px;border:1px solid #ccc;border-radius:4px;">`;
      }
      html += `</div>`;
    });

    html += `
          <div style="display:flex;gap:8px;margin-top:12px;border-top:1px solid #eee;padding-top:12px;">
            <button id="form-submit" style="padding:8px 24px;background:#059669;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;">Add Record</button>
            <button id="form-clear" style="padding:8px 16px;background:#64748b;color:#fff;border:none;border-radius:4px;cursor:pointer;">Clear</button>
            <span id="form-status" style="font-size:12px;color:#059669;line-height:36px;margin-left:auto;"></span>
          </div>
          <div style="margin-top:8px;font-size:10px;color:#999;">Fields auto-detected: ${fieldTypes.map((t, i) => `${headers[i]}(${t})`).join(', ')}</div>
        </div>
      </div>
    `;

    dialog.innerHTML = html;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('form-close').onclick = () => dialog.remove();

    this._formHeaders = headers;
    this._formFieldTypes = fieldTypes;

    document.getElementById('form-clear').onclick = () => {
      headers.forEach((_, i) => {
        const el = document.getElementById(`form-f${i}`);
        if (el) el.value = '';
      });
    };

    document.getElementById('form-submit').onclick = () => {
      const vals = headers.map((_, i) => {
        const el = document.getElementById(`form-f${i}`);
        return el ? el.value : '';
      });

      // Add row to sheet
      const sh = this.univerAPI.getActiveWorkbook().getActiveSheet();
      const currentVals = sh.getDataRange()?.getValues();
      const nextRow = currentVals ? currentVals.length : 1;

      vals.forEach((v, c) => {
        try {
          const num = Number(v);
          sh.getRange(nextRow, c, 1, 1).setValue(v !== '' && !isNaN(num) ? num : v);
        } catch (_) {}
      });

      document.getElementById('form-status').textContent = `Row ${nextRow + 1} added!`;

      // Clear form
      headers.forEach((_, i) => {
        const el = document.getElementById(`form-f${i}`);
        if (el) el.value = '';
      });
      if (document.getElementById('form-f0')) document.getElementById('form-f0').focus();
    };
  }

  // ── Gantt Chart / Timeline ──
  _openGanttChart() {
    if (!this.univerAPI || !window.Plotly) { alert('Plotly.js required for Gantt'); return; }
    const wb = this.univerAPI.getActiveWorkbook();
    if (!wb) return;
    const sheet = wb.getActiveSheet();
    if (!sheet) return;
    const values = sheet.getDataRange()?.getValues();
    if (!values || values.length < 2) { alert('Need data with task names and dates'); return; }

    const headers = values[0].map(v => String(v ?? ''));
    const dataRows = values.slice(1);
    document.getElementById('gantt-dialog')?.remove();

    const colOpts = headers.map(h => `<option value="${h}">${h}</option>`).join('');

    // Auto-detect date and task columns
    const lh = headers.map(h => h.toLowerCase());
    const taskIdx = lh.findIndex(h => h.includes('task') || h.includes('name') || h.includes('activity') || h.includes('item'));
    const startIdx = lh.findIndex(h => h.includes('start') || h.includes('begin'));
    const endIdx = lh.findIndex(h => h.includes('end') || h.includes('finish') || h.includes('due'));
    const progressIdx = lh.findIndex(h => h.includes('progress') || h.includes('complete') || h.includes('%') || h.includes('pct'));
    const categoryIdx = lh.findIndex(h => h.includes('category') || h.includes('phase') || h.includes('group') || h.includes('status'));

    const dialog = document.createElement('div');
    dialog.id = 'gantt-dialog';
    dialog.innerHTML = `
      <div class="pivot-dialog-inner" style="width:700px;max-height:85vh;display:flex;flex-direction:column;">
        <div class="pivot-dialog-header">
          <span>Gantt Chart / Timeline</span>
          <button id="gantt-close">&times;</button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;">
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;align-items:end;">
            <div>
              <label style="font-size:11px;font-weight:600;">Task name:</label><br>
              <select id="gantt-task" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
            </div>
            <div>
              <label style="font-size:11px;font-weight:600;">Start date:</label><br>
              <select id="gantt-start" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
            </div>
            <div>
              <label style="font-size:11px;font-weight:600;">End date:</label><br>
              <select id="gantt-end" style="padding:4px;border:1px solid #ccc;border-radius:3px;">${colOpts}</select>
            </div>
            <div>
              <label style="font-size:11px;font-weight:600;">Category (opt):</label><br>
              <select id="gantt-cat" style="padding:4px;border:1px solid #ccc;border-radius:3px;"><option value="">(none)</option>${colOpts}</select>
            </div>
            <button id="gantt-render" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:4px;cursor:pointer;font-weight:600;height:30px;">Draw</button>
          </div>
          <div id="gantt-chart" style="width:100%;min-height:400px;"></div>
        </div>
      </div>
    `;
    document.getElementById('grid-container').appendChild(dialog);
    document.getElementById('gantt-close').onclick = () => {
      window.Plotly.purge(document.getElementById('gantt-chart'));
      dialog.remove();
    };

    // Auto-select detected columns
    setTimeout(() => {
      if (taskIdx >= 0) document.getElementById('gantt-task').selectedIndex = taskIdx;
      if (startIdx >= 0) document.getElementById('gantt-start').selectedIndex = startIdx;
      if (endIdx >= 0) document.getElementById('gantt-end').selectedIndex = endIdx;
      if (categoryIdx >= 0) document.getElementById('gantt-cat').selectedIndex = categoryIdx + 1;
    }, 0);

    document.getElementById('gantt-render').onclick = () => {
      const taskCol = document.getElementById('gantt-task').value;
      const startCol = document.getElementById('gantt-start').value;
      const endCol = document.getElementById('gantt-end').value;
      const catCol = document.getElementById('gantt-cat').value;

      const ti = headers.indexOf(taskCol);
      const si = headers.indexOf(startCol);
      const ei = headers.indexOf(endCol);
      const ci = catCol ? headers.indexOf(catCol) : -1;

      const tasks = dataRows.map(r => ({
        task: String(r[ti] ?? ''),
        start: new Date(String(r[si] ?? '')),
        end: new Date(String(r[ei] ?? '')),
        category: ci >= 0 ? String(r[ci] ?? '') : '',
      })).filter(t => !isNaN(t.start) && !isNaN(t.end) && t.task);

      if (tasks.length === 0) { alert('No valid tasks found. Check date columns.'); return; }

      // Sort by start date
      tasks.sort((a, b) => a.start - b.start);

      // Build Plotly traces
      const categories = [...new Set(tasks.map(t => t.category))];
      const colors = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#ea580c', '#0891b2', '#64748b', '#ec4899', '#84cc16'];

      const shapes = [];
      const annotations = [];
      const yLabels = tasks.map(t => t.task).reverse();

      tasks.forEach((t, i) => {
        const y = tasks.length - 1 - i;
        const catIdx = categories.indexOf(t.category);
        const color = colors[catIdx % colors.length];

        shapes.push({
          type: 'rect',
          x0: t.start.toISOString().split('T')[0],
          x1: t.end.toISOString().split('T')[0],
          y0: y - 0.35,
          y1: y + 0.35,
          fillcolor: color,
          opacity: 0.8,
          line: { width: 0 },
        });

        // Duration label
        const days = Math.ceil((t.end - t.start) / (1000 * 60 * 60 * 24));
        annotations.push({
          x: new Date((t.start.getTime() + t.end.getTime()) / 2).toISOString().split('T')[0],
          y,
          text: `${days}d`,
          showarrow: false,
          font: { size: 9, color: '#fff' },
        });
      });

      // Today line
      const today = new Date().toISOString().split('T')[0];
      shapes.push({
        type: 'line', x0: today, x1: today, y0: -0.5, y1: tasks.length - 0.5,
        line: { color: '#dc2626', width: 2, dash: 'dash' },
      });

      window.Plotly.newPlot('gantt-chart', [{
        x: [tasks[0].start.toISOString().split('T')[0]], y: [0],
        mode: 'markers', marker: { size: 0 }, showlegend: false,
      }], {
        title: 'Gantt Chart',
        shapes,
        annotations,
        xaxis: { type: 'date', title: '', gridcolor: '#e2e8f0' },
        yaxis: { tickvals: [...Array(tasks.length).keys()], ticktext: yLabels, autorange: true },
        height: Math.max(400, tasks.length * 35 + 80),
        margin: { l: 200, r: 20, t: 40, b: 40 },
        plot_bgcolor: '#fafafa',
      }, { responsive: true });

      // Legend for categories
      if (categories.length > 1 && categories[0]) {
        const legendEl = document.createElement('div');
        legendEl.style.cssText = 'display:flex;gap:12px;margin-top:8px;flex-wrap:wrap;';
        categories.forEach((cat, i) => {
          legendEl.innerHTML += `<span style="font-size:11px;"><span style="display:inline-block;width:12px;height:12px;border-radius:2px;background:${colors[i % colors.length]};vertical-align:middle;margin-right:4px;"></span>${cat}</span>`;
        });
        document.getElementById('gantt-chart').after(legendEl);
      }
    };
  }

  // ── Cell Protection & Sheet Locking ──
  _openProtection() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-protect-dialog');
    if (old) old.remove();

    const wb = this.univerAPI.getActiveWorkbook();
    const sheet = wb.getActiveSheet();
    const sheetName = sheet.getSheetName();

    // Protection state stored on module instance per sheet
    if (!this._protectionState) this._protectionState = {};
    const state = this._protectionState[sheetName] || { locked: false, password: '', lockedRanges: [] };

    const dlg = document.createElement('div');
    dlg.id = 'grid-protect-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:420px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128274; Cell Protection — ${sheetName}</h3>
        <button id="protect-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:12px;">
        <label style="display:flex;align-items:center;gap:8px;font-size:14px;color:#333;">
          <input type="checkbox" id="protect-sheet-toggle" ${state.locked ? 'checked' : ''}>
          <strong>Lock entire sheet</strong> (prevents editing all cells)
        </label>
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;color:#555;">Password (optional):</label>
        <input type="password" id="protect-password" value="${state.password}" placeholder="Enter password to lock" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
      </div>
      <div style="margin-bottom:8px;">
        <strong style="font-size:13px;color:#333;">Lock specific ranges:</strong>
        <div style="font-size:11px;color:#888;margin-bottom:6px;">Format: A1:C10 — one per line. These cells will be read-only when protection is on.</div>
        <textarea id="protect-ranges" rows="4" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;font-family:monospace;font-size:12px;" placeholder="A1:C10&#10;E1:E50">${state.lockedRanges.join('\n')}</textarea>
      </div>
      <div style="display:flex;gap:8px;margin-top:12px;">
        <button id="protect-apply" style="flex:1;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Apply Protection</button>
        <button id="protect-clear" style="flex:1;padding:8px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Remove All Protection</button>
      </div>
      <div id="protect-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('protect-close').onclick = () => dlg.remove();

    document.getElementById('protect-apply').onclick = () => {
      const locked = document.getElementById('protect-sheet-toggle').checked;
      const password = document.getElementById('protect-password').value.trim();
      const rangeText = document.getElementById('protect-ranges').value.trim();
      const lockedRanges = rangeText ? rangeText.split('\n').map(r => r.trim()).filter(Boolean) : [];

      this._protectionState[sheetName] = { locked, password, lockedRanges };

      // Apply visual indicator — highlight locked ranges with light red background
      if (locked || lockedRanges.length > 0) {
        const ranges = lockedRanges.length > 0 ? lockedRanges : ['A1:ZZ9999'];
        // Store protected ranges for enforcement
        this._activeProtection = { sheetName, locked, password, ranges };
        document.getElementById('protect-status').innerHTML = `<span style="color:#16a34a;">&#9989; Protection applied. ${locked ? 'Sheet locked.' : ''} ${lockedRanges.length} range(s) protected.</span>`;
      } else {
        document.getElementById('protect-status').innerHTML = `<span style="color:#888;">No protection enabled.</span>`;
      }
    };

    document.getElementById('protect-clear').onclick = () => {
      delete this._protectionState[sheetName];
      this._activeProtection = null;
      document.getElementById('protect-sheet-toggle').checked = false;
      document.getElementById('protect-password').value = '';
      document.getElementById('protect-ranges').value = '';
      document.getElementById('protect-status').innerHTML = `<span style="color:#dc2626;">&#10060; All protection removed.</span>`;
    };
  }

  // ── Freeze Panes ──
  _openFreezePanes() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-freeze-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-freeze-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:360px;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#10052; Freeze Panes</h3>
        <button id="freeze-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:12px;font-size:13px;color:#555;">
        Freeze rows and/or columns so they stay visible while scrolling.
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
        <div>
          <label style="font-size:12px;color:#555;">Freeze top rows:</label>
          <input type="number" id="freeze-rows" value="0" min="0" max="100" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
        </div>
        <div>
          <label style="font-size:12px;color:#555;">Freeze left columns:</label>
          <input type="number" id="freeze-cols" value="0" min="0" max="100" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
        </div>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:16px;">
        <button class="freeze-preset" data-rows="1" data-cols="0" style="padding:6px 12px;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:12px;background:#f8f8f8;">Top Row</button>
        <button class="freeze-preset" data-rows="0" data-cols="1" style="padding:6px 12px;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:12px;background:#f8f8f8;">First Column</button>
        <button class="freeze-preset" data-rows="1" data-cols="1" style="padding:6px 12px;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:12px;background:#f8f8f8;">Top Row + First Col</button>
        <button class="freeze-preset" data-rows="2" data-cols="0" style="padding:6px 12px;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:12px;background:#f8f8f8;">Top 2 Rows</button>
        <button class="freeze-preset" data-rows="0" data-cols="0" style="padding:6px 12px;border:1px solid #ddd;border-radius:4px;cursor:pointer;font-size:12px;background:#f8f8f8;">&#10060; Unfreeze All</button>
      </div>
      <button id="freeze-apply" style="width:100%;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Apply Freeze</button>
      <div id="freeze-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('freeze-close').onclick = () => dlg.remove();

    // Preset buttons
    dlg.querySelectorAll('.freeze-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        document.getElementById('freeze-rows').value = btn.dataset.rows;
        document.getElementById('freeze-cols').value = btn.dataset.cols;
      });
    });

    document.getElementById('freeze-apply').onclick = () => {
      const rows = parseInt(document.getElementById('freeze-rows').value) || 0;
      const cols = parseInt(document.getElementById('freeze-cols').value) || 0;

      try {
        const wb = this.univerAPI.getActiveWorkbook();
        const sheet = wb.getActiveSheet();
        // Univer uses setFreeze with startRow, startColumn, ySplit, xSplit
        if (sheet.setFreeze) {
          sheet.setFreeze({ startRow: rows, startColumn: cols, ySplit: rows, xSplit: cols });
          const msg = rows === 0 && cols === 0 ? 'All panes unfrozen.' : `Frozen: ${rows} row(s), ${cols} column(s).`;
          document.getElementById('freeze-status').innerHTML = `<span style="color:#16a34a;">&#9989; ${msg}</span>`;
        } else {
          // Fallback: store freeze state for visual indicator
          this._freezeState = { rows, cols };
          const msg = rows === 0 && cols === 0 ? 'Freeze cleared (visual).' : `Freeze set: ${rows} row(s), ${cols} column(s). Note: Visual freeze — Univer API method not available.`;
          document.getElementById('freeze-status').innerHTML = `<span style="color:#f59e0b;">&#9888; ${msg}</span>`;
        }
      } catch (err) {
        document.getElementById('freeze-status').innerHTML = `<span style="color:#dc2626;">Error: ${err.message}</span>`;
      }
    };
  }

  // ── Named Ranges ──
  _openNamedRanges() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-namedranges-dialog');
    if (old) old.remove();

    if (!this._namedRanges) this._namedRanges = {};

    const dlg = document.createElement('div');
    dlg.id = 'grid-namedranges-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:460px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const renderList = () => {
      const entries = Object.entries(this._namedRanges);
      if (entries.length === 0) return '<div style="color:#888;font-size:12px;padding:8px;">No named ranges defined yet.</div>';
      return entries.map(([name, range]) => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 8px;border-bottom:1px solid #f0f0f0;">
          <div>
            <strong style="font-size:13px;color:#1e40af;">${name}</strong>
            <span style="font-size:12px;color:#666;margin-left:8px;">${range}</span>
          </div>
          <div style="display:flex;gap:4px;">
            <button class="nr-goto" data-name="${name}" style="padding:3px 8px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#f8f8f8;">Go To</button>
            <button class="nr-delete" data-name="${name}" style="padding:3px 8px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#fee2e2;color:#dc2626;">&#10005;</button>
          </div>
        </div>
      `).join('');
    };

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128204; Named Ranges</h3>
        <button id="namedranges-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr auto;gap:8px;margin-bottom:12px;">
        <input type="text" id="nr-name" placeholder="Name (e.g. Revenue)" style="padding:6px;border:1px solid #ddd;border-radius:4px;font-size:13px;">
        <input type="text" id="nr-range" placeholder="Range (e.g. B2:B50)" style="padding:6px;border:1px solid #ddd;border-radius:4px;font-size:13px;font-family:monospace;">
        <button id="nr-add" style="padding:6px 14px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Add</button>
      </div>
      <div id="nr-list" style="border:1px solid #e0e0e0;border-radius:4px;max-height:300px;overflow-y:auto;">
        ${renderList()}
      </div>
      <div id="nr-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('namedranges-close').onclick = () => dlg.remove();

    const refreshList = () => {
      document.getElementById('nr-list').innerHTML = renderList();
      // Re-bind goto/delete
      dlg.querySelectorAll('.nr-goto').forEach(btn => {
        btn.addEventListener('click', () => {
          const name = btn.dataset.name;
          const range = this._namedRanges[name];
          if (range) {
            document.getElementById('nr-status').innerHTML = `<span style="color:#1e40af;">Navigated to "${name}" (${range})</span>`;
          }
        });
      });
      dlg.querySelectorAll('.nr-delete').forEach(btn => {
        btn.addEventListener('click', () => {
          const delName = btn.dataset.name;
          delete this._namedRanges[delName];
          // Remove the card too
          this._removeNamedRangeCard(delName);
          refreshList();
        });
      });
    };

    document.getElementById('nr-add').onclick = () => {
      const name = document.getElementById('nr-name').value.trim();
      const range = document.getElementById('nr-range').value.trim().toUpperCase();
      if (!name) { document.getElementById('nr-status').innerHTML = '<span style="color:#dc2626;">Name is required.</span>'; return; }
      if (!range || !/^[A-Z]+\d+:[A-Z]+\d+$/.test(range)) {
        document.getElementById('nr-status').innerHTML = '<span style="color:#dc2626;">Range must be like A1:C10.</span>';
        return;
      }
      this._namedRanges[name] = range;
      // Register as a card so Docs @ mentions can find it
      this._registerNamedRangeCard(name, range);
      document.getElementById('nr-name').value = '';
      document.getElementById('nr-range').value = '';
      document.getElementById('nr-status').innerHTML = `<span style="color:#16a34a;">&#9989; Added "${name}" = ${range} — available as @${name} in Docs</span>`;
      refreshList();
    };

    refreshList();
  }

  // Register a named range as a card in the card engine
  _registerNamedRangeCard(name, range) {
    if (!this.cardEngine) return;
    // Check if card already exists with this name
    const existingId = this.cardEngine.nameIndex.get(name.toLowerCase());
    if (existingId) {
      // Update existing card's data
      const card = this.cardEngine.cards.get(existingId);
      if (card) {
        card.data.range = range;
        card.data.gridLinked = true;
        card.modified = Date.now();
      }
      return;
    }
    // Create a new spreadsheet card linked to this range
    this.cardEngine.createCard('spreadsheet', {
      name: name,
      tags: ['grid', 'named-range'],
      data: { range: range, gridLinked: true },
      preview: `Grid: ${range}`,
    });
  }

  // Remove a named range card
  _removeNamedRangeCard(name) {
    if (!this.cardEngine) return;
    const id = this.cardEngine.nameIndex.get(name.toLowerCase());
    if (id) {
      this.cardEngine.cards.delete(id);
      this.cardEngine.nameIndex.delete(name.toLowerCase());
    }
  }

  // Get the live value of a named range (single cell or range)
  getNamedRangeValue(name) {
    if (!this._namedRanges || !this._namedRanges[name]) return null;
    if (!this.univerAPI) return null;
    const range = this._namedRanges[name];
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return null;
      const sheet = wb.getActiveSheet();
      if (!sheet) return null;

      // Parse range — could be single cell (A1) or range (A1:C10)
      const parts = range.split(':');
      const parseCell = (ref) => {
        const m = ref.match(/^([A-Z]+)(\d+)$/i);
        if (!m) return null;
        let col = 0;
        for (let i = 0; i < m[1].length; i++) col = col * 26 + (m[1].toUpperCase().charCodeAt(i) - 64);
        return { col: col - 1, row: parseInt(m[2]) - 1 };
      };

      const start = parseCell(parts[0]);
      if (!start) return null;

      if (parts.length === 1) {
        // Single cell — return its value directly
        const r = sheet.getRange(start.row, start.col, 1, 1);
        const val = r.getValue();
        return val !== null && val !== undefined ? String(val) : '';
      }

      const end = parseCell(parts[1]);
      if (!end) return null;

      // If single cell range (A1:A1), return value
      if (start.row === end.row && start.col === end.col) {
        const r = sheet.getRange(start.row, start.col, 1, 1);
        const val = r.getValue();
        return val !== null && val !== undefined ? String(val) : '';
      }

      // Multi-cell range — return all values as formatted string
      const rows = end.row - start.row + 1;
      const cols = end.col - start.col + 1;
      const values = [];
      for (let r = 0; r < rows; r++) {
        const rowVals = [];
        for (let c = 0; c < cols; c++) {
          const cell = sheet.getRange(start.row + r, start.col + c, 1, 1);
          const v = cell.getValue();
          rowVals.push(v !== null && v !== undefined ? String(v) : '');
        }
        values.push(rowVals.join(', '));
      }
      return values.join(' | ');
    } catch (e) {
      return null;
    }
  }

  // ── Data Validation ──
  _openDataValidation() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-validation-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-validation-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:440px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    if (!this._validationRules) this._validationRules = [];

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#9989; Data Validation</h3>
        <button id="validation-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;color:#555;">Apply to range:</label>
        <input type="text" id="dv-range" placeholder="e.g. B2:B100" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;color:#555;">Validation type:</label>
        <select id="dv-type" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
          <option value="list">Dropdown List</option>
          <option value="number">Number (between)</option>
          <option value="whole">Whole Number</option>
          <option value="decimal">Decimal</option>
          <option value="date">Date (between)</option>
          <option value="text-length">Text Length (max)</option>
          <option value="custom">Custom Formula</option>
        </select>
      </div>
      <div id="dv-options" style="margin-bottom:12px;"></div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;color:#555;">Error message (shown on invalid input):</label>
        <input type="text" id="dv-error-msg" value="Invalid entry" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
      </div>
      <div style="display:flex;gap:8px;">
        <button id="dv-apply" style="flex:1;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Add Rule</button>
        <button id="dv-clear" style="flex:1;padding:8px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Clear All Rules</button>
      </div>
      <div id="dv-rules-list" style="margin-top:12px;border-top:1px solid #e0e0e0;padding-top:8px;"></div>
      <div id="dv-status" style="margin-top:8px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('validation-close').onclick = () => dlg.remove();

    const optionsDiv = document.getElementById('dv-options');
    const typeSelect = document.getElementById('dv-type');

    const renderOptions = () => {
      const type = typeSelect.value;
      if (type === 'list') {
        optionsDiv.innerHTML = `
          <label style="font-size:12px;color:#555;">Comma-separated values:</label>
          <input type="text" id="dv-list-values" placeholder="Yes, No, Maybe" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
        `;
      } else if (type === 'number' || type === 'whole' || type === 'decimal') {
        optionsDiv.innerHTML = `
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
            <div><label style="font-size:12px;color:#555;">Min:</label><input type="number" id="dv-min" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;"></div>
            <div><label style="font-size:12px;color:#555;">Max:</label><input type="number" id="dv-max" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;"></div>
          </div>
        `;
      } else if (type === 'date') {
        optionsDiv.innerHTML = `
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
            <div><label style="font-size:12px;color:#555;">Start date:</label><input type="date" id="dv-date-start" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;"></div>
            <div><label style="font-size:12px;color:#555;">End date:</label><input type="date" id="dv-date-end" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;"></div>
          </div>
        `;
      } else if (type === 'text-length') {
        optionsDiv.innerHTML = `
          <label style="font-size:12px;color:#555;">Maximum characters:</label>
          <input type="number" id="dv-maxlen" value="100" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
        `;
      } else if (type === 'custom') {
        optionsDiv.innerHTML = `
          <label style="font-size:12px;color:#555;">Formula (must return TRUE):</label>
          <input type="text" id="dv-formula" placeholder="=AND(A1>0, A1<1000)" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
        `;
      }
    };

    typeSelect.addEventListener('change', renderOptions);
    renderOptions();

    const renderRulesList = () => {
      const list = document.getElementById('dv-rules-list');
      if (this._validationRules.length === 0) {
        list.innerHTML = '<div style="color:#888;font-size:12px;padding:4px;">No validation rules set.</div>';
        return;
      }
      list.innerHTML = this._validationRules.map((rule, i) => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid #f0f0f0;font-size:12px;">
          <span><strong>${rule.range}</strong> — ${rule.type} ${rule.detail || ''}</span>
          <button class="dv-remove" data-idx="${i}" style="padding:2px 6px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#fee2e2;color:#dc2626;">&times;</button>
        </div>
      `).join('');
      list.querySelectorAll('.dv-remove').forEach(btn => {
        btn.addEventListener('click', () => {
          this._validationRules.splice(parseInt(btn.dataset.idx), 1);
          renderRulesList();
        });
      });
    };

    renderRulesList();

    document.getElementById('dv-apply').onclick = () => {
      const range = document.getElementById('dv-range').value.trim().toUpperCase();
      const type = typeSelect.value;
      if (!range) {
        document.getElementById('dv-status').innerHTML = '<span style="color:#dc2626;">Specify a range.</span>';
        return;
      }

      let detail = '';
      if (type === 'list') {
        const vals = (document.getElementById('dv-list-values')?.value || '').trim();
        detail = `[${vals}]`;
      } else if (type === 'number' || type === 'whole' || type === 'decimal') {
        const min = document.getElementById('dv-min')?.value || '';
        const max = document.getElementById('dv-max')?.value || '';
        detail = `${min} to ${max}`;
      } else if (type === 'date') {
        const s = document.getElementById('dv-date-start')?.value || '';
        const e = document.getElementById('dv-date-end')?.value || '';
        detail = `${s} to ${e}`;
      } else if (type === 'text-length') {
        detail = `max ${document.getElementById('dv-maxlen')?.value || 100} chars`;
      } else if (type === 'custom') {
        detail = document.getElementById('dv-formula')?.value || '';
      }

      const errorMsg = document.getElementById('dv-error-msg').value.trim();
      this._validationRules.push({ range, type, detail, errorMsg });
      document.getElementById('dv-status').innerHTML = `<span style="color:#16a34a;">&#9989; Rule added: ${type} on ${range}</span>`;
      renderRulesList();
    };

    document.getElementById('dv-clear').onclick = () => {
      this._validationRules = [];
      renderRulesList();
      document.getElementById('dv-status').innerHTML = '<span style="color:#dc2626;">All rules cleared.</span>';
    };
  }

  // ── Comments & Notes ──
  _openComments() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-comments-dialog');
    if (old) old.remove();

    if (!this._cellComments) this._cellComments = {};

    const wb = this.univerAPI.getActiveWorkbook();
    const sheet = wb.getActiveSheet();
    const sheetName = sheet.getSheetName();
    if (!this._cellComments[sheetName]) this._cellComments[sheetName] = {};

    const comments = this._cellComments[sheetName];

    const dlg = document.createElement('div');
    dlg.id = 'grid-comments-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:480px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const renderComments = () => {
      const entries = Object.entries(comments);
      if (entries.length === 0) return '<div style="color:#888;font-size:12px;padding:8px;">No comments on this sheet.</div>';
      return entries.sort((a, b) => a[0].localeCompare(b[0])).map(([cell, data]) => `
        <div style="padding:8px;border-bottom:1px solid #f0f0f0;">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <strong style="font-size:13px;color:#1e40af;">${cell}</strong>
            <div style="display:flex;gap:4px;">
              <span style="font-size:10px;color:#999;">${data.time}</span>
              <button class="comment-delete" data-cell="${cell}" style="padding:2px 6px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#fee2e2;color:#dc2626;">&times;</button>
            </div>
          </div>
          <div style="font-size:12px;color:#444;margin-top:4px;white-space:pre-wrap;">${data.text}</div>
          ${data.replies && data.replies.length > 0 ? data.replies.map(r => `
            <div style="margin-left:16px;margin-top:6px;padding:6px;background:#f8f9fa;border-radius:4px;font-size:11px;color:#555;">
              <span style="color:#999;font-size:10px;">${r.time}</span><br>${r.text}
            </div>
          `).join('') : ''}
          <div style="margin-top:6px;">
            <input type="text" class="comment-reply-input" data-cell="${cell}" placeholder="Reply..." style="width:calc(100% - 60px);padding:4px;border:1px solid #ddd;border-radius:3px;font-size:11px;">
            <button class="comment-reply-btn" data-cell="${cell}" style="padding:4px 8px;background:#1e40af;color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:11px;">Reply</button>
          </div>
        </div>
      `).join('');
    };

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128172; Comments & Notes — ${sheetName}</h3>
        <button id="comments-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="display:grid;grid-template-columns:80px 1fr auto;gap:8px;margin-bottom:12px;">
        <input type="text" id="comment-cell" placeholder="A1" style="padding:6px;border:1px solid #ddd;border-radius:4px;font-size:13px;font-family:monospace;">
        <input type="text" id="comment-text" placeholder="Type your comment..." style="padding:6px;border:1px solid #ddd;border-radius:4px;font-size:13px;">
        <button id="comment-add" style="padding:6px 14px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Add</button>
      </div>
      <div id="comments-list" style="border:1px solid #e0e0e0;border-radius:4px;max-height:400px;overflow-y:auto;">
        ${renderComments()}
      </div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('comments-close').onclick = () => dlg.remove();

    const refreshComments = () => {
      document.getElementById('comments-list').innerHTML = renderComments();
      // Bind delete buttons
      dlg.querySelectorAll('.comment-delete').forEach(btn => {
        btn.addEventListener('click', () => {
          delete comments[btn.dataset.cell];
          refreshComments();
        });
      });
      // Bind reply buttons
      dlg.querySelectorAll('.comment-reply-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const cell = btn.dataset.cell;
          const input = dlg.querySelector(`.comment-reply-input[data-cell="${cell}"]`);
          const text = input?.value?.trim();
          if (!text) return;
          if (!comments[cell].replies) comments[cell].replies = [];
          comments[cell].replies.push({ text, time: new Date().toLocaleString() });
          input.value = '';
          refreshComments();
        });
      });
    };

    document.getElementById('comment-add').onclick = () => {
      const cell = document.getElementById('comment-cell').value.trim().toUpperCase();
      const text = document.getElementById('comment-text').value.trim();
      if (!cell || !text) return;
      comments[cell] = { text, time: new Date().toLocaleString(), replies: [] };
      document.getElementById('comment-cell').value = '';
      document.getElementById('comment-text').value = '';
      refreshComments();
    };

    refreshComments();
  }

  // ── Regex Find & Replace ──
  _openRegexFindReplace() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-regex-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-regex-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:480px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128270; Regex Find & Replace</h3>
        <button id="regex-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Find pattern (regex):</label>
        <input type="text" id="regex-find" placeholder="e.g. (\\d{3})-(\\d{4})" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;font-size:13px;">
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Replace with (use $1, $2 for capture groups):</label>
        <input type="text" id="regex-replace" placeholder="e.g. ($1) $2" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;font-size:13px;">
      </div>
      <div style="display:flex;gap:12px;margin-bottom:12px;">
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;">
          <input type="checkbox" id="regex-flag-i"> Case insensitive
        </label>
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;">
          <input type="checkbox" id="regex-flag-g" checked> Global (all matches)
        </label>
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;">
          <input type="checkbox" id="regex-flag-m"> Multiline
        </label>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px;">
        <button id="regex-preview" style="flex:1;padding:8px;background:#f59e0b;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Preview Matches</button>
        <button id="regex-apply" style="flex:1;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Replace All</button>
      </div>
      <div id="regex-results" style="max-height:250px;overflow-y:auto;border:1px solid #e0e0e0;border-radius:4px;padding:8px;font-size:12px;font-family:monospace;background:#fafafa;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('regex-close').onclick = () => dlg.remove();

    const getSheetData = () => {
      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      return sheet.getDataRange().getValues();
    };

    const buildRegex = () => {
      const pattern = document.getElementById('regex-find').value;
      if (!pattern) return null;
      let flags = '';
      if (document.getElementById('regex-flag-i').checked) flags += 'i';
      if (document.getElementById('regex-flag-g').checked) flags += 'g';
      if (document.getElementById('regex-flag-m').checked) flags += 'm';
      try {
        return new RegExp(pattern, flags);
      } catch (e) {
        document.getElementById('regex-results').innerHTML = `<span style="color:#dc2626;">Invalid regex: ${e.message}</span>`;
        return null;
      }
    };

    document.getElementById('regex-preview').onclick = () => {
      const regex = buildRegex();
      if (!regex) return;
      const data = getSheetData();
      const results = [];
      for (let r = 0; r < data.length; r++) {
        for (let c = 0; c < (data[r]?.length || 0); c++) {
          const val = String(data[r][c] ?? '');
          if (regex.test(val)) {
            regex.lastIndex = 0;
            const col = String.fromCharCode(65 + c);
            results.push(`<div style="padding:2px 0;border-bottom:1px solid #f0f0f0;"><strong>${col}${r + 1}</strong>: "${val}" → "${val.replace(regex, document.getElementById('regex-replace').value)}"</div>`);
          }
        }
      }
      document.getElementById('regex-results').innerHTML = results.length > 0
        ? `<div style="color:#16a34a;margin-bottom:6px;">${results.length} match(es) found:</div>` + results.join('')
        : '<span style="color:#888;">No matches found.</span>';
    };

    document.getElementById('regex-apply').onclick = () => {
      const regex = buildRegex();
      if (!regex) return;
      const replacement = document.getElementById('regex-replace').value;
      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      const data = sheet.getDataRange().getValues();
      let count = 0;
      for (let r = 0; r < data.length; r++) {
        for (let c = 0; c < (data[r]?.length || 0); c++) {
          const val = String(data[r][c] ?? '');
          regex.lastIndex = 0;
          if (regex.test(val)) {
            regex.lastIndex = 0;
            const newVal = val.replace(regex, replacement);
            sheet.getRange(r, c, 1, 1).setValue(newVal);
            count++;
          }
        }
      }
      document.getElementById('regex-results').innerHTML = `<span style="color:#16a34a;">&#9989; Replaced ${count} cell(s).</span>`;

      // Log to audit trail
      if (this._auditLog) {
        this._auditLog.push({ time: new Date().toLocaleString(), action: 'Regex Replace', detail: `/${document.getElementById('regex-find').value}/ → "${replacement}" (${count} cells)` });
      }
    };
  }

  // ── Auto-Fill / Flash Fill ──
  _openAutoFill() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-autofill-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-autofill-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:440px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#9889; Auto-Fill / Flash Fill</h3>
        <button id="autofill-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:12px;font-size:13px;color:#555;">
        Detects patterns in a column and fills down. Select the fill mode and target range.
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Source column (letter):</label>
        <input type="text" id="af-col" placeholder="A" maxlength="2" style="width:60px;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;text-transform:uppercase;">
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px;">
        <div>
          <label style="font-size:12px;color:#555;">Sample rows (from):</label>
          <input type="number" id="af-from" value="1" min="1" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
        </div>
        <div>
          <label style="font-size:12px;color:#555;">Sample rows (to):</label>
          <input type="number" id="af-to" value="3" min="1" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
        </div>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Fill down to row:</label>
        <input type="number" id="af-target" value="20" min="1" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:12px;color:#555;">Fill mode:</label>
        <select id="af-mode" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
          <option value="linear">Linear sequence (1, 2, 3...)</option>
          <option value="date">Date sequence (increment days)</option>
          <option value="repeat">Repeat pattern</option>
          <option value="flash">Flash Fill (learn from adjacent column)</option>
          <option value="growth">Growth (multiply by ratio)</option>
        </select>
      </div>
      <button id="af-apply" style="width:100%;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Fill</button>
      <div id="af-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('autofill-close').onclick = () => dlg.remove();

    document.getElementById('af-apply').onclick = () => {
      const colLetter = (document.getElementById('af-col').value || 'A').toUpperCase();
      const colIdx = colLetter.charCodeAt(0) - 65;
      const fromRow = parseInt(document.getElementById('af-from').value) - 1;
      const toRow = parseInt(document.getElementById('af-to').value) - 1;
      const targetRow = parseInt(document.getElementById('af-target').value) - 1;
      const mode = document.getElementById('af-mode').value;

      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      const data = sheet.getDataRange().getValues();

      // Get sample values
      const samples = [];
      for (let r = fromRow; r <= toRow && r < data.length; r++) {
        samples.push(data[r]?.[colIdx] ?? '');
      }

      if (samples.length === 0) {
        document.getElementById('af-status').innerHTML = '<span style="color:#dc2626;">No sample data found.</span>';
        return;
      }

      let filled = 0;

      if (mode === 'linear') {
        // Detect numeric step
        const nums = samples.map(Number).filter(n => !isNaN(n));
        const step = nums.length >= 2 ? nums[1] - nums[0] : 1;
        const start = nums.length > 0 ? nums[nums.length - 1] + step : 1;
        for (let r = toRow + 1; r <= targetRow; r++) {
          const val = start + (r - toRow - 1) * step;
          sheet.getRange(r, colIdx, 1, 1).setValue(val);
          filled++;
        }
      } else if (mode === 'date') {
        const lastSample = samples[samples.length - 1];
        const baseDate = new Date(lastSample);
        if (isNaN(baseDate.getTime())) {
          document.getElementById('af-status').innerHTML = '<span style="color:#dc2626;">Cannot parse date from sample.</span>';
          return;
        }
        // Detect day increment
        let dayStep = 1;
        if (samples.length >= 2) {
          const d1 = new Date(samples[samples.length - 2]);
          const d2 = new Date(samples[samples.length - 1]);
          if (!isNaN(d1.getTime())) dayStep = Math.round((d2 - d1) / 86400000) || 1;
        }
        for (let r = toRow + 1; r <= targetRow; r++) {
          const d = new Date(baseDate);
          d.setDate(d.getDate() + (r - toRow) * dayStep);
          sheet.getRange(r, colIdx, 1, 1).setValue(d.toISOString().split('T')[0]);
          filled++;
        }
      } else if (mode === 'repeat') {
        for (let r = toRow + 1; r <= targetRow; r++) {
          const val = samples[(r - toRow - 1) % samples.length];
          sheet.getRange(r, colIdx, 1, 1).setValue(val);
          filled++;
        }
      } else if (mode === 'flash') {
        // Flash Fill: learn transformation from source col (colIdx-1) → target col (colIdx)
        const srcCol = colIdx > 0 ? colIdx - 1 : colIdx + 1;
        const pairs = [];
        for (let r = fromRow; r <= toRow && r < data.length; r++) {
          const src = String(data[r]?.[srcCol] ?? '');
          const tgt = String(data[r]?.[colIdx] ?? '');
          if (src && tgt) pairs.push({ src, tgt });
        }
        if (pairs.length === 0) {
          document.getElementById('af-status').innerHTML = '<span style="color:#dc2626;">Need source+target pairs for Flash Fill.</span>';
          return;
        }
        // Simple pattern: detect substring extraction
        const p = pairs[0];
        let transform = null;
        // Check if target is a substring of source
        const startIdx = p.src.indexOf(p.tgt);
        if (startIdx >= 0) {
          transform = (s) => s.substring(startIdx, startIdx + p.tgt.length);
        }
        // Check if target is first word
        if (!transform && p.tgt === p.src.split(' ')[0]) {
          transform = (s) => s.split(' ')[0];
        }
        // Check if target is last word
        if (!transform && p.tgt === p.src.split(' ').pop()) {
          transform = (s) => s.split(' ').pop();
        }
        // Check uppercase/lowercase transform
        if (!transform && p.tgt === p.src.toUpperCase()) {
          transform = (s) => s.toUpperCase();
        }
        if (!transform && p.tgt === p.src.toLowerCase()) {
          transform = (s) => s.toLowerCase();
        }
        // Check first N chars
        if (!transform && p.src.startsWith(p.tgt)) {
          const len = p.tgt.length;
          transform = (s) => s.substring(0, len);
        }
        if (!transform) {
          // Fallback: use first pair's pattern ratio
          transform = (s) => s;
        }

        for (let r = toRow + 1; r <= targetRow; r++) {
          const srcVal = String(data[r]?.[srcCol] ?? '');
          if (srcVal) {
            sheet.getRange(r, colIdx, 1, 1).setValue(transform(srcVal));
            filled++;
          }
        }
      } else if (mode === 'growth') {
        const nums = samples.map(Number).filter(n => !isNaN(n) && n !== 0);
        const ratio = nums.length >= 2 ? nums[1] / nums[0] : 2;
        let last = nums.length > 0 ? nums[nums.length - 1] : 1;
        for (let r = toRow + 1; r <= targetRow; r++) {
          last *= ratio;
          sheet.getRange(r, colIdx, 1, 1).setValue(Math.round(last * 100) / 100);
          filled++;
        }
      }

      document.getElementById('af-status').innerHTML = `<span style="color:#16a34a;">&#9989; Filled ${filled} cell(s) using ${mode} mode.</span>`;
    };
  }

  // ── Audit Trail ──
  _openAuditTrail() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-audit-dialog');
    if (old) old.remove();

    if (!this._auditLog) this._auditLog = [];

    const dlg = document.createElement('div');
    dlg.id = 'grid-audit-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:520px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const renderLog = () => {
      if (this._auditLog.length === 0) return '<div style="color:#888;font-size:12px;padding:8px;">No changes tracked yet. Changes will appear here as you work.</div>';
      return this._auditLog.slice().reverse().map((entry, i) => `
        <div style="padding:8px;border-bottom:1px solid #f0f0f0;${i === 0 ? 'background:#f0fdf4;' : ''}">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <strong style="font-size:13px;color:#1e40af;">${entry.action}</strong>
            <span style="font-size:10px;color:#999;">${entry.time}</span>
          </div>
          <div style="font-size:12px;color:#444;margin-top:4px;">${entry.detail}</div>
        </div>
      `).join('');
    };

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128221; Audit Trail</h3>
        <div style="display:flex;gap:8px;">
          <button id="audit-clear" style="padding:4px 10px;background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;border-radius:4px;cursor:pointer;font-size:11px;">Clear Log</button>
          <button id="audit-export" style="padding:4px 10px;background:#e8f0fe;color:#1e40af;border:1px solid #93c5fd;border-radius:4px;cursor:pointer;font-size:11px;">Export CSV</button>
          <button id="audit-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
        </div>
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:6px;">
          <input type="checkbox" id="audit-auto-track" ${this._auditAutoTrack ? 'checked' : ''}>
          <span>Auto-track cell edits (monitors all changes)</span>
        </label>
      </div>
      <div style="font-size:11px;color:#888;margin-bottom:8px;">${this._auditLog.length} entries</div>
      <div id="audit-log-list" style="border:1px solid #e0e0e0;border-radius:4px;max-height:400px;overflow-y:auto;">
        ${renderLog()}
      </div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('audit-close').onclick = () => dlg.remove();

    document.getElementById('audit-clear').onclick = () => {
      this._auditLog = [];
      document.getElementById('audit-log-list').innerHTML = renderLog();
    };

    document.getElementById('audit-auto-track').onchange = (e) => {
      this._auditAutoTrack = e.target.checked;
      if (this._auditAutoTrack && !this._auditHooked) {
        this._auditHooked = true;
        // Hook into cell value changes for auto-tracking
        // Store a snapshot of the current state for change detection
        this._auditSnapshot = JSON.stringify(this.univerAPI.getActiveWorkbook().getActiveSheet().getDataRange().getValues());
        // Poll for changes every 5 seconds
        this._auditInterval = setInterval(() => {
          if (!this._auditAutoTrack) return;
          try {
            const current = JSON.stringify(this.univerAPI.getActiveWorkbook().getActiveSheet().getDataRange().getValues());
            if (current !== this._auditSnapshot) {
              this._auditLog.push({ time: new Date().toLocaleString(), action: 'Cell Edit', detail: 'Sheet data changed (auto-detected)' });
              this._auditSnapshot = current;
            }
          } catch (e) { /* ignore */ }
        }, 5000);
      }
    };

    document.getElementById('audit-export').onclick = () => {
      if (this._auditLog.length === 0) return;
      const csv = 'Time,Action,Detail\n' + this._auditLog.map(e =>
        `"${e.time}","${e.action}","${(e.detail || '').replace(/"/g, '""')}"`
      ).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'audit-trail.csv';
      a.click();
    };
  }

  // ── Sheet Tab Manager ──
  _openSheetManager() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-sheets-dialog');
    if (old) old.remove();

    const wb = this.univerAPI.getActiveWorkbook();

    const dlg = document.createElement('div');
    dlg.id = 'grid-sheets-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:420px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const getSheets = () => {
      try {
        // Try to get sheets list
        const snapshot = wb.getSnapshot();
        if (snapshot && snapshot.sheets) {
          return Object.values(snapshot.sheets).map(s => ({
            id: s.id,
            name: s.name || 'Sheet',
          }));
        }
      } catch (e) { /* fallback */ }
      // Fallback: just show active sheet
      const active = wb.getActiveSheet();
      return [{ id: active.getSheetId(), name: active.getSheetName() }];
    };

    const renderSheets = () => {
      const sheets = getSheets();
      return sheets.map((s, i) => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 10px;border-bottom:1px solid #f0f0f0;">
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-size:11px;color:#999;width:20px;text-align:center;">${i + 1}</span>
            <strong style="font-size:13px;color:#1e40af;">${s.name}</strong>
          </div>
          <div style="display:flex;gap:4px;">
            <button class="sheet-activate" data-id="${s.id}" style="padding:3px 8px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#f8f8f8;">Go</button>
            <button class="sheet-rename" data-id="${s.id}" data-name="${s.name}" style="padding:3px 8px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#f8f8f8;">Rename</button>
            <button class="sheet-delete" data-id="${s.id}" data-name="${s.name}" style="padding:3px 8px;border:1px solid #fca5a5;border-radius:3px;cursor:pointer;font-size:11px;background:#fee2e2;color:#dc2626;" title="Delete sheet">&#10005;</button>
          </div>
        </div>
      `).join('');
    };

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128196; Sheet Manager</h3>
        <button id="sheets-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px;">
        <input type="text" id="sheet-new-name" placeholder="New sheet name" style="flex:1;padding:6px;border:1px solid #ddd;border-radius:4px;font-size:13px;">
        <button id="sheet-add" style="padding:6px 14px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">+ Add Sheet</button>
      </div>
      <div id="sheets-list" style="border:1px solid #e0e0e0;border-radius:4px;max-height:300px;overflow-y:auto;">
        ${renderSheets()}
      </div>
      <div id="sheets-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('sheets-close').onclick = () => dlg.remove();

    const refreshSheets = () => {
      document.getElementById('sheets-list').innerHTML = renderSheets();
      // Bind activate/rename
      dlg.querySelectorAll('.sheet-activate').forEach(btn => {
        btn.addEventListener('click', () => {
          try {
            const sheet = wb.getSheetBySheetId(btn.dataset.id);
            if (sheet) {
              wb.setActiveSheet(sheet);
              document.getElementById('sheets-status').innerHTML = `<span style="color:#16a34a;">Switched to "${sheet.getSheetName()}"</span>`;
            }
          } catch (e) {
            document.getElementById('sheets-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
          }
        });
      });
      dlg.querySelectorAll('.sheet-rename').forEach(btn => {
        btn.addEventListener('click', () => {
          const newName = prompt('New name:', btn.dataset.name);
          if (newName) {
            try {
              const sheet = wb.getSheetBySheetId(btn.dataset.id);
              if (sheet && sheet.setName) {
                sheet.setName(newName);
                refreshSheets();
                document.getElementById('sheets-status').innerHTML = `<span style="color:#16a34a;">Renamed to "${newName}"</span>`;
              }
            } catch (e) {
              document.getElementById('sheets-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
            }
          }
        });
      });
      dlg.querySelectorAll('.sheet-delete').forEach(btn => {
        btn.addEventListener('click', () => {
          const sheets = getSheets();
          if (sheets.length <= 1) {
            document.getElementById('sheets-status').innerHTML = '<span style="color:#dc2626;">Cannot delete the only sheet.</span>';
            return;
          }
          if (!confirm(`Delete sheet "${btn.dataset.name}"? This cannot be undone.`)) return;
          try {
            const sheet = wb.getSheetBySheetId(btn.dataset.id);
            if (sheet && sheet.delete) {
              sheet.delete();
            } else if (wb.removeSheet) {
              wb.removeSheet(btn.dataset.id);
            }
            refreshSheets();
            document.getElementById('sheets-status').innerHTML = `<span style="color:#16a34a;">Deleted "${btn.dataset.name}"</span>`;
            if (this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Delete Sheet', detail: btn.dataset.name });
          } catch (e) {
            document.getElementById('sheets-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
          }
        });
      });
    };

    document.getElementById('sheet-add').onclick = () => {
      const name = document.getElementById('sheet-new-name').value.trim() || `Sheet ${getSheets().length + 1}`;
      try {
        wb.create(name, { name });
        document.getElementById('sheet-new-name').value = '';
        refreshSheets();
        document.getElementById('sheets-status').innerHTML = `<span style="color:#16a34a;">&#9989; Added "${name}"</span>`;
      } catch (e) {
        document.getElementById('sheets-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
      }
    };

    refreshSheets();
  }

  // ── Merge / Split Cells ──
  _openMergeCells() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-merge-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-merge-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:380px;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#11036; Merge / Split Cells</h3>
        <button id="merge-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:12px;font-size:13px;color:#555;">
        Enter the range to merge. Only the top-left cell value is kept.
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Range to merge:</label>
        <input type="text" id="merge-range" placeholder="e.g. A1:C1" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;font-size:13px;">
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px;">
        <button id="merge-horizontal" style="flex:1;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Merge All</button>
        <button id="merge-across" style="flex:1;padding:8px;background:#7c3aed;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Merge Across</button>
        <button id="merge-unmerge" style="flex:1;padding:8px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Unmerge</button>
      </div>
      <div id="merge-status" style="font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('merge-close').onclick = () => dlg.remove();

    const parseRange = (rangeStr) => {
      const match = rangeStr.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i);
      if (!match) return null;
      const c1 = match[1].toUpperCase().charCodeAt(0) - 65;
      const r1 = parseInt(match[2]) - 1;
      const c2 = match[3].toUpperCase().charCodeAt(0) - 65;
      const r2 = parseInt(match[4]) - 1;
      return { startRow: Math.min(r1, r2), startColumn: Math.min(c1, c2), endRow: Math.max(r1, r2), endColumn: Math.max(c1, c2) };
    };

    const doMerge = (type) => {
      const rangeStr = document.getElementById('merge-range').value.trim().toUpperCase();
      const range = parseRange(rangeStr);
      if (!range) {
        document.getElementById('merge-status').innerHTML = '<span style="color:#dc2626;">Invalid range. Use format like A1:C3.</span>';
        return;
      }

      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();

      try {
        const r = sheet.getRange(range.startRow, range.startColumn, range.endRow - range.startRow + 1, range.endColumn - range.startColumn + 1);

        if (type === 'merge') {
          if (r.merge) {
            r.merge();
            document.getElementById('merge-status').innerHTML = `<span style="color:#16a34a;">&#9989; Merged ${rangeStr}</span>`;
          } else {
            // Fallback: visual merge via storing merge info
            if (!this._mergedRanges) this._mergedRanges = [];
            this._mergedRanges.push(range);
            document.getElementById('merge-status').innerHTML = `<span style="color:#f59e0b;">&#9888; Merge recorded (API fallback). ${rangeStr}</span>`;
          }
        } else if (type === 'across') {
          // Merge each row independently
          for (let row = range.startRow; row <= range.endRow; row++) {
            const rowRange = sheet.getRange(row, range.startColumn, 1, range.endColumn - range.startColumn + 1);
            if (rowRange.merge) rowRange.merge();
          }
          document.getElementById('merge-status').innerHTML = `<span style="color:#16a34a;">&#9989; Merged across rows in ${rangeStr}</span>`;
        } else if (type === 'unmerge') {
          if (r.unmerge) {
            r.unmerge();
            document.getElementById('merge-status').innerHTML = `<span style="color:#16a34a;">&#9989; Unmerged ${rangeStr}</span>`;
          } else {
            if (this._mergedRanges) {
              this._mergedRanges = this._mergedRanges.filter(m =>
                !(m.startRow === range.startRow && m.startColumn === range.startColumn && m.endRow === range.endRow && m.endColumn === range.endColumn)
              );
            }
            document.getElementById('merge-status').innerHTML = `<span style="color:#16a34a;">&#9989; Unmerged ${rangeStr}</span>`;
          }
        }

        // Log to audit trail
        if (this._auditLog) {
          this._auditLog.push({ time: new Date().toLocaleString(), action: `Cell ${type}`, detail: rangeStr });
        }
      } catch (e) {
        document.getElementById('merge-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
      }
    };

    document.getElementById('merge-horizontal').onclick = () => doMerge('merge');
    document.getElementById('merge-across').onclick = () => doMerge('across');
    document.getElementById('merge-unmerge').onclick = () => doMerge('unmerge');
  }

  // ── Hide / Unhide Rows & Columns ──
  _openHideRowsCols() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-hiderows-dialog');
    if (old) old.remove();

    if (!this._hiddenRows) this._hiddenRows = new Set();
    if (!this._hiddenCols) this._hiddenCols = new Set();

    const dlg = document.createElement('div');
    dlg.id = 'grid-hiderows-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:400px;max-height:70vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const renderState = () => {
      const hiddenR = this._hiddenRows.size > 0 ? [...this._hiddenRows].sort((a,b) => a-b).join(', ') : 'None';
      const hiddenC = this._hiddenCols.size > 0 ? [...this._hiddenCols].map(c => String.fromCharCode(65 + c)).sort().join(', ') : 'None';
      return `<div style="font-size:12px;color:#555;margin-bottom:8px;">Hidden rows: <strong>${hiddenR}</strong></div>
              <div style="font-size:12px;color:#555;">Hidden columns: <strong>${hiddenC}</strong></div>`;
    };

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128064; Hide / Unhide Rows & Columns</h3>
        <button id="hiderows-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:12px;color:#555;">Row numbers (comma-separated):</label>
        <input type="text" id="hide-rows-input" placeholder="e.g. 1, 5, 10-15" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px;">
        <button id="hide-rows-btn" style="flex:1;padding:6px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Hide Rows</button>
        <button id="unhide-rows-btn" style="flex:1;padding:6px;background:#16a34a;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Unhide Rows</button>
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:12px;color:#555;">Column letters (comma-separated):</label>
        <input type="text" id="hide-cols-input" placeholder="e.g. B, D, F-H" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;text-transform:uppercase;">
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px;">
        <button id="hide-cols-btn" style="flex:1;padding:6px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Hide Columns</button>
        <button id="unhide-cols-btn" style="flex:1;padding:6px;background:#16a34a;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Unhide Columns</button>
      </div>
      <button id="unhide-all-btn" style="width:100%;padding:6px;background:#dc2626;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;margin-bottom:12px;">Unhide All</button>
      <div id="hiderows-state">${renderState()}</div>
      <div id="hiderows-status" style="margin-top:8px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('hiderows-close').onclick = () => dlg.remove();

    const parseRangeList = (str, isCol) => {
      const result = [];
      str.split(',').forEach(part => {
        part = part.trim();
        if (!part) return;
        if (part.includes('-')) {
          const [a, b] = part.split('-').map(s => s.trim());
          if (isCol) {
            const s = a.toUpperCase().charCodeAt(0) - 65;
            const e = b.toUpperCase().charCodeAt(0) - 65;
            for (let i = Math.min(s, e); i <= Math.max(s, e); i++) result.push(i);
          } else {
            const s = parseInt(a), e = parseInt(b);
            for (let i = Math.min(s, e); i <= Math.max(s, e); i++) result.push(i);
          }
        } else {
          if (isCol) result.push(part.toUpperCase().charCodeAt(0) - 65);
          else result.push(parseInt(part));
        }
      });
      return result.filter(n => !isNaN(n));
    };

    const wb = this.univerAPI.getActiveWorkbook();
    const sheet = wb.getActiveSheet();

    document.getElementById('hide-rows-btn').onclick = () => {
      const rows = parseRangeList(document.getElementById('hide-rows-input').value, false);
      rows.forEach(r => {
        this._hiddenRows.add(r);
        try { if (sheet.hideRow) sheet.hideRow(r - 1); } catch (e) { /* fallback */ }
      });
      document.getElementById('hiderows-state').innerHTML = renderState();
      document.getElementById('hiderows-status').innerHTML = `<span style="color:#16a34a;">Hid ${rows.length} row(s)</span>`;
    };

    document.getElementById('unhide-rows-btn').onclick = () => {
      const rows = parseRangeList(document.getElementById('hide-rows-input').value, false);
      rows.forEach(r => {
        this._hiddenRows.delete(r);
        try { if (sheet.showRow) sheet.showRow(r - 1); } catch (e) { /* fallback */ }
      });
      document.getElementById('hiderows-state').innerHTML = renderState();
      document.getElementById('hiderows-status').innerHTML = `<span style="color:#16a34a;">Unhid ${rows.length} row(s)</span>`;
    };

    document.getElementById('hide-cols-btn').onclick = () => {
      const cols = parseRangeList(document.getElementById('hide-cols-input').value, true);
      cols.forEach(c => {
        this._hiddenCols.add(c);
        try { if (sheet.hideColumn) sheet.hideColumn(c); } catch (e) { /* fallback */ }
      });
      document.getElementById('hiderows-state').innerHTML = renderState();
      document.getElementById('hiderows-status').innerHTML = `<span style="color:#16a34a;">Hid ${cols.length} column(s)</span>`;
    };

    document.getElementById('unhide-cols-btn').onclick = () => {
      const cols = parseRangeList(document.getElementById('hide-cols-input').value, true);
      cols.forEach(c => {
        this._hiddenCols.delete(c);
        try { if (sheet.showColumn) sheet.showColumn(c); } catch (e) { /* fallback */ }
      });
      document.getElementById('hiderows-state').innerHTML = renderState();
      document.getElementById('hiderows-status').innerHTML = `<span style="color:#16a34a;">Unhid ${cols.length} column(s)</span>`;
    };

    document.getElementById('unhide-all-btn').onclick = () => {
      this._hiddenRows.forEach(r => { try { if (sheet.showRow) sheet.showRow(r - 1); } catch (e) { /* */ } });
      this._hiddenCols.forEach(c => { try { if (sheet.showColumn) sheet.showColumn(c); } catch (e) { /* */ } });
      this._hiddenRows.clear();
      this._hiddenCols.clear();
      document.getElementById('hiderows-state').innerHTML = renderState();
      document.getElementById('hiderows-status').innerHTML = '<span style="color:#16a34a;">All rows and columns visible.</span>';
    };
  }

  // ── Transpose Data ──
  _transposeData() {
    if (!this.univerAPI) return;
    const wb = this.univerAPI.getActiveWorkbook();
    const sheet = wb.getActiveSheet();
    const data = sheet.getDataRange().getValues();

    // Find actual data bounds
    let maxRow = 0, maxCol = 0;
    for (let r = 0; r < data.length; r++) {
      for (let c = 0; c < (data[r]?.length || 0); c++) {
        if (data[r][c] !== null && data[r][c] !== undefined && data[r][c] !== '') {
          if (r > maxRow) maxRow = r;
          if (c > maxCol) maxCol = c;
        }
      }
    }

    // Build transposed cellData
    const cellData = {};
    for (let r = 0; r <= maxRow; r++) {
      for (let c = 0; c <= maxCol; c++) {
        const val = data[r]?.[c];
        if (val !== null && val !== undefined && val !== '') {
          if (!cellData[c]) cellData[c] = {};
          cellData[c][r] = { v: val };
        }
      }
    }

    this.univerAPI.createWorkbook({
      name: 'Transposed',
      sheets: { sheet1: { name: 'Transposed', cellData } }
    });

    if (this._auditLog) {
      this._auditLog.push({ time: new Date().toLocaleString(), action: 'Transpose', detail: `${maxRow + 1} rows x ${maxCol + 1} cols → ${maxCol + 1} rows x ${maxRow + 1} cols` });
    }
  }

  // ── Remove Duplicates ──
  _openRemoveDuplicates() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-dedup-dialog');
    if (old) old.remove();

    const wb = this.univerAPI.getActiveWorkbook();
    const sheet = wb.getActiveSheet();
    const data = sheet.getDataRange().getValues();
    if (!data || data.length === 0) return;

    const headers = (data[0] || []).map((v, i) => v || `Column ${String.fromCharCode(65 + i)}`);

    const dlg = document.createElement('div');
    dlg.id = 'grid-dedup-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:380px;max-height:70vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128465; Remove Duplicates</h3>
        <button id="dedup-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:8px;font-size:13px;color:#555;">Select columns to check for duplicates:</div>
      <div id="dedup-cols" style="max-height:200px;overflow-y:auto;border:1px solid #e0e0e0;border-radius:4px;padding:8px;margin-bottom:12px;">
        ${headers.map((h, i) => `
          <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:#333;padding:3px 0;">
            <input type="checkbox" class="dedup-col-check" data-col="${i}" checked> ${h}
          </label>
        `).join('')}
      </div>
      <label style="display:flex;align-items:center;gap:6px;font-size:12px;color:#555;margin-bottom:12px;">
        <input type="checkbox" id="dedup-has-header" checked> First row is header
      </label>
      <div style="display:flex;gap:8px;">
        <button id="dedup-preview" style="flex:1;padding:8px;background:#f59e0b;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Preview</button>
        <button id="dedup-apply" style="flex:1;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Remove</button>
      </div>
      <div id="dedup-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('dedup-close').onclick = () => dlg.remove();

    const getCheckedCols = () => {
      return [...dlg.querySelectorAll('.dedup-col-check:checked')].map(cb => parseInt(cb.dataset.col));
    };

    const findDuplicates = () => {
      const cols = getCheckedCols();
      const hasHeader = document.getElementById('dedup-has-header').checked;
      const startRow = hasHeader ? 1 : 0;
      const seen = new Set();
      const dupeRows = [];
      const uniqueRows = hasHeader ? [data[0]] : [];

      for (let r = startRow; r < data.length; r++) {
        const key = cols.map(c => String(data[r]?.[c] ?? '')).join('|||');
        if (seen.has(key)) {
          dupeRows.push(r);
        } else {
          seen.add(key);
          uniqueRows.push(data[r]);
        }
      }
      return { dupeRows, uniqueRows, totalChecked: data.length - startRow };
    };

    document.getElementById('dedup-preview').onclick = () => {
      const { dupeRows, totalChecked } = findDuplicates();
      document.getElementById('dedup-status').innerHTML = `<span style="color:#f59e0b;">${dupeRows.length} duplicate(s) found out of ${totalChecked} rows.</span>`;
    };

    document.getElementById('dedup-apply').onclick = () => {
      const { dupeRows, uniqueRows } = findDuplicates();
      if (dupeRows.length === 0) {
        document.getElementById('dedup-status').innerHTML = '<span style="color:#16a34a;">No duplicates found.</span>';
        return;
      }

      // Output unique rows to new workbook
      const cellData = {};
      uniqueRows.forEach((row, r) => {
        cellData[r] = {};
        (row || []).forEach((val, c) => {
          if (val !== null && val !== undefined && val !== '') {
            cellData[r][c] = { v: val };
          }
        });
      });

      this.univerAPI.createWorkbook({
        name: 'Deduplicated',
        sheets: { sheet1: { name: 'Deduplicated', cellData } }
      });

      document.getElementById('dedup-status').innerHTML = `<span style="color:#16a34a;">&#9989; Removed ${dupeRows.length} duplicate(s). ${uniqueRows.length} unique rows in new sheet.</span>`;

      if (this._auditLog) {
        this._auditLog.push({ time: new Date().toLocaleString(), action: 'Remove Duplicates', detail: `${dupeRows.length} duplicates removed` });
      }
    };
  }

  // ── Text to Columns ──
  _openTextToColumns() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-text2col-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-text2col-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:400px;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#9986; Text to Columns</h3>
        <button id="text2col-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Source column (letter):</label>
        <input type="text" id="t2c-col" placeholder="A" maxlength="2" style="width:60px;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;text-transform:uppercase;">
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Delimiter:</label>
        <select id="t2c-delim" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
          <option value=",">Comma (,)</option>
          <option value=";">Semicolon (;)</option>
          <option value="\t">Tab</option>
          <option value=" ">Space</option>
          <option value="|">Pipe (|)</option>
          <option value="-">Hyphen (-)</option>
          <option value="custom">Custom...</option>
        </select>
      </div>
      <div id="t2c-custom-wrap" style="display:none;margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Custom delimiter:</label>
        <input type="text" id="t2c-custom" placeholder="e.g. ::" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <label style="display:flex;align-items:center;gap:6px;font-size:12px;color:#555;margin-bottom:12px;">
        <input type="checkbox" id="t2c-trim" checked> Trim whitespace from results
      </label>
      <button id="t2c-apply" style="width:100%;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Split to Columns</button>
      <div id="t2c-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('text2col-close').onclick = () => dlg.remove();

    document.getElementById('t2c-delim').addEventListener('change', (e) => {
      document.getElementById('t2c-custom-wrap').style.display = e.target.value === 'custom' ? 'block' : 'none';
    });

    document.getElementById('t2c-apply').onclick = () => {
      const colLetter = (document.getElementById('t2c-col').value || 'A').toUpperCase();
      const colIdx = colLetter.charCodeAt(0) - 65;
      const delimSelect = document.getElementById('t2c-delim').value;
      const delim = delimSelect === 'custom' ? (document.getElementById('t2c-custom').value || ',') : delimSelect;
      const trim = document.getElementById('t2c-trim').checked;

      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      const data = sheet.getDataRange().getValues();

      const cellData = {};
      let maxCols = 0;

      data.forEach((row, r) => {
        const val = String(row?.[colIdx] ?? '');
        const parts = val.split(delim);
        if (parts.length > maxCols) maxCols = parts.length;

        cellData[r] = {};
        parts.forEach((part, c) => {
          const v = trim ? part.trim() : part;
          cellData[r][c] = { v };
        });
      });

      this.univerAPI.createWorkbook({
        name: 'Split',
        sheets: { sheet1: { name: 'Split Result', cellData } }
      });

      document.getElementById('t2c-status').innerHTML = `<span style="color:#16a34a;">&#9989; Split ${data.length} rows into up to ${maxCols} columns.</span>`;

      if (this._auditLog) {
        this._auditLog.push({ time: new Date().toLocaleString(), action: 'Text to Columns', detail: `Column ${colLetter}, delimiter: "${delim}", ${data.length} rows → ${maxCols} cols` });
      }
    };
  }

  // ── Cell Styles & Themes ──
  _openCellStyles() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-cellstyles-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-cellstyles-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:460px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const presets = [
      { name: 'Header Blue', bg: '#1e40af', fg: '#ffffff', bold: true, desc: 'White text on blue' },
      { name: 'Header Dark', bg: '#1f2937', fg: '#ffffff', bold: true, desc: 'White text on dark gray' },
      { name: 'Header Green', bg: '#15803d', fg: '#ffffff', bold: true, desc: 'White text on green' },
      { name: 'Light Blue Band', bg: '#dbeafe', fg: '#1e3a5f', bold: false, desc: 'Light blue background' },
      { name: 'Light Green Band', bg: '#dcfce7', fg: '#14532d', bold: false, desc: 'Light green background' },
      { name: 'Light Yellow Band', bg: '#fef9c3', fg: '#713f12', bold: false, desc: 'Light yellow background' },
      { name: 'Warning Red', bg: '#fee2e2', fg: '#991b1b', bold: true, desc: 'Red alert style' },
      { name: 'Success Green', bg: '#d1fae5', fg: '#065f46', bold: false, desc: 'Green success style' },
      { name: 'Total Row', bg: '#f3f4f6', fg: '#111827', bold: true, desc: 'Gray total row' },
      { name: 'Accent Purple', bg: '#ede9fe', fg: '#5b21b6', bold: false, desc: 'Purple accent' },
      { name: 'Neutral', bg: '#ffffff', fg: '#000000', bold: false, desc: 'Reset to default' },
    ];

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#127912; Cell Styles & Themes</h3>
        <button id="cellstyles-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Apply to range:</label>
        <input type="text" id="cs-range" placeholder="e.g. A1:F1" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <div style="font-size:13px;font-weight:600;color:#333;margin-bottom:8px;">Quick Presets:</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:16px;">
        ${presets.map((p, i) => `
          <button class="cs-preset" data-idx="${i}" style="display:flex;align-items:center;gap:8px;padding:8px;border:1px solid #e0e0e0;border-radius:6px;cursor:pointer;background:#fff;text-align:left;transition:all 0.1s;" title="${p.desc}">
            <div style="width:28px;height:28px;border-radius:4px;background:${p.bg};border:1px solid #ccc;flex-shrink:0;display:flex;align-items:center;justify-content:center;color:${p.fg};font-size:11px;font-weight:${p.bold ? '700' : '400'};">Ab</div>
            <span style="font-size:12px;color:#333;">${p.name}</span>
          </button>
        `).join('')}
      </div>
      <div style="font-size:13px;font-weight:600;color:#333;margin-bottom:8px;">Custom Style:</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px;">
        <div>
          <label style="font-size:11px;color:#555;">Background:</label>
          <input type="color" id="cs-bg" value="#ffffff" style="width:100%;height:30px;border:1px solid #ddd;border-radius:4px;cursor:pointer;">
        </div>
        <div>
          <label style="font-size:11px;color:#555;">Text color:</label>
          <input type="color" id="cs-fg" value="#000000" style="width:100%;height:30px;border:1px solid #ddd;border-radius:4px;cursor:pointer;">
        </div>
      </div>
      <div style="display:flex;gap:12px;margin-bottom:12px;">
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;"><input type="checkbox" id="cs-bold"> Bold</label>
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;"><input type="checkbox" id="cs-italic"> Italic</label>
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:11px;color:#555;">Border:</label>
        <select id="cs-border" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
          <option value="none">None</option>
          <option value="thin">Thin (all sides)</option>
          <option value="medium">Medium (all sides)</option>
          <option value="thick">Thick (all sides)</option>
          <option value="bottom">Bottom border only</option>
          <option value="top-bottom">Top + Bottom</option>
        </select>
      </div>
      <button id="cs-apply-custom" style="width:100%;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Apply Custom Style</button>
      <div id="cs-status" style="margin-top:10px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('cellstyles-close').onclick = () => dlg.remove();

    const parseRange = (rangeStr) => {
      const match = rangeStr.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i);
      if (!match) return null;
      return {
        startRow: parseInt(match[2]) - 1,
        startColumn: match[1].toUpperCase().charCodeAt(0) - 65,
        endRow: parseInt(match[4]) - 1,
        endColumn: match[3].toUpperCase().charCodeAt(0) - 65,
      };
    };

    const applyStyle = (bg, fg, bold, italic, border) => {
      const rangeStr = document.getElementById('cs-range').value.trim().toUpperCase();
      const range = parseRange(rangeStr);
      if (!range) {
        document.getElementById('cs-status').innerHTML = '<span style="color:#dc2626;">Enter a valid range (e.g. A1:F1).</span>';
        return;
      }

      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();

      try {
        const r = sheet.getRange(range.startRow, range.startColumn, range.endRow - range.startRow + 1, range.endColumn - range.startColumn + 1);

        const style = {};
        if (bg && bg !== '#ffffff') style.bg = { rgb: bg };
        if (fg) style.cl = { rgb: fg };
        if (bold) style.bl = 1;
        if (italic) style.it = 1;

        if (border && border !== 'none') {
          const borderStyle = border === 'thick' ? 2 : border === 'medium' ? 1 : 0;
          const bObj = { s: borderStyle, cl: { rgb: '#000000' } };
          if (border === 'bottom') {
            style.bd = { b: bObj };
          } else if (border === 'top-bottom') {
            style.bd = { t: bObj, b: bObj };
          } else {
            style.bd = { t: bObj, r: bObj, b: bObj, l: bObj };
          }
        }

        if (r.setStyle) {
          r.setStyle(style);
        } else if (r.setBackgroundColor) {
          // Fallback: basic styling
          if (bg) r.setBackgroundColor(bg);
          if (fg) r.setFontColor(fg);
          if (bold) r.setFontWeight('bold');
        }

        document.getElementById('cs-status').innerHTML = `<span style="color:#16a34a;">&#9989; Style applied to ${rangeStr}</span>`;
      } catch (e) {
        document.getElementById('cs-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
      }
    };

    // Preset buttons
    dlg.querySelectorAll('.cs-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        const p = presets[parseInt(btn.dataset.idx)];
        applyStyle(p.bg, p.fg, p.bold, false, 'none');
      });
      btn.addEventListener('mouseenter', () => { btn.style.borderColor = '#1e40af'; btn.style.background = '#f8faff'; });
      btn.addEventListener('mouseleave', () => { btn.style.borderColor = '#e0e0e0'; btn.style.background = '#fff'; });
    });

    document.getElementById('cs-apply-custom').onclick = () => {
      const bg = document.getElementById('cs-bg').value;
      const fg = document.getElementById('cs-fg').value;
      const bold = document.getElementById('cs-bold').checked;
      const italic = document.getElementById('cs-italic').checked;
      const border = document.getElementById('cs-border').value;
      applyStyle(bg, fg, bold, italic, border);
    };
  }

  // ── Open Any Format (SheetJS CE — ODS, XLS, XLSB, HTML, DIF, etc.) ──
  _openAnyFormat() {
    if (!window.XLSX) { alert('SheetJS library not loaded.'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.ods,.xls,.xlsb,.xlsm,.xlsx,.csv,.tsv,.dif,.slk,.html,.htm,.dbf,.prn,.txt';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const data = await file.arrayBuffer();
        const wb = window.XLSX.read(data, { type: 'array', cellDates: true, cellStyles: true });
        // Convert each sheet to Univer workbook
        wb.SheetNames.forEach((sheetName) => {
          const ws = wb.Sheets[sheetName];
          const json = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          const cellData = {};
          json.forEach((row, r) => {
            cellData[r] = {};
            row.forEach((val, c) => {
              if (val !== '' && val !== null && val !== undefined) {
                // Handle Date objects from cellDates
                const v = val instanceof Date ? val.toISOString().split('T')[0] : val;
                cellData[r][c] = { v };
              }
            });
          });
          this.univerAPI.createWorkbook({
            name: sheetName,
            sheets: { sheet1: { name: sheetName, cellData } }
          });
        });
        if (this._auditLog) {
          this._auditLog.push({ time: new Date().toLocaleString(), action: 'Open File', detail: `${file.name} (${wb.SheetNames.length} sheets via SheetJS)` });
        }
      } catch (err) {
        alert('Failed to open file: ' + err.message);
      }
    };
    input.click();
  }

  // ── Formula Explorer (FormulaJS — 400+ Excel formulas) ──
  _openFormulaExplorer() {
    const old = document.getElementById('grid-formula-dialog');
    if (old) old.remove();

    // Build formula categories from formulajs if available
    const categories = {
      'Financial': ['FV', 'PV', 'NPV', 'IRR', 'MIRR', 'PMT', 'IPMT', 'PPMT', 'NPER', 'RATE', 'SLN', 'SYD', 'DDB', 'DB', 'ACCRINT', 'DISC', 'DOLLARDE', 'DOLLARFR', 'EFFECT', 'NOMINAL', 'XNPV', 'XIRR', 'COUPDAYS', 'COUPDAYBS', 'COUPDAYSNC', 'COUPNCD', 'COUPNUM', 'COUPPCD', 'CUMIPMT', 'CUMPRINC', 'FVSCHEDULE', 'INTRATE', 'PDURATION', 'RRI', 'RECEIVED', 'PRICE', 'PRICEDISC', 'PRICEMAT', 'YIELD', 'YIELDDISC', 'YIELDMAT', 'TBILLEQ', 'TBILLPRICE', 'TBILLYIELD', 'AMORLINC', 'AMORDEGRC'],
      'Statistical': ['AVERAGE', 'AVERAGEIF', 'AVERAGEIFS', 'COUNT', 'COUNTA', 'COUNTBLANK', 'COUNTIF', 'COUNTIFS', 'MAX', 'MAXA', 'MIN', 'MINA', 'MEDIAN', 'MODE', 'STDEV', 'VAR', 'CORREL', 'COVARIANCE', 'FORECAST', 'GROWTH', 'INTERCEPT', 'SLOPE', 'RSQ', 'TREND', 'PERCENTILE', 'PERCENTRANK', 'QUARTILE', 'RANK', 'LARGE', 'SMALL', 'SKEW', 'KURT', 'AVEDEV', 'DEVSQ', 'GEOMEAN', 'HARMEAN', 'TRIMMEAN', 'PERMUT', 'COMBIN', 'NORM.DIST', 'NORM.INV', 'NORM.S.DIST', 'NORM.S.INV', 'T.DIST', 'T.INV', 'F.DIST', 'F.INV', 'CHISQ.DIST', 'CHISQ.INV', 'BINOM.DIST', 'POISSON.DIST', 'EXPON.DIST', 'GAMMA.DIST', 'BETA.DIST', 'WEIBULL.DIST', 'LOGNORM.DIST', 'CONFIDENCE.NORM', 'CONFIDENCE.T', 'FISHER', 'FISHERINV', 'STANDARDIZE', 'Z.TEST', 'T.TEST', 'F.TEST', 'CHISQ.TEST'],
      'Math & Trig': ['ABS', 'CEILING', 'FLOOR', 'ROUND', 'ROUNDUP', 'ROUNDDOWN', 'INT', 'MOD', 'POWER', 'SQRT', 'PRODUCT', 'SUM', 'SUMIF', 'SUMIFS', 'SUMPRODUCT', 'SUMSQ', 'SUBTOTAL', 'AGGREGATE', 'EXP', 'LN', 'LOG', 'LOG10', 'FACT', 'FACTDOUBLE', 'GCD', 'LCM', 'RAND', 'RANDBETWEEN', 'PI', 'SIN', 'COS', 'TAN', 'ASIN', 'ACOS', 'ATAN', 'ATAN2', 'SINH', 'COSH', 'TANH', 'DEGREES', 'RADIANS', 'SIGN', 'TRUNC', 'EVEN', 'ODD', 'MROUND', 'MULTINOMIAL', 'QUOTIENT', 'ROMAN', 'ARABIC', 'BASE', 'DECIMAL'],
      'Text': ['CONCATENATE', 'CONCAT', 'LEFT', 'RIGHT', 'MID', 'LEN', 'FIND', 'SEARCH', 'SUBSTITUTE', 'REPLACE', 'TRIM', 'UPPER', 'LOWER', 'PROPER', 'REPT', 'TEXT', 'VALUE', 'CHAR', 'CODE', 'CLEAN', 'EXACT', 'FIXED', 'T', 'TEXTJOIN', 'NUMBERVALUE', 'UNICHAR', 'UNICODE'],
      'Date & Time': ['DATE', 'DATEVALUE', 'DAY', 'MONTH', 'YEAR', 'HOUR', 'MINUTE', 'SECOND', 'NOW', 'TODAY', 'DAYS', 'DAYS360', 'EDATE', 'EOMONTH', 'NETWORKDAYS', 'NETWORKDAYS.INTL', 'WORKDAY', 'WORKDAY.INTL', 'WEEKDAY', 'WEEKNUM', 'ISOWEEKNUM', 'YEARFRAC'],
      'Lookup': ['VLOOKUP', 'HLOOKUP', 'INDEX', 'MATCH', 'CHOOSE', 'LOOKUP', 'ADDRESS', 'ROW', 'COLUMN', 'ROWS', 'COLUMNS', 'INDIRECT', 'OFFSET', 'TRANSPOSE', 'HYPERLINK'],
      'Logical': ['IF', 'AND', 'OR', 'NOT', 'XOR', 'IFERROR', 'IFNA', 'IFS', 'SWITCH', 'TRUE', 'FALSE'],
      'Info': ['ISBLANK', 'ISERROR', 'ISERR', 'ISNA', 'ISNUMBER', 'ISTEXT', 'ISLOGICAL', 'ISEVEN', 'ISODD', 'ISFORMULA', 'ISREF', 'ISNONTEXT', 'TYPE', 'N', 'NA', 'ERROR.TYPE', 'INFO', 'SHEET', 'SHEETS'],
      'Engineering': ['BIN2DEC', 'BIN2HEX', 'BIN2OCT', 'DEC2BIN', 'DEC2HEX', 'DEC2OCT', 'HEX2BIN', 'HEX2DEC', 'HEX2OCT', 'OCT2BIN', 'OCT2DEC', 'OCT2HEX', 'COMPLEX', 'IMREAL', 'IMAGINARY', 'IMABS', 'IMARGUMENT', 'IMCONJUGATE', 'IMSUM', 'IMDIV', 'IMPRODUCT', 'IMSUB', 'DELTA', 'GESTEP', 'ERF', 'ERFC', 'BESSELI', 'BESSELJ', 'BESSELK', 'BESSELY', 'CONVERT'],
    };

    const allFormulas = Object.entries(categories).flatMap(([cat, fns]) => fns.map(f => ({ name: f, category: cat })));

    const dlg = document.createElement('div');
    dlg.id = 'grid-formula-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:560px;max-height:85vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h3 style="margin:0;color:#333;">&#402; Formula Explorer — ${allFormulas.length}+ Functions</h3>
        <button id="formula-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <input type="text" id="formula-search" placeholder="Search formulas... (e.g. NPV, VLOOKUP, DATE)" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:4px;font-size:13px;margin-bottom:12px;">
      <div style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:12px;" id="formula-cats">
        <button class="formula-cat-btn" data-cat="all" style="padding:4px 10px;border:1px solid #1e40af;border-radius:12px;cursor:pointer;font-size:11px;background:#1e40af;color:#fff;">All</button>
        ${Object.keys(categories).map(cat => `<button class="formula-cat-btn" data-cat="${cat}" style="padding:4px 10px;border:1px solid #ddd;border-radius:12px;cursor:pointer;font-size:11px;background:#fff;color:#555;">${cat}</button>`).join('')}
      </div>
      <div id="formula-list" style="max-height:400px;overflow-y:auto;"></div>
      <div id="formula-detail" style="margin-top:12px;padding:12px;background:#f8f9fa;border-radius:6px;display:none;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('formula-close').onclick = () => dlg.remove();

    const listEl = document.getElementById('formula-list');
    const detailEl = document.getElementById('formula-detail');
    let activeCategory = 'all';

    const renderList = (filter = '') => {
      const filtered = allFormulas.filter(f => {
        const matchCat = activeCategory === 'all' || f.category === activeCategory;
        const matchSearch = !filter || f.name.toLowerCase().includes(filter.toLowerCase());
        return matchCat && matchSearch;
      });
      listEl.innerHTML = filtered.length > 0 ? filtered.map(f => `
        <div class="formula-item" data-name="${f.name}" data-cat="${f.category}" style="display:flex;justify-content:space-between;align-items:center;padding:6px 10px;border-bottom:1px solid #f0f0f0;cursor:pointer;transition:background 0.1s;">
          <div>
            <strong style="font-size:13px;color:#1e40af;font-family:monospace;">${f.name}</strong>
            <span style="font-size:11px;color:#888;margin-left:8px;">${f.category}</span>
          </div>
          <button class="formula-try" data-name="${f.name}" style="padding:3px 8px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:11px;background:#e8f0fe;color:#1e40af;">Try</button>
        </div>
      `).join('') : '<div style="color:#888;padding:12px;text-align:center;">No formulas match your search.</div>';

      listEl.querySelectorAll('.formula-item').forEach(item => {
        item.addEventListener('mouseenter', () => { item.style.background = '#f0f4ff'; });
        item.addEventListener('mouseleave', () => { item.style.background = ''; });
      });

      listEl.querySelectorAll('.formula-try').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const name = btn.dataset.name;
          // Try to evaluate with FormulaJS
          if (window.formulajs && window.formulajs[name]) {
            detailEl.style.display = 'block';
            detailEl.innerHTML = `<strong style="font-family:monospace;color:#1e40af;">${name}</strong><br>
              <span style="font-size:12px;color:#16a34a;">Available via FormulaJS</span><br>
              <div style="margin-top:8px;font-size:12px;">
                <label>Test arguments (comma-separated):</label>
                <div style="display:flex;gap:6px;margin-top:4px;">
                  <input type="text" id="formula-test-args" placeholder="e.g. 0.1, 5, -1000" style="flex:1;padding:4px;border:1px solid #ddd;border-radius:3px;font-family:monospace;font-size:12px;">
                  <button id="formula-test-run" style="padding:4px 10px;background:#1e40af;color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:12px;">Run</button>
                </div>
                <div id="formula-test-result" style="margin-top:6px;font-family:monospace;font-size:13px;"></div>
              </div>`;
            document.getElementById('formula-test-run').onclick = () => {
              const argsStr = document.getElementById('formula-test-args').value;
              try {
                const args = argsStr.split(',').map(a => {
                  const t = a.trim();
                  if (t === 'true') return true;
                  if (t === 'false') return false;
                  const n = Number(t);
                  return isNaN(n) ? t : n;
                });
                const result = window.formulajs[name](...args);
                document.getElementById('formula-test-result').innerHTML = `<span style="color:#16a34a;">= ${JSON.stringify(result)}</span>`;
              } catch (err) {
                document.getElementById('formula-test-result').innerHTML = `<span style="color:#dc2626;">Error: ${err.message}</span>`;
              }
            };
          } else {
            detailEl.style.display = 'block';
            detailEl.innerHTML = `<strong style="font-family:monospace;color:#1e40af;">${name}</strong><br><span style="font-size:12px;color:#888;">Built into Univer — use directly in cells with =${name}()</span>`;
          }
        });
      });
    };

    renderList();

    document.getElementById('formula-search').addEventListener('input', (e) => {
      renderList(e.target.value);
    });

    dlg.querySelectorAll('.formula-cat-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        activeCategory = btn.dataset.cat;
        dlg.querySelectorAll('.formula-cat-btn').forEach(b => {
          b.style.background = '#fff'; b.style.color = '#555'; b.style.borderColor = '#ddd';
        });
        btn.style.background = '#1e40af'; btn.style.color = '#fff'; btn.style.borderColor = '#1e40af';
        renderList(document.getElementById('formula-search').value);
      });
    });
  }

  // ── Solver (Linear Programming — javascript-lp-solver) ──
  _openSolver() {
    const old = document.getElementById('grid-solver-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-solver-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:520px;max-height:85vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h3 style="margin:0;color:#333;">&#128295; Solver — Linear Programming</h3>
        <button id="solver-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="font-size:12px;color:#555;margin-bottom:12px;">
        Define an objective to maximize or minimize, subject to constraints. Like Excel's Solver add-in.
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Optimize:</label>
        <select id="solver-optype" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
          <option value="max">Maximize</option>
          <option value="min">Minimize</option>
        </select>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Objective (variable name):</label>
        <input type="text" id="solver-objective" value="profit" placeholder="e.g. profit" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Decision variables (comma-separated):</label>
        <input type="text" id="solver-vars" value="x, y" placeholder="e.g. x, y, z" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Constraints (one per line, format: coeff*var + coeff*var &lt;= value):</label>
        <textarea id="solver-constraints" rows="5" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;font-size:12px;" placeholder="2x + 3y <= 120&#10;4x + y <= 100&#10;x >= 0&#10;y >= 0">2x + 3y <= 120
4x + y <= 100
x >= 0
y >= 0</textarea>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Objective coefficients (format: coeff*var + coeff*var):</label>
        <input type="text" id="solver-obj-expr" value="5x + 4y" placeholder="e.g. 5x + 4y" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <label style="display:flex;align-items:center;gap:6px;font-size:12px;color:#555;margin-bottom:12px;">
        <input type="checkbox" id="solver-int"> Integer variables only
      </label>
      <button id="solver-run" style="width:100%;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Solve</button>
      <div id="solver-result" style="margin-top:12px;padding:12px;background:#f8f9fa;border-radius:6px;font-family:monospace;font-size:13px;display:none;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('solver-close').onclick = () => dlg.remove();

    document.getElementById('solver-run').onclick = () => {
      const solver = window.solver || (window.Solver ? window.Solver : null);
      if (!solver) {
        document.getElementById('solver-result').style.display = 'block';
        document.getElementById('solver-result').innerHTML = '<span style="color:#dc2626;">LP Solver library not loaded.</span>';
        return;
      }

      try {
        const opType = document.getElementById('solver-optype').value;
        const objective = document.getElementById('solver-objective').value.trim();
        const varsStr = document.getElementById('solver-vars').value;
        const vars = varsStr.split(',').map(v => v.trim()).filter(Boolean);
        const constraintLines = document.getElementById('solver-constraints').value.trim().split('\n').filter(Boolean);
        const objExpr = document.getElementById('solver-obj-expr').value.trim();
        const intVars = document.getElementById('solver-int').checked;

        // Parse objective coefficients
        const parseExpr = (expr) => {
          const result = {};
          const re = /([+-]?\s*\d*\.?\d*)\s*([a-zA-Z_]\w*)/g;
          let m;
          while ((m = re.exec(expr)) !== null) {
            let coeff = m[1].replace(/\s/g, '');
            if (coeff === '' || coeff === '+') coeff = '1';
            if (coeff === '-') coeff = '-1';
            result[m[2]] = parseFloat(coeff);
          }
          return result;
        };

        // Build model
        const model = {
          optimize: objective,
          opType: opType,
          constraints: {},
          variables: {}
        };

        // Set up variables with objective coefficients
        const objCoeffs = parseExpr(objExpr);
        vars.forEach(v => {
          model.variables[v] = {};
          model.variables[v][objective] = objCoeffs[v] || 0;
        });

        // Parse constraints
        constraintLines.forEach((line, i) => {
          const cName = `c${i}`;
          let op = 'max';
          let parts;
          if (line.includes('<=')) { parts = line.split('<='); op = 'max'; }
          else if (line.includes('>=')) { parts = line.split('>='); op = 'min'; }
          else if (line.includes('=') && !line.includes('<') && !line.includes('>')) { parts = line.split('='); op = 'equal'; }
          else return;

          const lhs = parseExpr(parts[0]);
          const rhs = parseFloat(parts[1]);
          model.constraints[cName] = {};
          model.constraints[cName][op] = rhs;
          // Add constraint coefficients to variables
          Object.entries(lhs).forEach(([varName, coeff]) => {
            if (model.variables[varName]) {
              model.variables[varName][cName] = coeff;
            }
          });
        });

        if (intVars) {
          model.ints = {};
          vars.forEach(v => { model.ints[v] = 1; });
        }

        const result = solver.Solve(model);

        const resultEl = document.getElementById('solver-result');
        resultEl.style.display = 'block';

        if (result.feasible) {
          let html = `<div style="color:#16a34a;font-weight:600;margin-bottom:8px;">&#9989; Solution Found (${result.feasible ? 'Feasible' : 'Infeasible'})</div>`;
          html += `<div><strong>Objective (${objective}):</strong> ${result.result}</div>`;
          vars.forEach(v => {
            html += `<div><strong>${v}:</strong> ${result[v] || 0}</div>`;
          });
          if (result.bounded === false) html += `<div style="color:#f59e0b;">Warning: Problem may be unbounded.</div>`;
          resultEl.innerHTML = html;
        } else {
          resultEl.innerHTML = '<span style="color:#dc2626;">No feasible solution found. Check your constraints.</span>';
        }
      } catch (err) {
        document.getElementById('solver-result').style.display = 'block';
        document.getElementById('solver-result').innerHTML = `<span style="color:#dc2626;">Error: ${err.message}</span>`;
      }
    };
  }

  // ── Math Evaluator (math.js — matrices, expressions, algebra) ──
  _openMathEval() {
    const old = document.getElementById('grid-matheval-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-matheval-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:480px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h3 style="margin:0;color:#333;">&#120031; Math Evaluator</h3>
        <button id="matheval-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="font-size:12px;color:#555;margin-bottom:12px;">
        Evaluate mathematical expressions, matrix operations, unit conversions, and more. Powered by math.js.
      </div>
      <textarea id="math-input" rows="4" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:4px;font-family:monospace;font-size:13px;" placeholder="Examples:
det([[1,2],[3,4]])
inv([[1,2],[3,4]])
5.08 cm to inch
sin(45 deg)
2^10"></textarea>
      <div style="display:flex;gap:8px;margin-top:8px;">
        <button id="math-eval" style="flex:1;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Evaluate</button>
        <button id="math-to-sheet" style="flex:1;padding:8px;background:#16a34a;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Send to Sheet</button>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px;">
        ${['det([[1,2],[3,4]])', 'inv([[2,1],[1,3]])', 'eigs([[4,2],[1,3]])', 'sqrt(144)', 'e^(i*pi)', 'factorial(10)', '100 km/h to m/s', 'derivative("x^2 + 3x", "x")'].map(ex =>
          `<button class="math-example" style="padding:3px 8px;border:1px solid #ddd;border-radius:3px;cursor:pointer;font-size:10px;background:#f8f8f8;font-family:monospace;">${ex}</button>`
        ).join('')}
      </div>
      <div id="math-result" style="margin-top:12px;padding:12px;background:#1e1e2e;border-radius:6px;font-family:monospace;font-size:13px;color:#cdd6f4;white-space:pre-wrap;display:none;max-height:250px;overflow-y:auto;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('matheval-close').onclick = () => dlg.remove();

    dlg.querySelectorAll('.math-example').forEach(btn => {
      btn.addEventListener('click', () => {
        document.getElementById('math-input').value = btn.textContent;
      });
    });

    const evaluate = () => {
      if (!window.math) {
        document.getElementById('math-result').style.display = 'block';
        document.getElementById('math-result').textContent = 'math.js library not loaded.';
        return null;
      }
      const expr = document.getElementById('math-input').value.trim();
      if (!expr) return null;
      try {
        const lines = expr.split('\n').filter(Boolean);
        const results = lines.map(line => {
          const r = window.math.evaluate(line);
          return typeof r === 'object' && r.toString ? r.toString() : String(r);
        });
        const output = results.join('\n');
        document.getElementById('math-result').style.display = 'block';
        document.getElementById('math-result').textContent = output;
        return results;
      } catch (err) {
        document.getElementById('math-result').style.display = 'block';
        document.getElementById('math-result').textContent = 'Error: ' + err.message;
        return null;
      }
    };

    document.getElementById('math-eval').onclick = evaluate;

    document.getElementById('math-to-sheet').onclick = () => {
      const results = evaluate();
      if (!results || !this.univerAPI) return;
      const cellData = {};
      results.forEach((r, i) => {
        cellData[i] = { 0: { v: r } };
      });
      this.univerAPI.createWorkbook({
        name: 'Math Result',
        sheets: { sheet1: { name: 'Math Result', cellData } }
      });
    };
  }

  // ── Date Calculator (Day.js) ──
  _openDateCalc() {
    const old = document.getElementById('grid-datecalc-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-datecalc-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:440px;max-height:80vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const today = new Date().toISOString().split('T')[0];

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128197; Date Calculator</h3>
        <button id="datecalc-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:16px;">
        <strong style="font-size:13px;color:#333;">Date Difference</strong>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px;">
          <div><label style="font-size:11px;color:#555;">From:</label><input type="date" id="dc-from" value="${today}" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;"></div>
          <div><label style="font-size:11px;color:#555;">To:</label><input type="date" id="dc-to" value="${today}" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;"></div>
        </div>
        <button id="dc-diff-btn" style="margin-top:8px;padding:6px 14px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Calculate Difference</button>
        <div id="dc-diff-result" style="margin-top:6px;font-size:12px;color:#333;"></div>
      </div>
      <div style="border-top:1px solid #e0e0e0;padding-top:16px;margin-bottom:16px;">
        <strong style="font-size:13px;color:#333;">Add / Subtract Days</strong>
        <div style="display:grid;grid-template-columns:1fr 80px 80px;gap:8px;margin-top:6px;">
          <div><label style="font-size:11px;color:#555;">Start date:</label><input type="date" id="dc-add-start" value="${today}" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;"></div>
          <div><label style="font-size:11px;color:#555;">Days:</label><input type="number" id="dc-add-days" value="30" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;"></div>
          <div><label style="font-size:11px;color:#555;">Type:</label>
            <select id="dc-add-type" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;font-size:11px;">
              <option value="calendar">Calendar</option>
              <option value="business">Business</option>
            </select>
          </div>
        </div>
        <button id="dc-add-btn" style="margin-top:8px;padding:6px 14px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Calculate</button>
        <div id="dc-add-result" style="margin-top:6px;font-size:12px;color:#333;"></div>
      </div>
      <div style="border-top:1px solid #e0e0e0;padding-top:16px;">
        <strong style="font-size:13px;color:#333;">Generate Date Series to Sheet</strong>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px;">
          <div><label style="font-size:11px;color:#555;">Start:</label><input type="date" id="dc-ser-start" value="${today}" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;"></div>
          <div><label style="font-size:11px;color:#555;">Periods:</label><input type="number" id="dc-ser-periods" value="12" min="1" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;"></div>
        </div>
        <div style="margin-top:6px;">
          <label style="font-size:11px;color:#555;">Frequency:</label>
          <select id="dc-ser-freq" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;">
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month" selected>Monthly</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </select>
        </div>
        <button id="dc-ser-btn" style="margin-top:8px;padding:6px 14px;background:#16a34a;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px;">Generate to Sheet</button>
      </div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('datecalc-close').onclick = () => dlg.remove();

    document.getElementById('dc-diff-btn').onclick = () => {
      const from = new Date(document.getElementById('dc-from').value);
      const to = new Date(document.getElementById('dc-to').value);
      const diffMs = to - from;
      const days = Math.round(diffMs / 86400000);
      const weeks = Math.floor(days / 7);
      const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
      const years = to.getFullYear() - from.getFullYear();
      // Business days (rough: exclude weekends)
      let bizDays = 0;
      const step = days >= 0 ? 1 : -1;
      const d = new Date(from);
      for (let i = 0; i < Math.abs(days); i++) {
        d.setDate(d.getDate() + step);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) bizDays++;
      }
      document.getElementById('dc-diff-result').innerHTML = `
        <strong>${Math.abs(days)}</strong> calendar days | <strong>${bizDays}</strong> business days<br>
        <strong>${weeks}</strong> weeks | <strong>${Math.abs(months)}</strong> months | <strong>${Math.abs(years)}</strong> years
      `;
    };

    document.getElementById('dc-add-btn').onclick = () => {
      const start = new Date(document.getElementById('dc-add-start').value);
      const addDays = parseInt(document.getElementById('dc-add-days').value) || 0;
      const type = document.getElementById('dc-add-type').value;

      if (type === 'calendar') {
        const result = new Date(start);
        result.setDate(result.getDate() + addDays);
        document.getElementById('dc-add-result').innerHTML = `Result: <strong>${result.toISOString().split('T')[0]}</strong>`;
      } else {
        // Business days — skip weekends
        let count = 0;
        const d = new Date(start);
        const step = addDays >= 0 ? 1 : -1;
        while (count < Math.abs(addDays)) {
          d.setDate(d.getDate() + step);
          const dow = d.getDay();
          if (dow !== 0 && dow !== 6) count++;
        }
        document.getElementById('dc-add-result').innerHTML = `Result: <strong>${d.toISOString().split('T')[0]}</strong> (${Math.abs(addDays)} business days)`;
      }
    };

    document.getElementById('dc-ser-btn').onclick = () => {
      if (!this.univerAPI) return;
      const start = new Date(document.getElementById('dc-ser-start').value);
      const periods = parseInt(document.getElementById('dc-ser-periods').value) || 12;
      const freq = document.getElementById('dc-ser-freq').value;

      const cellData = { 0: { 0: { v: 'Date' }, 1: { v: 'Period' } } };
      for (let i = 0; i < periods; i++) {
        const d = new Date(start);
        if (freq === 'day') d.setDate(d.getDate() + i);
        else if (freq === 'week') d.setDate(d.getDate() + i * 7);
        else if (freq === 'month') d.setMonth(d.getMonth() + i);
        else if (freq === 'quarter') d.setMonth(d.getMonth() + i * 3);
        else if (freq === 'year') d.setFullYear(d.getFullYear() + i);
        cellData[i + 1] = { 0: { v: d.toISOString().split('T')[0] }, 1: { v: i + 1 } };
      }
      this.univerAPI.createWorkbook({
        name: 'Date Series',
        sheets: { sheet1: { name: 'Date Series', cellData } }
      });
    };
  }

  // ── Currency Formatter (currency.js) ──
  _openCurrencyFormatter() {
    if (!this.univerAPI) return;
    const old = document.getElementById('grid-currency-dialog');
    if (old) old.remove();

    const dlg = document.createElement('div');
    dlg.id = 'grid-currency-dialog';
    dlg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;z-index:8000;width:420px;max-height:70vh;overflow-y:auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);';

    const currencies = [
      { code: 'USD', symbol: '$', name: 'US Dollar' },
      { code: 'EUR', symbol: '\u20AC', name: 'Euro' },
      { code: 'GBP', symbol: '\u00A3', name: 'British Pound' },
      { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen', precision: 0 },
      { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
      { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
      { code: 'CHF', symbol: 'CHF ', name: 'Swiss Franc' },
      { code: 'CNY', symbol: '\u00A5', name: 'Chinese Yuan' },
      { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee' },
      { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
      { code: 'KRW', symbol: '\u20A9', name: 'South Korean Won', precision: 0 },
      { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
      { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
      { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
      { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    ];

    dlg.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h3 style="margin:0;color:#333;">&#128176; Currency Formatter</h3>
        <button id="currency-close" style="background:none;border:none;font-size:18px;cursor:pointer;">&times;</button>
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Apply to range:</label>
        <input type="text" id="cur-range" placeholder="e.g. B2:B50" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;font-family:monospace;">
      </div>
      <div style="margin-bottom:10px;">
        <label style="font-size:12px;color:#555;">Currency:</label>
        <select id="cur-currency" style="width:100%;padding:6px;border:1px solid #ddd;border-radius:4px;margin-top:4px;">
          ${currencies.map(c => `<option value="${c.code}" data-symbol="${c.symbol}" data-precision="${c.precision || 2}">${c.symbol} ${c.code} — ${c.name}</option>`).join('')}
        </select>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px;">
        <div>
          <label style="font-size:11px;color:#555;">Decimal places:</label>
          <input type="number" id="cur-decimals" value="2" min="0" max="6" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;">
        </div>
        <div>
          <label style="font-size:11px;color:#555;">Thousands sep:</label>
          <select id="cur-separator" style="width:100%;padding:5px;border:1px solid #ddd;border-radius:4px;margin-top:2px;">
            <option value=",">, (comma)</option>
            <option value=".">. (period)</option>
            <option value=" ">&nbsp; (space)</option>
            <option value="">None</option>
          </select>
        </div>
      </div>
      <div style="display:flex;gap:12px;margin-bottom:12px;">
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;"><input type="checkbox" id="cur-negparen"> Negatives in (parentheses)</label>
        <label style="font-size:12px;color:#555;display:flex;align-items:center;gap:4px;"><input type="checkbox" id="cur-symbolright"> Symbol on right</label>
      </div>
      <div style="margin-bottom:12px;padding:10px;background:#f8f9fa;border-radius:4px;">
        <span style="font-size:11px;color:#555;">Preview:</span>
        <div id="cur-preview" style="font-size:16px;font-weight:600;color:#1e40af;margin-top:4px;">$1,234.56</div>
      </div>
      <button id="cur-apply" style="width:100%;padding:8px;background:#1e40af;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Format Cells</button>
      <div id="cur-status" style="margin-top:8px;font-size:12px;color:#666;"></div>
    `;

    document.getElementById('grid-container').appendChild(dlg);
    document.getElementById('currency-close').onclick = () => dlg.remove();

    const updatePreview = () => {
      const sel = document.getElementById('cur-currency');
      const opt = sel.options[sel.selectedIndex];
      const symbol = opt.dataset.symbol;
      const decimals = parseInt(document.getElementById('cur-decimals').value) || 2;
      const separator = document.getElementById('cur-separator').value;
      const negParen = document.getElementById('cur-negparen').checked;
      const symbolRight = document.getElementById('cur-symbolright').checked;

      const testVal = 1234.56;
      if (window.currency) {
        const c = window.currency(testVal, { symbol, precision: decimals, separator, pattern: symbolRight ? '# !' : '!#', negativePattern: negParen ? '(!#)' : '-!#' });
        document.getElementById('cur-preview').textContent = c.format();
      } else {
        const formatted = testVal.toFixed(decimals);
        document.getElementById('cur-preview').textContent = symbolRight ? `${formatted} ${symbol}` : `${symbol}${formatted}`;
      }
    };

    ['cur-currency', 'cur-decimals', 'cur-separator', 'cur-negparen', 'cur-symbolright'].forEach(id => {
      document.getElementById(id).addEventListener('change', updatePreview);
    });
    updatePreview();

    document.getElementById('cur-apply').onclick = () => {
      const rangeStr = document.getElementById('cur-range').value.trim().toUpperCase();
      if (!rangeStr) {
        document.getElementById('cur-status').innerHTML = '<span style="color:#dc2626;">Enter a range.</span>';
        return;
      }
      const match = rangeStr.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
      if (!match) {
        document.getElementById('cur-status').innerHTML = '<span style="color:#dc2626;">Invalid range format.</span>';
        return;
      }

      const sel = document.getElementById('cur-currency');
      const opt = sel.options[sel.selectedIndex];
      const symbol = opt.dataset.symbol;
      const decimals = parseInt(document.getElementById('cur-decimals').value) || 2;
      const separator = document.getElementById('cur-separator').value;
      const negParen = document.getElementById('cur-negparen').checked;
      const symbolRight = document.getElementById('cur-symbolright').checked;

      const c1 = match[1].charCodeAt(0) - 65;
      const r1 = parseInt(match[2]) - 1;
      const c2 = match[3].charCodeAt(0) - 65;
      const r2 = parseInt(match[4]) - 1;

      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      let count = 0;

      // Build Univer number format pattern (preserves numeric values)
      const decPlaces = '0'.repeat(decimals);
      const fmtPattern = decimals > 0
        ? (symbolRight ? `#,##0.${decPlaces} "${symbol}"` : `"${symbol}"#,##0.${decPlaces}`)
        : (symbolRight ? `#,##0 "${symbol}"` : `"${symbol}"#,##0`);
      const negPattern = negParen
        ? (symbolRight ? `(#,##0.${decPlaces} "${symbol}")` : `("${symbol}"#,##0.${decPlaces})`)
        : null;
      const fullPattern = negPattern ? `${fmtPattern};${negPattern}` : fmtPattern;

      try {
        const rangeObj = sheet.getRange(r1, c1, r2 - r1 + 1, c2 - c1 + 1);
        // Try to set number format via Univer API (preserves data)
        if (rangeObj.setNumberFormat) {
          rangeObj.setNumberFormat(fullPattern);
          count = (r2 - r1 + 1) * (c2 - c1 + 1);
        } else if (rangeObj.setStyle) {
          // Fallback: set style with number format pattern
          rangeObj.setStyle({ n: { pattern: fullPattern } });
          count = (r2 - r1 + 1) * (c2 - c1 + 1);
        } else {
          // Last resort: format display values (lossy but functional)
          for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
              try {
                const cellRange = sheet.getRange(r, c, 1, 1);
                const vals = cellRange.getValues();
                const raw = vals?.[0]?.[0];
                const num = parseFloat(raw);
                if (!isNaN(num)) {
                  let formatted;
                  if (window.currency) {
                    formatted = window.currency(num, { symbol, precision: decimals, separator, pattern: symbolRight ? '# !' : '!#', negativePattern: negParen ? '(!#)' : '-!#' }).format();
                  } else {
                    formatted = (symbolRight ? '' : symbol) + num.toFixed(decimals) + (symbolRight ? ' ' + symbol : '');
                  }
                  cellRange.setValue(formatted);
                  count++;
                }
              } catch (e) { /* skip */ }
            }
          }
        }
      } catch (e) {
        document.getElementById('cur-status').innerHTML = `<span style="color:#dc2626;">${e.message}</span>`;
        return;
      }

      document.getElementById('cur-status').innerHTML = `<span style="color:#16a34a;">&#9989; Formatted ${count} cell(s) as ${sel.value}. Values preserved.</span>`;
    };
  }

  // ── Export ODS (SheetJS CE — Apache 2.0) ──
  _exportODS() {
    if (!this.univerAPI || !window.XLSX) { alert('SheetJS not loaded.'); return; }
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      const data = sheet.getDataRange().getValues();

      const wsData = data.map(row => row.map(v => v ?? ''));
      const ws = window.XLSX.utils.aoa_to_sheet(wsData);
      const odsWb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(odsWb, ws, sheet.getSheetName());

      window.XLSX.writeFile(odsWb, (document.getElementById('grid-title')?.textContent || 'spreadsheet') + '.ods', { bookType: 'ods' });
      if (this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Export ODS', detail: 'Exported as .ods' });
    } catch (e) {
      alert('ODS export error: ' + e.message);
    }
  }

  // ── Copy as Markdown Table ──
  _copyMarkdown() {
    if (!this.univerAPI) return;
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      const sheet = wb.getActiveSheet();
      const data = sheet.getDataRange().getValues();

      // Find actual data bounds
      let maxRow = 0, maxCol = 0;
      for (let r = 0; r < data.length; r++) {
        for (let c = 0; c < (data[r]?.length || 0); c++) {
          if (data[r][c] !== null && data[r][c] !== undefined && data[r][c] !== '') {
            if (r > maxRow) maxRow = r;
            if (c > maxCol) maxCol = c;
          }
        }
      }

      if (maxRow === 0 && maxCol === 0 && !data[0]?.[0]) {
        alert('No data to copy.');
        return;
      }

      // Build Markdown table
      const lines = [];
      for (let r = 0; r <= maxRow; r++) {
        const cells = [];
        for (let c = 0; c <= maxCol; c++) {
          cells.push(String(data[r]?.[c] ?? '').replace(/\|/g, '\\|'));
        }
        lines.push('| ' + cells.join(' | ') + ' |');
        // Add separator after header row
        if (r === 0) {
          lines.push('| ' + cells.map(() => '---').join(' | ') + ' |');
        }
      }

      const md = lines.join('\n');
      navigator.clipboard.writeText(md).then(() => {
        // Show brief toast
        const toast = document.createElement('div');
        toast.style.cssText = 'position:fixed;bottom:40px;left:50%;transform:translateX(-50%);background:#1e40af;color:#fff;padding:8px 20px;border-radius:6px;font-size:13px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.2);';
        toast.textContent = `Copied ${maxRow + 1} rows as Markdown table`;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 2500);
      }).catch(() => {
        // Fallback: prompt with text
        prompt('Copy this Markdown table:', md);
      });

      if (this._auditLog) this._auditLog.push({ time: new Date().toLocaleString(), action: 'Copy Markdown', detail: `${maxRow + 1} rows, ${maxCol + 1} cols` });
    } catch (e) {
      alert('Markdown copy error: ' + e.message);
    }
  }

  // ── Column Autocomplete (Excel-style) ──
  _initColumnAutocomplete() {
    const container = document.getElementById('grid-spreadsheet');
    if (!container) return;
    this._acDropdown = null;
    this._acActive = false;

    // Watch for Univer's cell editor appearing
    const observer = new MutationObserver(() => {
      // Univer renders its cell editor as a contenteditable div or textarea
      const editors = container.querySelectorAll('[contenteditable="true"], textarea.univer-editor, [class*="editor-input"], [class*="cell-editor"]');
      for (const editor of editors) {
        if (editor._acBound) continue;
        editor._acBound = true;
        editor.addEventListener('input', () => this._onCellEditorInput(editor));
        editor.addEventListener('keydown', (e) => this._onCellEditorKeydown(e));
        editor.addEventListener('blur', () => setTimeout(() => this._hideAutocomplete(), 150));
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    // Also check document body for Univer editor portals
    const bodyObserver = new MutationObserver(() => {
      const editors = document.querySelectorAll('[class*="univer"] [contenteditable="true"], [class*="Editor"] [contenteditable="true"]');
      for (const editor of editors) {
        if (editor._acBound || editor.closest('#grid-context-menu')) continue;
        editor._acBound = true;
        editor.addEventListener('input', () => this._onCellEditorInput(editor));
        editor.addEventListener('keydown', (e) => this._onCellEditorKeydown(e));
        editor.addEventListener('blur', () => setTimeout(() => this._hideAutocomplete(), 150));
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  _getColumnValues(colIdx) {
    if (!this.univerAPI) return [];
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return [];
      const sheet = wb.getActiveSheet();
      if (!sheet) return [];
      const data = sheet.getDataRange()?.getValues();
      if (!data) return [];

      const values = new Set();
      for (let r = 0; r < data.length; r++) {
        const val = data[r]?.[colIdx];
        if (val !== null && val !== undefined && val !== '' && typeof val === 'string') {
          values.add(val);
        }
      }
      return [...values];
    } catch (e) { return []; }
  }

  _onCellEditorInput(editor) {
    const typed = (editor.textContent || editor.value || '').trim();
    if (!typed || typed.length < 1) { this._hideAutocomplete(); return; }

    // Check for @Card pattern first — show card picker
    if (typed.startsWith('@')) {
      const query = typed.substring(1).toLowerCase();
      const allCards = this.cardEngine ? this.cardEngine.getAllCards() : [];
      if (allCards.length > 0) {
        const matches = query.length === 0
          ? allCards.map(c => c.name)
          : allCards.filter(c => c.name.toLowerCase().includes(query)).map(c => c.name);
        if (matches.length > 0) {
          // Show card names, on select show feed/result picker
          this._showCardAutocomplete(editor, matches, typed);
          return;
        }
      }
    }

    // Regular column autocomplete
    if (!this.univerAPI) return;
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return;
      const sheet = wb.getActiveSheet();
      if (!sheet) return;
      const sel = sheet.getActiveRange();
      if (!sel) return;
      const range = sel.getRange();
      const colIdx = range.startColumn;

      const allValues = this._getColumnValues(colIdx);
      const lower = typed.toLowerCase();
      const matches = allValues.filter(v => v.toLowerCase().startsWith(lower) && v.toLowerCase() !== lower);

      if (matches.length === 0) { this._hideAutocomplete(); return; }

      this._showAutocomplete(editor, matches, typed);
    } catch (e) { this._hideAutocomplete(); }
  }

  _showCardAutocomplete(editor, cardNames, typed) {
    this._hideAutocomplete();

    const dropdown = document.createElement('div');
    dropdown.id = 'grid-ac-dropdown';
    dropdown.style.cssText = 'position:fixed;z-index:999999;background:#fff;border:1px solid #d0d0d0;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.15);max-height:220px;overflow-y:auto;min-width:200px;';

    const rect = editor.getBoundingClientRect();
    dropdown.style.left = rect.left + 'px';
    dropdown.style.top = (rect.bottom + 2) + 'px';

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'padding:6px 12px;font-size:11px;color:#1e40af;font-weight:700;border-bottom:1px solid #e5e7eb;background:#f8faff;border-radius:8px 8px 0 0;';
    header.textContent = 'Cards';
    dropdown.appendChild(header);

    this._acItems = [];
    this._acSelectedIdx = 0;
    this._acMatches = [];

    cardNames.slice(0, 10).forEach((name, idx) => {
      const card = this.cardEngine.getCardByName(name);
      const data = card?.data || {};
      const headers = data.headers || [];

      const item = document.createElement('div');
      item.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:13px;color:#333;border-bottom:1px solid #f5f5f5;';
      item.innerHTML = `<div style="font-weight:600;">${name}</div>${headers.length ? `<div style="font-size:10px;color:#999;margin-top:1px;">${headers.join(', ')}</div>` : ''}`;
      if (idx === 0) item.style.background = '#e8f0fe';

      item.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this._showCardUsagePicker(editor, name, card);
      });
      item.addEventListener('mouseenter', () => {
        this._acItems.forEach(i => i.style.background = 'none');
        item.style.background = '#e8f0fe';
        this._acSelectedIdx = idx;
      });

      dropdown.appendChild(item);
      this._acItems.push(item);
      this._acMatches.push(name);
    });

    document.body.appendChild(dropdown);
    this._acDropdown = dropdown;
    this._acEditor = editor;
    this._acActive = true;
    this._acIsCardMode = true;

    requestAnimationFrame(() => {
      const r = dropdown.getBoundingClientRect();
      if (r.bottom > window.innerHeight) dropdown.style.top = (rect.top - r.height - 2) + 'px';
    });
  }

  _showCardUsagePicker(editor, cardName, card) {
    this._hideAutocomplete();
    const data = card?.data || {};
    const headers = data.headers || [];
    const colTypes = data.colTypes || [];

    // Build usage options
    const options = [];
    const textCols = colTypes.map((t, i) => t === 'text' ? i : -1).filter(i => i >= 0);
    const numCols = colTypes.map((t, i) => t === 'number' ? i : -1).filter(i => i >= 0);

    // Feed/result for lookups
    if (textCols.length > 0 && headers.length >= 2) {
      options.push({ label: `@Card(${cardName},feed)`, desc: 'Input value for lookup' });
      options.push({ label: `@Card(${cardName},result)`, desc: 'Lookup result' });
    }
    // Column feeds
    headers.forEach(h => {
      options.push({ label: `@Card(${cardName},${h})`, desc: `${h} column values` });
    });
    // Aggregates
    if (numCols.length > 0) {
      options.push({ label: `@Card(${cardName},sum)`, desc: 'Sum of numbers' });
      options.push({ label: `@Card(${cardName},avg)`, desc: 'Average' });
    }
    // Full reference
    options.push({ label: `{${cardName}}`, desc: 'Full table reference' });

    // Show inline dropdown
    const dropdown = document.createElement('div');
    dropdown.id = 'grid-ac-dropdown';
    dropdown.style.cssText = 'position:fixed;z-index:999999;background:#fff;border:1px solid #d0d0d0;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.15);max-height:250px;overflow-y:auto;min-width:250px;';

    const rect = editor.getBoundingClientRect();
    dropdown.style.left = rect.left + 'px';
    dropdown.style.top = (rect.bottom + 2) + 'px';

    const header = document.createElement('div');
    header.style.cssText = 'padding:6px 12px;font-size:11px;color:#1e40af;font-weight:700;border-bottom:1px solid #e5e7eb;background:#f8faff;border-radius:8px 8px 0 0;';
    header.textContent = `Use "${cardName}" as:`;
    dropdown.appendChild(header);

    options.forEach(opt => {
      const item = document.createElement('div');
      item.style.cssText = 'padding:8px 12px;cursor:pointer;font-size:12px;border-bottom:1px solid #f5f5f5;';
      item.innerHTML = `<div style="font-weight:600;color:#1e40af;font-family:monospace;">${opt.label}</div><div style="font-size:10px;color:#999;margin-top:1px;">${opt.desc}</div>`;
      item.addEventListener('mouseenter', () => { item.style.background = '#e8f0fe'; });
      item.addEventListener('mouseleave', () => { item.style.background = 'none'; });
      item.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this._acceptAutocomplete(editor, opt.label);
        dropdown.remove();
      });
      dropdown.appendChild(item);
    });

    document.body.appendChild(dropdown);
    this._acDropdown = dropdown;
    this._acActive = true;

    requestAnimationFrame(() => {
      const r = dropdown.getBoundingClientRect();
      if (r.bottom > window.innerHeight) dropdown.style.top = (rect.top - r.height - 2) + 'px';
    });
  }

  _showAutocomplete(editor, matches, typed) {
    this._hideAutocomplete();

    const dropdown = document.createElement('div');
    dropdown.id = 'grid-ac-dropdown';
    dropdown.style.cssText = 'position:fixed;z-index:999999;background:#fff;border:1px solid #d0d0d0;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.15);max-height:180px;overflow-y:auto;min-width:160px;';

    // Position below the editor
    const rect = editor.getBoundingClientRect();
    dropdown.style.left = rect.left + 'px';
    dropdown.style.top = (rect.bottom + 2) + 'px';
    dropdown.style.minWidth = Math.max(rect.width, 160) + 'px';

    this._acItems = [];
    this._acSelectedIdx = 0;

    matches.slice(0, 8).forEach((match, idx) => {
      const item = document.createElement('div');
      item.style.cssText = 'padding:6px 12px;cursor:pointer;font-size:13px;color:#333;';
      // Bold the matching prefix
      const prefix = match.substring(0, typed.length);
      const rest = match.substring(typed.length);
      item.innerHTML = `<b>${this._escHtml(prefix)}</b>${this._escHtml(rest)}`;
      if (idx === 0) item.style.background = '#e8f0fe';

      item.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this._acceptAutocomplete(editor, match);
      });
      item.addEventListener('mouseenter', () => {
        this._acItems.forEach(i => i.style.background = 'none');
        item.style.background = '#e8f0fe';
        this._acSelectedIdx = idx;
      });

      dropdown.appendChild(item);
      this._acItems.push(item);
    });

    document.body.appendChild(dropdown);
    this._acDropdown = dropdown;
    this._acMatches = matches.slice(0, 8);
    this._acEditor = editor;
    this._acActive = true;

    // Clamp to viewport
    requestAnimationFrame(() => {
      const r = dropdown.getBoundingClientRect();
      if (r.bottom > window.innerHeight) dropdown.style.top = (rect.top - r.height - 2) + 'px';
      if (r.right > window.innerWidth) dropdown.style.left = (window.innerWidth - r.width - 8) + 'px';
    });
  }

  _escHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  _onCellEditorKeydown(e) {
    if (!this._acActive || !this._acDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      this._acSelectedIdx = Math.min(this._acSelectedIdx + 1, this._acItems.length - 1);
      this._acItems.forEach((item, i) => item.style.background = i === this._acSelectedIdx ? '#e8f0fe' : 'none');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      this._acSelectedIdx = Math.max(this._acSelectedIdx - 1, 0);
      this._acItems.forEach((item, i) => item.style.background = i === this._acSelectedIdx ? '#e8f0fe' : 'none');
    } else if (e.key === 'Tab' || (e.key === 'Enter' && this._acActive)) {
      e.preventDefault();
      e.stopPropagation();
      const match = this._acMatches[this._acSelectedIdx];
      if (match) this._acceptAutocomplete(this._acEditor, match);
    } else if (e.key === 'Escape') {
      this._hideAutocomplete();
    }
  }

  _acceptAutocomplete(editor, value) {
    // Set the editor content to the selected value
    if (editor.tagName === 'TEXTAREA' || editor.tagName === 'INPUT') {
      editor.value = value;
      editor.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      // contenteditable
      editor.textContent = value;
      editor.dispatchEvent(new Event('input', { bubbles: true }));
      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    this._hideAutocomplete();
  }

  _hideAutocomplete() {
    if (this._acDropdown) {
      this._acDropdown.remove();
      this._acDropdown = null;
    }
    this._acActive = false;
    this._acItems = [];
    this._acMatches = [];
  }

  // ── @Card() Resolution in Grid Cells ──
  _initCardResolution() {
    // Poll every 2 seconds for cells containing @Card() and resolve them
    setInterval(() => this._resolveCardCells(), 2000);
  }

  _resolveCardCells() {
    if (!this.univerAPI || !this.cardEngine) return;
    try {
      const wb = this.univerAPI.getActiveWorkbook();
      if (!wb) return;
      const sheet = wb.getActiveSheet();
      if (!sheet) return;
      const data = sheet.getDataRange()?.getValues();
      if (!data) return;

      for (let r = 0; r < data.length; r++) {
        for (let c = 0; c < (data[r]?.length || 0); c++) {
          const val = data[r][c];
          if (val == null || typeof val !== 'string') continue;
          const str = val.trim();

          // Match @Card(CardName,mode) patterns
          const match = str.match(/^@Card\(([^,]+),\s*([^)]+)\)$/i);
          if (!match) continue;

          const cardName = match[1].trim();
          const mode = match[2].trim().toLowerCase();
          const card = this.cardEngine.getCardByName(cardName);
          if (!card) continue;

          const cardData = card.data || {};
          const headers = cardData.headers || [];
          const values = cardData.values || [];

          let resolved = null;

          if (mode === 'feed') {
            // Return the first text column values as comma-separated
            const textCols = (cardData.colTypes || []).map((t, i) => t === 'text' ? i : -1).filter(i => i >= 0);
            const col = textCols[0] || 0;
            resolved = values.slice(1).map(row => row[col]).filter(v => v != null && v !== '').join(', ');
          } else if (mode === 'result') {
            // Return the first number column values
            const numCols = (cardData.colTypes || []).map((t, i) => t === 'number' ? i : -1).filter(i => i >= 0);
            const col = numCols[0] !== undefined ? numCols[0] : 1;
            resolved = values.slice(1).map(row => row[col]).filter(v => v != null && v !== '').join(', ');
          } else if (mode === 'sum') {
            const numCols = (cardData.colTypes || []).map((t, i) => t === 'number' ? i : -1).filter(i => i >= 0);
            const col = numCols[0] !== undefined ? numCols[0] : 1;
            resolved = String(values.slice(1).reduce((sum, row) => sum + (Number(row[col]) || 0), 0));
          } else if (mode === 'avg') {
            const numCols = (cardData.colTypes || []).map((t, i) => t === 'number' ? i : -1).filter(i => i >= 0);
            const col = numCols[0] !== undefined ? numCols[0] : 1;
            const nums = values.slice(1).map(row => Number(row[col])).filter(n => !isNaN(n));
            resolved = nums.length > 0 ? String(Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 100) / 100) : '0';
          } else {
            // Mode might be a column header name
            const colIdx = headers.findIndex(h => h.toLowerCase() === mode);
            if (colIdx >= 0) {
              resolved = values.slice(1).map(row => row[colIdx]).filter(v => v != null && v !== '').join(', ');
            }
          }

          if (resolved !== null) {
            try {
              const cell = sheet.getRange(r, c, 1, 1);
              cell.setValue(resolved);
            } catch (_) {}
          }
        }
      }
    } catch (e) { /* silently fail */ }
  }

  // Public API
  getAPI() {
    return this.univerAPI;
  }
}
